import { useRef, useEffect, useCallback, useMemo, useState } from 'react';
import * as fabric from 'fabric';
import type {
  EditorTool,
  BrushSettings,
  CanvasNativeExportFormat,
  ShapeSettings,
  ShapeFillValue,
  TextSettings,
  ShapeType,
  MarqueeShape,
  LayerMask,
  LayerType,
  EditorDocument,
  RetouchSettings,
  RetouchVariant,
  PenSettings,
  VectorAnchor,
  VectorPath,
  SliceRect,
} from '../types';
import { computeMarqueeBounds } from '../lib/marquee-bounds';
import { fabricShapeToVectorPath } from '../lib/fabric-shape-to-vector';
import { bboxesDisjoint, isCanvasFullyTransparent } from '../lib/canvas-utils';
import { contoursToSvgPath } from '../lib/marquee-path';
import { traverseDescendants } from '../lib/layer-tree';
import { linearAngleToUnitVector, unitVectorToLinearAngle } from '../lib/gradient-angle';
import { PixelStrokeFilter, PIXEL_STROKE_TYPE } from '../lib/pixel-stroke-filter';
import { snapBoundingBox } from '../lib/snap';
import { smartSnap, type LayerBBox, type SmartSnapMatch } from '../lib/smart-snap';

/** Walk every Fabric object on the canvas and snapshot the
 *  bboxes of those eligible as smart-snap targets — i.e. visible,
 *  non-overlay, NOT the dragged object itself. Called once per
 *  drag (lazy on first `object:moving` tick) and cached for the
 *  rest of that drag; cleared on `mouse:up`. */
function collectSmartSnapTargets(canvas: fabric.Canvas, dragged: fabric.Object): LayerBBox[] {
  const out: LayerBBox[] = [];
  for (const obj of canvas.getObjects()) {
    if (obj === dragged) continue;
    if (!obj.visible) continue;
    const tagged = obj as unknown as {
      _isMarquee?: boolean;
      _isCropRect?: boolean;
      _isCloneReticle?: boolean;
      _isQuickMaskOverlay?: boolean;
      _isTransformOutline?: boolean;
      _isPaintOverlay?: boolean;
      excludeFromExport?: boolean;
      layerId?: string;
    };
    if (
      tagged._isMarquee ||
      tagged._isCropRect ||
      tagged._isCloneReticle ||
      tagged._isQuickMaskOverlay ||
      tagged._isTransformOutline ||
      tagged._isPaintOverlay ||
      tagged.excludeFromExport
    ) {
      continue;
    }
    // Force a coords refresh on each candidate — like the dragged
    // bbox itself, other objects' aCoords may be stale if they
    // were repositioned without a setCoords call. Cheap; runs
    // once per drag, not per tick.
    obj.setCoords();
    const br = obj.getBoundingRect();
    out.push({
      id: tagged.layerId ?? `__fabric_${out.length}__`,
      left: br.left,
      top: br.top,
      width: br.width,
      height: br.height,
    });
  }
  return out;
}
import { PixelGlowFilter, PIXEL_GLOW_TYPE } from '../lib/pixel-glow-filter';
import {
  binaryMaskToCanvas,
  canvasToBinaryMask,
  dilateMask,
  erodeMask,
  polygonPointsToMask,
} from '../lib/mask-morphology';
import { selectionToBinaryMask, invertMaskInPlace, isMaskNonEmpty } from '../lib/selection-mask';
import {
  ensureCanvasElement,
  resolveOrRegisterPaintSurface,
  type PaintSurface,
} from '../lib/paint-surface';
import {
  installPaintSurfaceSerializer,
  installMaskRawSrcCache,
  invalidateMaskRawSrcCache,
} from '../lib/paint-surface-serializer';
import {
  maskMaterializationConfig,
  materializeEffectiveMaskInto,
} from '../lib/mask-materialization';
import { rasterizeVectorMaskInto } from '../lib/vector-mask';
import { rasterizeProjective, type Quad as ProjectiveQuad } from '../lib/projective-transform';
import {
  pathToSvgData,
  recomputeCurvatureHandles,
  populateSmoothAnchorHandles,
  hitTestAnchor,
  hitTestHandle,
  bakeVectorPath,
} from '../lib/vector-path';
import {
  rasterizeWarp,
  defaultWarpGrid,
  applyWarpPreset,
  type WarpGrid,
  type WarpPreset,
} from '../lib/warp-mesh';
import {
  buildCloneBrushMask,
  applyHealToneShift,
  applyMaskedPixelMix,
  buildEdgeExtendedPaddedSource,
  blurredPatchImageData,
  featherStampCanvas,
  supportsCanvasFilterBlur,
  dodgeBurnRangeWeight,
  DODGE_BURN_SPACING_RATIO,
  DODGE_BURN_PER_DAB_SCALE,
  MAX_RETOUCH_LAYER_PIXELS,
} from '../lib/retouch-helpers';
import { createTapTracker } from '../lib/touch-tap-tracker';
import { isTouchEvent } from '../lib/touch-event';
import {
  resolveTextStyleProps,
  ensureInsideStrokeRenderOverride,
  type TextStylingDeps,
} from '../lib/text-styling';
import { ensureFontLoaded, whenFontReady } from '../lib/fonts';
import type { ParsedSvgTextLayer } from '../lib/svg-text-import';
import type { SvgExportTextPayload } from '../lib/svg-export';

const MARQUEE_STROKE_SCREEN_PX = 1;
const MARQUEE_DASH_SCREEN_PX = [5, 3];
const MARQUEE_FILL_ALPHA = 0.1;
const MARQUEE_DEFAULT_COLOR = '#0078d7';

// Path Edit overlay sizing — Tier 2 Group A PR-B. All values in
// SCENE pixels; combined with `strokeUniform: true` on the Fabric
// objects they stay visually consistent across zoom levels.
const PATH_EDIT_ANCHOR_SIZE_SCENE = 8;
const PATH_EDIT_ANCHOR_HIT_RADIUS_SCENE = 6;
const PATH_EDIT_HANDLE_RADIUS_SCENE = 4;
const PATH_EDIT_HANDLE_HIT_RADIUS_SCENE = 6;
const PATH_EDIT_OVERLAY_COLOR = '#0078d7';

// Paint-bucket SVG (Lucide-style) used as the fill tool's cursor. Encoded
// inline so it ships with the bundle without an extra asset round-trip.
// White fill + black stroke keep it visible on any canvas color. Hotspot is
// at the bucket's lower-left tip where paint conceptually drips.
const FILL_CURSOR_SVG =
  `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">` +
  `<path fill="white" d="m19 11-8-8-8.6 8.6a2 2 0 0 0 0 2.8l5.2 5.2c.8.8 2 .8 2.8 0L19 11Z"/>` +
  `<path d="m5 2 5 5"/>` +
  `<path d="M2 13h15"/>` +
  `<path fill="white" d="M22 20a2 2 0 1 1-4 0c0-1.6 1.7-2.4 2-4 .3 1.6 2 2.4 2 4Z"/>` +
  `</svg>`;
const FILL_CURSOR = `url("data:image/svg+xml;utf8,${encodeURIComponent(FILL_CURSOR_SVG)}") 4 20, crosshair`;

const MARQUEE_STYLE: Partial<fabric.FabricObjectProps> = {
  strokeWidth: MARQUEE_STROKE_SCREEN_PX,
  strokeDashArray: [...MARQUEE_DASH_SCREEN_PX],
  strokeUniform: true,
  selectable: false,
  evented: false,
  excludeFromExport: true,
  // Fabric v7 changed the default origin from 'left'/'top' to
  // 'center'/'center'. Our marquee drag math sets `left/top` to the
  // top-left corner via `Math.min(startX, pointer.x)`, then sets
  // `width/height` from the drag delta — that's all top-left
  // semantics. Without this, rect/ellipse marquees would render
  // shifted by (width/2, height/2) from the user's actual drag.
  originX: 'left',
  originY: 'top',
};

/** Tier 2 Group F sub-piece #5 — Slice overlay visual style. Slice
 *  overlays differ from marquees in three ways:
 *    1. Cyan dashed border (not the marquee blue) so the two are
 *       visually distinguishable when both are present (a user can
 *       have a marquee selection AND defined slices simultaneously
 *       on different tools).
 *    2. Translucent fill — picks up clicks for selection / drag.
 *       Without fill, only the border is clickable.
 *    3. Resizable + draggable — selectable / evented / hasControls
 *       are TRUE so Fabric's native selection routes the user's
 *       drag and corner-resize into the standard `object:modified`
 *       event, which we listen to globally for dispatch back to
 *       reducer state. Rotation is locked (slices are axis-aligned). */
const SLICE_FILL = 'rgba(34, 211, 238, 0.10)';
const SLICE_STROKE = '#06b6d4';
const SLICE_STROKE_SCREEN_PX = 1.5;
const SLICE_DASH_SCREEN_PX: readonly number[] = [6, 4];

/** In-flight drag preview style — same color cue as committed
 *  slices, but no controls (the drag hasn't finalized so resize
 *  handles would be confusing). On `mouse:up` the pending rect is
 *  discarded and a real overlay is mounted from the dispatched
 *  state. */
const SLICE_PENDING_STYLE: Partial<fabric.FabricObjectProps> = {
  fill: SLICE_FILL,
  stroke: SLICE_STROKE,
  strokeWidth: SLICE_STROKE_SCREEN_PX,
  strokeDashArray: [...SLICE_DASH_SCREEN_PX],
  strokeUniform: true,
  selectable: false,
  evented: false,
  excludeFromExport: true,
  hasControls: false,
  originX: 'left',
  originY: 'top',
};

/** Build a Fabric Control wired to a skew-only action handler.
 *  Used by Free Transform's Skew sub-mode to override the default
 *  mid-edge controls (which scale-or-skew based on Alt modifier)
 *  with controls that skew unconditionally on bare drag.
 *
 *  `position` encodes the edge: 'x-' = ml, 'x+' = mr, 'y-' = mt,
 *  'y+' = mb. The axis MAPPING matches Fabric's own
 *  `scaleOrSkewActionName` convention (commonControls.ts +
 *  scaleSkew.ts):
 *    - control.y === 0 (left/right edges, ml/mr) → skew Y axis
 *    - control.x === 0 (top/bottom edges, mt/mb) → skew X axis
 *  Geometrically intuitive: dragging the LEFT edge up/down shears
 *  the rect vertically (Y skew); dragging the TOP edge left/right
 *  shears horizontally (X skew). My initial implementation had
 *  this mapping inverted — handlers ran but bailed out of the
 *  lockSkewing check on the wrong axis, producing the "do
 *  nothing" symptom on mid-edge drag.
 *
 *  The pre-transform control is passed in so we inherit visual
 *  sizing — only the action handler changes. */
function buildSkewMidEdgeControl(
  position: 'x-' | 'x+' | 'y-' | 'y+',
  base: fabric.Control,
): fabric.Control {
  const x = position === 'x-' ? -0.5 : position === 'x+' ? 0.5 : 0;
  const y = position === 'y-' ? -0.5 : position === 'y+' ? 0.5 : 0;
  const isHorizontalEdge = position === 'x-' || position === 'x+';
  return new fabric.Control({
    x,
    y,
    actionHandler: isHorizontalEdge
      ? fabric.controlsUtils.skewHandlerY
      : fabric.controlsUtils.skewHandlerX,
    cursorStyleHandler: fabric.controlsUtils.skewCursorStyleHandler,
    actionName: 'skewing',
    // Inherit visual styling from the default control so the
    // user-facing handle size stays consistent across modes.
    sizeX: base.sizeX,
    sizeY: base.sizeY,
    touchSizeX: base.touchSizeX,
    touchSizeY: base.touchSizeY,
  });
}

/** Build the dashed Fabric overlay objects that visually connect
 *  the non-affine Transform handles. For Distort/Perspective: a
 *  closed dashed quad through the 4 corners. For Warp: dashed
 *  rows + columns of the 3×3 grid showing the mesh structure.
 *
 *  Outlines render UNDER the handles so users can grab handles
 *  cleanly even when they overlap an outline edge. */
function buildNonAffineOutlines(
  mode: 'distort' | 'perspective' | 'warp',
  points: readonly { x: number; y: number }[],
): fabric.FabricObject[] {
  const lineStyle: Partial<fabric.FabricObjectProps> = {
    stroke: '#0078d7',
    strokeWidth: 1,
    strokeUniform: true,
    strokeDashArray: [4, 3],
    fill: 'transparent',
    selectable: false,
    evented: false,
    hoverCursor: 'default',
    objectCaching: false,
    excludeFromExport: true,
    originX: 'left',
    originY: 'top',
  };
  if (mode !== 'warp') {
    const polyline = new fabric.Polyline(
      [
        { x: points[0].x, y: points[0].y },
        { x: points[1].x, y: points[1].y },
        { x: points[2].x, y: points[2].y },
        { x: points[3].x, y: points[3].y },
        { x: points[0].x, y: points[0].y },
      ],
      lineStyle,
    );
    (polyline as unknown as { _isTransformOutline: boolean })._isTransformOutline = true;
    return [polyline];
  }
  // Warp: 3 rows + 3 columns. Each rendered as its own Polyline so
  // the mesh visualization is clear. Indices in 3×3 grid:
  //   0  1  2
  //   3  4  5
  //   6  7  8
  const rows = [
    [points[0], points[1], points[2]],
    [points[3], points[4], points[5]],
    [points[6], points[7], points[8]],
  ];
  const cols = [
    [points[0], points[3], points[6]],
    [points[1], points[4], points[7]],
    [points[2], points[5], points[8]],
  ];
  const lines: fabric.FabricObject[] = [];
  for (const r of rows) {
    const pl = new fabric.Polyline(
      r.map((p) => ({ x: p.x, y: p.y })),
      lineStyle,
    );
    (pl as unknown as { _isTransformOutline: boolean })._isTransformOutline = true;
    lines.push(pl);
  }
  for (const c of cols) {
    const pl = new fabric.Polyline(
      c.map((p) => ({ x: p.x, y: p.y })),
      lineStyle,
    );
    (pl as unknown as { _isTransformOutline: boolean })._isTransformOutline = true;
    lines.push(pl);
  }
  return lines;
}

/** Build the identity-warp 3×3 grid spanning the given scene-space
 *  bounding box. Wraps `defaultWarpGrid` from the warp-mesh lib by
 *  offsetting from the bbox origin. */
function defaultWarpGridForBbox(bbox: {
  left: number;
  top: number;
  width: number;
  height: number;
}): WarpGrid {
  const base = defaultWarpGrid(bbox.width, bbox.height);
  return base.map((p) => ({ x: p.x + bbox.left, y: p.y + bbox.top })) as unknown as WarpGrid;
}

/** Inputs the rasterize-and-commit path reads from a non-affine
 *  transform session — the subset shared by the live-preview tick
 *  and the Apply-time bake. Both call sites build their density
 *  from a session-stored source canvas (preview-downscaled for
 *  live, full-resolution for bake), so this type only captures
 *  the deformation state itself. */
export interface NonAffineDeformation {
  mode: 'distort' | 'perspective' | 'warp';
  distortQuad: ProjectiveQuad;
  warpGrid: WarpGrid;
  /** Original (pre-deformation) bbox of the layer in scene coords.
   *  Used solely to compute the destination canvas's pixel density
   *  (source.width / bbox.width). */
  bbox: { left: number; top: number; width: number; height: number };
}

/** Rasterize the current deformation into a fresh destination
 *  canvas and commit it to the target FabricImage. Used by both
 *  the live-preview tick (with `source = sourcePreview`) and the
 *  Apply-time bake (with `source = sourceFull`). Differences
 *  between the two callers are encoded entirely in the `source`
 *  and `subdivisions` arguments — the AABB-fitting math, the
 *  density math, the `setElement`/matrix commit are identical.
 *
 *  The matrix commit is a deliberate full reset: scale to map
 *  destination-canvas pixels back to scene units of the AABB,
 *  and zero the rotation/skew/flip terms. This is what makes
 *  successive ticks composable — each tick replaces the target's
 *  element and matrix from scratch rather than accumulating
 *  transform on top of the previous tick. */
export function commitNonAffineDeformation(
  target: fabric.FabricImage,
  source: HTMLCanvasElement,
  deformation: NonAffineDeformation,
  subdivisions: { projective: number; warp: number },
): void {
  const handlesPts: { x: number; y: number }[] =
    deformation.mode === 'warp' ? [...deformation.warpGrid] : [...deformation.distortQuad];
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of handlesPts) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  const dW = Math.max(1, maxX - minX);
  const dH = Math.max(1, maxY - minY);
  const density = source.width / deformation.bbox.width;
  const dest = document.createElement('canvas');
  dest.width = Math.max(1, Math.round(dW * density));
  dest.height = Math.max(1, Math.round(dH * density));
  const sx = dest.width / dW;
  const sy = dest.height / dH;
  const toLocal = (p: { x: number; y: number }) => ({
    x: (p.x - minX) * sx,
    y: (p.y - minY) * sy,
  });
  if (deformation.mode === 'distort' || deformation.mode === 'perspective') {
    const q: ProjectiveQuad = [
      toLocal(deformation.distortQuad[0]),
      toLocal(deformation.distortQuad[1]),
      toLocal(deformation.distortQuad[2]),
      toLocal(deformation.distortQuad[3]),
    ];
    rasterizeProjective(source, q, dest, { subdivisions: subdivisions.projective });
  } else {
    const g = deformation.warpGrid.map(toLocal) as unknown as WarpGrid;
    rasterizeWarp(source, g, dest, { subdivisions: subdivisions.warp });
  }
  target.setElement(dest);
  target.set({
    left: minX,
    top: minY,
    scaleX: dW / dest.width,
    scaleY: dH / dest.height,
    angle: 0,
    skewX: 0,
    skewY: 0,
    flipX: false,
    flipY: false,
  });
  target.setCoords();
  target.dirty = true;
}

function hexToRgba(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
    return hex;
  }
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function applyMarqueeColor(obj: fabric.Object, color: string): void {
  obj.set({
    stroke: color,
    fill: hexToRgba(color, MARQUEE_FILL_ALPHA),
  });
}

const CROP_RECT_STYLE: Partial<fabric.FabricObjectProps> = {
  fill: 'rgba(0, 120, 215, 0.08)',
  stroke: '#0078d7',
  strokeUniform: true,
  hasControls: true,
  hasBorders: true,
  borderColor: '#0078d7',
  borderDashArray: [5, 3],
  lockRotation: true,
  lockScalingFlip: true,
  // The crop box is resized via its handles only — it must NOT pan as a
  // whole. Photoshop's crop frame stays anchored (dragging the interior
  // moves the image beneath a stationary frame); a free-floating crop
  // rectangle you can drag anywhere on the canvas is non-standard and was
  // easy to do by accident, nudging the region off the intended area.
  // Movement is locked here; scaling via the corner/edge controls is
  // unaffected (lockMovement only blocks object drag, not control resize).
  lockMovementX: true,
  lockMovementY: true,
  selectable: true,
  evented: true,
  excludeFromExport: true,
  objectCaching: false,
  // v7 default origin is center; the crop rect is positioned by its
  // top-left corner via `left: 0, top: 0` + explicit width/height.
  originX: 'left',
  originY: 'top',
  cornerColor: '#ffffff',
  cornerStrokeColor: '#0078d7',
  cornerSize: 12,
  transparentCorners: false,
  cornerStyle: 'rect',
};

/**
 * Build a binary selection mask using either flood-fill (contiguous) or a global
 * threshold scan (non-contiguous). The tolerance is the maximum allowed
 * per-channel difference (0–255, Photoshop-style).
 */
function computeWandMask(
  imgData: ImageData,
  startX: number,
  startY: number,
  tolerance: number,
  contiguous: boolean,
): Uint8Array {
  const { data, width, height } = imgData;
  const mask = new Uint8Array(width * height);

  if (startX < 0 || startX >= width || startY < 0 || startY >= height) return mask;

  const startIdx = (startY * width + startX) * 4;
  const sr = data[startIdx];
  const sg = data[startIdx + 1];
  const sb = data[startIdx + 2];

  const matches = (i: number): boolean => {
    const di = i * 4;
    return (
      Math.abs(data[di] - sr) <= tolerance &&
      Math.abs(data[di + 1] - sg) <= tolerance &&
      Math.abs(data[di + 2] - sb) <= tolerance
    );
  };

  if (contiguous) {
    const queue: number[] = [startY * width + startX];
    mask[startY * width + startX] = 1;
    let head = 0;
    while (head < queue.length) {
      const idx = queue[head++];
      const x = idx % width;
      const y = (idx - x) / width;
      const neighbors: [number, number][] = [
        [x + 1, y],
        [x - 1, y],
        [x, y + 1],
        [x, y - 1],
      ];
      for (const [nx, ny] of neighbors) {
        if (nx < 0 || nx >= width || ny < 0 || ny >= height) continue;
        const nidx = ny * width + nx;
        if (mask[nidx]) continue;
        if (matches(nidx)) {
          mask[nidx] = 1;
          queue.push(nidx);
        }
      }
    }
  } else {
    for (let i = 0; i < width * height; i++) {
      if (matches(i)) mask[i] = 1;
    }
  }

  return mask;
}

/**
 * Trace the boundary contours of a binary mask. Walks pixel-corner edges
 * between selected and unselected cells, then stitches edges into closed
 * polygons. Returns one polygon per disconnected region (and one per hole).
 */
function traceContours(mask: Uint8Array, w: number, h: number): { x: number; y: number }[][] {
  const isSelected = (x: number, y: number): boolean =>
    x >= 0 && x < w && y >= 0 && y < h && mask[y * w + x] === 1;

  type Pt = { x: number; y: number };
  const ptKey = (p: Pt) => `${p.x},${p.y}`;
  const edgeKey = (a: Pt, b: Pt) =>
    a.x < b.x || (a.x === b.x && a.y < b.y)
      ? `${a.x},${a.y}-${b.x},${b.y}`
      : `${b.x},${b.y}-${a.x},${a.y}`;

  const edges: [Pt, Pt][] = [];
  const adj = new Map<string, [Pt, Pt][]>();

  const addEdge = (a: Pt, b: Pt) => {
    const e: [Pt, Pt] = [a, b];
    edges.push(e);
    const ka = ptKey(a);
    const kb = ptKey(b);
    if (!adj.has(ka)) adj.set(ka, []);
    if (!adj.has(kb)) adj.set(kb, []);
    adj.get(ka)!.push(e);
    adj.get(kb)!.push(e);
  };

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (!isSelected(x, y)) continue;
      if (!isSelected(x, y - 1)) addEdge({ x, y }, { x: x + 1, y });
      if (!isSelected(x + 1, y)) addEdge({ x: x + 1, y }, { x: x + 1, y: y + 1 });
      if (!isSelected(x, y + 1)) addEdge({ x, y: y + 1 }, { x: x + 1, y: y + 1 });
      if (!isSelected(x - 1, y)) addEdge({ x, y }, { x, y: y + 1 });
    }
  }

  const visited = new Set<string>();
  const contours: Pt[][] = [];

  for (const startEdge of edges) {
    if (visited.has(edgeKey(startEdge[0], startEdge[1]))) continue;
    const polygon: Pt[] = [startEdge[0]];
    let currentEdge = startEdge;
    let currentPoint = startEdge[0];
    while (true) {
      visited.add(edgeKey(currentEdge[0], currentEdge[1]));
      const nextPoint =
        currentEdge[0].x === currentPoint.x && currentEdge[0].y === currentPoint.y
          ? currentEdge[1]
          : currentEdge[0];
      polygon.push(nextPoint);
      const candidates = adj.get(ptKey(nextPoint)) || [];
      const nextEdge = candidates.find((e) => !visited.has(edgeKey(e[0], e[1])));
      if (!nextEdge) break;
      currentEdge = nextEdge;
      currentPoint = nextPoint;
    }
    if (polygon.length >= 3) contours.push(polygon);
  }

  return contours;
}

/** Translate a `ShapeSettings.fill` value into a Fabric-compatible
 *  fill — string for solid colors, `fabric.Gradient` for linear /
 *  radial. Plain hex strings (the legacy/most-common shape) pass
 *  through unchanged. The gradient is built in normalized coords
 *  (0..1) over the shape's bounding box via `gradientUnits:
 *  'percentage'`, so the same value renders consistently across
 *  rect/circle/triangle without us having to plumb width/height
 *  through every call site. */
type FabricShapeFill = string | fabric.Gradient<'linear'> | fabric.Gradient<'radial'>;

function resolveShapeFill(fill: string | ShapeFillValue): FabricShapeFill {
  if (typeof fill === 'string') return fill;
  if (fill.type === 'solid') return fill.color;

  const colorStops = [...fill.stops]
    .sort((a, b) => a.offset - b.offset)
    .map((s) => ({ offset: s.offset, color: s.color }));

  if (fill.type === 'linear') {
    const { dx, dy } = linearAngleToUnitVector(fill.angle);
    return new fabric.Gradient<'linear'>({
      type: 'linear',
      gradientUnits: 'percentage',
      coords: { x1: 0.5 - dx, y1: 0.5 - dy, x2: 0.5 + dx, y2: 0.5 + dy },
      colorStops,
    });
  }

  // Radial — center at 0.5,0.5; outer radius 0.5 (so it just fills
  // the shape's bounding box). Inner radius 0 = a single point.
  return new fabric.Gradient<'radial'>({
    type: 'radial',
    gradientUnits: 'percentage',
    coords: { x1: 0.5, y1: 0.5, r1: 0, x2: 0.5, y2: 0.5, r2: 0.5 },
    colorStops,
  });
}

/** Read a Fabric fill back into a `ShapeFillValue`. Round-trips
 *  whatever `resolveShapeFill` produced (within rounding error on
 *  the angle). Falls back to `'transparent'` for non-string,
 *  non-Gradient fills (e.g., Pattern), which the panel can render
 *  but not edit. */
function readShapeFill(fill: fabric.FabricObject['fill']): string | ShapeFillValue {
  if (typeof fill === 'string') return fill;
  if (fill instanceof fabric.Gradient) {
    const stops = ((fill.colorStops ?? []) as Array<{ offset: number; color: string }>).map(
      (s) => ({ offset: s.offset, color: s.color }),
    );
    if (fill.type === 'linear') {
      const c = fill.coords as { x1: number; y1: number; x2: number; y2: number };
      const angle = unitVectorToLinearAngle(c.x2 - c.x1, c.y2 - c.y1);
      return { type: 'linear', angle: Math.round(angle), stops };
    }
    return { type: 'radial', stops };
  }
  return 'transparent';
}

/** Convert a list of contour vertex arrays into a single fabric.Path
 *  with `fillRule: 'evenodd'` so nested contours render as holes
 *  rather than overlapping fills. Returns null when there are no
 *  drawable contours.
 *
 *  Why a single Path: traceContours can return an outer perimeter
 *  plus one or more inner silhouettes (e.g., after invert, or for
 *  masks with holes like a person standing with arm akimbo).
 *  Rendering each contour as its own fabric.Polygon with a
 *  translucent fill caused the holes to fill IN ON TOP of the outer
 *  fill — the user reads that as "everything is selected" rather
 *  than "the donut between contours is selected." A Path with
 *  evenodd fill correctly subtracts inner contours from outer ones,
 *  matching what Photoshop / Affinity show. */
function buildMarqueePath(contours: { x: number; y: number }[][]): fabric.Path | null {
  const d = contoursToSvgPath(contours);
  if (!d) return null;
  const path = new fabric.Path(d, {
    ...MARQUEE_STYLE,
    fillRule: 'evenodd',
    objectCaching: false,
  });
  return path;
}

function buildCropRect(canvas: fabric.Canvas, width: number, height: number): fabric.Rect {
  const zoom = canvas.getZoom() || 1;
  const rect = new fabric.Rect({
    ...CROP_RECT_STYLE,
    left: 0,
    top: 0,
    width,
    height,
    strokeWidth: 1 / zoom,
    strokeDashArray: [5 / zoom, 3 / zoom],
  });
  (rect as unknown as { _isCropRect: boolean })._isCropRect = true;
  return rect;
}

function tagAsMarquee(obj: fabric.Object): void {
  (obj as unknown as { _isMarquee: boolean })._isMarquee = true;
}

function isMarqueeObject(obj: fabric.Object): boolean {
  return (obj as unknown as { _isMarquee?: boolean })._isMarquee === true;
}

/** Build a slice overlay Fabric.Rect with selectable controls
 *  enabled. The rect is positioned at the slice's scene-space
 *  origin; resize and move are handled by Fabric's native controls
 *  and reported via `object:modified`. Stroke + dash widths are
 *  counter-zoomed so the visual outline stays constant on-screen
 *  regardless of viewport zoom. Rotation is locked to keep slices
 *  axis-aligned (PSD / Photoshop don't support rotated slices in
 *  v1 either). The caller's `tagAsSliceOverlay` runs after this
 *  returns; the helper itself stays purely geometric. */
function buildSliceOverlay(canvas: fabric.Canvas, slice: SliceRect): fabric.Rect {
  const zoom = canvas.getZoom() || 1;
  const rect = new fabric.Rect({
    left: slice.x,
    top: slice.y,
    width: slice.width,
    height: slice.height,
    fill: SLICE_FILL,
    stroke: SLICE_STROKE,
    strokeWidth: SLICE_STROKE_SCREEN_PX / zoom,
    strokeDashArray: SLICE_DASH_SCREEN_PX.map((v) => v / zoom),
    strokeUniform: true,
    originX: 'left',
    originY: 'top',
    selectable: true,
    evented: true,
    hasControls: true,
    hasBorders: false,
    lockRotation: true,
    lockSkewingX: true,
    lockSkewingY: true,
    objectCaching: false,
    excludeFromExport: true,
  });
  // Hide the rotation handle (`mtr`) — slices are axis-aligned.
  // Keep all eight corner / edge handles so the user can resize
  // from any direction.
  rect.setControlsVisibility({ mtr: false });
  return rect;
}

/** When a slice overlay is resized via Fabric's native corner
 *  controls, Fabric mutates `scaleX` / `scaleY` rather than
 *  `width` / `height`. Before dispatching UPDATE_SLICE we need to
 *  bake the scale into the dimensions so the document's
 *  `SliceRect` carries a faithful "scene-space width × height"
 *  (not a width × scaleX product the export pipeline would have
 *  to recompute). Mirrors the standard Fabric idiom; also resets
 *  the controls so subsequent drags start from scale=1. */
function normalizeSliceScaleIntoDimensions(rect: fabric.Rect): {
  x: number;
  y: number;
  width: number;
  height: number;
} {
  // Fabric's resize controls can produce negative scaleX/scaleY when
  // the user drags a corner past the opposite corner (Fabric represents
  // a horizontally / vertically "flipped" rect as scale * -1, NOT as
  // a sign flip on width). Without `Math.abs`, multiplying through
  // collapses to a negative which `Math.max(1, ...)` then clamps to 1
  // — silently dropping the user's resize to a 1×1 pixel slice. The
  // sign carries no semantic meaning for axis-aligned slices (we
  // already lock rotation + skew at the control level), so we
  // collapse it absolutely. Also: Fabric repositions the rect's
  // `left`/`top` to the new top-left when a negative scale is
  // committed (it tracks the visual origin), so reading them back
  // post-modify is still correct.
  const scaleX = Math.abs(rect.scaleX ?? 1);
  const scaleY = Math.abs(rect.scaleY ?? 1);
  const width = Math.max(1, Math.round((rect.width ?? 0) * scaleX));
  const height = Math.max(1, Math.round((rect.height ?? 0) * scaleY));
  rect.set({ width, height, scaleX: 1, scaleY: 1 });
  rect.setCoords();
  return {
    x: rect.left ?? 0,
    y: rect.top ?? 0,
    width,
    height,
  };
}

/** Tier 2 Group F sub-piece #5 — Slice tool overlay tagging. Matches
 *  the `_isMarquee` / `_isCropRect` pattern. Slice overlay objects
 *  are created/torn-down by `setTool('slice')` and removed when the
 *  user switches away — they are NOT persisted in `canvas.toJSON()`
 *  (the slice metadata lives on `ImageEditorState.slices` and is
 *  serialized into the IDB session record separately, never as a
 *  Fabric object). Tagged Rects are exempted from the tool lock
 *  loops so Fabric's native selection / resize / drag controls
 *  work on slice overlays. */
function tagAsSliceOverlay(obj: fabric.Object, sliceId: string): void {
  const tagged = obj as unknown as { _isSliceOverlay: true; _sliceId: string };
  tagged._isSliceOverlay = true;
  tagged._sliceId = sliceId;
  // Mark as excluded from `canvas.toJSON()` so slice overlays never
  // round-trip through session save/load; the SliceRect array on
  // ImageEditorState is the single source of truth.
  (obj as unknown as { excludeFromExport: boolean }).excludeFromExport = true;
}

function isSliceOverlay(obj: fabric.Object): boolean {
  return (obj as unknown as { _isSliceOverlay?: boolean })._isSliceOverlay === true;
}

function getSliceIdFromObject(obj: fabric.Object): string | undefined {
  return (obj as unknown as { _sliceId?: string })._sliceId;
}

function setObjectLayerId(obj: fabric.Object, layerId: string): void {
  (obj as unknown as { layerId: string }).layerId = layerId;
}

export function getObjectLayerId(obj: fabric.Object): string | undefined {
  return (obj as unknown as { layerId?: string }).layerId;
}

/** Yields every FabricImage on the canvas paired with its layer id —
 *  the candidate set `resolveOrRegisterPaintSurface` scans to lazily
 *  register a paint surface for a layer that's never been brush-touched.
 *  A generator so the scan is lazy: the resolver short-circuits when the
 *  layer already has a registered surface (the common case) and never
 *  iterates this at all. Shared by every active-layer paint/snapshot path
 *  (`getActiveLayerSnapshot`, `runOnActiveLayerFullImage`,
 *  `runSmartDeleteOnActiveLayer`, `runUpscaleOnActiveLayer`) so the
 *  read-side and write-side resolve a layer's surface identically and
 *  can't drift — a divergence would let an AI tool read one layer and
 *  write another. */
function* imageLayerCandidates(
  canvas: fabric.Canvas,
): Iterable<{ id: string; image: fabric.FabricImage }> {
  for (const obj of canvas.getObjects()) {
    if (!(obj instanceof fabric.FabricImage)) continue;
    const id = getObjectLayerId(obj);
    if (!id) continue;
    yield { id, image: obj };
  }
}

/**
 * Build a curved Fabric Path that text can follow. The slider value (-100..100)
 * scales an apex offset proportional to the text width; positive arches up
 * (smile), negative arches down (frown). Returns null for a flat (0) value or
 * non-positive width.
 */
function buildTextArchPath(width: number, arch: number): fabric.Path | null {
  if (arch === 0 || width <= 0) return null;
  // Apex height ≈ 25% of text width at full slider; control point is twice
  // the apex offset from the chord (quadratic-bezier midpoint property).
  const apex = (arch / 100) * (width * 0.25);
  const controlY = -apex * 2;
  const pathData = `M 0 0 Q ${width / 2} ${controlY} ${width} 0`;
  const path = new fabric.Path(pathData, {
    fill: '',
    stroke: '',
    objectCaching: false,
    visible: false,
  });
  return path;
}

function tagAsBackground(obj: fabric.Object): void {
  (obj as unknown as { _isBackground: boolean })._isBackground = true;
}

function isBackgroundImage(obj: fabric.Object): boolean {
  return (obj as unknown as { _isBackground?: boolean })._isBackground === true;
}

/** Tag the canvas-bounds outline so we can find / skip it during
 *  selection, hit-testing, export, and JSON serialization. The
 *  outline is chrome — not a layer, not part of the document. */
function tagAsCanvasBoundsOverlay(obj: fabric.Object): void {
  (obj as unknown as { _isCanvasBoundsOverlay: boolean })._isCanvasBoundsOverlay = true;
  (obj as unknown as { excludeFromExport: boolean }).excludeFromExport = true;
}

function isCanvasBoundsOverlay(obj: fabric.Object): boolean {
  return (obj as unknown as { _isCanvasBoundsOverlay?: boolean })._isCanvasBoundsOverlay === true;
}

/** Tag the canvas-fill rect (Extension fill from the Canvas Size
 *  dialog). Distinct from the bounds outline because they have
 *  different stack-order needs (fill above outline) and different
 *  toJSON behavior (excludeFromExport so the fill doesn't persist
 *  across session reloads — fills are session-scoped in v1). */
function tagAsCanvasFillOverlay(obj: fabric.Object): void {
  (obj as unknown as { _isCanvasFillOverlay: boolean })._isCanvasFillOverlay = true;
  (obj as unknown as { excludeFromExport: boolean }).excludeFromExport = true;
}

function isCanvasFillOverlay(obj: fabric.Object): boolean {
  return (obj as unknown as { _isCanvasFillOverlay?: boolean })._isCanvasFillOverlay === true;
}

function isCanvasChrome(obj: fabric.Object): boolean {
  return isCanvasBoundsOverlay(obj) || isCanvasFillOverlay(obj);
}

/** Run a render-producing callback (e.g. `canvas.toDataURL`) with
 *  the canvas-bounds outline temporarily hidden, then restore it.
 *  The outline is editor chrome — its 1px gray stroke would
 *  otherwise bake a faint border into every raster export.
 *
 *  Synchronous: `toDataURL` is itself synchronous, and the only
 *  current callers wrap a single sync call. If a future async caller
 *  needs this, hoist the restore into a try/finally chain. */
function runWithoutCanvasBoundsOverlay<T>(canvas: fabric.Canvas, fn: () => T): T {
  const outline = canvas.getObjects().find(isCanvasBoundsOverlay);
  if (!outline) return fn();
  const wasVisible = outline.visible !== false;
  if (wasVisible) outline.visible = false;
  try {
    return fn();
  } finally {
    if (wasVisible) outline.visible = true;
  }
}

/** Constrain Fabric's render to the document's canvas bounds via
 *  PER-OBJECT clipPath. Without this, objects extending past (W, H)
 *  — e.g. a Background layer whose pixel buffer extends beyond a
 *  shrunken canvas — render in the pasteboard area too.
 *
 *  Per-object rather than canvas-level (`canvas.clipPath`) because
 *  the canvas-level approach also clips selection chrome (the
 *  bounding box and handles Fabric draws around the active object).
 *  Per-object clipPath only constrains the object's own pixel
 *  rendering — selection chrome stays free to render at the
 *  object's full bounding box, so the user can see how large a
 *  layer actually is when it extends past the visible canvas.
 *
 *  The clip rect is `absolutePositioned: true` (scene coords) so a
 *  single shared rect can be assigned to every object. Canvas
 *  chrome (the bounds outline AND the fill rect) is skipped — both
 *  are already scoped to canvas bounds by their own geometry, and
 *  clipping the outline would cut its stroke in half. Idempotent. */
function ensureCanvasClipRegion(canvas: fabric.Canvas, w: number, h: number): void {
  if (w <= 0 || h <= 0) {
    canvas.getObjects().forEach((obj) => {
      if (isCanvasChrome(obj)) return;
      // Only clear OUR clip rects; user masks (FabricImage clipPaths)
      // stay put. A `w<=0` collapse shouldn't blow away a user-painted
      // layer mask alongside the canvas-bounds clip.
      if (obj.clipPath instanceof fabric.FabricImage) return;
      obj.clipPath = undefined;
    });
    return;
  }
  // Per-object clipPath instance — matches the `object:added` handler's
  // pattern. Sharing a single rect across many objects works today
  // because Fabric reads clipPath but doesn't mutate it, but a future
  // Fabric version or a custom renderer that touches clipPath state
  // would propagate the mutation across every sharer. Per-object
  // instances cost one rect allocation per object on resize — cheap
  // enough not to optimize prematurely.
  canvas.getObjects().forEach((obj) => {
    if (isCanvasChrome(obj)) return;
    // Preserve existing layer-mask clipPaths. Any FabricImage clipPath
    // on a layer is a user mask (the only path that assigns FabricImage
    // clipPaths is addLayerMask / applyLayerMaskFromCanvas; rect
    // clipPaths are this helper's own canvas-bounds clip). Without this
    // guard, every loadFromJSON / canvas-resize would blindly overwrite
    // the mask with the canvas-bounds rect — the mask survived the
    // serialize round-trip just to get wiped post-load. User-mask
    // clipPaths are sized to canvas dims with absolutePositioned, so
    // they already constrain rendering to the canvas bounds via their
    // alpha (pixels outside the mask's element = alpha 0 = clipped).
    if (obj.clipPath instanceof fabric.FabricImage) return;
    obj.clipPath = buildCanvasClipRect(w, h);
  });
}

/** Create / resize the canvas-fill rect to (0, 0, w, h) with the
 *  given color. `null` color removes the rect entirely (transparent
 *  fill — what the user sees through is the wrapper bg / their
 *  transparency checker toggle). Idempotent — call any time the
 *  dims OR the fill color changes. */
function ensureCanvasFillOverlay(
  canvas: fabric.Canvas,
  w: number,
  h: number,
  color: string | null,
): void {
  const existing = canvas.getObjects().find(isCanvasFillOverlay) as fabric.Rect | undefined;
  if (color === null || w <= 0 || h <= 0) {
    if (existing) canvas.remove(existing);
    return;
  }
  if (existing) {
    existing.set({ width: w, height: h, fill: color });
    existing.setCoords();
    return;
  }
  const fill = new fabric.Rect({
    left: 0,
    top: 0,
    originX: 'left',
    originY: 'top',
    width: w,
    height: h,
    fill: color,
    stroke: '',
    strokeWidth: 0,
    selectable: false,
    evented: false,
    hoverCursor: 'default',
  });
  tagAsCanvasFillOverlay(fill);
  canvas.add(fill);
}

/** Restore chrome stack order: fill at the very back, outline at
 *  the very front. Call any time chrome is added OR a new user
 *  object is added (Fabric appends new objects to the top, pushing
 *  outline below them).
 *
 *  Final order: fill (back-most) → user layers → outline (front-
 *  most). Fill behind layers so it shows only through transparent
 *  pixels of upper layers. Outline in FRONT so its 1px stroke is
 *  always visible — including over opaque-source images like a
 *  loaded JPEG that would otherwise hide a behind-everything
 *  outline. The stroke renders at canvas bounds, half inside
 *  (over content) and half outside (over pasteboard); the inside
 *  half is the visible canvas-edge marker. */
function restackCanvasChrome(canvas: fabric.Canvas): void {
  const fill = canvas.getObjects().find(isCanvasFillOverlay);
  const outline = canvas.getObjects().find(isCanvasBoundsOverlay);
  if (fill) canvas.sendObjectToBack(fill);
  if (outline) canvas.bringObjectToFront(outline);
}

/** Per-object clip rectangle at scene (0, 0, w, h). One shared
 *  instance per resize is fine — Fabric reads clipPath each render
 *  but doesn't mutate it. */
function buildCanvasClipRect(w: number, h: number): fabric.Rect {
  return new fabric.Rect({
    left: 0,
    top: 0,
    originX: 'left',
    originY: 'top',
    width: w,
    height: h,
    absolutePositioned: true,
  });
}

/** Create / update the canvas-bounds outline at (0, 0, w, h) AND
 *  refresh the canvas's clip region AND resize the canvas-fill rect
 *  to match. Three pieces of chrome that all track the document's
 *  canvas size; the seven dim-change sites only have to call one
 *  helper. Outline stroke uses `strokeUniform` so the line stays
 *  1 screen-px at every zoom. Idempotent.
 *
 *  Fill color is supplied — callers pass `undefined` to leave the
 *  fill rect's color unchanged (just resize), or an explicit color
 *  / `null` to update it. The dim-change sites in the canvas hook
 *  read the current color from `canvasFillColorRef` and pass it
 *  through here; the dedicated `setCanvasFillColor` setter passes
 *  the new color directly. */
function ensureCanvasBoundsOverlay(
  canvas: fabric.Canvas,
  w: number,
  h: number,
  fillColor?: string | null,
): void {
  ensureCanvasClipRegion(canvas, w, h);
  if (fillColor !== undefined) {
    ensureCanvasFillOverlay(canvas, w, h, fillColor);
  } else {
    // Just resize the existing fill rect if there is one (color
    // change is a separate operation handled by setCanvasFillColor).
    const existing = canvas.getObjects().find(isCanvasFillOverlay) as fabric.Rect | undefined;
    if (existing && w > 0 && h > 0) {
      existing.set({ width: w, height: h });
      existing.setCoords();
    }
  }
  if (w <= 0 || h <= 0) {
    restackCanvasChrome(canvas);
    return;
  }
  const existing = canvas.getObjects().find(isCanvasBoundsOverlay) as fabric.Rect | undefined;
  if (existing) {
    existing.set({ width: w, height: h });
    existing.setCoords();
  } else {
    const outline = new fabric.Rect({
      left: 0,
      top: 0,
      originX: 'left',
      originY: 'top',
      width: w,
      height: h,
      fill: '',
      // Mid-tone gray with partial alpha — visible against both light
      // (white canvas, default fill) and dark (pasteboard) backdrops
      // without being garishly prominent.
      stroke: 'rgba(127, 127, 127, 0.6)',
      strokeWidth: 1,
      strokeUniform: true,
      selectable: false,
      evented: false,
      hoverCursor: 'default',
    });
    tagAsCanvasBoundsOverlay(outline);
    canvas.add(outline);
  }
  restackCanvasChrome(canvas);
}

/** True when `obj` is a paint-surface OVERLAY created by
 *  `ensurePaintSurface` for a non-bg image layer (tagged with
 *  `id: 'paint_<layerId>'`). These overlays share the underlying
 *  layer's id-tag so brush/retouch handlers can find their write
 *  target by layerId, but they are doc-sized transparent
 *  FabricImages — NOT user-selectable layer content. The bg
 *  paint surface is the bg image itself (no separate overlay)
 *  so this helper returns false for it. */
function isPaintOverlay(obj: fabric.Object): boolean {
  const id = (obj as fabric.Object & { id?: string }).id;
  return typeof id === 'string' && id.startsWith('paint_');
}

/** Custom property name used to attach the user-painted RAW alpha
 *  canvas to a mask FabricImage. The clipPath FabricImage's element
 *  is the EFFECTIVE alpha (post feather/invert/density); the RAW
 *  canvas is the source of truth that brush strokes paint into and
 *  that materialization derives the effective from.
 *
 *  Lazy allocation: sessions saved before the materialization split
 *  shipped only have the effective canvas (which equalled raw at the
 *  time, since slow-path features were no-ops). On first paint /
 *  config change after load, `ensureRawMaskCanvas` copies the
 *  effective into a fresh raw — perfect in the common case where
 *  config hasn't changed, lossy only when the user adjusts feather/
 *  invert/density after a reload of a session that had non-default
 *  config baked in. Acceptable v1 trade-off; the alternative is a
 *  parallel persisted FabricImage which complicates every layer-walk
 *  call site (see `_isQuickMaskOverlay` filtering pattern). */
const RAW_MASK_CANVAS_KEY = '_fiasRawMaskCanvas';

type MaskFabricImageWithRaw = fabric.FabricImage & {
  [RAW_MASK_CANVAS_KEY]?: HTMLCanvasElement;
};

function readRawMaskCanvas(cp: fabric.Object | undefined): HTMLCanvasElement | null {
  if (!(cp instanceof fabric.FabricImage)) return null;
  const raw = (cp as MaskFabricImageWithRaw)[RAW_MASK_CANVAS_KEY];
  return raw instanceof HTMLCanvasElement ? raw : null;
}

function attachRawMaskCanvas(cp: fabric.FabricImage, raw: HTMLCanvasElement): void {
  (cp as MaskFabricImageWithRaw)[RAW_MASK_CANVAS_KEY] = raw;
}

/** Defensive fallback for paths that race ahead of
 *  `applyLayerOrder`'s rehydration pass — under normal flow every
 *  mask gets raw attached at creation (`addLayerMask` /
 *  `applyLayerMaskFromCanvas`) or at rehydration (post-load /
 *  post-undo). When this lazy path fires, it copies the current
 *  effective into raw; the resulting raw is lossy when the saved
 *  effective had non-default config baked in (user can't recover
 *  crisp edges from a feathered-then-toggled-back state). Logged
 *  once per process in dev so the brittle path surfaces rather
 *  than silently degrading. */
let warnedAboutLazyRawAllocation = false;
function ensureRawMaskCanvas(cp: fabric.FabricImage): HTMLCanvasElement | null {
  const existing = readRawMaskCanvas(cp);
  if (existing) return existing;
  const el = cp.getElement();
  if (!(el instanceof HTMLCanvasElement)) return null;
  const raw = document.createElement('canvas');
  raw.width = el.width;
  raw.height = el.height;
  const ctx = raw.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(el, 0, 0);
  attachRawMaskCanvas(cp, raw);
  if (import.meta.env.DEV && !warnedAboutLazyRawAllocation) {
    warnedAboutLazyRawAllocation = true;
    console.warn(
      '[image-editor] mask clipPath had no raw canvas at first access — ' +
        'lazily allocated from effective. Soft failure (mask still renders) ' +
        'but indicates `applyLayerOrder` rehydration ran late or was bypassed.',
    );
  }
  return raw;
}

/** Install the per-instance dataURL cache that makes the mask clipPath
 *  serialize RAW instead of EFFECTIVE. Critical for lossless session
 *  save/load and undo/redo: without RAW serialization, every snapshot
 *  bakes the current feather/invert/density into the canvas pixels and
 *  toggling those fields back to default after restore can't recover
 *  the original edges. With caching, the encode runs once per RAW
 *  change instead of once per snapshot — see `paint-surface-serializer.ts`
 *  for the rationale.
 *
 *  Per-instance override rather than a Fabric class registration:
 *  loadFromJSON rebuilds objects from base classes, so the override
 *  must be reinstalled on each fresh FabricImage that comes out of
 *  rehydration — done by the rehydration loop alongside raw
 *  reattachment. Idempotent. */
function installMaskClipPathSerializer(cp: fabric.FabricImage): void {
  installMaskRawSrcCache(cp, readRawMaskCanvas);
}

/** Tier 2 Group B — collect every `fontFamily` referenced by IText
 *  objects in a serialized canvas JSON and start the @font-face
 *  fetch for each. Awaits all of them in parallel so the caller
 *  can guarantee Fabric IText's construction-time `initDimensions`
 *  measures glyphs against the real fonts rather than the system
 *  fallback. Non-Google fonts and unknown family names resolve
 *  immediately. */
async function preloadFontsFromJSON(json: object): Promise<void> {
  const objects = (
    json as { objects?: Array<{ type?: string; fontFamily?: string; fontSize?: number }> }
  ).objects;
  if (!Array.isArray(objects)) return;
  const families = new Map<string, number>();
  for (const obj of objects) {
    if (!obj || typeof obj !== 'object') continue;
    // `i-text` is what Fabric serializes; `IText` is the constructor name.
    // Defensively match both, plus plain `Text` for safety.
    const type = typeof obj.type === 'string' ? obj.type.toLowerCase() : '';
    if (type !== 'i-text' && type !== 'itext' && type !== 'text') continue;
    if (typeof obj.fontFamily !== 'string' || !obj.fontFamily) continue;
    // Track the largest font size per family so the FontFaceSet
    // load spec covers the most expensive measurement up front.
    const size = typeof obj.fontSize === 'number' ? obj.fontSize : 16;
    const prev = families.get(obj.fontFamily) ?? 0;
    if (size > prev) families.set(obj.fontFamily, size);
  }
  if (families.size === 0) return;
  await Promise.all(
    Array.from(families.entries()).map(([family, size]) => whenFontReady(family, size)),
  );
}

// `ensureInsideStrokeRenderOverride` lives in `../lib/text-styling`
// so it's unit-testable without the Fabric runtime — see that file
// for the rationale + the fallback-path tests.

// `ensureCanvasElement` + `resolveOrRegisterPaintSurface` live in
// `../lib/paint-surface` so they're unit-testable without the
// Fabric runtime — see that file for the rationale.

/**
 * Convert a stroke render (where any non-transparent pixel is part of the
 * stroke shape) into an alpha mask whose every stroke pixel is opaque at
 * `opacity` (0..1). Used by the eraser bake to decouple the *preview*
 * color's alpha (a fixed semi-transparent gray, picked for visibility on
 * any background) from the bake's actual erase strength.
 *
 * `opacity = 1` produces a fully-opaque mask → destination-out fully
 * erases. `opacity = 0.5` halves the alpha removed at each pixel. The
 * input stroke shape is preserved; only the alpha values are normalized.
 */
function buildEraserMask(strokeEl: HTMLCanvasElement, opacity: number): HTMLCanvasElement {
  const w = strokeEl.width;
  const h = strokeEl.height;
  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  const ctx = out.getContext('2d');
  if (!ctx) return out;
  ctx.drawImage(strokeEl, 0, 0);
  const data = ctx.getImageData(0, 0, w, h);
  const targetAlpha = Math.round(Math.max(0, Math.min(1, opacity)) * 255);
  const px = data.data;
  for (let i = 3; i < px.length; i += 4) {
    if (px[i] > 0) px[i] = targetAlpha;
  }
  ctx.putImageData(data, 0, 0);
  return out;
}

/**
 * Apply the active marquee's geometry as a clip on the given 2D context.
 * Wand selections use their cached binary mask via destination-in compositing
 * (caller must do that on a temp canvas — see `bakeStrokeOntoSurface`).
 *
 * Returns true if a clip was applied; false if no marquee is active or its
 * geometry isn't usable.
 */
function applyMarqueeClipToContext(
  ctx: CanvasRenderingContext2D,
  m: {
    shape: MarqueeShape;
    obj: fabric.Object | null;
    points: { x: number; y: number }[];
    isDrawing: boolean;
  },
  /** Scene-space offset of the surface canvas's (0, 0). When the
   *  surface is the bg image or a doc-aligned paint overlay this
   *  is (0, 0) and the marquee geometry is used unmodified. For
   *  a non-bg image-backed paint surface (paste / rasterize), the
   *  surface canvas is at the layer's scene `(left, top)` — the
   *  marquee path needs to shift by `-offset` so the clip lands
   *  on the right canvas-local pixels. */
  offsetX: number = 0,
  offsetY: number = 0,
): boolean {
  if (!m.obj || m.isDrawing) return false;
  ctx.beginPath();
  if (m.shape === 'rect') {
    const r = m.obj as fabric.Rect;
    ctx.rect((r.left ?? 0) - offsetX, (r.top ?? 0) - offsetY, r.width ?? 0, r.height ?? 0);
  } else if (m.shape === 'ellipse') {
    const e = m.obj as fabric.Ellipse;
    const rx = e.rx ?? 0;
    const ry = e.ry ?? 0;
    ctx.ellipse(
      (e.left ?? 0) + rx - offsetX,
      (e.top ?? 0) + ry - offsetY,
      rx,
      ry,
      0,
      0,
      Math.PI * 2,
    );
  } else if ((m.shape === 'lasso' || m.shape === 'polygon') && m.points.length >= 3) {
    ctx.moveTo(m.points[0].x - offsetX, m.points[0].y - offsetY);
    for (let i = 1; i < m.points.length; i++) {
      ctx.lineTo(m.points[i].x - offsetX, m.points[i].y - offsetY);
    }
    ctx.closePath();
  } else {
    // wand handled via caller's destination-in path; fallthrough returns false
    return false;
  }
  ctx.clip();
  return true;
}

/**
 * Rasterize a finalized marquee selection to a binary mask of size
 * `width × height`. `mask[y*width+x] === 1` iff scene pixel (x,y) is inside
 * the marquee. Returns null if the marquee isn't finalized or its geometry
 * isn't usable. Used by the fill tool to constrain flood-fill results to
 * the marquee region.
 */
function buildMarqueeMask(
  m: {
    shape: MarqueeShape;
    obj: fabric.Object | null;
    extraObjs: fabric.Object[];
    points: { x: number; y: number }[];
    isDrawing: boolean;
    wandMaskCanvas: HTMLCanvasElement | null;
  },
  width: number,
  height: number,
): Uint8Array | null {
  if (!m.obj || m.isDrawing) return null;

  // Wand: alpha channel of the cached mask canvas IS the binary mask.
  if (m.shape === 'wand' && m.wandMaskCanvas) {
    const ctx = m.wandMaskCanvas.getContext('2d');
    if (!ctx) return null;
    const data = ctx.getImageData(0, 0, width, height).data;
    const mask = new Uint8Array(width * height);
    for (let i = 0; i < mask.length; i++) {
      mask[i] = data[i * 4 + 3] > 0 ? 1 : 0;
    }
    return mask;
  }

  // Other shapes (rect / ellipse / lasso / polygon): rasterize by filling
  // the clipped region on a fresh canvas, then read its alpha channel.
  const c = document.createElement('canvas');
  c.width = width;
  c.height = height;
  const ctx = c.getContext('2d');
  if (!ctx) return null;
  const clipped = applyMarqueeClipToContext(ctx, m);
  if (!clipped) return null;
  ctx.fillStyle = 'white';
  ctx.fillRect(0, 0, width, height);
  const data = ctx.getImageData(0, 0, width, height).data;
  const mask = new Uint8Array(width * height);
  for (let i = 0; i < mask.length; i++) {
    mask[i] = data[i * 4 + 3] > 0 ? 1 : 0;
  }
  return mask;
}

/**
 * The in-hook variant of a paint surface — one per paint-capable layer.
 * `canvas` is the pixel store, `fabricImg` is its on-canvas wrapper, and
 * `offsetX/Y` cache the scene-space position of canvas pixel (0, 0) at
 * the time the surface was registered. Mirrors the shape stored in
 * `paintSurfacesRef` (see the long doc comment there).
 *
 * Distinct from the leaner `PaintSurface<F>` in `lib/paint-surface.ts`
 * (which is generic and dependency-free for unit testing) — this one
 * carries the offset cache the hook needs for scene↔surface coord
 * mapping and is bound to `fabric.FabricImage`.
 */
type LayerPaintSurface = {
  canvas: HTMLCanvasElement;
  fabricImg: fabric.FabricImage;
  offsetX: number;
  offsetY: number;
};

/**
 * Compute a scene-coords bounding rect for the active marquee. Combines
 * the primary marquee with any extras (lasso multi-segment / wand). For
 * wand the source `wandMaskCanvas` already provides scene-space pixel
 * coverage; we use the canvas's full extent as a conservative bbox.
 * Returns `null` when no usable geometry exists.
 *
 * Pure helpers (`isCanvasFullyTransparent`, `bboxesDisjoint`) used to
 * sit here too; they were extracted to `lib/canvas-utils.ts` so they
 * have a test home that doesn't need to spin up Fabric. This one stays
 * inline because it consumes Fabric's `Rect`/`Ellipse` instance fields
 * directly — the marquee-bounds.ts equivalent (`computeMarqueeBounds`)
 * takes the plain-numeric shape descriptors and is the right home for
 * the geometric math; this wrapper exists to adapt fabric refs into
 * the form `computeMarqueeBounds` expects.
 */
function computeMarqueeSceneBBox(m: {
  shape: MarqueeShape;
  obj: fabric.Object | null;
  extraObjs: fabric.Object[];
  points: { x: number; y: number }[];
  isDrawing: boolean;
  wandMaskCanvas: HTMLCanvasElement | null;
}): { left: number; top: number; width: number; height: number } | null {
  if (!m.obj || m.isDrawing) return null;
  if (m.shape === 'wand') {
    if (!m.wandMaskCanvas) return null;
    return {
      left: 0,
      top: 0,
      width: m.wandMaskCanvas.width,
      height: m.wandMaskCanvas.height,
    };
  }
  if (m.shape === 'rect') {
    const r = m.obj as fabric.Rect;
    return computeMarqueeBounds({
      shape: 'rect',
      left: r.left ?? 0,
      top: r.top ?? 0,
      width: r.width ?? 0,
      height: r.height ?? 0,
    });
  }
  if (m.shape === 'ellipse') {
    const e = m.obj as fabric.Ellipse;
    return computeMarqueeBounds({
      shape: 'ellipse',
      left: e.left ?? 0,
      top: e.top ?? 0,
      rx: e.rx ?? 0,
      ry: e.ry ?? 0,
    });
  }
  if ((m.shape === 'lasso' || m.shape === 'polygon') && m.points.length >= 2) {
    // computeMarqueeBounds requires >=3 points for lasso/polygon (the
    // tested invariant for a closed polygon). For our purposes — a
    // pre-finalize bbox for the marquee-drag bbox check — we accept >=2,
    // so do the min/max inline rather than relaxing the lib helper.
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const p of m.points) {
      if (p.x < minX) minX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.x > maxX) maxX = p.x;
      if (p.y > maxY) maxY = p.y;
    }
    return { left: minX, top: minY, width: maxX - minX, height: maxY - minY };
  }
  return null;
}

/**
 * Resolve the paint target for the currently-active layer. This is the
 * canonical entrypoint every tool that writes pixels MUST use — fill,
 * cut, copy, retouch variants, brush bake, marquee delete. The two
 * inputs are the live refs `activeLayerIdRef` and `ensurePaintSurfaceRef`;
 * the helper returns `{ layerId, surface }` for the active layer (auto-
 * vivifying the surface for empty layers via `ensurePaintSurface`), or
 * `null` if there's no active layer or no usable surface.
 *
 * Tools must treat a `null` return as "abort — do nothing." Reaching
 * for `getBackgroundImage()` instead is the bug pattern this helper
 * exists to prevent: it caused fill to always paint the BG layer, and
 * the `.get`-instead-of-`.ensure` variant caused cut/copy to silently
 * no-op on freshly-added empty layers.
 */
function resolveActivePaintTarget(
  activeLayerIdRef: { readonly current: string | null },
  ensurePaintSurfaceRef: {
    readonly current: ((layerId: string) => LayerPaintSurface | null) | null;
  },
): { layerId: string; surface: LayerPaintSurface } | null {
  const layerId = activeLayerIdRef.current;
  if (!layerId) return null;
  const surface = ensurePaintSurfaceRef.current?.(layerId) ?? null;
  if (!surface) return null;
  return { layerId, surface };
}

/** Tools that should preserve the active marquee when switched to. */
const MARQUEE_PRESERVING_TOOLS: ReadonlySet<EditorTool> = new Set<EditorTool>([
  'marquee',
  'move',
  'draw',
  'eraser',
  'fill',
  'retouch',
  'ai',
]);

/** Tools that route ALL mouse events through their own canvas-
 *  level handlers and need every layer locked (selectable=false,
 *  evented=false) so Fabric's hit-test doesn't run Move-tool drag
 *  in parallel. Consulted by `selectLayerObjects` /
 *  `selectLayersObjects` so panel-side layer clicks don't undo
 *  the lock that `setTool` installed. */
const TOOLS_REQUIRING_LOCKED_LAYERS: ReadonlySet<EditorTool> = new Set<EditorTool>([
  'marquee',
  'retouch',
]);

/**
 * Counter-scale stroke width and dash pattern by the current canvas zoom so the
 * marquee outline stays at a constant screen-pixel size regardless of zoom level.
 * Without this, dashed strokes shrink to fractions of a pixel when zoomed out.
 */
function applyMarqueeStroke(canvas: fabric.Canvas, obj: fabric.Object): void {
  const zoom = canvas.getZoom() || 1;
  obj.set({
    strokeWidth: MARQUEE_STROKE_SCREEN_PX / zoom,
    strokeDashArray: MARQUEE_DASH_SCREEN_PX.map((v) => v / zoom),
  });
}

/** Tier 2 Group F sub-piece #5 — counter-zoom every slice overlay's
 *  stroke + dash so the visual outline stays at a constant screen-
 *  pixel size regardless of viewport zoom. Mirrors
 *  `applyMarqueeStroke`'s purpose; called from the same zoom-update
 *  sites (`mouse:wheel`, programmatic `setZoom` / `zoomBy`, fit-to-
 *  container). Iterates the slice overlay map directly — calling
 *  through `syncSliceOverlays` would also reset geometry, which is
 *  the wrong contract here. */
function applySliceStrokes(canvas: fabric.Canvas, overlays: Map<string, fabric.Rect>): void {
  if (overlays.size === 0) return;
  const zoom = canvas.getZoom() || 1;
  const strokeWidth = SLICE_STROKE_SCREEN_PX / zoom;
  const dash = SLICE_DASH_SCREEN_PX.map((v) => v / zoom);
  for (const obj of overlays.values()) {
    obj.set({ strokeWidth, strokeDashArray: dash });
  }
}

const POLYGON_CLOSE_THRESHOLD_PX = 10;

interface UseFabricCanvasOptions {
  canvasElRef: React.RefObject<HTMLCanvasElement | null>;
  wrapperRef: React.RefObject<HTMLDivElement | null>;
  onObjectModified?: () => void;
  onSelectionChanged?: (objectIds: string[]) => void;
  /** Fired when an object on the canvas becomes the active object — receives
   *  the layerId of that object (or null if it has none). Not fired on clears. */
  onLayerSelected?: (layerId: string | null) => void;
  /** Fired when the user clicks an empty area while the Shape tool is active.
   *  EditorPage handles by creating a layer and adding a new shape at the
   *  click position. */
  onShapeToolClick?: (point: { x: number; y: number }) => void;
  /** Same idea for the Text tool — fired on empty-area click. */
  onTextToolClick?: (point: { x: number; y: number }) => void;
  /** Fired when the pen tool finalizes a path — the user pressed
   *  Enter, double-clicked, or clicked back on the first anchor to
   *  close. EditorPage creates a vector LayerNode + mounts the
   *  Fabric.Path on the canvas. The hook discards its preview
   *  state immediately before invoking; callers should treat the
   *  passed-in path as immutable. */
  onPenToolFinalize?: (path: VectorPath, options: { closed: boolean }) => void;
  /** Fired when the Path Edit overlay's selected anchor changes
   *  (canvas-side click selects / deselects). EditorPage mirrors
   *  the new index into `state.pathEditSelectedAnchorIndex` so the
   *  EditPathPanel can render the type radio for the right anchor. */
  onPathEditSelectionChanged?: (anchorIndex: number | null) => void;
  onCanvasDimensions?: (width: number, height: number) => void;
  onPathCreated?: () => void;
  onZoomChanged?: (zoom: number) => void;
  /** Fired whenever the Clone Stamp source point changes (set or
   *  cleared). EditorPage mirrors this into React state so the
   *  RetouchPanel can show "Source: (x, y)" or "Source: not set." */
  onCloneSourceChanged?: (point: { x: number; y: number } | null) => void;
  /** Fired when the user attempts a Clone stroke without a source
   *  set — gives the panel a chance to surface a hint. */
  onCloneStrokeRejected?: () => void;
  /** Fired when a brush / eraser stroke commits while the active
   *  layer's mask is a vector mask (and the edit target is the
   *  mask). Vector masks don't accept raster paint; the canvas
   *  hook swallows the stroke and signals via this callback so
   *  EditorPage can show a one-time toast pointing at the
   *  "Convert to Raster Mask" entry. Tier 2 Group A PR-D. */
  onMaskPaintBlocked?: (layerId: string) => void;
  /** Fired when an action that's bound to the active marquee
   *  (brush, eraser, fill, cut, copy, delete) completes with zero
   *  pixels affected — typically because the stroke fell outside
   *  the marquee or the marquee region on the active layer is
   *  fully transparent. EditorPage shows a per-action toast so the
   *  user understands why the operation appeared to do nothing.
   *  Fires at most once per action invocation. */
  onMarqueeActionNoPixels?: (
    action: 'brush' | 'eraser' | 'cut' | 'copy' | 'delete' | 'fill',
  ) => void;
  /** Tier 2 Group F — fired after a brush / eraser stroke (or any
   *  other pixel-mutating op routed through `path:created`) bakes
   *  onto a layer's pixel surface. EditorPage flips
   *  `LayerNode.rasterModified` to true so the PSD export path
   *  knows the layer's raster has diverged from its PSD source.
   *  NOT fired for mask edits (those go through the mask branch and
   *  don't divert the layer's raster). */
  onLayerRasterModified?: (layerId: string) => void;
  /** Returns the current `LayerType` of a layer node (or null/
   *  undefined when no such layer exists). The canvas hook reads
   *  this via `ensurePaintSurface` to decide whether a paint
   *  operation requires a rasterize-before-paint confirmation
   *  (shape / text / vector → prompt; everything else → silent).
   *  Backed by `documentRef.current.layers[layerId]?.type` on the
   *  EditorPage side. */
  getLayerType?: (layerId: string) => LayerType | null | undefined;
  /** Fired when a paint operation is attempted on a shape / text /
   *  vector layer. EditorPage opens the `RasterizeBeforePaintDialog`;
   *  on confirm it calls `canvas.approveLayerForPaint(layerId)` which
   *  swaps the Fabric object to a FabricImage and fires
   *  `onLayerConvertedToPaint` so the LayerNode's `type` field
   *  catches up. The paint op that triggered this is aborted (the
   *  surface returned null); the user retries after confirming. */
  onRasterizeBeforePaintNeeded?: (info: {
    layerId: string;
    sourceType: 'shape' | 'text' | 'vector';
    action: 'brush' | 'eraser' | 'retouch' | 'fill' | 'ai';
  }) => void;
  /** Fired by `approveLayerForPaint` after the Fabric-side rasterize
   *  succeeds. EditorPage dispatches `convertLayerToPaint(layerId)`
   *  in response so the LayerNode.type field flips to `'paint'` and
   *  consumers (e.g. `hasRasterizableTarget`, the ShapePanel show/
   *  hide) see the new reality. */
  onLayerConvertedToPaint?: (layerId: string) => void;
  /** Fired during an Alt + right-button drag (Photoshop's on-canvas
   *  tool-resize gesture) with the new absolute tip size for the
   *  active size-based tool. EditorPage clamps + routes it to the
   *  Brush width or Retouch size. Desktop/mouse only — right-button
   *  events are ignored by Fabric's drag pipeline. */
  onAdjustToolSize?: (newSize: number) => void;
  /** Fired true when an Alt + right-button tool-resize drag starts
   *  and false when it ends. EditorPage uses it to suppress the
   *  clone/heal "set source" cursor during the drag so the live
   *  tip-size circle is shown instead of the alt-held crosshair. */
  onToolResizeChange?: (active: boolean) => void;
}

/** Result of a `bakeStrokeOntoMask` call.
 *
 *  - `'painted'` — stroke landed on the layer's RAW mask alpha;
 *    the materialize + dirty pass ran. The fallback to
 *    `bakeStrokeOntoSurface` is NOT used.
 *  - `'no-mask'` — the layer doesn't have a mask (or the canvas
 *    isn't ready). Caller (`bakeStrokeOnto…` orchestrator) falls
 *    back to painting on the pixel surface.
 *  - `'vector-blocked'` — the layer has a VECTOR mask. Stroke is
 *    swallowed (no surface fallback either) and
 *    `onMaskPaintBlocked` has been fired so EditorPage can
 *    surface a toast. Tier 2 Group A PR-D. */
export type MaskBakeResult = 'painted' | 'no-mask' | 'vector-blocked';

function closePolygonMarquee(
  canvas: fabric.Canvas,
  m: {
    obj: fabric.Object | null;
    points: { x: number; y: number }[];
    isDrawing: boolean;
  },
  color: string,
): void {
  if (m.obj) canvas.remove(m.obj);
  const closed = new fabric.Polygon(m.points, MARQUEE_STYLE);
  tagAsMarquee(closed);
  applyMarqueeColor(closed, color);
  applyMarqueeStroke(canvas, closed);
  canvas.add(closed);
  m.obj = closed;
  m.isDrawing = false;
}

/**
 * Recreate the live-drawing polyline preview with the latest points. Mutating
 * `points` on an existing Polyline doesn't recalculate its bounding box, so
 * new points outside the original bbox get clipped on render. Replacing the
 * object outright keeps the preview in sync with the captured path.
 */
function replaceMarqueePolyline(
  canvas: fabric.Canvas,
  m: {
    obj: fabric.Object | null;
    points: { x: number; y: number }[];
  },
  color: string,
): void {
  if (m.obj) canvas.remove(m.obj);
  const poly = new fabric.Polyline(m.points, MARQUEE_STYLE);
  tagAsMarquee(poly);
  applyMarqueeColor(poly, color);
  applyMarqueeStroke(canvas, poly);
  canvas.add(poly);
  m.obj = poly;
}

function clientPointFromEvent(e: Event): { x: number; y: number } {
  if ('touches' in e && (e as TouchEvent).touches.length > 0) {
    const t = (e as TouchEvent).touches[0];
    return { x: t.clientX, y: t.clientY };
  }
  if ('changedTouches' in e && (e as TouchEvent).changedTouches.length > 0) {
    const t = (e as TouchEvent).changedTouches[0];
    return { x: t.clientX, y: t.clientY };
  }
  const me = e as MouseEvent;
  return { x: me.clientX ?? 0, y: me.clientY ?? 0 };
}

/** Viewport info for scrollbar indicators */
/** Event payload published every time `object:moving` snaps an
 *  object to a guide or grid line. Consumers (the Guides
 *  overlay) subscribe via `subscribeToSnapMatch` to flash the
 *  matched feature for visual feedback. `null` on an axis means
 *  no snap fired for that axis on this tick. */
export interface SnapMatchEvent {
  /** Guide id, the literal string `'grid'`, or `null`. */
  matchedX: string | 'grid' | null;
  matchedY: string | 'grid' | null;
}

/** Event payload published by the smart-guides snap path. The
 *  source bbox is the dragged layer's post-snap bounding rect;
 *  the X/Y match metadata tells the indicator overlay which
 *  other layer the source aligned to and how. */
export interface SmartSnapMatchEvent {
  /** Dragged layer's bbox AFTER the smart-snap correction —
   *  enables the overlay to draw a line FROM this bbox TO the
   *  matched target. */
  source: LayerBBox;
  matchX: SmartSnapMatch | null;
  matchY: SmartSnapMatch | null;
  /** Snapshot of every target bbox at drag-start. The overlay
   *  uses this to look up the target's full geometry for label
   *  placement (distance between non-overlapping edges). */
  targets: LayerBBox[];
}

export interface ViewportInfo {
  thumbWidthRatio: number;
  thumbHeightRatio: number;
  scrollXRatio: number;
  scrollYRatio: number;
  showH: boolean;
  showV: boolean;
  /** Current Fabric zoom (a[0] of viewportTransform). 1 = 100%.
   *  Surfaced so overlays (Rulers, Grid, Guides) can compute
   *  scene↔screen transforms without coupling to the canvas hook. */
  zoom: number;
  /** Scene-space (0,0) in viewport-pixel coords — a.k.a.
   *  `viewportTransform[4]` / `[5]`. Combined with `zoom`, lets
   *  overlays project any scene-space coordinate onto the screen
   *  via `screen = scene * zoom + pan`. */
  panX: number;
  panY: number;
  /** Wrapper element dimensions in screen pixels (matches
   *  `wrapper.clientWidth` / `clientHeight`). Lets overlays size
   *  themselves without a separate ResizeObserver. */
  viewportWidth: number;
  viewportHeight: number;
}

export function useFabricCanvas({
  canvasElRef,
  wrapperRef,
  onObjectModified,
  onSelectionChanged,
  onLayerSelected,
  onShapeToolClick,
  onTextToolClick,
  onPenToolFinalize,
  onPathEditSelectionChanged,
  onCanvasDimensions,
  onPathCreated,
  onZoomChanged,
  onCloneSourceChanged,
  onCloneStrokeRejected,
  onMaskPaintBlocked,
  onMarqueeActionNoPixels,
  onLayerRasterModified,
  getLayerType,
  onRasterizeBeforePaintNeeded,
  onLayerConvertedToPaint,
  onAdjustToolSize,
  onToolResizeChange,
}: UseFabricCanvasOptions) {
  const canvasRef = useRef<fabric.Canvas | null>(null);
  const backgroundImageRef = useRef<fabric.FabricImage | null>(null);
  const imageDimsRef = useRef<{ width: number; height: number }>({ width: 0, height: 0 });
  /** Current Extension fill color (Canvas Size dialog). Tracked here
   *  so that dim-change ops (loadImage, setCanvasSize, etc.) can
   *  resize the chrome fill rect alongside the outline + clip
   *  region — they all flow through ensureCanvasBoundsOverlay, which
   *  reads this ref. The dedicated `setCanvasFillColor` setter
   *  updates this ref and passes the new value explicitly. */
  const canvasFillColorRef = useRef<string | null>(null);
  const activeToolRef = useRef<EditorTool>('move');
  const marqueeRef = useRef<{
    shape: MarqueeShape;
    obj: fabric.Object | null;
    /** Additional contour polygons for shapes (like wand) that produce multiple regions. */
    extraObjs: fabric.Object[];
    /** Cached binary mask canvas for wand selections — used by the eraser path. */
    wandMaskCanvas: HTMLCanvasElement | null;
    /** Wand-mode tolerance and contiguous flag, latched at click time. */
    wandTolerance: number;
    wandContiguous: boolean;
    points: { x: number; y: number }[];
    startX: number;
    startY: number;
    isDrawing: boolean;
  }>({
    shape: 'rect',
    obj: null,
    extraObjs: [],
    wandMaskCanvas: null,
    wandTolerance: 32,
    wandContiguous: true,
    points: [],
    startX: 0,
    startY: 0,
    isDrawing: false,
  });
  const panRef = useRef<{ isPanning: boolean; lastX: number; lastY: number }>({
    isPanning: false,
    lastX: 0,
    lastY: 0,
  });
  /** Move-tool drag of the marquee outline. When the user has a
   *  finalized marquee and clicks inside its bbox with the Move tool
   *  active, subsequent pointermoves shift the marquee geometry by
   *  the scene-coord delta from the start point. `initial` is a
   *  snapshot of the marquee's position at drag start so the move
   *  is computed against a stable reference (deltas from the original,
   *  not from the previous frame — avoids drift). Wand selections
   *  aren't supported here; their region is defined by a doc-sized
   *  alpha mask, not by geometry, so dragging would require
   *  re-stamping the mask each tick. Deferred. */
  const marqueeDragRef = useRef<{
    isDragging: boolean;
    startSceneX: number;
    startSceneY: number;
    initial: {
      shape: MarqueeShape;
      objLeft: number;
      objTop: number;
      extras: { obj: fabric.Object; left: number; top: number }[];
      points: { x: number; y: number }[];
    } | null;
  }>({ isDragging: false, startSceneX: 0, startSceneY: 0, initial: null });
  /** Tracks the prior canvas-state snapshot taken when Space was
   *  pressed, so `setSpaceHeld(false)` can restore everything
   *  cleanly. Null when Space isn't currently held. */
  const preSpacePanRef = useRef<{
    tool: EditorTool;
    isDrawingMode: boolean;
    defaultCursor: string;
    selection: boolean;
  } | null>(null);
  /** Tier 2 Group D — listeners notified each time the snap
   *  handler matches a guide / grid line. Used by the Guides
   *  overlay to flash the matched guide in magenta for visual
   *  feedback (so the user sees WHICH feature caused the snap). */
  const snapMatchListenersRef = useRef<Set<(m: SnapMatchEvent) => void>>(new Set());
  /** Smart-guide config: whether the feature is enabled, plus
   *  the per-drag-start snapshot of other-layer bboxes. Cached
   *  across `object:moving` ticks so we don't walk the layer
   *  tree at 60 Hz. Cleared on `mouse:up`. */
  const smartSnapConfigRef = useRef<{
    enabled: boolean;
    /** Latched at mouse:down; null at idle. Subsequent ticks
     *  reuse this snapshot — the set of "other layers" can't
     *  change during a drag. */
    targets: LayerBBox[] | null;
    /** True when the user is currently holding Ctrl — temporarily
     *  disables smart-snap so they can drop a layer freely
     *  without aligning to anything. Read at every object:moving
     *  tick. EditorPage mirrors `window` Ctrl-held state into
     *  this ref via `setSmartSnapDisabled`. */
    ctrlDisabled: boolean;
  }>({
    enabled: true,
    targets: null,
    ctrlDisabled: false,
  });
  /** Listeners for smart-snap-match events — published whenever
   *  the smart-snap handler matches another layer's edge/center.
   *  The `SmartGuides` overlay subscribes and renders magenta
   *  indicator lines + distance labels for SNAP_FLASH_MS. */
  const smartSnapMatchListenersRef = useRef<Set<(m: SmartSnapMatchEvent) => void>>(new Set());

  /** Tier 2 Group D — snap-to-guides / snap-to-grid configuration
   *  read by the `object:moving` handler. Updated synchronously
   *  by EditorPage's `state.viewSettings` mirror via the exposed
   *  `setSnapConfig` setter. Default `tolerance: 0` means snapping
   *  is OFF — the handler short-circuits without doing any work. */
  const snapConfigRef = useRef<{
    snapToGuides: boolean;
    snapToGrid: boolean;
    gridSize: number;
    guides: import('../types/image-editor.types').Guide[];
    /** Screen-pixel pull distance — converted to scene pixels at
     *  snap time via the current zoom. */
    toleranceScreen: number;
  }>({
    snapToGuides: false,
    snapToGrid: false,
    gridSize: 100,
    guides: [],
    // 12 screen pixels — middle ground between Sketch (~5) and
    // Photoshop (~16). Tight enough that close-but-not-aligned
    // doesn't accidentally snap, loose enough that a mouse drag
    // can reach the snap zone without pixel-perfect aim.
    toleranceScreen: 12,
  });
  const pinchRef = useRef<{ isPinching: boolean; startDist: number; startZoom: number }>({
    isPinching: false,
    startDist: 0,
    startZoom: 1,
  });
  const forceSquareRef = useRef(false);
  const marqueeColorRef = useRef(MARQUEE_DEFAULT_COLOR);
  const wandToleranceRef = useRef(32);
  const wandContiguousRef = useRef(true);
  const cropRectRef = useRef<fabric.Rect | null>(null);
  /** Tier 2 Group F sub-piece #5 — Slice tool state.
   *
   *  `objects` is the map from sliceId to the Fabric.Rect overlay
   *  currently on canvas (only populated while the Slice tool is
   *  active). The label child texts aren't tracked here — they're
   *  attached to the Rect overlay via Fabric's `.controls` /
   *  per-object render hook so they move with the rect natively;
   *  see `mountSliceOverlay`. The pending-draw block tracks the
   *  in-flight new-slice drag so `mouse:move` / `mouse:up` can
   *  finalize.
   *
   *  `callbacks` is the dispatch-into-React surface for slice-
   *  create / slice-update / slice-remove. EditorPage installs
   *  these once via `setSliceCallbacks`; subsequent canvas events
   *  go through the ref so we don't have to re-register handlers
   *  every render. Null when the SlicePanel isn't yet mounted
   *  (during the very first render before the EditorPage
   *  installer-effect runs). */
  const sliceStateRef = useRef<{
    objects: Map<string, fabric.Rect>;
    pending: {
      obj: fabric.Rect | null;
      startX: number;
      startY: number;
      isDrawing: boolean;
    };
    /** Mirror of `state.slices` so the canvas-side mouse:up that
     *  fires after a drag-create can read the latest array (for
     *  generating the next "Slice N" default name; though the
     *  reducer also derives that — the ref is purely for the
     *  syncSliceOverlays diff). */
    current: readonly SliceRect[];
    callbacks: {
      onCreate: (rect: { x: number; y: number; width: number; height: number }) => void;
      onUpdate: (id: string, patch: Partial<Omit<SliceRect, 'id'>>) => void;
    } | null;
  }>({
    objects: new Map(),
    pending: { obj: null, startX: 0, startY: 0, isDrawing: false },
    current: [],
    callbacks: null,
  });
  const activeLayerIdRef = useRef<string | null>(null);
  /** Most-recent canvas click in scene coords. Recorded on every
   *  `mouse:down` regardless of active tool. Used by paste so a fresh
   *  paste lands where the user last clicked rather than at a fixed
   *  position. `null` until the user clicks the canvas at least once. */
  const lastClickSceneRef = useRef<{ x: number; y: number } | null>(null);
  /** True between fabric `mouse:down` and `mouse:up` on the canvas.
   *  Belt-and-suspenders gate for the autosave debounce — the window
   *  pointerdown listener in EditorPage is the primary signal, but
   *  Fabric's internal pointer-event handling can swallow the event
   *  before it bubbles, so we also track it canvas-side. Save paths
   *  consult this via `getIsCanvasInteracting` and defer while it's
   *  true so a marquee drag (or any tool that holds the pointer) can't
   *  collide with a `canvas.toJSON()` serialization pass. */
  const canvasInteractionRef = useRef(false);
  /** Mirror of `state.autoSelectLayers`. Move-tool mouse:down reads
   *  this to decide between Fabric's grab-on-click (true) and the
   *  always-drag-active override (false). */
  const autoSelectLayersRef = useRef(true);
  /** Active fabric object snapshot taken in `mouse:down:before` so
   *  the Move-tool override can recover what was active BEFORE
   *  Fabric's internal `__onMouseDown` swapped to whatever the click
   *  hit. Reset each `mouse:down`. */
  const moveActiveAtDownRef = useRef<fabric.Object | null>(null);
  /** Move-tool always-drag-active state. Set in `mouse:down` when
   *  `autoSelectLayers === false` and the click DID NOT land on the
   *  previously-active object; `mouse:move` translates the captured
   *  active by the scene-coord delta. */
  const moveActiveDragRef = useRef<{
    isDragging: boolean;
    target: fabric.Object | null;
    startSceneX: number;
    startSceneY: number;
    initialLeft: number;
    initialTop: number;
  }>({
    isDragging: false,
    target: null,
    startSceneX: 0,
    startSceneY: 0,
    initialLeft: 0,
    initialTop: 0,
  });
  /** Edit-target snapshot kept in sync from React state via a
   *  setter helper. Read inside the brush stroke listener (which
   *  fires via Fabric event, outside the React render lifecycle).
   *  Default 'layer' for any layer not in the map. */
  const editTargetsRef = useRef<Record<string, 'layer' | 'mask'>>({});
  /** Latest mask config per layer, mirrored from `state.document` by
   *  `EditorPage` via `setMaskConfig`. `bakeStrokeOntoMask` reads this
   *  to materialize the post-stroke effective alpha with the current
   *  feather/invert/density baked in — without this mirror, in-stroke
   *  feedback would lose those features (the stroke would only
   *  appear after the next document-change tick). Empty map means
   *  every layer is treated as identity (the addLayerMask defaults).
   *  Map deletion happens in `removeLayerMask`. */
  const maskConfigsRef = useRef<Map<string, LayerMask>>(new Map());
  /** Set of text layer ids currently bound to a vector layer's
   *  path (Tier 2 Group A PR-E). Mirrored from `state.document` by
   *  EditorPage's text-path-sync effect via `setTextPathBinding`.
   *  `updateActiveText` reads this so font-size / style edits don't
   *  clobber the bound `fabric.IText.path` with an arch-derived
   *  path on the same dispatch — when the layer is bound, the
   *  `.path` slot is managed exclusively by the sync effect. */
  const textPathBoundLayerIdsRef = useRef<Set<string>>(new Set());

  // Clone Stamp tool state. Source point lives in scene coords
  // (so it survives viewport pan/zoom). `armed` flips true when
  // the user clicks the "Set Source" button — the next canvas
  // tap then records the source and disarms. The stroke-time
  // state (offset, source snapshot, brush mask) is rebuilt on
  // each mouse:down inside the stroke handler.
  const cloneSourceRef = useRef<{ x: number; y: number } | null>(null);
  const cloneArmedRef = useRef(false);
  /** Aligned-mode offset persisted across strokes. Set on the
   *  first stroke after Set Source; cleared on next Set Source.
   *  When non-null AND `clone.aligned === true` at stroke start,
   *  the offset takes precedence over the freshly-computed
   *  `source - paintStart` so subsequent strokes resume sampling
   *  at the offset the user last left off at — Photoshop's
   *  Aligned behavior. */
  const cloneAlignedOffsetRef = useRef<{ x: number; y: number } | null>(null);
  /** Per-stroke clone state. Built on mouse:down when a stroke
   *  begins, cleared on mouse:up. The offset is `source - paintStart`,
   *  so each subsequent paint sample at P pulls source pixels from
   *  `P + offset`. The source snapshot is captured at stroke start
   *  so subsequent paint sampling is fast (no compositing per
   *  stamp). */
  const cloneStrokeRef = useRef<{
    offset: { x: number; y: number };
    sourceSnapshot: HTMLCanvasElement;
    brushMask: HTMLCanvasElement;
    /** Cached ImageData of the brush mask. The Heal post-process
     *  reads it to weight per-pixel averages — getImageData per
     *  stamp would be wasteful since the mask is constant for
     *  the whole stroke. Cached lazily on first need. */
    brushMaskImageData: ImageData | null;
    radius: number;
    /** When true (Aligned mode), the offset persists across
     *  release+restroke. When false, each new stroke restarts
     *  from `source` as if the paintStart were fresh. */
    aligned: boolean;
    /** Per-stroke alpha: opacity × flow, both 0–100, normalized
     *  to 0–1. */
    strokeAlpha: number;
    /** Which Retouch variant owns this stroke. Both Clone and
     *  Heal share the source-set + stamp pipeline; Heal layers a
     *  per-stamp tone correction on top so the cloned texture
     *  blends with the destination's lighting. */
    variant: 'clone' | 'heal';
    /** Heal mode captured at stroke start so a mid-stroke
     *  toggle doesn't produce inconsistent dabs. `normal` runs
     *  the additive tone shift; `replace` skips it (becomes
     *  Clone-equivalent stamp invoked from the Heal variant).
     *  Ignored for `variant === 'clone'`. */
    healMode: 'normal' | 'replace';
    /** Stylus-pressure flags captured at stroke start (Retouch panel
     *  toggles). When set AND the pointer is a pen, each stamp scales
     *  its tip radius / composite alpha by the live pen pressure. */
    pressureSize: boolean;
    pressureOpacity: boolean;
    pressureFlow: boolean;
  } | null>(null);
  /** Per-stroke Smudge state. Built on mouse:down and cleared on
   *  mouse:up. The sampleBuffer is the trailing pixel patch that
   *  the smudge "finger" carries from one dab to the next — at
   *  each dab we mix it with the destination patch, write the
   *  mixed result onto the surface, then overwrite the sample
   *  buffer with that result so the next dab drags the freshly-
   *  smeared content forward. Smudge has no source-set state, so
   *  none of the clone-namespace refs apply.
   *
   *  Strength = 0 → no effect. Strength = 1 → sample fully
   *  overwrites at the brush center (mask-weighted toward the
   *  periphery). Stroke alpha (opacity × flow) further scales the
   *  per-dab change at the destination level. */
  const smudgeStrokeRef = useRef<{
    sampleBuffer: HTMLCanvasElement;
    brushMask: HTMLCanvasElement;
    /** Cached ImageData of the brush mask — read once per stroke
     *  and reused for every dab's per-pixel mix. */
    brushMaskImageData: ImageData | null;
    radius: number;
    /** 0–1, copy of `retouchSettings.smudge.strength / 100` taken
     *  at stroke start. */
    strength: number;
    /** opacity × flow, both 0–100, normalized to 0–1. */
    strokeAlpha: number;
  } | null>(null);
  /** Per-stroke Blur state. Built on mouse:down and cleared on
   *  mouse:up. Unlike Smudge there's no carry-forward sample
   *  buffer — each dab re-reads the dest patch, blurs it fresh,
   *  and mixes the blur into the dest by Strength. Repeated dabs
   *  on the same area accumulate (the just-blurred pixels become
   *  the source of the NEXT blur), so the user can build up
   *  smoothness by dragging back and forth. Strength is captured
   *  at stroke start so a mid-stroke slider drag doesn't change
   *  the per-dab feel. */
  const blurStrokeRef = useRef<{
    brushMask: HTMLCanvasElement;
    /** Cached ImageData of the brush mask — read once per stroke. */
    brushMaskImageData: ImageData | null;
    radius: number;
    /** Gaussian sigma for the per-dab blur, in pixels. Derived
     *  from brush radius at stroke start (kernel size scales with
     *  brush size, matching Photoshop semantics — Strength
     *  controls mix amount, not kernel size). */
    sigma: number;
    /** 0–1, copy of `retouchSettings.blur.strength / 100`. */
    strength: number;
    /** opacity × flow, both 0–100, normalized to 0–1. */
    strokeAlpha: number;
    /** Last point a dab fired at (scene coords). The next dab fires
     *  only after the cursor moves ≥ radius × DODGE_BURN_SPACING_RATIO,
     *  so a heavy per-pixel Gaussian dab isn't recomputed on every
     *  high-frequency mouse:move (Dodge/Burn/Sponge already do this). */
    lastDabX: number;
    lastDabY: number;
  } | null>(null);
  /** Per-stroke state for the Sharpen variant. Same shape as
   *  `blurStrokeRef` — sharpen is unsharp-mask, which requires
   *  the same Gaussian-of-the-dest-patch that Blur computes, so
   *  it inherits radius/sigma/strength/strokeAlpha verbatim. The
   *  difference is only what we mix BACK into the surface: Blur
   *  uses the blurred patch directly, Sharpen uses
   *  `dest + (dest - blurred)` (the high-frequency detail
   *  amplified by 1×). Repeated dabs accumulate detail because
   *  each dab re-reads the (slightly sharpened) dest. */
  const sharpenStrokeRef = useRef<{
    brushMask: HTMLCanvasElement;
    brushMaskImageData: ImageData | null;
    radius: number;
    sigma: number;
    /** 0–1, copy of `retouchSettings.sharpen.strength / 100`. */
    strength: number;
    strokeAlpha: number;
    /** Last point a dab fired at (scene coords) — brush-spacing gate,
     *  same as Blur/Dodge/Burn/Sponge so the unsharp-mask dab isn't
     *  recomputed on every mouse:move. */
    lastDabX: number;
    lastDabY: number;
    /** Per-stroke cumulative brush-mask-alpha applied at each
     *  pixel of the active layer surface (Uint8, 0–255 = no
     *  capacity used → fully consumed). Sharpen is unbounded
     *  under recursion (`2*dest - blurred` amplifies high-freq
     *  content on each pass), so without a cap a normal drag
     *  blows out within ~5 overlapping dabs. We cap at one full
     *  mask-weighted dose per pixel per stroke — Photoshop's
     *  Sharpen-tool behavior: a drag sharpens each region once;
     *  to push further the user releases and re-drags. Sized
     *  lazily on the first dab to match `target.width × height`,
     *  cleared on mouse:up. */
    appliedMask: Uint8Array | null;
    appliedMaskW: number;
    appliedMaskH: number;
  } | null>(null);
  /** Per-stroke state for the Dodge variant. Lightens pixels under
   *  the brush by `(255 - orig) * exposure * rangeWeight`, where
   *  rangeWeight is a luminance-band curve (shadows / midtones /
   *  highlights). Two limiters apply:
   *   - **Brush spacing** (radius × DODGE_BURN_SPACING_RATIO):
   *     skips zero-effect dabs on hold-still and reduces compute
   *     during slow drags.
   *   - **Per-stroke cap** (`appliedMask`): each pixel can absorb
   *     at most one full brush-mask-alpha dose per stroke, even
   *     if the cursor passes over it many times. Without this,
   *     a single drag at exposure=50% in shadows mode pushes
   *     dark pixels (L=0.1) all the way to ~107 (mid-gray) over
   *     8 overlapping dabs — drag became "gray draw tool", not
   *     "lift shadows a bit". Same Uint8Array pattern as
   *     Sharpen; allocated lazily on first dab, freed by nulling
   *     the ref on mouse:up. To layer more dodge on a region the
   *     user releases and re-strokes — additive across strokes,
   *     bounded within one stroke. */
  const dodgeStrokeRef = useRef<{
    brushMask: HTMLCanvasElement;
    brushMaskImageData: ImageData | null;
    radius: number;
    range: 'shadows' | 'midtones' | 'highlights';
    /** 0–1, copy of `retouchSettings.dodge.exposure / 100`. */
    exposure: number;
    strokeAlpha: number;
    /** Last point at which a dab was fired (scene coords); the
     *  next dab fires only if the cursor has moved at least
     *  `radius * DODGE_BURN_SPACING_RATIO` since this point. */
    lastDabX: number;
    lastDabY: number;
    /** Per-stroke cumulative brush-mask-alpha applied at each
     *  pixel of the active layer surface (Uint8, 255 = fully
     *  consumed). See type doc above for rationale. */
    appliedMask: Uint8Array | null;
    appliedMaskW: number;
    appliedMaskH: number;
  } | null>(null);
  /** Per-stroke state for the Burn variant. Same shape as
   *  `dodgeStrokeRef` — Burn is the multiplicative inverse of
   *  Dodge: `pixel * (1 - m)` per channel pushes toward 0
   *  instead of toward 255. Range curves, brush spacing, and
   *  per-stroke cap all behave identically to Dodge — see the
   *  Dodge type doc for the full rationale on why the cap is
   *  necessary even for "naturally bounded" math. */
  const burnStrokeRef = useRef<{
    brushMask: HTMLCanvasElement;
    brushMaskImageData: ImageData | null;
    radius: number;
    range: 'shadows' | 'midtones' | 'highlights';
    /** 0–1, copy of `retouchSettings.burn.exposure / 100`. */
    exposure: number;
    strokeAlpha: number;
    lastDabX: number;
    lastDabY: number;
    appliedMask: Uint8Array | null;
    appliedMaskW: number;
    appliedMaskH: number;
  } | null>(null);
  /** Per-stroke state for the Sponge variant. Shifts saturation
   *  (toward gray of same luminance for desaturate, away from it
   *  for saturate) without changing perceived brightness. Same
   *  brush-spacing + per-stroke cap pattern as Dodge/Burn — see
   *  the dodgeStrokeRef type doc for the full rationale. The
   *  cap matters most for *saturate* mode: the per-pixel formula
   *  `pixel + (pixel - L) * m` amplifies distance from the
   *  pixel's own luminance gray, so under recursion (overlapping
   *  dabs) it grows colors past 0/255 quickly and clips. */
  const spongeStrokeRef = useRef<{
    brushMask: HTMLCanvasElement;
    brushMaskImageData: ImageData | null;
    radius: number;
    mode: 'saturate' | 'desaturate';
    /** 0–1, copy of `retouchSettings.sponge.flow / 100` — the
     *  variant-specific Flow slider. Distinct from the tool-
     *  level `flow` (which combines with `opacity` into
     *  `strokeAlpha`). */
    flow: number;
    strokeAlpha: number;
    lastDabX: number;
    lastDabY: number;
    appliedMask: Uint8Array | null;
    appliedMaskW: number;
    appliedMaskH: number;
  } | null>(null);
  /** Mirror of `state.retouchSettings` from React, set via
   *  `setRetouchSettings`. Read inside Fabric mouse handlers. */
  const retouchSettingsRef = useRef<RetouchSettings | null>(null);
  /** Reticle Fabric object showing the current Clone source on
   *  the canvas. null when no source set or Clone variant not
   *  active. Stored as a flat array of `fabric.Rect` bars (no
   *  Group wrapper) plus the current center, so we can move all
   *  bars as a unit by tracking the center separately rather
   *  than reading each Rect's `.left` back to compute deltas. */
  const cloneReticleRef = useRef<{
    bars: fabric.Rect[];
    left: number;
    top: number;
  } | null>(null);

  /** Pen tool working state (Tier 2 Group A). The tool accumulates
   *  anchors across multiple mouse events before the user finalizes
   *  (Enter / double-click / click-on-first-anchor). The preview
   *  Fabric.Path is recreated whenever the path changes; on
   *  finalize, EditorPage creates a real vector LayerNode + mounts
   *  a persistent Fabric.Path via `addVectorObject`, and this ref
   *  is cleared.
   *
   *  Tag on the preview Fabric.Path: `_isPenPreview: true` — same
   *  pattern as `_isMarquee` / `_isCloneReticle` / etc. so the
   *  layer-order helper can keep it on top and so the cleanup step
   *  can identify orphans. */
  const penStateRef = useRef<{
    anchors: VectorAnchor[];
    previewObj: fabric.Path | null;
    /** Pen variant only: when mouse is held down with the just-
     *  placed anchor still being "dragged" — handles are being
     *  set by the drag vector. */
    draggingAnchorIndex: number | null;
    /** Freeform variant only: true while the user holds the mouse
     *  down sampling points. */
    freeformActive: boolean;
  } | null>(null);
  // The active variant is read fresh from `penSettingsRef.current`
  // at every event — caching it on session start would make
  // mid-draw chip-row changes silently no-op until the next path.
  /** Mirror of `state.penSettings` from EditorPage so the canvas-
   *  side mouse handlers see the latest variant + stroke + fill
   *  without re-binding the listeners. EditorPage calls
   *  `setPenSettings` whenever the panel changes. */
  const penSettingsRef = useRef<PenSettings>({
    variant: 'curvature',
    autoAddDelete: true,
    strokeColor: '#000000',
    strokeWidth: 2,
    fill: 'transparent',
    freeformMagnetic: false,
    freeformWidth: 50,
  });

  /** Quick Mask working state (Tier 1 Group D). The QM canvas is
   *  a full-image alpha mask — opaque-white where selected,
   *  transparent elsewhere. The overlay is a separate FabricImage
   *  tagged `_isQuickMaskOverlay` that renders on top of every
   *  layer, showing the un-selected area as a 50%-opaque red
   *  matte. Refs hold canvas-side state; React state holds only
   *  the boolean `quickMaskActive` (atomically toggled with the
   *  active tool — see useImageEditorState's reducer). */
  const quickMaskCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const quickMaskOverlayRef = useRef<fabric.FabricImage | null>(null);
  /** Per-stroke state for Quick Mask painting. `mode='add'` paints
   *  white into the alpha mask (grows the selection); `mode='subtract'`
   *  paints alpha=0 (shrinks). Alt-held mid-stroke is detected on
   *  mouse:down and locked for the duration of the stroke — toggling
   *  add/subtract mid-stroke produces unpredictable results in
   *  Photoshop too, so we match that.
   *
   *  Opacity semantics: a single stroke applies AT MOST `opacity`
   *  worth of alpha, regardless of how many dabs the pointer covers.
   *  To get that, dabs stamp the full-alpha brush footprint into a
   *  per-stroke TEMP canvas (whose alpha saturates at 1 along the
   *  brush path even with overlapping dabs), and the QM canvas is
   *  re-derived per move tick as `baseline + temp@opacity` (add) or
   *  `baseline − temp@opacity` (subtract). `baseline` is the QM
   *  snapshot at mouse:down so multi-stroke layering still works
   *  (each new stroke composes on top of the previous strokes'
   *  committed result). `dirty*` tracks the bbox of pixels written
   *  since the last QM flush so per-tick work is proportional to
   *  brush movement, not canvas size. */
  const quickMaskStrokeRef = useRef<{
    mode: 'add' | 'subtract';
    radius: number;
    hardness: number;
    opacity: number;
    lastX: number;
    lastY: number;
    temp: HTMLCanvasElement;
    baseline: HTMLCanvasElement;
    dirtyMinX: number;
    dirtyMinY: number;
    dirtyMaxX: number;
    dirtyMaxY: number;
  } | null>(null);
  /** Default mode for new QM strokes, mirrored from React state via
   *  `setQuickMaskMode`. Alt held at mouse-down inverts whichever
   *  mode this carries (Photoshop convention). */
  const quickMaskModeRef = useRef<'add' | 'subtract'>('add');
  /** Forward-reference holders so the QM brush handlers (registered
   *  in the mount-time canvas-events effect) can call helpers that
   *  are defined later as `useCallback`s. Same pattern as
   *  `ensurePaintSurfaceRef`. */
  const paintQuickMaskDabRef = useRef<((x: number, y: number) => void) | null>(null);
  const refreshQuickMaskOverlayRef = useRef<(() => void) | null>(null);

  const brushColorRef = useRef('#000000');
  /** Width + opacity snapshot of the current brush settings,
   *  mirrored from React state. The Draw tool routes through
   *  Fabric's `freeDrawingBrush` (which has its own width/color)
   *  so reading-back from there worked; Quick Mask paints onto a
   *  separate alpha mask without involving Fabric's brush, so we
   *  need a direct reference. Updated by `updateBrush`. */
  const brushSizeRef = useRef(4);
  const brushOpacityRef = useRef(1);
  /** Soft-edge feather in pixels for Draw / Eraser strokes. 0 = the
   *  bake uses the temp canvas as-is (sharp at the stroke's natural
   *  anti-aliased edge). Read inside `bakeStrokeOntoSurface` to set
   *  `ctx.filter = 'blur(Npx)'` on the bake `drawImage`. Mirrored
   *  from React state by `updateBrush` / `enableDrawMode`. */
  const brushFeatherRef = useRef(0);
  // Eraser strength as a fraction in [0, 1]. Decoupled from the eraser
  // brush's *preview* color (which is a fixed semi-transparent gray for
  // visibility) — the bake uses this value to set the alpha of the
  // destination-out mask, so 1.0 fully erases and 0 leaves pixels alone.
  // Mirrors brushSettings.opacity so the user has one stroke-strength
  // slider for both Draw and Eraser tools.
  const eraserOpacityRef = useRef(1);
  // Paint surfaces — one HTMLCanvasElement per paint-capable layer, wrapped
  // in a Fabric Image. New strokes render onto the active layer's surface.
  //
  // `offsetX` / `offsetY` is the scene-space position of the canvas's (0,0)
  // pixel. For the bg image and for empty/paint layers (added via the +
  // button), the surface canvas spans the document at identity transform —
  // offset is (0, 0) and scene coords == canvas pixel coords. For non-bg
  // image-backed layers (paste, image), the surface canvas IS the image's
  // intrinsic canvas, positioned at the image's scene (left, top); writes
  // at scene (sx, sy) translate to canvas pixel (sx − offsetX, sy − offsetY).
  // Layers with non-identity scale, rotation or skew are auto-rasterized to
  // an axis-aligned identity-scale FabricImage at first paint so this simple
  // translate is sufficient — no inverse matrix needed.
  const paintSurfacesRef = useRef(new Map<string, LayerPaintSurface>());
  // Forward-reference holder for ensurePaintSurface so callbacks defined
  // earlier in the file (e.g. deleteSelected) can call it without triggering
  // a use-before-declaration error.
  const ensurePaintSurfaceRef = useRef<((layerId: string) => LayerPaintSurface | null) | null>(
    null,
  );
  // Mirror every parent-supplied callback into a ref so the init-only
  // useEffect (which sets up Fabric event handlers exactly once) always
  // dispatches to the latest closure. Without this, callers would have to
  // wrap each callback in a stable useCallback or risk stale state inside
  // the handler.
  const onZoomChangedRef = useRef(onZoomChanged);
  onZoomChangedRef.current = onZoomChanged;
  const onAdjustToolSizeRef = useRef(onAdjustToolSize);
  onAdjustToolSizeRef.current = onAdjustToolSize;
  const onToolResizeChangeRef = useRef(onToolResizeChange);
  onToolResizeChangeRef.current = onToolResizeChange;
  const onLayerSelectedRef = useRef(onLayerSelected);
  onLayerSelectedRef.current = onLayerSelected;
  const onShapeToolClickRef = useRef(onShapeToolClick);
  onShapeToolClickRef.current = onShapeToolClick;
  const onTextToolClickRef = useRef(onTextToolClick);
  onTextToolClickRef.current = onTextToolClick;
  const onPenToolFinalizeRef = useRef(onPenToolFinalize);
  onPenToolFinalizeRef.current = onPenToolFinalize;
  const onPathEditSelectionChangedRef = useRef(onPathEditSelectionChanged);
  onPathEditSelectionChangedRef.current = onPathEditSelectionChanged;
  const onObjectModifiedRef = useRef(onObjectModified);
  onObjectModifiedRef.current = onObjectModified;
  const onSelectionChangedRef = useRef(onSelectionChanged);
  onSelectionChangedRef.current = onSelectionChanged;
  /** Subscribers to cursor scene-coords updates. The Fabric
   *  `mouse:move` handler iterates this Set on every event;
   *  consumers (StatusBar) attach via `subscribeToCursorMove`
   *  and manage their own local state. Keeping the cursor out
   *  of the reducer avoids re-rendering the whole EditorPage
   *  subtree at 60+ Hz just from hover. */
  const cursorListenersRef = useRef<Set<(x: number, y: number) => void>>(new Set());
  const onPathCreatedRef = useRef(onPathCreated);
  onPathCreatedRef.current = onPathCreated;
  const onCanvasDimensionsRef = useRef(onCanvasDimensions);
  onCanvasDimensionsRef.current = onCanvasDimensions;
  // Clone Stamp callbacks — surfaced to EditorPage so the panel
  // can mirror source state and warn the user on rejected
  // strokes (no source set).
  const onCloneSourceChangedRef = useRef<
    ((p: { x: number; y: number } | null) => void) | undefined
  >(undefined);
  onCloneSourceChangedRef.current = onCloneSourceChanged;
  const onCloneStrokeRejectedRef = useRef<(() => void) | undefined>(undefined);
  onCloneStrokeRejectedRef.current = onCloneStrokeRejected;
  const onMaskPaintBlockedRef = useRef<((layerId: string) => void) | undefined>(undefined);
  onMaskPaintBlockedRef.current = onMaskPaintBlocked;
  const onMarqueeActionNoPixelsRef = useRef<
    ((action: 'brush' | 'eraser' | 'cut' | 'copy' | 'delete' | 'fill') => void) | undefined
  >(undefined);
  onMarqueeActionNoPixelsRef.current = onMarqueeActionNoPixels;
  const onLayerRasterModifiedRef = useRef<((layerId: string) => void) | undefined>(undefined);
  onLayerRasterModifiedRef.current = onLayerRasterModified;
  // Lets `ensurePaintSurface` read the latest LayerNode.type without
  // taking a dep on the full document or routing through a state
  // accessor. Returns undefined if EditorPage hasn't provided one yet
  // (legacy callers / tests) — in that case the gate falls through
  // and paint behaves as it did pre-Tier-2-Group-A confirmation.
  const getLayerTypeRef = useRef<UseFabricCanvasOptions['getLayerType']>(undefined);
  getLayerTypeRef.current = getLayerType;
  const onRasterizeBeforePaintNeededRef =
    useRef<UseFabricCanvasOptions['onRasterizeBeforePaintNeeded']>(undefined);
  onRasterizeBeforePaintNeededRef.current = onRasterizeBeforePaintNeeded;
  const onLayerConvertedToPaintRef =
    useRef<UseFabricCanvasOptions['onLayerConvertedToPaint']>(undefined);
  onLayerConvertedToPaintRef.current = onLayerConvertedToPaint;
  // Per-layer set tracking the in-flight rasterize-before-paint
  // dialog. `ensurePaintSurface` fires the callback only when a
  // prompt is not already pending for the layer — so a burst of
  // paint events (a freehand stroke is many bake calls) opens the
  // dialog exactly once. Cleared on confirm / cancel by the
  // matching approveLayerForPaint / cancelLayerForPaint calls.
  const paintPromptPendingRef = useRef<Set<string>>(new Set());
  // Auto-replay map for paint ops that were aborted by the
  // rasterize-before-paint gate. The path:created handler (brush /
  // eraser) registers a closure here BEFORE bake; if the bake
  // aborts AND a prompt is pending, the closure stays queued and
  // `approveLayerForPaint` fires it after the rasterize swap.
  // Cancel drops the closure so a rejected stroke doesn't ghost.
  // Single-click ops (fill) and multi-event ops (retouch, AI) do
  // not auto-replay in v1 — the user redoes those after confirm.
  const pendingPaintReplayRef = useRef<Map<string, () => void>>(new Map());
  // `onLayerRasterModified` fires at every pixel-mutating chokepoint
  // that bakes into a layer's surface — used by the PSD export path
  // (Tier 2 Group F PR 2b) to decide whether to emit the layer as
  // raster vs restore the original PSD bytes from the stash. Coverage:
  //   - brush + eraser via the `path:created` handler below
  //   - retouch family (clone, heal, smudge, blur, sharpen, dodge,
  //     burn, sponge) via each variant's `mouse:up` handler
  //   - paint-bucket flood-fill via its `mouse:down` handler
  //   - `bakeNonAffineOverlay` for distort / perspective / warp Apply
  //   - `AdjustmentsModal` destructive Apply (when "Apply as adjustment
  //     layer" was OFF) via EditorPage's `handleAdjustmentCommit`
  //   - `runSmartDeleteOnActiveLayer` for mask-aware AI ops (Smart
  //     Delete, Inpaint, mask-localized Search-Replace)
  //   - `runOnActiveLayerFullImage` for whole-image AI transforms
  //     (Search-Replace, Image-to-Image, Upscale, Replace-Background,
  //     Recolor, etc.)
  // Mask edits do NOT fire — the mask is editor-extrinsic and the
  // export merge re-emits the editor's mask wholesale.
  // Vectorize / paste-from-clipboard do NOT fire — they create NEW
  // layers (no PSD origin → no stash restore even if the flag were
  // false), not in-place mutations.
  // Forward-reference holder for `renderCloneReticle` so the
  // mouse handlers above can invoke it before the function is
  // declared further down in the hook body.
  const renderCloneReticleRef = useRef<((overridePos?: { x: number; y: number }) => void) | null>(
    null,
  );

  /**
   * Resolve the background image. Returns the cached ref if it's still on the
   * canvas; otherwise scans the canvas for a non-selectable FabricImage,
   * rebinds the ref + dims, and returns it. Returns null if the canvas has no
   * image at all.
   *
   * Use this rather than reading `backgroundImageRef.current` directly — it
   * tolerates strict-mode double-mounts and any other path that drops the ref
   * out of sync with the actual canvas state.
   */
  const getBackgroundImage = useCallback((): fabric.FabricImage | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const current = backgroundImageRef.current;
    if (current && canvas.contains(current)) return current;

    // Prefer the explicit `_isBackground` tag; fall back to the legacy
    // "non-selectable FabricImage" check for sessions saved before tagging.
    const found = canvas
      .getObjects()
      .find(
        (o) =>
          o instanceof fabric.FabricImage &&
          (isBackgroundImage(o) || !(o as fabric.FabricImage & { selectable: boolean }).selectable),
      ) as fabric.FabricImage | undefined;

    if (found) {
      tagAsBackground(found);
      backgroundImageRef.current = found;
      imageDimsRef.current = {
        width: found.width || 1,
        height: found.height || 1,
      };
      return found;
    }

    backgroundImageRef.current = null;
    return null;
  }, []);
  const updateViewportInfoRef = useRef<() => void>(() => {});

  const [viewport, setViewport] = useState<ViewportInfo>({
    thumbWidthRatio: 1,
    thumbHeightRatio: 1,
    scrollXRatio: 0,
    scrollYRatio: 0,
    showH: false,
    showV: false,
    zoom: 1,
    panX: 0,
    panY: 0,
    viewportWidth: 0,
    viewportHeight: 0,
  });

  // Reactive flags that drive Cut / Copy / Paste toolbar button enable
  // states. `hasMarqueeSelection` flips on every marquee finalize / clear;
  // `hasClipboard` flips when the in-memory clipboard ref is populated by
  // copy or cut. The clipboard ref itself is non-reactive so the heavy
  // bitmap doesn't trigger re-renders.
  const [hasMarqueeSelection, setHasMarqueeSelection] = useState(false);
  const [hasClipboard, setHasClipboard] = useState(false);
  const clipboardRef = useRef<{
    canvas: HTMLCanvasElement;
    left: number;
    top: number;
  } | null>(null);
  // Helper: re-evaluate `hasMarqueeSelection` from the current marquee
  // state. Called at every finalize and clear point so React state stays
  // in sync with the marqueeRef object identity.
  const refreshMarqueeSelectionState = useCallback(() => {
    const m = marqueeRef.current;
    setHasMarqueeSelection(!!m.obj && !m.isDrawing);
  }, []);

  /** Compute scrollbar indicator state from current viewport
   *  transform. Called by every pan / zoom / wheel handler — at
   *  60+ Hz on a continuous pan. The shallow-compare guard at
   *  the bottom is important: without it `setViewport({...})`
   *  allocates a fresh object identity even when none of the
   *  six ratios changed (e.g. wheel-zoom that lands on the same
   *  rounded ratios). The fresh identity rolls into the
   *  `useFabricCanvas` API memo's deps and invalidates `canvas`
   *  identity downstream, which refires every effect in
   *  EditorPage that lists `canvas` in its deps. Cap the churn
   *  at the source. */
  const updateViewportInfo = useCallback(() => {
    const canvas = canvasRef.current;
    const wrapper = wrapperRef.current;
    if (!canvas || !wrapper) return;

    const vt = canvas.viewportTransform;
    if (!vt) return;

    const zoom = vt[0];
    const panX = vt[4];
    const panY = vt[5];
    const vpW = wrapper.clientWidth;
    const vpH = wrapper.clientHeight;
    const imgW = imageDimsRef.current.width;
    const imgH = imageDimsRef.current.height;

    let next: ViewportInfo;
    if (!imgW || !imgH) {
      next = {
        thumbWidthRatio: 1,
        thumbHeightRatio: 1,
        scrollXRatio: 0,
        scrollYRatio: 0,
        showH: false,
        showV: false,
        zoom,
        panX,
        panY,
        viewportWidth: vpW,
        viewportHeight: vpH,
      };
    } else {
      const contentW = imgW * zoom;
      const contentH = imgH * zoom;

      const thumbWR = Math.min(1, vpW / contentW);
      const thumbHR = Math.min(1, vpH / contentH);

      const maxPanX = 0;
      const minPanX = vpW - contentW;
      const scrollXR = minPanX < maxPanX ? (maxPanX - panX) / (maxPanX - minPanX) : 0;

      const maxPanY = 0;
      const minPanY = vpH - contentH;
      const scrollYR = minPanY < maxPanY ? (maxPanY - panY) / (maxPanY - minPanY) : 0;

      next = {
        thumbWidthRatio: thumbWR,
        thumbHeightRatio: thumbHR,
        scrollXRatio: Math.max(0, Math.min(1, scrollXR)),
        scrollYRatio: Math.max(0, Math.min(1, scrollYR)),
        showH: thumbWR < 0.99,
        showV: thumbHR < 0.99,
        zoom,
        panX,
        panY,
        viewportWidth: vpW,
        viewportHeight: vpH,
      };
    }

    setViewport((prev) => {
      if (
        prev.thumbWidthRatio === next.thumbWidthRatio &&
        prev.thumbHeightRatio === next.thumbHeightRatio &&
        prev.scrollXRatio === next.scrollXRatio &&
        prev.scrollYRatio === next.scrollYRatio &&
        prev.showH === next.showH &&
        prev.showV === next.showV &&
        prev.zoom === next.zoom &&
        prev.panX === next.panX &&
        prev.panY === next.panY &&
        prev.viewportWidth === next.viewportWidth &&
        prev.viewportHeight === next.viewportHeight
      ) {
        return prev;
      }
      return next;
    });
  }, [wrapperRef]);

  // Keep ref in sync so event handlers in useEffect can call the latest version
  updateViewportInfoRef.current = updateViewportInfo;

  // Initialize canvas
  useEffect(() => {
    const canvasEl = canvasElRef.current;
    const wrapper = wrapperRef.current;
    if (!canvasEl || !wrapper) return;

    const canvas = new fabric.Canvas(canvasEl, {
      backgroundColor: 'transparent',
      selection: true,
      preserveObjectStacking: true,
    });

    canvasRef.current = canvas;
    // Expose to window for ad-hoc DevTools debugging.
    if (typeof window !== 'undefined') {
      (window as unknown as { __fiasCanvas?: fabric.Canvas }).__fiasCanvas = canvas;
    }

    // Reusable per-dab offscreen canvases. The Retouch variants
    // each allocated 1–2 fresh canvases per dab via
    // `document.createElement('canvas')`, totalling ~240
    // allocations/sec during a fast drag and a steady stream of
    // GC churn. These caches outlive individual strokes and are
    // resized lazily by the helper as the brush radius changes.
    // The width-setter clears both the bitmap and the context
    // state per the canvas spec, so callers don't need to
    // clearRect first. The hint passed on the first
    // `getContext` call sticks for the lifetime of the canvas;
    // we keep stamp / destSample uses willReadFrequently-
    // consistent so the hint matches the dominant access
    // pattern for each cache.
    const stampCanvasCache: { canvas: HTMLCanvasElement | null } = { canvas: null };
    const destSampleCanvasCache: { canvas: HTMLCanvasElement | null } = { canvas: null };
    const acquireDabCanvas = (
      cache: { canvas: HTMLCanvasElement | null },
      size: number,
    ): HTMLCanvasElement => {
      if (!cache.canvas) cache.canvas = document.createElement('canvas');
      cache.canvas.width = size;
      cache.canvas.height = size;
      return cache.canvas;
    };

    // Force Fabric's auto-created wrapper to fill our container
    if (canvas.wrapperEl) {
      canvas.wrapperEl.style.position = 'absolute';
      canvas.wrapperEl.style.inset = '0';
      canvas.wrapperEl.style.width = '100%';
      canvas.wrapperEl.style.height = '100%';
    }

    // Canvas element always matches wrapper size
    const resize = () => {
      const w = wrapper.clientWidth;
      const h = wrapper.clientHeight;
      if (w > 0 && h > 0) {
        canvas.setDimensions({ width: w, height: h });
        canvas.renderAll();
      }
    };
    resize();

    const observer = new ResizeObserver(resize);
    observer.observe(wrapper);

    /**
     * Composite the visible rendering of every Fabric object
     * tagged with `layerId` into `ctx` at scene-space coords.
     * Used by retouch tools to snapshot the layer's actual
     * appearance as the clone/heal source — the paint surface
     * alone is empty for a non-bg layer (separate overlay) so
     * sampling from it would produce blank stamps.
     *
     * Iterates in z-order (`getObjects()` is bottom-to-top), so
     * the result mirrors what the user sees on screen. Skips
     * invisible objects. Each object's `toCanvasElement()` is
     * positioned at its scene-space bbox.
     */
    const snapshotLayerToContext = (ctx: CanvasRenderingContext2D, layerId: string): void => {
      for (const obj of canvas.getObjects()) {
        if (getObjectLayerId(obj) !== layerId) continue;
        if (obj.visible === false) continue;
        const objWithToCanvas = obj as fabric.FabricObject & {
          toCanvasElement?: () => HTMLCanvasElement;
        };
        if (!objWithToCanvas.toCanvasElement) continue;
        const rendered = objWithToCanvas.toCanvasElement();
        if (!rendered || rendered.width === 0 || rendered.height === 0) continue;
        const br = obj.getBoundingRect();
        ctx.drawImage(rendered, br.left, br.top);
      }
    };

    // Event listeners
    // Apply the per-object canvas clip to every newly-added object
    // so future strokes / pastes / AI outputs stay clipped to the
    // canvas without manual upkeep at each add site. Chrome (the
    // canvas-bounds outline) opts out. No clip is set yet when no
    // document is loaded (imageDimsRef is zero), in which case the
    // first dim-change site (loadImage / initEmptyCanvas) will
    // re-apply clips to every object including this one via
    // ensureCanvasClipRegion.
    canvas.on('object:added', (opt) => {
      const obj = opt.target;
      if (!obj) return;
      // Chrome (outline + fill) opts out of the per-object clip
      // because they're already canvas-scoped by their own geometry.
      // For real layers: apply the clip and re-stack chrome so the
      // new layer renders above the fill rect (Fabric adds new
      // objects to the top, pushing chrome by 1; the explicit
      // restack keeps chrome at the back as user objects accrue).
      if (isCanvasChrome(obj)) return;
      const w = imageDimsRef.current.width;
      const h = imageDimsRef.current.height;
      if (w > 0 && h > 0 && !obj.clipPath) {
        obj.clipPath = buildCanvasClipRect(w, h);
      }
      restackCanvasChrome(canvas);
    });

    // Crop rect — clamp its bounding box to the document during a handle
    // resize so the crop region can never extend past the canvas (the box
    // can only ever select pixels that exist; movement is already locked
    // in CROP_RECT_STYLE). The unified edge-clamp below works for every
    // handle: it pins the four edges of the box to [0, imgW] × [0, imgH]
    // and recomputes left/top + scaleX/scaleY, so the anchored (opposite)
    // corner stays put while the dragged corner stops at the canvas edge.
    canvas.on('object:scaling', (opt) => {
      const target = opt.target;
      if (!target || !(target as unknown as { _isCropRect?: boolean })._isCropRect) return;
      const imgW = imageDimsRef.current.width;
      const imgH = imageDimsRef.current.height;
      if (imgW <= 0 || imgH <= 0) return;
      const w = target.width ?? 0;
      const h = target.height ?? 0;
      if (w <= 0 || h <= 0) return;
      const left = target.left ?? 0;
      const top = target.top ?? 0;
      const right = left + w * (target.scaleX ?? 1);
      const bottom = top + h * (target.scaleY ?? 1);
      const clampedLeft = Math.max(0, Math.min(left, imgW));
      const clampedTop = Math.max(0, Math.min(top, imgH));
      const clampedRight = Math.max(clampedLeft, Math.min(right, imgW));
      const clampedBottom = Math.max(clampedTop, Math.min(bottom, imgH));
      target.set({
        left: clampedLeft,
        top: clampedTop,
        scaleX: (clampedRight - clampedLeft) / w,
        scaleY: (clampedBottom - clampedTop) / h,
      });
      target.setCoords();
    });

    canvas.on('object:modified', (opt) => {
      // Tier 2 Group F sub-piece #5 — slice overlay modifications
      // route through a dedicated `object:modified` listener that
      // dispatches UPDATE_SLICE into the reducer; they don't touch
      // the layer document. Skip the generic onObjectModified call
      // here so a slice resize doesn't add a redundant entry to the
      // layer-history undo stack (the snapshot would be identical
      // because slice overlays carry `excludeFromExport: true`, but
      // mutating refs + tracking would still fire).
      if (opt.target && isSliceOverlay(opt.target)) return;
      onObjectModifiedRef.current?.();
    });
    // Tier 2 Group D — snap-to-guides / snap-to-grid. Fires per
    // Fabric move tick during a Move-tool drag. Reads the latest
    // config via the ref (so toggling a snap setting mid-drag
    // takes effect on the next tick). Skipped for marquee /
    // overlay objects (the visual chrome shouldn't snap), for
    // active Free Transform sessions (which own positioning), and
    // when no snap mode is enabled.
    canvas.on('object:moving', (opt) => {
      const target = opt.target;
      if (!target) return;
      const cfg = snapConfigRef.current;
      const smartCfg = smartSnapConfigRef.current;
      const wantSmart = smartCfg.enabled && !smartCfg.ctrlDisabled;
      // Bail entirely when nothing wants to fire. Note we DO
      // continue when smart-snap is enabled even if guides/grid
      // are off — smart-snap is its own independent feature.
      if (!cfg.snapToGuides && !cfg.snapToGrid && !wantSmart) return;
      // Skip overlay-style objects: marquee, crop rect, clone
      // reticle, quick-mask overlay, paint surface overlays.
      // Each is tagged with a `_isX` flag at creation.
      const tagged = target as unknown as {
        _isMarquee?: boolean;
        _isCropRect?: boolean;
        _isCloneReticle?: boolean;
        _isQuickMaskOverlay?: boolean;
        _isTransformOutline?: boolean;
      };
      if (
        tagged._isMarquee ||
        tagged._isCropRect ||
        tagged._isCloneReticle ||
        tagged._isQuickMaskOverlay ||
        tagged._isTransformOutline
      ) {
        return;
      }
      // Active Free Transform / non-affine session owns the
      // object's position via its own overlay; bailing here keeps
      // the two interaction layers from fighting.
      if (activeToolRef.current === 'ai') return;
      const zoom = canvas.getZoom() || 1;
      const toleranceScene = cfg.toleranceScreen / zoom;
      if (toleranceScene <= 0) return;
      // Refresh the cached `aCoords` BEFORE reading the bounding
      // rect. Fabric's internal `_translateObject` (the drag
      // handler) sets `target.left/top` from the pointer but does
      // NOT call `setCoords()` — so `getBoundingRect()` (which
      // reads from `aCoords`) returns a one-frame-stale bbox.
      // Without this refresh, every tick the snap delta is
      // computed against the PRIOR snapped position rather than
      // the current Fabric-recomputed one, so `target.left` ends
      // up at `(snap - pointerDelta)` instead of `snap` — the
      // object perpetually oscillates around the guide and never
      // aligns. This single call is the difference between a
      // sticky snap and persistent jitter.
      target.setCoords();
      const br = target.getBoundingRect();

      // First pass — guides + grid snap (the existing path).
      const result = snapBoundingBox(br.left, br.top, br.width, br.height, {
        tolerance: toleranceScene,
        snapToGuides: cfg.snapToGuides,
        snapToGrid: cfg.snapToGrid,
        gridSize: cfg.gridSize,
        guides: cfg.guides,
      });

      // Second pass — smart-snap. Runs only when the user has
      // enabled the feature, isn't holding Ctrl, and we have
      // other layers to align against. Per-axis: a guide/grid
      // match wins; smart-snap fills axes the first pass left
      // unmatched. This gives user-placed guides priority over
      // structural alignment (the user's explicit intent beats
      // any incidental edge-coincidence with another layer).
      let smartMatchX: SmartSnapMatch | null = null;
      let smartMatchY: SmartSnapMatch | null = null;
      let smartTargetsForEvent: LayerBBox[] = [];
      if (wantSmart) {
        // Lazy snapshot on first tick of a drag — the layer set
        // doesn't change during a single drag, and the alternative
        // (a per-tick `getObjects()` + bbox walk) would burn the
        // 60Hz budget on documents with many layers.
        if (smartCfg.targets === null) {
          smartCfg.targets = collectSmartSnapTargets(canvas, target);
        }
        smartTargetsForEvent = smartCfg.targets;
        // Source bbox uses post-guide-snap coords so the smart
        // snap aligns against the corrected position — otherwise
        // a guide-snapped X with un-snapped Y might pull the bbox
        // away from the guide as smart-snap takes Y.
        const sourceBBox: LayerBBox = {
          id: getObjectLayerId(target) ?? '__source__',
          left: result.left,
          top: result.top,
          width: br.width,
          height: br.height,
        };
        const sm = smartSnap({
          tolerance: toleranceScene,
          source: sourceBBox,
          targets: smartCfg.targets,
        });
        // Per-axis preference: guides/grid first, then smart.
        if (!result.matchedX && sm.matchX) {
          smartMatchX = sm.matchX;
          result.left = sm.left;
        }
        if (!result.matchedY && sm.matchY) {
          smartMatchY = sm.matchY;
          result.top = sm.top;
        }
      }

      // Translate by the delta between the bbox's current top-left
      // and the snapped one. `target.left/top` are the object's
      // ORIGIN (typically top-left for FabricImage, but may be
      // center for objects with `originX/Y: 'center'`); applying
      // the delta directly avoids needing to know the origin.
      const dx = result.left - br.left;
      const dy = result.top - br.top;
      if (dx === 0 && dy === 0) return;
      target.set({
        left: (target.left ?? 0) + dx,
        top: (target.top ?? 0) + dy,
      });
      target.setCoords();

      // Publish to snap-match listeners so the Guides overlay can
      // flash the matched guide(s) for visual feedback.
      if (snapMatchListenersRef.current.size > 0) {
        const evt: SnapMatchEvent = {
          matchedX: result.matchedX,
          matchedY: result.matchedY,
        };
        for (const cb of snapMatchListenersRef.current) cb(evt);
      }
      // Publish to smart-snap-match listeners — fires only when at
      // least one axis matched another layer. The overlay reads
      // `source` + `targets` to draw a magenta line from the
      // dragged bbox to the matched target.
      if (smartSnapMatchListenersRef.current.size > 0 && (smartMatchX || smartMatchY)) {
        const finalSource: LayerBBox = {
          id: getObjectLayerId(target) ?? '__source__',
          left: result.left,
          top: result.top,
          width: br.width,
          height: br.height,
        };
        const evt: SmartSnapMatchEvent = {
          source: finalSource,
          matchX: smartMatchX,
          matchY: smartMatchY,
          targets: smartTargetsForEvent,
        };
        for (const cb of smartSnapMatchListenersRef.current) cb(evt);
      }
    });
    // Clear the per-drag smart-snap target snapshot once the
    // pointer comes up. Fires unconditionally — every mouse:up
    // (regardless of tool) signals end-of-drag. Cheap (one
    // ref assignment) so no need to gate on whether we had a
    // snapshot in the first place.
    canvas.on('mouse:up', () => {
      smartSnapConfigRef.current.targets = null;
    });

    canvas.on('path:created', (e) => {
      const path = (e as { path?: fabric.Object }).path;
      if (!path) {
        onPathCreatedRef.current?.();
        return;
      }
      // Always remove the auto-added Fabric Path from the main canvas first;
      // bake takes the unbound path and renders it onto the active layer's
      // surface via a temporary StaticCanvas.
      canvas.remove(path);
      const layerId = activeLayerIdRef.current;
      if (layerId) {
        const isEraser = activeToolRef.current === 'eraser';
        // Route to mask alpha when the active edit target is the
        // mask AND the layer actually has a mask. Otherwise paint
        // into the layer's pixel surface as before. Tier 0
        // documents always hit the surface branch.
        const target = editTargetsRef.current[layerId] ?? 'layer';
        const maskResult: MaskBakeResult =
          target === 'mask' ? bakeStrokeOntoMask(path, layerId, isEraser) : 'no-mask';
        if (maskResult === 'no-mask') {
          // No mask attached (or canvas not ready) — fall back to
          // painting on the pixel surface. This is the Tier 0
          // path that predates masks entirely.
          const baked = bakeStrokeOntoSurface(path, layerId, isEraser);
          if (baked) {
            // Tier 2 Group F — the layer's raster has diverged from
            // its PSD source (if any). EditorPage flips the
            // rasterModified flag so the export path writes this
            // layer as raster instead of restoring from the stash.
            onLayerRasterModifiedRef.current?.(layerId);
          } else if (paintPromptPendingRef.current.has(layerId)) {
            // Bake aborted because the rasterize-before-paint gate
            // fired. Capture this stroke so `approveLayerForPaint`
            // can replay it after the layer is rasterized — the
            // user's drag becomes their actual paint without having
            // to retrace it. Cancel drops the closure.
            pendingPaintReplayRef.current.set(layerId, () => {
              const ok = bakeStrokeOntoSurface(path, layerId, isEraser);
              if (ok) onLayerRasterModifiedRef.current?.(layerId);
            });
          }
        }
        // `'painted'` — already wrote to RAW + materialized EFFECTIVE,
        // nothing else to do. Mask edits don't fire
        // onLayerRasterModified because the mask is editor-extrinsic
        // and the export merge re-emits it wholesale. `'vector-blocked'`
        // — stroke is swallowed by design; `onMaskPaintBlocked` has
        // already fired so EditorPage can toast.
      }
      // Synchronous render so the user sees the baked stroke in the same
      // frame the live preview was cleared.
      canvas.renderAll();
      onPathCreatedRef.current?.();
    });

    const fireSelection = (selected: fabric.FabricObject[] | undefined) => {
      const objs = selected ?? [];
      const ids = objs.map((o) => (o as fabric.FabricObject & { id?: string }).id || '');
      onSelectionChangedRef.current?.(ids);
      // Pull the layerId from the first selected object — sufficient for the
      // common single-object case; for multi-select, fall back to that object's
      // layer to drive the panel highlight.
      const layerId = objs.length > 0 ? (getObjectLayerId(objs[0]) ?? null) : null;
      onLayerSelectedRef.current?.(layerId);
    };

    // When a single non-bg object is clicked with the move tool, expand the
    // selection to include every other object on the same layer (e.g. the
    // layer's paint surface) so they translate together. Without this, drawing
    // baked into a shape layer's paint surface would stay behind when the
    // shape is moved.
    let isExpanding = false;
    const expandSelectionToLayer = (selected: fabric.FabricObject[] | undefined) => {
      if (isExpanding) return;
      if (activeToolRef.current !== 'move') return;
      if (!selected || selected.length !== 1) return;
      const target = selected[0];
      if (isBackgroundImage(target) || isMarqueeObject(target)) return;
      const layerId = getObjectLayerId(target);
      if (!layerId) return;
      const peers = canvas.getObjects().filter(
        (o) =>
          o !== target &&
          getObjectLayerId(o) === layerId &&
          !isMarqueeObject(o) &&
          !isBackgroundImage(o) &&
          // Paint overlays are doc-sized scene-aligned UI
          // artifacts. Including them in the ActiveSelection
          // inflates the Move-tool bbox to the full document
          // (the user sees a giant bbox far past the layer's
          // image). Tradeoff: retouch strokes don't translate
          // when the layer moves — acceptable v1 since the
          // overlay is also being clipped to the image bbox
          // at creation time (see `ensurePaintSurface`).
          !isPaintOverlay(o),
      );
      if (peers.length === 0) return;
      const all = [target, ...peers];
      isExpanding = true;
      try {
        canvas.discardActiveObject();
        const sel = new fabric.ActiveSelection(all, { canvas });
        canvas.setActiveObject(sel);
        canvas.requestRenderAll();
      } finally {
        isExpanding = false;
      }
    };

    const handleSelection = (selected: fabric.FabricObject[] | undefined) => {
      fireSelection(selected);
      expandSelectionToLayer(selected);
    };
    canvas.on('selection:created', (e) => handleSelection(e.selected));
    canvas.on('selection:updated', (e) => handleSelection(e.selected));
    canvas.on('selection:cleared', () => {
      onSelectionChangedRef.current?.([]);
      // Move tool: the background image is the viewport-pan surface and
      // must stay non-evented whenever it isn't the actively-selected
      // object. Selecting the Background layer from the panel flips it
      // evented (selectLayerObjects) so it can be dragged with its bbox
      // showing; once that selection clears (e.g. a click on empty
      // canvas to dismiss the bbox), restore the non-evented state —
      // otherwise the next canvas click would grab and drag the
      // background instead of panning the viewport.
      if (activeToolRef.current === 'move') {
        const bg = getBackgroundImage();
        if (bg) bg.set({ selectable: false, evented: false });
      }
    });

    // Cursor-position broadcast (drives the StatusBar X/Y readout +
    // ruler indicators). mouse:move fires far faster than the display
    // refreshes and each broadcast triggers a React setState in the
    // StatusBar subscriber, so we coalesce to one broadcast per frame:
    // record the latest scene point, flush once per rAF. (Same fix as
    // the pinch handler.)
    let cursorRafId: number | null = null;
    let pendingCursor: { x: number; y: number } | null = null;
    const flushCursor = () => {
      cursorRafId = null;
      const next = pendingCursor;
      pendingCursor = null;
      if (!next) return;
      const listeners = cursorListenersRef.current;
      for (const cb of listeners) cb(next.x, next.y);
    };
    canvas.on('mouse:move', (opt) => {
      if (!opt.e) return;
      const listeners = cursorListenersRef.current;
      if (listeners.size === 0) return;
      const scenePoint = canvas.getScenePoint(opt.e);
      pendingCursor = { x: Math.round(scenePoint.x), y: Math.round(scenePoint.y) };
      if (cursorRafId === null) cursorRafId = requestAnimationFrame(flushCursor);
    });

    // Mouse wheel zoom — zoom toward the cursor position
    canvas.on('mouse:wheel', (opt) => {
      const e = opt.e as WheelEvent;
      e.preventDefault();
      e.stopPropagation();

      const delta = e.deltaY;
      const vt = canvas.viewportTransform;
      if (!vt) return;

      const currentZoom = vt[0];
      const factor = delta > 0 ? 0.9 : 1.1;
      const newZoom = Math.max(0.1, Math.min(10, currentZoom * factor));

      // Zoom toward the mouse position. `zoomToPoint` expects a point
      // in canvas-element (viewport) pixel coordinates — i.e., before
      // the viewport transform is applied. Passing a scene-space point
      // would double-transform and the image would jump away from the
      // cursor on every wheel tick. (The touch-zoom path uses
      // `touchMidpoint`, which is already canvas-relative.)
      const point = canvas.getViewportPoint(e);
      canvas.zoomToPoint(point, newZoom);
      if (marqueeRef.current.obj) {
        applyMarqueeStroke(canvas, marqueeRef.current.obj);
        for (const o of marqueeRef.current.extraObjs) applyMarqueeStroke(canvas, o);
      }
      applySliceStrokes(canvas, sliceStateRef.current.objects);
      canvas.renderAll();

      onZoomChangedRef.current?.(newZoom);
      updateViewportInfoRef.current();
    });

    // Universal click tracker — record the last canvas click in scene
    // coords so callers (paste, drop) can position new content at the
    // user's last interaction point. Runs before any tool gate so every
    // mouse:down updates the ref, including clicks that the active tool
    // would otherwise early-return on. Also flips
    // `canvasInteractionRef` so the autosave gate can defer mid-gesture.
    canvas.on('mouse:down', (opt) => {
      canvasInteractionRef.current = true;
      if (!opt.e) return;
      const p = canvas.getScenePoint(opt.e);
      lastClickSceneRef.current = { x: p.x, y: p.y };
    });
    canvas.on('mouse:up', () => {
      canvasInteractionRef.current = false;
    });

    // Move tool, Auto-Select OFF — capture the pre-click active object
    // BEFORE Fabric's internal `__onMouseDown` swaps it to whatever the
    // click hit. `mouse:down:before` is the only hook that fires
    // before that swap. The Move-tool `mouse:down` handler below
    // consults this to decide between Fabric's grab-on-click behavior
    // and the always-drag-active override.
    canvas.on('mouse:down:before', () => {
      if (activeToolRef.current !== 'move') return;
      if (autoSelectLayersRef.current) return;
      moveActiveAtDownRef.current = canvas.getActiveObject() ?? null;
    });

    // Move tool — drag empty canvas to pan the viewport (drag on object lets Fabric move it)
    canvas.on('mouse:down', (opt) => {
      // Tool handlers all gate on `activeToolRef.current === '<tool>'`
      // and early-return otherwise. During a non-affine Transform
      // session, `setupNonAffineOverlay` overrides the ref to 'ai'
      // (a tool with no registered handlers) so every tool's
      // mouse:* path early-returns uniformly. Restored on
      // teardown.
      if (activeToolRef.current !== 'move') return;
      if (opt.e === undefined) return;

      // Marquee drag: when there's a finalized non-wand marquee and
      // the click lands inside its scene bbox, hijack the gesture so
      // the user can reposition the selection outline. Runs BEFORE
      // findTarget so the click doesn't fall through to a panning
      // gesture on the marquee's own canvas area (the marquee
      // fabric.Object is selectable=false / evented=false, so it
      // doesn't register as a hit-target naturally).
      const pointer = canvas.getScenePoint(opt.e);
      const m = marqueeRef.current;
      if (m.obj && !m.isDrawing && m.shape !== 'wand') {
        const bbox = computeMarqueeSceneBBox(m);
        if (
          bbox &&
          pointer.x >= bbox.left &&
          pointer.x <= bbox.left + bbox.width &&
          pointer.y >= bbox.top &&
          pointer.y <= bbox.top + bbox.height
        ) {
          marqueeDragRef.current = {
            isDragging: true,
            startSceneX: pointer.x,
            startSceneY: pointer.y,
            initial: {
              shape: m.shape,
              objLeft: m.obj.left ?? 0,
              objTop: m.obj.top ?? 0,
              extras: m.extraObjs.map((o) => ({
                obj: o,
                left: o.left ?? 0,
                top: o.top ?? 0,
              })),
              points: m.points.map((p) => ({ x: p.x, y: p.y })),
            },
          };
          // Fabric's `__onMouseDown` runs BEFORE this `mouse:down`
          // event handler and has already set the underlying layer as
          // the active object + initialized `_currentTransform` to
          // start a drag-translate. Without canceling both, the layer
          // moves alongside the marquee on every `mouse:move`. We
          // discard the active object (clears selection state and
          // fires `selection:cleared`) and null `_currentTransform`
          // (internal field, but the only documented escape hatch in
          // v7 for cancelling an in-flight drag set up by the
          // implicit selectable-target pathway).
          canvas.discardActiveObject();
          (
            canvas as fabric.Canvas & {
              _currentTransform: unknown;
            }
          )._currentTransform = null;
          canvas.defaultCursor = 'move';
          canvas.setCursor('move');
          return;
        }
      }

      // Auto-Select OFF override: drag whichever layer was active
      // BEFORE this click, regardless of where on canvas the click
      // landed. Matches Photoshop's Move-tool default. We let Fabric
      // handle clicks that DID land on the previously-active object's
      // body/controls — that's the normal drag/resize/rotate path —
      // and only override when the click would otherwise have either
      // hit empty area (which today starts a pan) or hit a DIFFERENT
      // layer (which today switches active to it).
      const wasActive = moveActiveAtDownRef.current;
      moveActiveAtDownRef.current = null;
      if (!autoSelectLayersRef.current && wasActive) {
        const hit = canvas.findTarget(opt.e).target;
        if (hit !== wasActive) {
          // Fabric's `__onMouseDown` may have already changed the
          // active object to `hit` (or to null) and set up
          // `_currentTransform` on the new target. Restore the prior
          // active and cancel that transform so nothing else moves.
          if (canvas.getActiveObject() !== wasActive) {
            canvas.discardActiveObject();
            canvas.setActiveObject(wasActive);
          }
          (canvas as fabric.Canvas & { _currentTransform: unknown })._currentTransform = null;
          moveActiveDragRef.current = {
            isDragging: true,
            target: wasActive,
            startSceneX: pointer.x,
            startSceneY: pointer.y,
            initialLeft: wasActive.left ?? 0,
            initialTop: wasActive.top ?? 0,
          };
          canvas.defaultCursor = 'move';
          canvas.setCursor('move');
          canvas.requestRenderAll();
          return;
        }
        // Click landed ON the active object — let Fabric handle the
        // drag/resize/rotate via its `_currentTransform`. Fall
        // through to the existing findTarget check, which will
        // early-return because there's a target.
      }

      // Fabric v7 changed `findTarget` to always return a
      // `FullTargetsInfoWithContainer` object (truthy even when no
      // hit) instead of the v6 `FabricObject | undefined`. Read
      // `.target` to recover the v6 boolean semantics — without this,
      // the early-return fires on every click and silently breaks
      // the move tool / marquee / "click empty canvas to add another
      // text or shape" flow.
      if (canvas.findTarget(opt.e).target) return;

      // Empty-area press (the pasteboard around the document, or any
      // blank canvas region): clear the active object so its bounding
      // box / transform handles dismiss. Fabric does this implicitly
      // for mouse input but NOT for touch, so a tap outside the canvas
      // on mobile left the box stuck — discard explicitly here so the
      // behavior is identical for pointer and touch. (The Auto-Select
      // OFF "keep dragging the active layer" path returned earlier, so
      // reaching here always means a genuine deselect.)
      if (canvas.getActiveObject()) {
        canvas.discardActiveObject();
        // A tap (no drag) won't otherwise trigger a render, so the
        // handles would linger until the next paint — repaint now.
        canvas.requestRenderAll();
      }

      const point = clientPointFromEvent(opt.e);
      panRef.current = { isPanning: true, lastX: point.x, lastY: point.y };
      canvas.defaultCursor = 'grabbing';
      canvas.setCursor('grabbing');
    });

    canvas.on('mouse:move', (opt) => {
      // Marquee-drag takes priority over pan when active. Recompute
      // every shape's position from the captured `initial` snapshot —
      // never from the current values — so floating-point drift can't
      // accumulate across hundreds of mouse:move events.
      // Auto-Select OFF Move-tool drag of the captured active layer.
      // Takes priority over both marquee-drag and pan. Computes from
      // the snapshot at drag start so floating-point drift can't
      // accumulate across hundreds of `mouse:move` events.
      const activeDrag = moveActiveDragRef.current;
      if (activeDrag.isDragging && activeDrag.target && opt.e) {
        const pointer2 = canvas.getScenePoint(opt.e);
        const dx = pointer2.x - activeDrag.startSceneX;
        const dy = pointer2.y - activeDrag.startSceneY;
        activeDrag.target.set({
          left: activeDrag.initialLeft + dx,
          top: activeDrag.initialTop + dy,
        });
        activeDrag.target.setCoords();
        canvas.requestRenderAll();
        return;
      }

      const drag = marqueeDragRef.current;
      if (drag.isDragging && drag.initial && opt.e) {
        const pointer = canvas.getScenePoint(opt.e);
        const dx = pointer.x - drag.startSceneX;
        const dy = pointer.y - drag.startSceneY;
        const m = marqueeRef.current;
        if (!m.obj) return;
        m.obj.set({ left: drag.initial.objLeft + dx, top: drag.initial.objTop + dy });
        m.obj.setCoords();
        for (const e of drag.initial.extras) {
          e.obj.set({ left: e.left + dx, top: e.top + dy });
          e.obj.setCoords();
        }
        if (drag.initial.shape === 'lasso' || drag.initial.shape === 'polygon') {
          // Update the marquee's logical point set AND rebuild the
          // Polyline — the Polyline's `points` array isn't a live
          // reference to `m.points` so a plain set() on the polyline
          // doesn't reflect the moved points.
          m.points = drag.initial.points.map((p) => ({ x: p.x + dx, y: p.y + dy }));
          replaceMarqueePolyline(canvas, m, marqueeColorRef.current);
        }
        canvas.requestRenderAll();
        return;
      }

      if (!panRef.current.isPanning || !opt.e) return;
      const point = clientPointFromEvent(opt.e);
      const dx = point.x - panRef.current.lastX;
      const dy = point.y - panRef.current.lastY;
      canvas.relativePan(new fabric.Point(dx, dy));
      panRef.current.lastX = point.x;
      panRef.current.lastY = point.y;
      updateViewportInfoRef.current();
    });

    canvas.on('mouse:up', () => {
      if (moveActiveDragRef.current.isDragging) {
        const dragged = moveActiveDragRef.current.target;
        moveActiveDragRef.current = {
          isDragging: false,
          target: null,
          startSceneX: 0,
          startSceneY: 0,
          initialLeft: 0,
          initialTop: 0,
        };
        canvas.defaultCursor = 'grab';
        canvas.setCursor('grab');
        // Snapshot the move — matches Fabric's regular `object:modified`
        // flow that EditorPage wires to `history.saveSnapshot`. Without
        // this, the always-drag-active path produces no undo entry.
        if (dragged) onObjectModifiedRef.current?.();
        return;
      }
      if (marqueeDragRef.current.isDragging) {
        marqueeDragRef.current = {
          isDragging: false,
          startSceneX: 0,
          startSceneY: 0,
          initial: null,
        };
        canvas.defaultCursor = 'grab';
        canvas.setCursor('grab');
        return;
      }
      if (panRef.current.isPanning) {
        panRef.current.isPanning = false;
        canvas.defaultCursor = 'grab';
      }
    });

    // Space-pan — Tier 2 Group D. When the user holds Space (via
    // `setSpaceHeld(true)` from EditorPage's key tracker), the active
    // tool is overridden to 'space-pan' and this handler claims every
    // mouse:down regardless of whether it hit an object. The existing
    // mouse:move / mouse:up pan handlers above already gate on
    // `panRef.current.isPanning` (not the tool), so they handle the
    // drag completion correctly for both 'move' and 'space-pan'.
    canvas.on('mouse:down', (opt) => {
      if (activeToolRef.current !== 'space-pan' || !opt.e) return;
      const point = clientPointFromEvent(opt.e);
      panRef.current = { isPanning: true, lastX: point.x, lastY: point.y };
      canvas.defaultCursor = 'grabbing';
      canvas.setCursor('grabbing');
    });

    // True when a scene-space point lies within the document bounds.
    // Clicks on the surrounding pasteboard must NOT create content —
    // a stray text / shape dropped off-canvas is invisible (clipped to
    // the document) yet still adds a layer the user can't see.
    const pointInDocument = (p: { x: number; y: number }): boolean => {
      const { width, height } = imageDimsRef.current;
      return width > 0 && height > 0 && p.x >= 0 && p.x <= width && p.y >= 0 && p.y <= height;
    };

    // ── Tap-to-place tools commit on RELEASE for touch input ──────────
    // The one-shot "tap" tools — text, shape, fill, and the click-based
    // marquee shapes (polygon, magic wand) — all act on a single tap. A
    // two-finger pan begins with one finger landing first; firing these
    // on that first contact drops a stray text box / shape / fill, a
    // phantom polygon vertex, or replaces the selection before we can
    // tell the gesture is a pan. For TOUCH we therefore defer the action
    // to the matching `mouse:up` and let `onTouchStart` cancel it the
    // instant a second finger lands. Mouse and pen run synchronously on
    // press as before, so desktop/stylus behavior is unchanged.
    //
    // Continuous tools (draw, eraser, retouch/clone/heal/smudge/blur/…)
    // are deliberately NOT deferred — they must engage on press to feel
    // responsive, and a two-finger pan that interrupts them is handled by
    // the abandon + pointer-suppressor path (see `onTouchStart` and
    // `suppressPointerDuringGesture`).
    let pendingTapTool: (() => void) | null = null;
    const runOrDeferTapTool = (e: Event | undefined, action: () => void): void => {
      if (isTouchEvent(e)) {
        // Don't arm while a pinch is already in progress — this guards the
        // second finger's own `mouse:down`, whose ordering relative to its
        // `touchstart` (which sets `isPinching`) is not guaranteed across
        // browsers.
        if (pinchRef.current.isPinching) return;
        pendingTapTool = action;
        return;
      }
      action();
    };
    canvas.on('mouse:up', () => {
      // Commit a deferred touch tap. No-op for mouse/pen (which never arm
      // a pending action) and for releases that ended a cancelled gesture
      // (`onTouchStart` nulls `pendingTapTool`).
      if (!pendingTapTool) return;
      const action = pendingTapTool;
      pendingTapTool = null;
      action();
    });

    // Shape tool — click on empty canvas to drop a new shape at the click
    // position. Click on an existing shape lets Fabric handle it (select/drag).
    canvas.on('mouse:down', (opt) => {
      if (activeToolRef.current !== 'shape') return;
      if (!opt.e) return;
      if (canvas.findTarget(opt.e).target) return;
      const point = canvas.getScenePoint(opt.e);
      if (!pointInDocument(point)) return;
      runOrDeferTapTool(opt.e, () => onShapeToolClickRef.current?.({ x: point.x, y: point.y }));
    });

    // Text tool — click on empty canvas to drop a new IText at the click position.
    canvas.on('mouse:down', (opt) => {
      if (activeToolRef.current !== 'text') return;
      if (!opt.e) return;
      if (canvas.findTarget(opt.e).target) return;
      const point = canvas.getScenePoint(opt.e);
      if (!pointInDocument(point)) return;
      runOrDeferTapTool(opt.e, () => onTextToolClickRef.current?.({ x: point.x, y: point.y }));
    });

    // ── Touch text selection: double-tap = word, triple-tap = all ──────
    // Fabric's IText selects a word on dblclick and a line on tripleclick,
    // but both ride the DOM `dblclick` event and its native click count
    // (`e.detail`), which browsers never produce for touch — so on mobile
    // a double-tap on text did nothing. We count the taps ourselves and
    // drive Fabric's `selectWord` / `selectAll`. The selection is applied
    // in a `requestAnimationFrame` because the canvas `mouse:up` event
    // fires BEFORE the IText's own `mouseUpHandler` (which is what enters
    // editing on the second tap); deferring one frame lets editing settle
    // so our selection isn't clobbered by Fabric's cursor placement.
    // Mouse/pen are untouched — this only arms for touch input (via
    // `isTouchEvent`) and Fabric's native dblclick/tripleclick still
    // drives desktop.
    // Tap count is tracked per-IText (the target is the tracker `key`, so
    // tapping a different text box restarts the count) via the shared
    // `createTapTracker` helper.
    const textTap = createTapTracker();
    canvas.on('mouse:up', (opt) => {
      if (!isTouchEvent(opt.e)) return;
      const e = opt.e as PointerEvent;
      const target = opt.target;
      if (!(target instanceof fabric.IText) || !target.editable) return;
      const sp = canvas.getScenePoint(e);
      const count = textTap.register(sp.x, sp.y, canvas.getZoom(), target);
      if (count < 2) return;
      requestAnimationFrame(() => {
        if (!target.canvas) return; // disposed between tap and frame
        if (!target.isEditing) target.enterEditing(e);
        if (count >= 3) {
          target.selectAll();
        } else {
          target.selectWord(target.getSelectionStartFromPointer(e));
        }
        canvas.requestRenderAll();
      });
      // Cap at triple; a 4th tap starts a fresh single-tap cycle.
      if (count >= 3) textTap.reset();
    });

    // Pen tool (Tier 2 Group A). Three variants share the same
    // anchor accumulator; the per-event behavior branches on the
    // current variant. Click-back-on-first-anchor closes the path
    // (Photoshop convention); double-click finishes as open. The
    // close-anchor detection uses scene-pixel distance so it
    // scales with zoom naturally.
    const PEN_CLOSE_RADIUS = 8;
    const FREEFORM_MIN_SAMPLE_DIST = 4;
    // Touch double-tap finishes an OPEN pen path — the touch equivalent of
    // the desktop double-click (`mouse:dblclick`, which touch never fires)
    // and the Enter key (no keyboard on mobile). A quick second tap nearby
    // finalizes. Mouse/pen keep using `mouse:dblclick` (below); this only
    // arms for touch input (via `isTouchEvent`).
    const penTap = createTapTracker();
    canvas.on('mouse:down', (opt) => {
      if (activeToolRef.current !== 'pen') return;
      if (!opt.e) return;
      const settings = penSettingsRef.current;
      const point = canvas.getScenePoint(opt.e);
      const isTouch = isTouchEvent(opt.e);

      // Initialise the working state on the first click of a new
      // session. Subsequent clicks append to the existing list.
      if (!penStateRef.current) {
        penStateRef.current = {
          anchors: [],
          previewObj: null,
          draggingAnchorIndex: null,
          freeformActive: false,
        };
        // Fresh path — drop any stale double-tap timing from a prior one.
        penTap.reset();
      }
      const state = penStateRef.current;

      if (settings.variant === 'freeform') {
        // Freeform starts an active sampling stroke. Each mouse:move
        // adds a sample point; mouse:up finishes the path.
        state.freeformActive = true;
        state.anchors = [{ x: point.x, y: point.y, type: 'smooth' }];
        updatePenPreview();
        return;
      }

      // Curvature / Pen — first check for click-on-first-anchor
      // to close the path. Requires ≥2 anchors so a single-anchor
      // session doesn't auto-close.
      if (state.anchors.length >= 2 && settings.autoAddDelete) {
        const a0 = state.anchors[0];
        const dx = a0.x - point.x;
        const dy = a0.y - point.y;
        if (dx * dx + dy * dy <= PEN_CLOSE_RADIUS * PEN_CLOSE_RADIUS) {
          finalizePenPath(true);
          return;
        }
      }

      // Touch double-tap → finish the path as OPEN (no keyboard / dblclick
      // on mobile). A quick second tap near the previous one with ≥2
      // anchors finalizes; the first tap of the pair already placed its
      // anchor, so we finalize as-is rather than adding a duplicate. The
      // close-on-first-anchor check above still takes precedence, so a
      // double-tap landing on the start anchor closes instead.
      if (isTouch) {
        const tapCount = penTap.register(point.x, point.y, canvas.getZoom());
        if (tapCount >= 2 && state.anchors.length >= 2) {
          penTap.reset();
          finalizePenPath(false);
          return;
        }
      }

      // Otherwise append a fresh anchor and recompute handles per
      // variant.
      const newAnchor: VectorAnchor = {
        x: point.x,
        y: point.y,
        type: settings.variant === 'curvature' ? 'smooth' : 'corner',
      };
      state.anchors.push(newAnchor);

      if (settings.variant === 'curvature') {
        // Catmull-Rom-style: every click re-fits all handles for
        // a C1-continuous curve through every anchor.
        const positions = state.anchors.map((a) => ({ x: a.x, y: a.y, type: a.type }));
        state.anchors = recomputeCurvatureHandles(positions, false);
      } else {
        // Pen variant: stage the just-placed anchor for drag-to-
        // create-smooth-handles via mouse:move.
        state.draggingAnchorIndex = state.anchors.length - 1;
      }
      // (The tap was already registered with `penTap` above — no separate
      // record needed; the next tap compares against it.)
      updatePenPreview();
    });

    canvas.on('mouse:move', (opt) => {
      if (activeToolRef.current !== 'pen') return;
      const state = penStateRef.current;
      if (!state || !opt.e) return;
      const settings = penSettingsRef.current;
      const point = canvas.getScenePoint(opt.e);

      if (settings.variant === 'freeform') {
        if (!state.freeformActive) return;
        const last = state.anchors[state.anchors.length - 1];
        const dx = point.x - last.x;
        const dy = point.y - last.y;
        if (dx * dx + dy * dy < FREEFORM_MIN_SAMPLE_DIST * FREEFORM_MIN_SAMPLE_DIST) return;
        state.anchors.push({ x: point.x, y: point.y, type: 'smooth' });
        updatePenPreview();
        return;
      }

      if (state.draggingAnchorIndex === null) return;
      const dragIdx = state.draggingAnchorIndex;
      const anchor = state.anchors[dragIdx];
      if (!anchor) return;
      // Pen variant drag: pull-vector becomes the outgoing handle;
      // mirror into the incoming handle so the segment-on-either-
      // side runs smoothly through the anchor. Above a tiny
      // threshold the type promotes from 'corner' to 'smooth'.
      const outDx = point.x - anchor.x;
      const outDy = point.y - anchor.y;
      const len = Math.hypot(outDx, outDy);
      const next: VectorAnchor =
        len < 2
          ? {
              ...anchor,
              type: 'corner',
              inX: undefined,
              inY: undefined,
              outX: undefined,
              outY: undefined,
            }
          : {
              ...anchor,
              type: 'smooth',
              inX: -outDx,
              inY: -outDy,
              outX: outDx,
              outY: outDy,
            };
      state.anchors[dragIdx] = next;
      updatePenPreview();
    });

    canvas.on('mouse:up', () => {
      if (activeToolRef.current !== 'pen') return;
      const state = penStateRef.current;
      if (!state) return;
      const settings = penSettingsRef.current;

      if (settings.variant === 'freeform') {
        if (!state.freeformActive) return;
        state.freeformActive = false;
        // Smooth the sampled stroke before finalize: Catmull-Rom
        // through every sample produces a clean curve in place of
        // the raw polyline.
        if (state.anchors.length >= 2) {
          const positions = state.anchors.map((a) => ({ x: a.x, y: a.y, type: 'smooth' as const }));
          state.anchors = recomputeCurvatureHandles(positions, false);
          // Freeform commits the whole path on mouse:up — one
          // stroke = one finalized path.
          finalizePenPath(false);
        } else {
          // Pure click-without-drag in Freeform is a no-op.
          clearPenState();
        }
        return;
      }

      // Pen variant: end the in-progress drag; the anchor's
      // handles are already in their final shape from mouse:move.
      if (state.draggingAnchorIndex !== null) {
        state.draggingAnchorIndex = null;
      }
    });

    // Double-click finishes the current pen path as OPEN — the
    // alternative to Enter from the keyboard. Mirrors Photoshop's
    // double-click-to-finish convention.
    canvas.on('mouse:dblclick', () => {
      if (activeToolRef.current !== 'pen') return;
      if (penStateRef.current) finalizePenPath(false);
    });

    // Path Edit mode (Tier 2 Group A PR-B). Gated on the overlay
    // ref being non-null — when active, `activeToolRef` is forced
    // to `'ai'` so every other tool handler short-circuits, and
    // the canvas-level mouse:* hits below take over.
    canvas.on('mouse:down', (opt) => {
      const overlay = pathEditOverlayRef.current;
      if (!overlay || !opt.e) return;
      const point = canvas.getScenePoint(opt.e);
      const sub = overlay.workingPath.subPaths[0];
      if (!sub) return;

      // Selected anchor's handles take hit-test priority — when a
      // handle is rendered, it sits on top of the anchor and the
      // user's intent is almost always to grab the handle.
      //
      // Hit radii divide by zoom so the clickable area on SCREEN
      // matches the rendered handle/anchor size. Without this,
      // zoomed-in views would have huge hit zones (a single screen
      // pixel covers many scene pixels) and zoomed-out views would
      // require pixel-perfect clicks on tiny anchors.
      const zoom = canvas.getZoom() || 1;
      const handleHitRadius = PATH_EDIT_HANDLE_HIT_RADIUS_SCENE / zoom;
      const anchorHitRadius = PATH_EDIT_ANCHOR_HIT_RADIUS_SCENE / zoom;
      if (overlay.selectedAnchorIndex !== null) {
        const sel = sub.anchors[overlay.selectedAnchorIndex];
        if (sel) {
          const handle = hitTestHandle(sel, point.x, point.y, handleHitRadius);
          if (handle) {
            overlay.drag = {
              kind: handle === 'in' ? 'handleIn' : 'handleOut',
              anchorIndex: overlay.selectedAnchorIndex,
            };
            return;
          }
        }
      }

      // Anchor hit-test next. A new selection refreshes the
      // overlay so the previously-selected anchor's handles
      // disappear and the new selection's appear.
      const hit = hitTestAnchor(overlay.workingPath, point.x, point.y, anchorHitRadius);
      if (hit) {
        overlay.selectedAnchorIndex = hit.anchorIndex;
        overlay.drag = { kind: 'anchor', anchorIndex: hit.anchorIndex };
        updatePathEditOverlayRef.current?.();
        onPathEditSelectionChangedRef.current?.(hit.anchorIndex);
        return;
      }

      // Empty space — clear the selection. No drag.
      if (overlay.selectedAnchorIndex !== null) {
        overlay.selectedAnchorIndex = null;
        overlay.drag = null;
        updatePathEditOverlayRef.current?.();
        onPathEditSelectionChangedRef.current?.(null);
      }
    });

    canvas.on('mouse:move', (opt) => {
      const overlay = pathEditOverlayRef.current;
      if (!overlay || !overlay.drag || !opt.e) return;
      const point = canvas.getScenePoint(opt.e);
      const sub = overlay.workingPath.subPaths[0];
      if (!sub) return;
      const idx = overlay.drag.anchorIndex;
      const a = sub.anchors[idx];
      if (!a) return;

      if (overlay.drag.kind === 'anchor') {
        // Move the anchor. Handles are stored RELATIVE to the
        // anchor, so they translate with it for free — no extra
        // arithmetic needed here.
        sub.anchors[idx] = { ...a, x: point.x, y: point.y };
      } else if (overlay.drag.kind === 'handleIn') {
        const dx = point.x - a.x;
        const dy = point.y - a.y;
        // Mirror to the OUT handle when the anchor is `smooth`
        // so both segments stay tangent-continuous. `asymmetric`
        // keeps tangent direction but not magnitude; `corner`
        // doesn't mirror at all. The mirror runs only when the
        // outX/outY are defined (otherwise we'd be inventing a
        // tangent the user didn't ask for).
        const next: VectorAnchor = { ...a, inX: dx, inY: dy };
        if (a.type === 'smooth' && (a.outX !== undefined || a.outY !== undefined)) {
          next.outX = -dx;
          next.outY = -dy;
        } else if (a.type === 'asymmetric' && (a.outX !== undefined || a.outY !== undefined)) {
          const inLen = Math.hypot(dx, dy);
          const outLen = Math.hypot(a.outX ?? 0, a.outY ?? 0);
          if (inLen > 0 && outLen > 0) {
            const k = outLen / inLen;
            next.outX = -dx * k;
            next.outY = -dy * k;
          }
        }
        sub.anchors[idx] = next;
      } else if (overlay.drag.kind === 'handleOut') {
        const dx = point.x - a.x;
        const dy = point.y - a.y;
        const next: VectorAnchor = { ...a, outX: dx, outY: dy };
        if (a.type === 'smooth' && (a.inX !== undefined || a.inY !== undefined)) {
          next.inX = -dx;
          next.inY = -dy;
        } else if (a.type === 'asymmetric' && (a.inX !== undefined || a.inY !== undefined)) {
          const outLen = Math.hypot(dx, dy);
          const inLen = Math.hypot(a.inX ?? 0, a.inY ?? 0);
          if (outLen > 0 && inLen > 0) {
            const k = inLen / outLen;
            next.inX = -dx * k;
            next.inY = -dy * k;
          }
        }
        sub.anchors[idx] = next;
      }
      updatePathEditOverlayRef.current?.();
    });

    canvas.on('mouse:up', () => {
      const overlay = pathEditOverlayRef.current;
      if (overlay) overlay.drag = null;
    });

    // Clone Stamp + Heal Brush (Retouch tool, variant=clone or
    // heal). Both variants share the custom mouse:down/move/up
    // handlers, the source-set state, the reticle, and the stamp
    // pipeline. Heal layers a per-stamp tone correction on top of
    // the stamped source pixels (transferring source TEXTURE
    // while matching destination TONE). Three input paths
    // converge on `setCloneSource`: armed-mode tap, alt-click,
    // and long-press (handled in the touch listeners below). On
    // a non-source click, mouse:down begins a stroke; mouse:move
    // stamps source pixels at each cursor sample; mouse:up ends
    // and (if Aligned=false) clears the offset.
    const isStampVariant = (v: RetouchVariant | undefined): v is 'clone' | 'heal' =>
      v === 'clone' || v === 'heal';
    // Live pen pressure for the active Clone/Heal stamp (1 = mouse /
    // finger, i.e. no pressure effect). Updated from each stamp event;
    // read by stampClone. Only pen (Apple Pencil / S-Pen) reports real
    // pressure — clamped to a floor so a light nib still paints.
    const penPressureOf = (e: unknown): number => {
      const pe = e as PointerEvent;
      return pe && pe.pointerType === 'pen' ? Math.max(0.1, pe.pressure || 0) : 1;
    };
    let cloneStrokePressure = 1;
    canvas.on('mouse:down', (opt) => {
      if (activeToolRef.current !== 'retouch') return;
      const settings = retouchSettingsRef.current;
      if (!isStampVariant(settings?.variant)) return;
      if (!opt.e) return;
      const point = canvas.getScenePoint(opt.e);

      // Source-set paths: armed (Set Source button) or alt-click.
      const altPressed = (opt.e as MouseEvent).altKey === true;
      if (cloneArmedRef.current || altPressed) {
        cloneSourceRef.current = { x: point.x, y: point.y };
        cloneArmedRef.current = false;
        // A new source resets any persisted Aligned offset —
        // otherwise the next stroke would sample with the OLD
        // offset relative to the new source, which feels broken.
        cloneAlignedOffsetRef.current = null;
        renderCloneReticleRef.current?.();
        onCloneSourceChangedRef.current?.({ x: point.x, y: point.y });
        return;
      }

      // Otherwise, this is the start of a paint stroke. Source
      // must be set, otherwise no-op (with a hint surfaced via
      // callback).
      if (!cloneSourceRef.current) {
        onCloneStrokeRejectedRef.current?.();
        return;
      }
      const layerId = activeLayerIdRef.current;
      if (!layerId) return;

      const surface = ensurePaintSurfaceRef.current?.(layerId);
      if (!surface) return;

      // Build the per-stroke state. Source snapshot = the active
      // layer's full visible rendering, composited at scene-space
      // coords. For the bg layer that's identical to `surface.canvas`
      // (the bg paint surface IS its own canvas at identity).
      // For non-bg layers it composites the underlying image AND
      // the paint overlay together — otherwise clone-stamp would
      // sample the empty overlay and produce invisible strokes.
      // Only Sample=current is supported; Sample=currentBelow /
      // all need cross-layer compositing and aren't implemented.
      const dims = imageDimsRef.current;
      const snapshot = document.createElement('canvas');
      snapshot.width = dims.width;
      snapshot.height = dims.height;
      const sCtx = snapshot.getContext('2d', { willReadFrequently: true });
      if (!sCtx) return;
      snapshotLayerToContext(sCtx, layerId);

      const radius = Math.max(1, Math.round(settings.size / 2));
      const brushMask = buildCloneBrushMask(radius, settings.hardness / 100);
      // Aligned mode lives on the Clone settings only — Heal is
      // always non-aligned (the common heal workflow is
      // dab-and-re-source rather than continuous drag).
      const aligned = settings.variant === 'clone' ? settings.clone.aligned : false;
      const offset =
        aligned && cloneAlignedOffsetRef.current
          ? { ...cloneAlignedOffsetRef.current }
          : {
              x: cloneSourceRef.current.x - point.x,
              y: cloneSourceRef.current.y - point.y,
            };
      // Stash the offset so the next stroke can pick it up when
      // Aligned remains on. If Aligned flips off mid-session, we
      // recompute fresh anyway.
      if (aligned) cloneAlignedOffsetRef.current = { ...offset };

      cloneStrokeRef.current = {
        offset,
        sourceSnapshot: snapshot,
        brushMask,
        brushMaskImageData: null,
        radius,
        aligned,
        strokeAlpha: (settings.opacity / 100) * (settings.flow / 100),
        variant: settings.variant,
        healMode: settings.heal.mode,
        pressureSize: settings.pressureSize,
        pressureOpacity: settings.pressureOpacity,
        pressureFlow: settings.pressureFlow,
      };

      // Capture the initial pen pressure (1 for mouse / finger), then
      // stamp the first sample.
      cloneStrokePressure = penPressureOf(opt.e);
      stampClone(point.x, point.y);
    });

    canvas.on('mouse:move', (opt) => {
      if (activeToolRef.current !== 'retouch') return;
      const settings = retouchSettingsRef.current;
      if (!isStampVariant(settings?.variant)) return;
      if (!opt.e) return;
      const point = canvas.getScenePoint(opt.e);
      // Active stroke: stamp at cursor (the stamp loop also moves
      // the reticle to cursor + offset).
      if (cloneStrokeRef.current) {
        cloneStrokePressure = penPressureOf(opt.e);
        stampClone(point.x, point.y);
        return;
      }
      // Hover-only paths (no stroke): drive the reticle so the
      // user can preview where the next stamp will sample from.
      // Skip while source-set mode is active — clicking is about
      // to set a new source, so hover-tracking the stale source
      // would be misleading.
      if (cloneArmedRef.current) return;
      if ((opt.e as MouseEvent).altKey) return;
      const src = cloneSourceRef.current;
      if (!src) return;
      const reticle = cloneReticleRef.current;
      if (!reticle) return;
      // Aligned mode with persisted offset: reticle follows
      // cursor at offset (live preview of next sample location).
      // Otherwise: reticle sits at the source point — no movement
      // needed, but ensure it's there in case zoom/pan changed.
      // Heal in v1 is always non-aligned, so we only consult the
      // clone-namespace toggle when on the clone variant.
      const aligned =
        settings.variant === 'clone' && settings.clone.aligned && cloneAlignedOffsetRef.current;
      const target = aligned
        ? {
            x: point.x + cloneAlignedOffsetRef.current!.x,
            y: point.y + cloneAlignedOffsetRef.current!.y,
          }
        : src;
      if (reticle.left !== target.x || reticle.top !== target.y) {
        for (const bar of reticle.bars) {
          bar.set({ left: target.x, top: target.y });
          bar.setCoords();
        }
        reticle.left = target.x;
        reticle.top = target.y;
        canvas.requestRenderAll();
      }
    });

    canvas.on('mouse:up', () => {
      if (activeToolRef.current !== 'retouch') return;
      const settings = retouchSettingsRef.current;
      if (!isStampVariant(settings?.variant)) return;
      const stroke = cloneStrokeRef.current;
      if (!stroke) return;
      cloneStrokeRef.current = null;
      if (stroke.aligned) {
        // Reticle stays at the last sampling position so the user
        // can see where the next stroke will resume from. Aligned
        // offset already persisted in mouse:down.
      } else {
        // Drop the persisted offset so the next stroke recomputes
        // from source. Reset the reticle visually to the original
        // source point so the user sees that subsequent strokes
        // start over from there.
        cloneAlignedOffsetRef.current = null;
        renderCloneReticleRef.current?.();
      }
      // Tell consumers a stroke just baked pixels — for snapshot
      // capture and dirty-marking via the existing pipeline.
      onObjectModifiedRef.current?.();
      // Tier 2 Group F — Clone / Heal commits pixels to the
      // active layer's surface. Fire rasterModified so the PSD
      // export path emits this layer as raster (instead of
      // restoring it from the stash).
      const activeId = activeLayerIdRef.current;
      if (activeId) onLayerRasterModifiedRef.current?.(activeId);
    });

    // Stamp the source snapshot at one cursor position. Reads
    // `cloneStrokeRef` for the per-stroke offset and brush
    // configuration. The composite is:
    //   1. Crop the source snapshot at (cursor + offset) — radius
    //   2. Apply the brush mask (radial alpha falloff) via source-in
    //   3. Apply per-stroke alpha (opacity × flow)
    //   4. Composite source-over onto the layer's paint surface
    //      at `cursor`
    function stampClone(cx: number, cy: number) {
      const stroke = cloneStrokeRef.current;
      if (!stroke) return;
      const layerId = activeLayerIdRef.current;
      if (!layerId) return;
      const surface = ensurePaintSurfaceRef.current?.(layerId);
      if (!surface) return;
      const target = surface.canvas;
      const targetCtx = target.getContext('2d');
      if (!targetCtx) return;

      // Stylus pressure (1 for mouse / finger): scale the tip radius
      // and/or composite alpha per the stroke's captured toggles. The
      // brush mask was built once at base radius — scaling it down with
      // drawImage (below) avoids a per-dab mask rebuild.
      const sizeFactor = stroke.pressureSize ? cloneStrokePressure : 1;
      const alphaFactor = stroke.pressureOpacity || stroke.pressureFlow ? cloneStrokePressure : 1;
      const r = Math.max(1, Math.round(stroke.radius * sizeFactor));
      const size = r * 2;
      const sx = Math.round(cx + stroke.offset.x - r);
      const sy = Math.round(cy + stroke.offset.y - r);
      // Convert scene cursor coords to the surface canvas's local
      // pixel coords. Bg surface has offset (0, 0) so this is a
      // no-op there. A pasted/rasterized image layer has its own
      // intrinsic canvas as the surface, positioned at the layer's
      // scene (offsetX, offsetY) — strokes need to be subtracted
      // by the offset to land on the right pixels.
      const dx = Math.round(cx - r - surface.offsetX);
      const dy = Math.round(cy - r - surface.offsetY);

      // Compose a temp stamp = source crop × brush mask × stroke
      // alpha, then drawImage onto the target. Doing it on a temp
      // canvas keeps the math tidy and survives partial-out-of-
      // bounds source crops (drawImage clips silently).
      const stamp = document.createElement('canvas');
      stamp.width = size;
      stamp.height = size;
      const stampCtx = stamp.getContext('2d', { willReadFrequently: stroke.variant === 'heal' });
      if (!stampCtx) return;
      stampCtx.drawImage(stroke.sourceSnapshot, sx, sy, size, size, 0, 0, size, size);

      // Heal in `normal` mode: shift the stamp's tone toward
      // the destination's average color in the brush footprint,
      // weighted by the brush mask. Net effect — the cloned
      // TEXTURE is preserved but the MEAN COLOR matches what's
      // already there, so the patch blends in instead of
      // looking pasted-on. Done before the mask composite so
      // the shift uses the full source crop's pixel data, not
      // the post-masked thin edge.
      //
      // Heal in `replace` mode (or Clone, always): skip the
      // tone shift — source pixels stamp directly. Replace mode
      // is functionally equivalent to Clone but kept available
      // from the Heal variant so users who've set up a heal
      // workflow can do a clone-style dab without switching
      // tools.
      if (stroke.variant === 'heal' && stroke.healMode === 'normal') {
        // Build the dest patch as a baseline+target composite for
        // non-bg layers (else heal samples an empty overlay and
        // shifts the stamp toward transparent black → "very dark
        // line" symptom). For bg the composite degenerates to a
        // straight read of `target`.
        const healDest = acquireDabCanvas(destSampleCanvasCache, size);
        const healDestCtx = healDest.getContext('2d', { willReadFrequently: true });
        if (healDestCtx) {
          healDestCtx.drawImage(target, dx, dy, size, size, 0, 0, size, size);
          applyHealToneShift(stampCtx, healDest, size, stroke);
        }
      }

      stampCtx.globalCompositeOperation = 'destination-in';
      // Scale the (base-radius) mask to the pressure-adjusted size.
      stampCtx.drawImage(stroke.brushMask, 0, 0, size, size);
      stampCtx.globalCompositeOperation = 'source-over';

      // Apply marquee clipping if a finalized selection is
      // active — same rule as bakeStrokeOntoSurface.
      const m = marqueeRef.current;
      const hasMarquee = !!m.obj && !m.isDrawing;

      targetCtx.save();
      if (hasMarquee) applyMarqueeClipToContext(targetCtx, m, surface.offsetX, surface.offsetY);
      targetCtx.globalAlpha = stroke.strokeAlpha * alphaFactor;
      targetCtx.drawImage(stamp, dx, dy);
      targetCtx.globalAlpha = 1;
      targetCtx.restore();

      surface.fabricImg.setElement(target);
      surface.fabricImg.dirty = true;
      surface.fabricImg.setCoords();

      // Move the source reticle to the live sampling position so
      // the user sees exactly where pixels are being pulled from
      // as they drag. After mouse:up, the reticle either stays
      // (Aligned ON) or snaps back to the original source
      // (Aligned OFF) — both cases handled in mouse:up.
      const reticle = cloneReticleRef.current;
      if (reticle) {
        const tx = cx + stroke.offset.x;
        const ty = cy + stroke.offset.y;
        for (const bar of reticle.bars) {
          bar.set({ left: tx, top: ty });
          bar.setCoords();
        }
        reticle.left = tx;
        reticle.top = ty;
      }

      canvas.requestRenderAll();
    }

    // Smudge (Retouch tool, variant=smudge). Drags a sample
    // buffer along the stroke, mixing it with the destination
    // pixels by Strength at each dab — the classic "wet finger"
    // smear. Has its own pipeline (no source-set / reticle); the
    // first dab seeds the sample buffer either from the dest
    // patch (default) or from the foreground color (Finger
    // Painting toggle).
    canvas.on('mouse:down', (opt) => {
      if (activeToolRef.current !== 'retouch') return;
      const settings = retouchSettingsRef.current;
      if (settings?.variant !== 'smudge') return;
      if (!opt.e) return;
      const point = canvas.getScenePoint(opt.e);

      const layerId = activeLayerIdRef.current;
      if (!layerId) return;
      const surface = ensurePaintSurfaceRef.current?.(layerId);
      if (!surface) return;
      const target = surface.canvas;

      const radius = Math.max(1, Math.round(settings.size / 2));
      const size = radius * 2;
      // Convert scene cursor coords to canvas-local. See per-dab
      // sites for the rationale.
      const dx = Math.round(point.x - radius - surface.offsetX);
      const dy = Math.round(point.y - radius - surface.offsetY);

      // Seed the sample buffer. Default = current dest patch
      // (smudge starts from what's there). Finger Painting =
      // start with the foreground color, so the first dab paints
      // a streak that fades into the dest as the stroke smears.
      const sampleBuffer = document.createElement('canvas');
      sampleBuffer.width = size;
      sampleBuffer.height = size;
      const sCtx = sampleBuffer.getContext('2d', { willReadFrequently: true });
      if (!sCtx) return;
      if (settings.smudge.fingerPainting) {
        sCtx.fillStyle = brushColorRef.current;
        sCtx.fillRect(0, 0, size, size);
      } else {
        sCtx.drawImage(target, dx, dy, size, size, 0, 0, size, size);
      }

      const brushMask = buildCloneBrushMask(radius, settings.hardness / 100);

      smudgeStrokeRef.current = {
        sampleBuffer,
        brushMask,
        brushMaskImageData: null,
        radius,
        strength: settings.smudge.strength / 100,
        strokeAlpha: (settings.opacity / 100) * (settings.flow / 100),
      };

      // First dab. With Finger Painting OFF this is a no-op
      // (sample = dest at this position so the mix yields the
      // dest unchanged) — the smear builds up as the user drags.
      // With Finger Painting ON this paints the initial color
      // streak.
      smudgeDab(point.x, point.y);
    });

    canvas.on('mouse:move', (opt) => {
      if (activeToolRef.current !== 'retouch') return;
      const settings = retouchSettingsRef.current;
      if (settings?.variant !== 'smudge') return;
      if (!opt.e) return;
      if (!smudgeStrokeRef.current) return;
      const point = canvas.getScenePoint(opt.e);
      smudgeDab(point.x, point.y);
    });

    canvas.on('mouse:up', () => {
      if (activeToolRef.current !== 'retouch') return;
      const settings = retouchSettingsRef.current;
      if (settings?.variant !== 'smudge') return;
      const stroke = smudgeStrokeRef.current;
      if (!stroke) return;
      smudgeStrokeRef.current = null;
      onObjectModifiedRef.current?.();
      // Tier 2 Group F — Smudge baked pixels.
      const activeId = activeLayerIdRef.current;
      if (activeId) onLayerRasterModifiedRef.current?.(activeId);
    });

    // One smudge dab. Read the dest patch + sample buffer + mask,
    // run the per-pixel mix, write the result to the surface
    // (marquee-clipped, scaled by stroke alpha), and replace the
    // sample buffer with the mixed result so the next dab carries
    // it forward. Strength is captured at stroke start so a mid-
    // stroke slider drag doesn't change the smear feel.
    function smudgeDab(cx: number, cy: number) {
      const stroke = smudgeStrokeRef.current;
      if (!stroke) return;
      const layerId = activeLayerIdRef.current;
      if (!layerId) return;
      const surface = ensurePaintSurfaceRef.current?.(layerId);
      if (!surface) return;
      const target = surface.canvas;
      const targetCtx = target.getContext('2d');
      if (!targetCtx) return;

      const r = stroke.radius;
      const size = r * 2;
      // Convert scene cursor coords to the surface canvas's local
      // pixel coords. Bg surface has offset (0, 0) so this is a
      // no-op there. A pasted/rasterized image layer has its own
      // intrinsic canvas as the surface, positioned at the layer's
      // scene (offsetX, offsetY) — strokes need to be subtracted
      // by the offset to land on the right pixels.
      const dx = Math.round(cx - r - surface.offsetX);
      const dy = Math.round(cy - r - surface.offsetY);

      // Read the current dest patch.
      const destSample = acquireDabCanvas(destSampleCanvasCache, size);
      const destCtx = destSample.getContext('2d', { willReadFrequently: true });
      if (!destCtx) return;
      destCtx.drawImage(target, dx, dy, size, size, 0, 0, size, size);
      const destImg = destCtx.getImageData(0, 0, size, size);

      // Read the sample buffer (the trailing pixels carried
      // from the previous dab — or the seed at stroke start).
      const sampleCtx = stroke.sampleBuffer.getContext('2d', { willReadFrequently: true });
      if (!sampleCtx) return;
      const sampleImg = sampleCtx.getImageData(0, 0, size, size);

      // Lazy-cache the mask ImageData on the stroke so per-dab
      // getImageData stays cheap (mask doesn't change per-stroke).
      if (!stroke.brushMaskImageData) {
        const mctx = stroke.brushMask.getContext('2d', { willReadFrequently: true });
        if (!mctx) return;
        stroke.brushMaskImageData = mctx.getImageData(0, 0, size, size);
      }
      const maskImg = stroke.brushMaskImageData;

      // Per-pixel mix → mixed ImageData.
      const out = applyMaskedPixelMix(destImg, sampleImg, maskImg, stroke.strength);

      // Render the mixed result onto a stamp canvas so we can
      // composite it onto the surface with optional marquee clip
      // and stroke-alpha scaling. globalAlpha + source-over with
      // an opaque-ish stamp gives `final = stamp*a + dest*(1-a)`,
      // which scales the per-dab change correctly when
      // opacity*flow < 1.
      const stamp = acquireDabCanvas(stampCanvasCache, size);
      const stampCtx = stamp.getContext('2d');
      if (!stampCtx) return;
      stampCtx.putImageData(out, 0, 0);

      const m = marqueeRef.current;
      const hasMarquee = !!m.obj && !m.isDrawing;

      targetCtx.save();
      if (hasMarquee) applyMarqueeClipToContext(targetCtx, m, surface.offsetX, surface.offsetY);
      targetCtx.globalAlpha = stroke.strokeAlpha;
      targetCtx.drawImage(stamp, dx, dy);
      targetCtx.globalAlpha = 1;
      targetCtx.restore();

      // Carry the mix forward — next dab drags THIS result, not
      // the prior sample. That's what produces the continuous
      // smear (each dab is a slightly shifted blend of all
      // upstream pixels along the path).
      sampleCtx.clearRect(0, 0, size, size);
      sampleCtx.putImageData(out, 0, 0);

      surface.fabricImg.setElement(target);
      surface.fabricImg.dirty = true;
      surface.fabricImg.setCoords();
      canvas.requestRenderAll();
    }

    // Blur (Retouch tool, variant=blur). Reads the dest patch
    // under the brush, runs a Gaussian blur on it (with padding
    // so the kernel doesn't sample transparent black from outside
    // the patch), and mixes the blurred result back into the dest
    // by Strength. Independent pipeline from Smudge — no carry-
    // forward sample buffer. Repeated dabs accumulate because
    // each dab re-reads the (already-slightly-blurred) dest, so
    // the user builds up smoothness by dragging back and forth.
    canvas.on('mouse:down', (opt) => {
      if (activeToolRef.current !== 'retouch') return;
      const settings = retouchSettingsRef.current;
      if (settings?.variant !== 'blur') return;
      if (!opt.e) return;
      const point = canvas.getScenePoint(opt.e);

      const layerId = activeLayerIdRef.current;
      if (!layerId) return;
      if (!ensurePaintSurfaceRef.current?.(layerId)) return;

      const radius = Math.max(1, Math.round(settings.size / 2));
      const brushMask = buildCloneBrushMask(radius, settings.hardness / 100);
      // Sigma scales with brush radius, NOT strength — matching
      // Photoshop's Blur tool semantics (the kernel is intrinsic
      // to brush size, strength controls mix amount). radius/4
      // gives a visually meaningful kernel without crushing
      // detail in one dab; min 0.5 keeps strength=tiny still
      // doing something.
      const sigma = Math.max(0.5, radius / 4);

      blurStrokeRef.current = {
        brushMask,
        brushMaskImageData: null,
        radius,
        sigma,
        strength: settings.blur.strength / 100,
        strokeAlpha: (settings.opacity / 100) * (settings.flow / 100),
        lastDabX: point.x,
        lastDabY: point.y,
      };

      blurDab(point.x, point.y);
    });

    canvas.on('mouse:move', (opt) => {
      if (activeToolRef.current !== 'retouch') return;
      const settings = retouchSettingsRef.current;
      if (settings?.variant !== 'blur') return;
      if (!opt.e) return;
      const stroke = blurStrokeRef.current;
      if (!stroke) return;
      const point = canvas.getScenePoint(opt.e);
      // Brush spacing — skip the heavy Gaussian dab until the cursor
      // has moved at least a quarter brush-radius since the last dab.
      const minStep = stroke.radius * DODGE_BURN_SPACING_RATIO;
      const ddx = point.x - stroke.lastDabX;
      const ddy = point.y - stroke.lastDabY;
      if (ddx * ddx + ddy * ddy < minStep * minStep) return;
      stroke.lastDabX = point.x;
      stroke.lastDabY = point.y;
      blurDab(point.x, point.y);
    });

    canvas.on('mouse:up', () => {
      if (activeToolRef.current !== 'retouch') return;
      const settings = retouchSettingsRef.current;
      if (settings?.variant !== 'blur') return;
      if (!blurStrokeRef.current) return;
      blurStrokeRef.current = null;
      onObjectModifiedRef.current?.();
      // Tier 2 Group F — Blur baked pixels.
      const activeId = activeLayerIdRef.current;
      if (activeId) onLayerRasterModifiedRef.current?.(activeId);
    });

    // One blur dab. Reads a *padded* dest patch (size + 2*pad)
    // so the Gaussian kernel always has real pixels in its
    // sample radius — without padding, the blur near the patch
    // edge pulls in transparent black from outside and produces
    // a dark vignette artifact. Pad ≈ 2*sigma covers ~95% of
    // the kernel weight.
    function blurDab(cx: number, cy: number) {
      const stroke = blurStrokeRef.current;
      if (!stroke) return;
      const layerId = activeLayerIdRef.current;
      if (!layerId) return;
      const surface = ensurePaintSurfaceRef.current?.(layerId);
      if (!surface) return;
      const target = surface.canvas;
      const targetCtx = target.getContext('2d');
      if (!targetCtx) return;

      const r = stroke.radius;
      const size = r * 2;
      // Convert scene cursor coords to the surface canvas's local
      // pixel coords. Bg surface has offset (0, 0) so this is a
      // no-op there. A pasted/rasterized image layer has its own
      // intrinsic canvas as the surface, positioned at the layer's
      // scene (offsetX, offsetY) — strokes need to be subtracted
      // by the offset to land on the right pixels.
      const dx = Math.round(cx - r - surface.offsetX);
      const dy = Math.round(cy - r - surface.offsetY);

      const pad = Math.max(0, Math.ceil(stroke.sigma * 2));

      // Padded source: brush footprint plus a `pad`-pixel
      // border on each side, with the border filled by edge-
      // extension of the in-bounds surface. Without edge-
      // extension, the Gaussian below pulls transparent black
      // from outside the canvas and stamps a dark halo near the
      // edge. See `buildEdgeExtendedPaddedSource`.
      const padded = buildEdgeExtendedPaddedSource(target, dx, dy, size, pad);
      if (!padded) return;

      // Gaussian-blur the padded patch. Uses the GPU canvas filter
      // where supported, else a JS separable Gaussian — canvas
      // `ctx.filter` is unsupported on many mobile browsers (iOS
      // Safari <16.4, various WebViews), where it silently no-ops and
      // the Blur brush would do nothing. See `blurredPatchImageData`.
      const blurredImg = blurredPatchImageData(padded, size, pad, stroke.sigma);
      if (!blurredImg) return;

      // Read dest patch (un-blurred) and mask.
      const destSample = acquireDabCanvas(destSampleCanvasCache, size);
      const destCtx = destSample.getContext('2d', { willReadFrequently: true });
      if (!destCtx) return;
      destCtx.drawImage(target, dx, dy, size, size, 0, 0, size, size);
      const destImg = destCtx.getImageData(0, 0, size, size);

      if (!stroke.brushMaskImageData) {
        const mctx = stroke.brushMask.getContext('2d', { willReadFrequently: true });
        if (!mctx) return;
        stroke.brushMaskImageData = mctx.getImageData(0, 0, size, size);
      }
      const maskImg = stroke.brushMaskImageData;

      // Mix: out = dest*(1-m) + blurred*m, m = maskAlpha *
      // strength. Strength=0 → no change; strength=1 → full blur
      // at brush center, fading to no-change at the soft edge.
      const out = applyMaskedPixelMix(destImg, blurredImg, maskImg, stroke.strength);

      const stamp = acquireDabCanvas(stampCanvasCache, size);
      const stampCtx = stamp.getContext('2d');
      if (!stampCtx) return;
      stampCtx.putImageData(out, 0, 0);

      const m = marqueeRef.current;
      const hasMarquee = !!m.obj && !m.isDrawing;

      targetCtx.save();
      if (hasMarquee) applyMarqueeClipToContext(targetCtx, m, surface.offsetX, surface.offsetY);
      targetCtx.globalAlpha = stroke.strokeAlpha;
      targetCtx.drawImage(stamp, dx, dy);
      targetCtx.globalAlpha = 1;
      targetCtx.restore();

      surface.fabricImg.setElement(target);
      surface.fabricImg.dirty = true;
      surface.fabricImg.setCoords();
      canvas.requestRenderAll();
    }

    // Sharpen (Retouch tool, variant=sharpen). Unsharp-mask: the
    // dest patch is blurred (Gaussian, padded source like Blur),
    // and the difference dest-minus-blurred is the high-frequency
    // detail. We mix `dest + detail` back into the surface, with
    // the brush-mask + Strength weighting via applyMaskedPixelMix.
    // Strength=0 → no change; strength=1 → full detail boost at
    // brush center, fading at the soft edge. Repeated dabs
    // accumulate because each dab re-reads the (already-slightly-
    // sharpened) dest.
    canvas.on('mouse:down', (opt) => {
      if (activeToolRef.current !== 'retouch') return;
      const settings = retouchSettingsRef.current;
      if (settings?.variant !== 'sharpen') return;
      if (!opt.e) return;
      const point = canvas.getScenePoint(opt.e);

      const layerId = activeLayerIdRef.current;
      if (!layerId) return;
      if (!ensurePaintSurfaceRef.current?.(layerId)) return;

      const radius = Math.max(1, Math.round(settings.size / 2));
      const brushMask = buildCloneBrushMask(radius, settings.hardness / 100);
      // Sigma scales with brush radius (same kernel choice as
      // Blur); Strength controls mix amount, not kernel size.
      const sigma = Math.max(0.5, radius / 4);

      sharpenStrokeRef.current = {
        brushMask,
        brushMaskImageData: null,
        radius,
        sigma,
        strength: settings.sharpen.strength / 100,
        strokeAlpha: (settings.opacity / 100) * (settings.flow / 100),
        lastDabX: point.x,
        lastDabY: point.y,
        appliedMask: null,
        appliedMaskW: 0,
        appliedMaskH: 0,
      };

      sharpenDab(point.x, point.y);
    });

    canvas.on('mouse:move', (opt) => {
      if (activeToolRef.current !== 'retouch') return;
      const settings = retouchSettingsRef.current;
      if (settings?.variant !== 'sharpen') return;
      if (!opt.e) return;
      const stroke = sharpenStrokeRef.current;
      if (!stroke) return;
      const point = canvas.getScenePoint(opt.e);
      // Brush spacing — skip the heavy unsharp-mask dab until the
      // cursor has moved at least a quarter brush-radius.
      const minStep = stroke.radius * DODGE_BURN_SPACING_RATIO;
      const ddx = point.x - stroke.lastDabX;
      const ddy = point.y - stroke.lastDabY;
      if (ddx * ddx + ddy * ddy < minStep * minStep) return;
      stroke.lastDabX = point.x;
      stroke.lastDabY = point.y;
      sharpenDab(point.x, point.y);
    });

    canvas.on('mouse:up', () => {
      if (activeToolRef.current !== 'retouch') return;
      const settings = retouchSettingsRef.current;
      if (settings?.variant !== 'sharpen') return;
      if (!sharpenStrokeRef.current) return;
      sharpenStrokeRef.current = null;
      onObjectModifiedRef.current?.();
      // Tier 2 Group F — Sharpen baked pixels.
      const activeId = activeLayerIdRef.current;
      if (activeId) onLayerRasterModifiedRef.current?.(activeId);
    });

    // One sharpen dab. Mirrors blurDab's padded-source dance —
    // see that function for the rationale on padding (avoids dark
    // halos at the patch edge from the Gaussian sampling outside
    // the surface).
    function sharpenDab(cx: number, cy: number) {
      const stroke = sharpenStrokeRef.current;
      if (!stroke) return;
      const layerId = activeLayerIdRef.current;
      if (!layerId) return;
      const surface = ensurePaintSurfaceRef.current?.(layerId);
      if (!surface) return;
      const target = surface.canvas;
      const targetCtx = target.getContext('2d');
      if (!targetCtx) return;

      const r = stroke.radius;
      const size = r * 2;
      // Convert scene cursor coords to the surface canvas's local
      // pixel coords. Bg surface has offset (0, 0) so this is a
      // no-op there. A pasted/rasterized image layer has its own
      // intrinsic canvas as the surface, positioned at the layer's
      // scene (offsetX, offsetY) — strokes need to be subtracted
      // by the offset to land on the right pixels.
      const dx = Math.round(cx - r - surface.offsetX);
      const dy = Math.round(cy - r - surface.offsetY);

      const pad = Math.max(0, Math.ceil(stroke.sigma * 2));

      // Padded source with edge-extended border — see Blur.
      const padded = buildEdgeExtendedPaddedSource(target, dx, dy, size, pad);
      if (!padded) return;

      // Same GPU-or-JS Gaussian as the Blur brush — `ctx.filter` is
      // unreliable on mobile, so route the unsharp blur through the
      // shared fallback helper (see `blurredPatchImageData`).
      const blurredImg = blurredPatchImageData(padded, size, pad, stroke.sigma);
      if (!blurredImg) return;

      const destSample = acquireDabCanvas(destSampleCanvasCache, size);
      const destCtx = destSample.getContext('2d', { willReadFrequently: true });
      if (!destCtx) return;
      destCtx.drawImage(target, dx, dy, size, size, 0, 0, size, size);
      const destImg = destCtx.getImageData(0, 0, size, size);

      // Unsharp-mask sample: per-pixel `dest + (dest - blurred)`,
      // i.e. `2*dest - blurred`. Out-of-range values clamp
      // automatically because ImageData is Uint8ClampedArray.
      // Alpha is preserved from dest — sharpening must never
      // change opacity.
      const sharpenedImg = new ImageData(size, size);
      const sData = sharpenedImg.data;
      const dData = destImg.data;
      const bData = blurredImg.data;
      const len = sData.length;
      for (let i = 0; i < len; i += 4) {
        sData[i] = 2 * dData[i] - bData[i];
        sData[i + 1] = 2 * dData[i + 1] - bData[i + 1];
        sData[i + 2] = 2 * dData[i + 2] - bData[i + 2];
        sData[i + 3] = dData[i + 3];
      }

      if (!stroke.brushMaskImageData) {
        const mctx = stroke.brushMask.getContext('2d', { willReadFrequently: true });
        if (!mctx) return;
        stroke.brushMaskImageData = mctx.getImageData(0, 0, size, size);
      }
      const maskImg = stroke.brushMaskImageData;

      // Lazy-allocate the per-stroke applied-mask buffer on the
      // first dab — only now do we know the active layer's
      // dimensions. Uint8 (resolution 1/255) is plenty for a mix
      // weight; a 4MP layer = 4MB which is acceptable for the
      // duration of one stroke. Layers above
      // `MAX_RETOUCH_LAYER_PIXELS` (100MP / 100MB) abandon the
      // stroke rather than allocating an unbounded buffer.
      if (!stroke.appliedMask) {
        const lw = target.width;
        const lh = target.height;
        if (lw * lh > MAX_RETOUCH_LAYER_PIXELS) {
          console.warn(
            `[retouch] Sharpen: layer ${lw}×${lh} (${lw * lh} px) exceeds ${MAX_RETOUCH_LAYER_PIXELS} pixel budget — abandoning stroke`,
          );
          sharpenStrokeRef.current = null;
          return;
        }
        stroke.appliedMaskW = lw;
        stroke.appliedMaskH = lh;
        stroke.appliedMask = new Uint8Array(lw * lh);
      }
      const applied = stroke.appliedMask;
      const aw = stroke.appliedMaskW;
      const ah = stroke.appliedMaskH;

      // Inline mix with per-pixel capacity check. `applied[li]`
      // tracks cumulative brush-mask-alpha applied at layer
      // pixel (lx, ly) during this stroke (0–255, where 255 =
      // a full-strength dose has been delivered). Each new dab
      // can only contribute the lesser of (a) what its brush
      // mask requests at this pixel and (b) what's still
      // available before the cap. Final mix weight m =
      // (give/255) * stroke.strength.
      const out = new ImageData(size, size);
      const oData = out.data;
      const destData = destImg.data;
      const sharpData = sharpenedImg.data;
      const maskData = maskImg.data;
      for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
          const i = (y * size + x) * 4;
          const lx = dx + x;
          const ly = dy + y;
          let m = 0;
          if (lx >= 0 && lx < aw && ly >= 0 && ly < ah) {
            const requestUnits = maskData[i + 3];
            const li = ly * aw + lx;
            const remainingUnits = 255 - applied[li];
            const giveUnits = requestUnits < remainingUnits ? requestUnits : remainingUnits;
            if (giveUnits > 0) {
              applied[li] = applied[li] + giveUnits;
              m = (giveUnits / 255) * stroke.strength;
            }
          }
          const inv = 1 - m;
          oData[i] = destData[i] * inv + sharpData[i] * m;
          oData[i + 1] = destData[i + 1] * inv + sharpData[i + 1] * m;
          oData[i + 2] = destData[i + 2] * inv + sharpData[i + 2] * m;
          oData[i + 3] = destData[i + 3] * inv + sharpData[i + 3] * m;
        }
      }

      const stamp = acquireDabCanvas(stampCanvasCache, size);
      const stampCtx = stamp.getContext('2d');
      if (!stampCtx) return;
      stampCtx.putImageData(out, 0, 0);

      const m = marqueeRef.current;
      const hasMarquee = !!m.obj && !m.isDrawing;

      targetCtx.save();
      if (hasMarquee) applyMarqueeClipToContext(targetCtx, m, surface.offsetX, surface.offsetY);
      targetCtx.globalAlpha = stroke.strokeAlpha;
      targetCtx.drawImage(stamp, dx, dy);
      targetCtx.globalAlpha = 1;
      targetCtx.restore();

      surface.fabricImg.setElement(target);
      surface.fabricImg.dirty = true;
      surface.fabricImg.setCoords();
      canvas.requestRenderAll();
    }

    // Dodge (Retouch tool, variant=dodge). Lightens pixels under
    // the brush via the standard `pixel + (255 - pixel) *
    // exposure * rangeWeight` formula (asymptotic toward 255 —
    // bounded). rangeWeight is luminance-banded
    // (shadows / midtones / highlights). The per-dab math is
    // bounded so we don't need Sharpen's per-stroke cap, but
    // dab cadence on a held cursor would still escalate fast at
    // high exposure — brush spacing throttles that: a dab fires
    // only when the cursor has moved at least
    // `radius * DODGE_BURN_SPACING_RATIO` since the last dab.
    // Drag = controlled buildup, hold-still = nothing further,
    // back-and-forth in one stroke = layered effect (the PS
    // Dodge feel).
    canvas.on('mouse:down', (opt) => {
      if (activeToolRef.current !== 'retouch') return;
      const settings = retouchSettingsRef.current;
      if (settings?.variant !== 'dodge') return;
      if (!opt.e) return;
      const point = canvas.getScenePoint(opt.e);

      const layerId = activeLayerIdRef.current;
      if (!layerId) return;
      if (!ensurePaintSurfaceRef.current?.(layerId)) return;

      const radius = Math.max(1, Math.round(settings.size / 2));
      const brushMask = buildCloneBrushMask(radius, settings.hardness / 100);

      dodgeStrokeRef.current = {
        brushMask,
        brushMaskImageData: null,
        radius,
        range: settings.dodge.range,
        exposure: settings.dodge.exposure / 100,
        strokeAlpha: (settings.opacity / 100) * (settings.flow / 100),
        lastDabX: point.x,
        lastDabY: point.y,
        appliedMask: null,
        appliedMaskW: 0,
        appliedMaskH: 0,
      };

      dodgeDab(point.x, point.y);
    });

    canvas.on('mouse:move', (opt) => {
      if (activeToolRef.current !== 'retouch') return;
      const settings = retouchSettingsRef.current;
      if (settings?.variant !== 'dodge') return;
      if (!opt.e) return;
      const stroke = dodgeStrokeRef.current;
      if (!stroke) return;
      const point = canvas.getScenePoint(opt.e);

      // Brush spacing — only fire a dab if we've moved at least
      // a quarter brush-radius since the last dab.
      const minStep = stroke.radius * DODGE_BURN_SPACING_RATIO;
      const ddx = point.x - stroke.lastDabX;
      const ddy = point.y - stroke.lastDabY;
      if (ddx * ddx + ddy * ddy < minStep * minStep) return;

      stroke.lastDabX = point.x;
      stroke.lastDabY = point.y;
      dodgeDab(point.x, point.y);
    });

    canvas.on('mouse:up', () => {
      if (activeToolRef.current !== 'retouch') return;
      const settings = retouchSettingsRef.current;
      if (settings?.variant !== 'dodge') return;
      if (!dodgeStrokeRef.current) return;
      dodgeStrokeRef.current = null;
      onObjectModifiedRef.current?.();
      // Tier 2 Group F — Dodge baked pixels.
      const activeId = activeLayerIdRef.current;
      if (activeId) onLayerRasterModifiedRef.current?.(activeId);
    });

    // One dodge dab. Reads the dest patch directly (no padding
    // canvas needed — Dodge has no Gaussian step, so there's no
    // edge-sample-from-outside issue), computes the dodged
    // sample per-pixel using luminance + range weight, then
    // mixes it into the surface weighted by brush mask alpha.
    // Per-channel application — color may shift slightly toward
    // white at strong dodge; modern Photoshop's "Protect Tones"
    // mode is HSL-based and is a v2 feature.
    function dodgeDab(cx: number, cy: number) {
      const stroke = dodgeStrokeRef.current;
      if (!stroke) return;
      const layerId = activeLayerIdRef.current;
      if (!layerId) return;
      const surface = ensurePaintSurfaceRef.current?.(layerId);
      if (!surface) return;
      const target = surface.canvas;
      const targetCtx = target.getContext('2d');
      if (!targetCtx) return;

      const r = stroke.radius;
      const size = r * 2;
      // Convert scene cursor coords to the surface canvas's local
      // pixel coords. Bg surface has offset (0, 0) so this is a
      // no-op there. A pasted/rasterized image layer has its own
      // intrinsic canvas as the surface, positioned at the layer's
      // scene (offsetX, offsetY) — strokes need to be subtracted
      // by the offset to land on the right pixels.
      const dx = Math.round(cx - r - surface.offsetX);
      const dy = Math.round(cy - r - surface.offsetY);

      const destSample = acquireDabCanvas(destSampleCanvasCache, size);
      const destCtx = destSample.getContext('2d', { willReadFrequently: true });
      if (!destCtx) return;
      destCtx.drawImage(target, dx, dy, size, size, 0, 0, size, size);
      const destImg = destCtx.getImageData(0, 0, size, size);

      if (!stroke.brushMaskImageData) {
        const mctx = stroke.brushMask.getContext('2d', { willReadFrequently: true });
        if (!mctx) return;
        stroke.brushMaskImageData = mctx.getImageData(0, 0, size, size);
      }
      const maskImg = stroke.brushMaskImageData;

      // Lazy-allocate the per-stroke applied-mask buffer on the
      // first dab — only now do we know the active layer's
      // dimensions. See sharpenDab + MAX_RETOUCH_LAYER_PIXELS
      // for the same pattern + size cap.
      if (!stroke.appliedMask) {
        const lw = target.width;
        const lh = target.height;
        if (lw * lh > MAX_RETOUCH_LAYER_PIXELS) {
          console.warn(
            `[retouch] Dodge: layer ${lw}×${lh} (${lw * lh} px) exceeds ${MAX_RETOUCH_LAYER_PIXELS} pixel budget — abandoning stroke`,
          );
          dodgeStrokeRef.current = null;
          return;
        }
        stroke.appliedMaskW = lw;
        stroke.appliedMaskH = lh;
        stroke.appliedMask = new Uint8Array(lw * lh);
      }
      const applied = stroke.appliedMask;
      const aw = stroke.appliedMaskW;
      const ah = stroke.appliedMaskH;

      const out = new ImageData(size, size);
      const oData = out.data;
      const dData = destImg.data;
      const mData = maskImg.data;
      const exposure = stroke.exposure;
      const range = stroke.range;
      // Per-pixel mix with cap. Each pixel's request this dab is
      // its brush-mask-alpha; the actual contribution is capped
      // by remaining capacity (255 − applied[px]). The capacity
      // unit is brush-mask-alpha (0–255), independent of
      // exposure / rangeWeight / the per-dab scale, so the cap
      // semantics ("each pixel absorbs at most one full mask
      // dose per stroke") hold uniformly across slider settings.
      // Final m at this pixel = (give/255) × exposure ×
      // rangeWeight × DODGE_BURN_PER_DAB_SCALE.
      for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
          const i = (y * size + x) * 4;
          const r0 = dData[i];
          const g0 = dData[i + 1];
          const b0 = dData[i + 2];
          const a0 = dData[i + 3];
          const lx = dx + x;
          const ly = dy + y;
          let m = 0;
          if (lx >= 0 && lx < aw && ly >= 0 && ly < ah) {
            const requestUnits = mData[i + 3];
            const li = ly * aw + lx;
            const remainingUnits = 255 - applied[li];
            const giveUnits = requestUnits < remainingUnits ? requestUnits : remainingUnits;
            if (giveUnits > 0) {
              applied[li] = applied[li] + giveUnits;
              const lum = (0.299 * r0 + 0.587 * g0 + 0.114 * b0) / 255;
              const rw = dodgeBurnRangeWeight(lum, range);
              m = (giveUnits / 255) * exposure * rw * DODGE_BURN_PER_DAB_SCALE;
            }
          }
          oData[i] = r0 + (255 - r0) * m;
          oData[i + 1] = g0 + (255 - g0) * m;
          oData[i + 2] = b0 + (255 - b0) * m;
          oData[i + 3] = a0;
        }
      }

      const stamp = acquireDabCanvas(stampCanvasCache, size);
      const stampCtx = stamp.getContext('2d');
      if (!stampCtx) return;
      stampCtx.putImageData(out, 0, 0);

      const m = marqueeRef.current;
      const hasMarquee = !!m.obj && !m.isDrawing;

      targetCtx.save();
      if (hasMarquee) applyMarqueeClipToContext(targetCtx, m, surface.offsetX, surface.offsetY);
      targetCtx.globalAlpha = stroke.strokeAlpha;
      targetCtx.drawImage(stamp, dx, dy);
      targetCtx.globalAlpha = 1;
      targetCtx.restore();

      surface.fabricImg.setElement(target);
      surface.fabricImg.dirty = true;
      surface.fabricImg.setCoords();
      canvas.requestRenderAll();
    }

    // Burn (Retouch tool, variant=burn). Multiplicative inverse
    // of Dodge: `pixel * (1 - m)` per channel pushes toward 0
    // instead of toward 255. Same range curves
    // (dodgeBurnRangeWeight), same brush spacing, same
    // per-stroke cap. See dodgeDab and the dodgeStrokeRef type
    // doc for the rationale on each — the asymmetry between
    // Dodge and Burn is purely "lighten vs darken"; everything
    // else is shared.
    canvas.on('mouse:down', (opt) => {
      if (activeToolRef.current !== 'retouch') return;
      const settings = retouchSettingsRef.current;
      if (settings?.variant !== 'burn') return;
      if (!opt.e) return;
      const point = canvas.getScenePoint(opt.e);

      const layerId = activeLayerIdRef.current;
      if (!layerId) return;
      if (!ensurePaintSurfaceRef.current?.(layerId)) return;

      const radius = Math.max(1, Math.round(settings.size / 2));
      const brushMask = buildCloneBrushMask(radius, settings.hardness / 100);

      burnStrokeRef.current = {
        brushMask,
        brushMaskImageData: null,
        radius,
        range: settings.burn.range,
        exposure: settings.burn.exposure / 100,
        strokeAlpha: (settings.opacity / 100) * (settings.flow / 100),
        lastDabX: point.x,
        lastDabY: point.y,
        appliedMask: null,
        appliedMaskW: 0,
        appliedMaskH: 0,
      };

      burnDab(point.x, point.y);
    });

    canvas.on('mouse:move', (opt) => {
      if (activeToolRef.current !== 'retouch') return;
      const settings = retouchSettingsRef.current;
      if (settings?.variant !== 'burn') return;
      if (!opt.e) return;
      const stroke = burnStrokeRef.current;
      if (!stroke) return;
      const point = canvas.getScenePoint(opt.e);

      const minStep = stroke.radius * DODGE_BURN_SPACING_RATIO;
      const ddx = point.x - stroke.lastDabX;
      const ddy = point.y - stroke.lastDabY;
      if (ddx * ddx + ddy * ddy < minStep * minStep) return;

      stroke.lastDabX = point.x;
      stroke.lastDabY = point.y;
      burnDab(point.x, point.y);
    });

    canvas.on('mouse:up', () => {
      if (activeToolRef.current !== 'retouch') return;
      const settings = retouchSettingsRef.current;
      if (settings?.variant !== 'burn') return;
      if (!burnStrokeRef.current) return;
      burnStrokeRef.current = null;
      onObjectModifiedRef.current?.();
      // Tier 2 Group F — Burn baked pixels.
      const activeId = activeLayerIdRef.current;
      if (activeId) onLayerRasterModifiedRef.current?.(activeId);
    });

    function burnDab(cx: number, cy: number) {
      const stroke = burnStrokeRef.current;
      if (!stroke) return;
      const layerId = activeLayerIdRef.current;
      if (!layerId) return;
      const surface = ensurePaintSurfaceRef.current?.(layerId);
      if (!surface) return;
      const target = surface.canvas;
      const targetCtx = target.getContext('2d');
      if (!targetCtx) return;

      const r = stroke.radius;
      const size = r * 2;
      // Convert scene cursor coords to the surface canvas's local
      // pixel coords. Bg surface has offset (0, 0) so this is a
      // no-op there. A pasted/rasterized image layer has its own
      // intrinsic canvas as the surface, positioned at the layer's
      // scene (offsetX, offsetY) — strokes need to be subtracted
      // by the offset to land on the right pixels.
      const dx = Math.round(cx - r - surface.offsetX);
      const dy = Math.round(cy - r - surface.offsetY);

      const destSample = acquireDabCanvas(destSampleCanvasCache, size);
      const destCtx = destSample.getContext('2d', { willReadFrequently: true });
      if (!destCtx) return;
      destCtx.drawImage(target, dx, dy, size, size, 0, 0, size, size);
      const destImg = destCtx.getImageData(0, 0, size, size);

      if (!stroke.brushMaskImageData) {
        const mctx = stroke.brushMask.getContext('2d', { willReadFrequently: true });
        if (!mctx) return;
        stroke.brushMaskImageData = mctx.getImageData(0, 0, size, size);
      }
      const maskImg = stroke.brushMaskImageData;

      if (!stroke.appliedMask) {
        const lw = target.width;
        const lh = target.height;
        if (lw * lh > MAX_RETOUCH_LAYER_PIXELS) {
          console.warn(
            `[retouch] Burn: layer ${lw}×${lh} (${lw * lh} px) exceeds ${MAX_RETOUCH_LAYER_PIXELS} pixel budget — abandoning stroke`,
          );
          burnStrokeRef.current = null;
          return;
        }
        stroke.appliedMaskW = lw;
        stroke.appliedMaskH = lh;
        stroke.appliedMask = new Uint8Array(lw * lh);
      }
      const applied = stroke.appliedMask;
      const aw = stroke.appliedMaskW;
      const ah = stroke.appliedMaskH;

      const out = new ImageData(size, size);
      const oData = out.data;
      const dData = destImg.data;
      const mData = maskImg.data;
      const exposure = stroke.exposure;
      const range = stroke.range;
      // Per-pixel mix, cap-aware (mirrors dodgeDab's loop). The
      // only difference is the formula: `pixel * (1 - m)`
      // multiplies toward 0 instead of `pixel + (255 - pixel)*m`
      // adding toward 255.
      for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
          const i = (y * size + x) * 4;
          const r0 = dData[i];
          const g0 = dData[i + 1];
          const b0 = dData[i + 2];
          const a0 = dData[i + 3];
          const lx = dx + x;
          const ly = dy + y;
          let m = 0;
          if (lx >= 0 && lx < aw && ly >= 0 && ly < ah) {
            const requestUnits = mData[i + 3];
            const li = ly * aw + lx;
            const remainingUnits = 255 - applied[li];
            const giveUnits = requestUnits < remainingUnits ? requestUnits : remainingUnits;
            if (giveUnits > 0) {
              applied[li] = applied[li] + giveUnits;
              const lum = (0.299 * r0 + 0.587 * g0 + 0.114 * b0) / 255;
              const rw = dodgeBurnRangeWeight(lum, range);
              m = (giveUnits / 255) * exposure * rw * DODGE_BURN_PER_DAB_SCALE;
            }
          }
          const inv = 1 - m;
          oData[i] = r0 * inv;
          oData[i + 1] = g0 * inv;
          oData[i + 2] = b0 * inv;
          oData[i + 3] = a0;
        }
      }

      const stamp = acquireDabCanvas(stampCanvasCache, size);
      const stampCtx = stamp.getContext('2d');
      if (!stampCtx) return;
      stampCtx.putImageData(out, 0, 0);

      const m = marqueeRef.current;
      const hasMarquee = !!m.obj && !m.isDrawing;

      targetCtx.save();
      if (hasMarquee) applyMarqueeClipToContext(targetCtx, m, surface.offsetX, surface.offsetY);
      targetCtx.globalAlpha = stroke.strokeAlpha;
      targetCtx.drawImage(stamp, dx, dy);
      targetCtx.globalAlpha = 1;
      targetCtx.restore();

      surface.fabricImg.setElement(target);
      surface.fabricImg.dirty = true;
      surface.fabricImg.setCoords();
      canvas.requestRenderAll();
    }

    // Sponge (Retouch tool, variant=sponge). Shifts saturation
    // without affecting brightness:
    //   desaturate: pixel + (L - pixel) * m   (toward gray of L)
    //   saturate:   pixel + (pixel - L) * m   (away from gray of L)
    // where L = perceived luminance of the pixel. Same brush
    // spacing + per-stroke cap pattern as Dodge/Burn. See
    // dodgeStrokeRef type doc for the full rationale on each
    // limiter — the asymmetry between Sponge and Dodge/Burn is
    // (a) Mode replaces Range bands, (b) the per-channel formula
    // is luminance-relative instead of brightness-shift.
    canvas.on('mouse:down', (opt) => {
      if (activeToolRef.current !== 'retouch') return;
      const settings = retouchSettingsRef.current;
      if (settings?.variant !== 'sponge') return;
      if (!opt.e) return;
      const point = canvas.getScenePoint(opt.e);

      const layerId = activeLayerIdRef.current;
      if (!layerId) return;
      if (!ensurePaintSurfaceRef.current?.(layerId)) return;

      const radius = Math.max(1, Math.round(settings.size / 2));
      const brushMask = buildCloneBrushMask(radius, settings.hardness / 100);

      spongeStrokeRef.current = {
        brushMask,
        brushMaskImageData: null,
        radius,
        mode: settings.sponge.mode,
        flow: settings.sponge.flow / 100,
        strokeAlpha: (settings.opacity / 100) * (settings.flow / 100),
        lastDabX: point.x,
        lastDabY: point.y,
        appliedMask: null,
        appliedMaskW: 0,
        appliedMaskH: 0,
      };

      spongeDab(point.x, point.y);
    });

    canvas.on('mouse:move', (opt) => {
      if (activeToolRef.current !== 'retouch') return;
      const settings = retouchSettingsRef.current;
      if (settings?.variant !== 'sponge') return;
      if (!opt.e) return;
      const stroke = spongeStrokeRef.current;
      if (!stroke) return;
      const point = canvas.getScenePoint(opt.e);

      const minStep = stroke.radius * DODGE_BURN_SPACING_RATIO;
      const ddx = point.x - stroke.lastDabX;
      const ddy = point.y - stroke.lastDabY;
      if (ddx * ddx + ddy * ddy < minStep * minStep) return;

      stroke.lastDabX = point.x;
      stroke.lastDabY = point.y;
      spongeDab(point.x, point.y);
    });

    canvas.on('mouse:up', () => {
      if (activeToolRef.current !== 'retouch') return;
      const settings = retouchSettingsRef.current;
      if (settings?.variant !== 'sponge') return;
      if (!spongeStrokeRef.current) return;
      spongeStrokeRef.current = null;
      onObjectModifiedRef.current?.();
      // Tier 2 Group F — Sponge baked pixels.
      const activeId = activeLayerIdRef.current;
      if (activeId) onLayerRasterModifiedRef.current?.(activeId);
    });

    function spongeDab(cx: number, cy: number) {
      const stroke = spongeStrokeRef.current;
      if (!stroke) return;
      const layerId = activeLayerIdRef.current;
      if (!layerId) return;
      const surface = ensurePaintSurfaceRef.current?.(layerId);
      if (!surface) return;
      const target = surface.canvas;
      const targetCtx = target.getContext('2d');
      if (!targetCtx) return;

      const r = stroke.radius;
      const size = r * 2;
      // Convert scene cursor coords to the surface canvas's local
      // pixel coords. Bg surface has offset (0, 0) so this is a
      // no-op there. A pasted/rasterized image layer has its own
      // intrinsic canvas as the surface, positioned at the layer's
      // scene (offsetX, offsetY) — strokes need to be subtracted
      // by the offset to land on the right pixels.
      const dx = Math.round(cx - r - surface.offsetX);
      const dy = Math.round(cy - r - surface.offsetY);

      const destSample = acquireDabCanvas(destSampleCanvasCache, size);
      const destCtx = destSample.getContext('2d', { willReadFrequently: true });
      if (!destCtx) return;
      destCtx.drawImage(target, dx, dy, size, size, 0, 0, size, size);
      const destImg = destCtx.getImageData(0, 0, size, size);

      if (!stroke.brushMaskImageData) {
        const mctx = stroke.brushMask.getContext('2d', { willReadFrequently: true });
        if (!mctx) return;
        stroke.brushMaskImageData = mctx.getImageData(0, 0, size, size);
      }
      const maskImg = stroke.brushMaskImageData;

      if (!stroke.appliedMask) {
        const lw = target.width;
        const lh = target.height;
        if (lw * lh > MAX_RETOUCH_LAYER_PIXELS) {
          console.warn(
            `[retouch] Sponge: layer ${lw}×${lh} (${lw * lh} px) exceeds ${MAX_RETOUCH_LAYER_PIXELS} pixel budget — abandoning stroke`,
          );
          spongeStrokeRef.current = null;
          return;
        }
        stroke.appliedMaskW = lw;
        stroke.appliedMaskH = lh;
        stroke.appliedMask = new Uint8Array(lw * lh);
      }
      const applied = stroke.appliedMask;
      const aw = stroke.appliedMaskW;
      const ah = stroke.appliedMaskH;

      const out = new ImageData(size, size);
      const oData = out.data;
      const dData = destImg.data;
      const mData = maskImg.data;
      const flow = stroke.flow;
      const isSaturate = stroke.mode === 'saturate';
      // Per-pixel mix, cap-aware (mirrors dodgeDab / burnDab
      // structure). The per-channel formula is luminance-
      // relative — each channel is pulled toward / pushed away
      // from the *pixel's own* luminance gray, which preserves
      // perceived brightness while shifting saturation.
      for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
          const i = (y * size + x) * 4;
          const r0 = dData[i];
          const g0 = dData[i + 1];
          const b0 = dData[i + 2];
          const a0 = dData[i + 3];
          const lx = dx + x;
          const ly = dy + y;
          let m = 0;
          if (lx >= 0 && lx < aw && ly >= 0 && ly < ah) {
            const requestUnits = mData[i + 3];
            const li = ly * aw + lx;
            const remainingUnits = 255 - applied[li];
            const giveUnits = requestUnits < remainingUnits ? requestUnits : remainingUnits;
            if (giveUnits > 0) {
              applied[li] = applied[li] + giveUnits;
              m = (giveUnits / 255) * flow * DODGE_BURN_PER_DAB_SCALE;
            }
          }
          const lum = 0.299 * r0 + 0.587 * g0 + 0.114 * b0;
          if (isSaturate) {
            // Push away from gray of same luminance. Out-of-
            // range RGB clamps via Uint8ClampedArray on the
            // ImageData write.
            oData[i] = r0 + (r0 - lum) * m;
            oData[i + 1] = g0 + (g0 - lum) * m;
            oData[i + 2] = b0 + (b0 - lum) * m;
          } else {
            // Mix toward gray of same luminance.
            oData[i] = r0 + (lum - r0) * m;
            oData[i + 1] = g0 + (lum - g0) * m;
            oData[i + 2] = b0 + (lum - b0) * m;
          }
          oData[i + 3] = a0;
        }
      }

      const stamp = acquireDabCanvas(stampCanvasCache, size);
      const stampCtx = stamp.getContext('2d');
      if (!stampCtx) return;
      stampCtx.putImageData(out, 0, 0);

      const m = marqueeRef.current;
      const hasMarquee = !!m.obj && !m.isDrawing;

      targetCtx.save();
      if (hasMarquee) applyMarqueeClipToContext(targetCtx, m, surface.offsetX, surface.offsetY);
      targetCtx.globalAlpha = stroke.strokeAlpha;
      targetCtx.drawImage(stamp, dx, dy);
      targetCtx.globalAlpha = 1;
      targetCtx.restore();

      surface.fabricImg.setElement(target);
      surface.fabricImg.dirty = true;
      surface.fabricImg.setCoords();
      canvas.requestRenderAll();
    }

    // Fill tool — click to flood-fill the clicked pixel color region with the
    // current brush color. The fill is composited directly onto the active
    // layer's paint surface (NOT a new floating object, NOT the background
    // image) and clipped to the active marquee if one is finalized.
    // Tolerance / contiguous reuse the magic-wand settings.
    //
    // Active-layer invariant: this handler resolves its target via
    // `resolveActivePaintTarget`. Reaching for `getBackgroundImage()` here
    // was the original Fill bug — fills always landed on BG regardless of
    // the active layer.
    // Paint-bucket flood fill. Tap-based, so on touch it commits on
    // release (via `runOrDeferTapTool`) so the first finger of a
    // two-finger pan can't flood the layer.
    const runFillAt = (pointer: fabric.Point) => {
      const target = resolveActivePaintTarget(activeLayerIdRef, ensurePaintSurfaceRef);
      if (!target) return;
      const { layerId, surface } = target;
      const surfaceCtx = surface.canvas.getContext('2d', { willReadFrequently: true });
      if (!surfaceCtx) return;
      const surfaceW = surface.canvas.width;
      const surfaceH = surface.canvas.height;
      if (surfaceW <= 0 || surfaceH <= 0) return;

      // surface.fabricImg.left/top is the scene location of surface pixel
      // (0, 0). Pin to integers BEFORE deriving the surface-local click
      // coord and BEFORE the marquee-intersection loop — Fabric stores
      // arbitrary float left/top after a drag, and JS array index
      // truncation on a `x + fractionalOffsetX` term read the wrong
      // pixel of the marquee mask, zeroing the wand seed and producing
      // a false-positive "Nothing to fill" toast for clicks inside the
      // marquee on any non-axis-aligned-integer surface offset.
      const offsetX = Math.round(surface.fabricImg.left ?? 0);
      const offsetY = Math.round(surface.fabricImg.top ?? 0);
      const lx = Math.round(pointer.x) - offsetX;
      const ly = Math.round(pointer.y) - offsetY;
      if (lx < 0 || lx >= surfaceW || ly < 0 || ly >= surfaceH) return;

      // Sample the active layer's OWN pixels for the wand seed — never the
      // background image. On a transparent empty layer this seeds from
      // rgba(0,0,0,0) and (under default tolerance) fills the empty
      // region, which matches the Photoshop "fill on an empty layer"
      // behavior.
      const imgData = surfaceCtx.getImageData(0, 0, surfaceW, surfaceH);
      const mask = computeWandMask(
        imgData,
        lx,
        ly,
        wandToleranceRef.current,
        wandContiguousRef.current,
      );

      // If a finalized marquee is active, constrain the fill: the click
      // must land inside the marquee, and the wand mask is intersected
      // with the marquee mask. The marquee is in scene coords; we build
      // it at document dims and index into it with the surface's scene
      // offset.
      const fillMarquee = marqueeRef.current;
      if (fillMarquee.obj && !fillMarquee.isDrawing) {
        const docW = imageDimsRef.current.width;
        const docH = imageDimsRef.current.height;
        if (docW <= 0 || docH <= 0) return;
        const sceneMask = buildMarqueeMask(fillMarquee, docW, docH);
        if (sceneMask) {
          const clickSx = Math.round(pointer.x);
          const clickSy = Math.round(pointer.y);
          if (clickSx < 0 || clickSx >= docW || clickSy < 0 || clickSy >= docH) {
            onMarqueeActionNoPixelsRef.current?.('fill');
            return;
          }
          if (!sceneMask[clickSy * docW + clickSx]) {
            onMarqueeActionNoPixelsRef.current?.('fill');
            return;
          }
          for (let y = 0; y < surfaceH; y++) {
            const sy = y + offsetY;
            const rowBase = y * surfaceW;
            if (sy < 0 || sy >= docH) {
              for (let x = 0; x < surfaceW; x++) mask[rowBase + x] = 0;
              continue;
            }
            const sceneRowBase = sy * docW;
            for (let x = 0; x < surfaceW; x++) {
              const sx = x + offsetX;
              if (sx < 0 || sx >= docW) {
                mask[rowBase + x] = 0;
                continue;
              }
              if (!sceneMask[sceneRowBase + sx]) mask[rowBase + x] = 0;
            }
          }
        }
      }

      // Build a surface-sized RGBA stamp (brushColor where mask=1,
      // transparent elsewhere) and composite it onto the surface with
      // source-over so underlying pixels outside the mask are preserved.
      const color = brushColorRef.current;
      const r = parseInt(color.slice(1, 3), 16) || 0;
      const g = parseInt(color.slice(3, 5), 16) || 0;
      const b = parseInt(color.slice(5, 7), 16) || 0;
      const stampData = surfaceCtx.createImageData(surfaceW, surfaceH);
      let painted = 0;
      for (let i = 0; i < mask.length; i++) {
        if (mask[i]) {
          const di = i * 4;
          stampData.data[di] = r;
          stampData.data[di + 1] = g;
          stampData.data[di + 2] = b;
          stampData.data[di + 3] = 255;
          painted++;
        }
      }
      if (painted === 0) {
        // Wand + marquee intersection produced zero pixels — usually the
        // user clicked a region whose flood-fill area lies entirely
        // outside the marquee.
        onMarqueeActionNoPixelsRef.current?.('fill');
        return;
      }

      // putImageData would overwrite alpha; instead route through an
      // offscreen canvas + drawImage so the fill composites with
      // source-over against the existing surface pixels.
      const stampCanvas = document.createElement('canvas');
      stampCanvas.width = surfaceW;
      stampCanvas.height = surfaceH;
      const stampCtx = stampCanvas.getContext('2d');
      if (!stampCtx) return;
      stampCtx.putImageData(stampData, 0, 0);
      surfaceCtx.drawImage(stampCanvas, 0, 0);

      // Force Fabric to re-bind via setElement — dirty alone isn't enough
      // (see bakeStrokeOntoSurface's note).
      surface.fabricImg.setElement(surface.canvas);
      surface.fabricImg.dirty = true;
      surface.fabricImg.setCoords();
      canvas.renderAll();
      onObjectModifiedRef.current?.();
      // Paint-bucket modified the layer's raster pixels — flag for the
      // export path so it emits raster rather than restoring from the
      // PSD stash.
      onLayerRasterModifiedRef.current?.(layerId);
    };
    canvas.on('mouse:down', (opt) => {
      if (activeToolRef.current !== 'fill') return;
      if (!opt.e) return;
      const pointer = canvas.getScenePoint(opt.e);
      runOrDeferTapTool(opt.e, () => runFillAt(pointer));
    });

    // Marquee selection — supports rect, ellipse, lasso, and polygon shapes.
    // Rect/ellipse: drag to size. Lasso: drag to capture freeform path.
    // Polygon: each click adds a vertex; close by clicking near the first point or double-clicking.
    // Marquee `mouse:down` body, factored out so the tap-based shapes
    // (polygon, magic wand) can be deferred to release on touch — see the
    // handler below and `runOrDeferTapTool`.
    //
    // `isTouch` enables the touch double-tap polygon close via `polygonTap`
    // (mouse/pen keep using `mouse:dblclick`, which touch never fires).
    const polygonTap = createTapTracker();
    const runMarqueeDown = (pointer: fabric.Point, isTouch = false) => {
      const m = marqueeRef.current;

      if (m.shape === 'rect' || m.shape === 'ellipse') {
        if (m.obj) canvas.remove(m.obj);
        const obj =
          m.shape === 'rect'
            ? new fabric.Rect({
                ...MARQUEE_STYLE,
                left: pointer.x,
                top: pointer.y,
                width: 0,
                height: 0,
              })
            : new fabric.Ellipse({
                ...MARQUEE_STYLE,
                left: pointer.x,
                top: pointer.y,
                rx: 0,
                ry: 0,
              });
        tagAsMarquee(obj);
        applyMarqueeColor(obj, marqueeColorRef.current);
        applyMarqueeStroke(canvas, obj);
        canvas.add(obj);
        m.obj = obj;
        m.startX = pointer.x;
        m.startY = pointer.y;
        m.points = [];
        m.isDrawing = true;
      } else if (m.shape === 'lasso') {
        if (m.obj) canvas.remove(m.obj);
        m.points = [{ x: pointer.x, y: pointer.y }];
        const poly = new fabric.Polyline(m.points, MARQUEE_STYLE);
        tagAsMarquee(poly);
        applyMarqueeColor(poly, marqueeColorRef.current);
        applyMarqueeStroke(canvas, poly);
        canvas.add(poly);
        m.obj = poly;
        m.isDrawing = true;
      } else if (m.shape === 'polygon') {
        if (!m.isDrawing) {
          if (m.obj) canvas.remove(m.obj);
          m.points = [{ x: pointer.x, y: pointer.y }];
          const poly = new fabric.Polyline(m.points, MARQUEE_STYLE);
          tagAsMarquee(poly);
          applyMarqueeColor(poly, marqueeColorRef.current);
          applyMarqueeStroke(canvas, poly);
          canvas.add(poly);
          m.obj = poly;
          m.isDrawing = true;
          // Seed the tap tracker with the first vertex so a later same-spot
          // double-tap can be recognized as a close.
          if (isTouch) {
            polygonTap.reset();
            polygonTap.register(pointer.x, pointer.y, canvas.getZoom());
          }
        } else {
          // Touch double-tap → close. The desktop close path is the
          // `mouse:dblclick` handler below, which never fires for touch, so
          // recognize a quick second tap near the previous one here. The
          // first tap of the pair already added its vertex, so we close with
          // the points as-is (the tap position is the final vertex), exactly
          // like the desktop dblclick after it pops its duplicate vertex.
          const tapCount = isTouch
            ? polygonTap.register(pointer.x, pointer.y, canvas.getZoom())
            : 1;
          if (tapCount >= 2 && m.points.length >= 3) {
            polygonTap.reset();
            closePolygonMarquee(canvas, m, marqueeColorRef.current);
            canvas.renderAll();
            refreshMarqueeSelectionState();
            return;
          }
          const first = m.points[0];
          const dx = pointer.x - first.x;
          const dy = pointer.y - first.y;
          const threshold = POLYGON_CLOSE_THRESHOLD_PX / canvas.getZoom();
          if (m.points.length >= 3 && dx * dx + dy * dy < threshold * threshold) {
            polygonTap.reset();
            closePolygonMarquee(canvas, m, marqueeColorRef.current);
          } else {
            m.points = [...m.points, { x: pointer.x, y: pointer.y }];
            replaceMarqueePolyline(canvas, m, marqueeColorRef.current);
            // (tap registered above for the next double-tap comparison)
          }
        }
      } else if (m.shape === 'wand') {
        // TODO(active-layer-resolver): magic wand currently samples the
        // background image only, matching neither Photoshop's default
        // (sample active layer) nor its "sample all layers" mode. Fixing
        // requires building `m.wandMaskCanvas` from the active layer's
        // paint surface but keeping the mask canvas at doc/scene
        // dimensions so downstream consumers (bakeStrokeOntoSurface
        // line ~12059, deleteSelected line ~7214) don't need rework.
        // Out of scope for the fill/cut resolver pass; left as a
        // distinct UX-policy decision.
        const bg = getBackgroundImage();
        if (!bg) {
          canvas.renderAll();
          return;
        }
        const imgEl = bg.getElement() as HTMLImageElement | HTMLCanvasElement;
        const bgW = bg.width || 0;
        const bgH = bg.height || 0;
        const px = Math.round(pointer.x);
        const py = Math.round(pointer.y);
        if (px < 0 || px >= bgW || py < 0 || py >= bgH) {
          canvas.renderAll();
          return;
        }

        // Clear any prior wand selection first.
        if (m.obj) canvas.remove(m.obj);
        for (const o of m.extraObjs) canvas.remove(o);
        m.obj = null;
        m.extraObjs = [];
        m.points = [];
        m.wandMaskCanvas = null;

        // Sample image pixels and compute the mask.
        const offscreen = document.createElement('canvas');
        offscreen.width = bgW;
        offscreen.height = bgH;
        const ctx = offscreen.getContext('2d');
        if (!ctx) {
          canvas.renderAll();
          return;
        }
        ctx.drawImage(imgEl, 0, 0);
        const imgData = ctx.getImageData(0, 0, bgW, bgH);
        const tolerance = wandToleranceRef.current;
        const contiguous = wandContiguousRef.current;
        const mask = computeWandMask(imgData, px, py, tolerance, contiguous);

        // Cache the mask as an opaque-where-selected canvas for the eraser path.
        // `willReadFrequently` so the morphology helpers can `getImageData`
        // back from this canvas without the browser warning.
        const maskCanvas = document.createElement('canvas');
        maskCanvas.width = bgW;
        maskCanvas.height = bgH;
        const maskCtx = maskCanvas.getContext('2d', { willReadFrequently: true });
        if (maskCtx) {
          const maskData = maskCtx.createImageData(bgW, bgH);
          for (let i = 0; i < mask.length; i++) {
            if (mask[i]) {
              const di = i * 4;
              maskData.data[di] = 255;
              maskData.data[di + 1] = 255;
              maskData.data[di + 2] = 255;
              maskData.data[di + 3] = 255;
            }
          }
          maskCtx.putImageData(maskData, 0, 0);
        }
        m.wandMaskCanvas = maskCanvas;
        m.wandTolerance = tolerance;
        m.wandContiguous = contiguous;

        // Trace contours into a single fabric.Path with evenodd
        // fill, so masks with holes (subject + interior bg) render
        // as a topology-correct donut instead of overlapping fills.
        const contours = traceContours(mask, bgW, bgH);
        const path = buildMarqueePath(contours);
        if (path) {
          tagAsMarquee(path);
          applyMarqueeColor(path, marqueeColorRef.current);
          applyMarqueeStroke(canvas, path);
          canvas.add(path);
          m.obj = path;
          m.extraObjs = [];
        }
        m.isDrawing = false;
      }
      canvas.renderAll();
      refreshMarqueeSelectionState();
    };
    canvas.on('mouse:down', (opt) => {
      if (activeToolRef.current !== 'marquee') return;
      if (opt.e === undefined) return;
      if (canvas.findTarget(opt.e).target) return;
      const pointer = canvas.getScenePoint(opt.e);
      // Polygon and magic wand are tap-based; defer them to release on
      // touch so the first finger of a two-finger pan adds no phantom
      // vertex and doesn't replace the selection. Rect / ellipse / lasso
      // are press-drag-release and run on press (a pinch interrupting the
      // drag is discarded by `onTouchStart`).
      const shape = marqueeRef.current.shape;
      const isTouch = isTouchEvent(opt.e);
      if (shape === 'polygon' || shape === 'wand') {
        runOrDeferTapTool(opt.e, () => runMarqueeDown(pointer, isTouch));
      } else {
        runMarqueeDown(pointer, isTouch);
      }
    });

    canvas.on('mouse:move', (opt) => {
      const m = marqueeRef.current;
      if (!m.isDrawing || !m.obj || !opt.e) return;
      if (m.shape === 'polygon') return; // polygon advances on click, not move

      const pointer = canvas.getScenePoint(opt.e);

      const shiftHeld = !!(opt.e as MouseEvent).shiftKey;
      const constrainSquare = forceSquareRef.current || shiftHeld;

      if (m.shape === 'rect') {
        const dx = pointer.x - m.startX;
        const dy = pointer.y - m.startY;
        let width: number;
        let height: number;
        let left: number;
        let top: number;
        if (constrainSquare) {
          const size = Math.max(Math.abs(dx), Math.abs(dy));
          width = size;
          height = size;
          left = dx >= 0 ? m.startX : m.startX - size;
          top = dy >= 0 ? m.startY : m.startY - size;
        } else {
          width = Math.abs(dx);
          height = Math.abs(dy);
          left = Math.min(m.startX, pointer.x);
          top = Math.min(m.startY, pointer.y);
        }
        (m.obj as fabric.Rect).set({ left, top, width, height });
      } else if (m.shape === 'ellipse') {
        const dx = pointer.x - m.startX;
        const dy = pointer.y - m.startY;
        let w: number;
        let h: number;
        let left: number;
        let top: number;
        if (constrainSquare) {
          const size = Math.max(Math.abs(dx), Math.abs(dy));
          w = size;
          h = size;
          left = dx >= 0 ? m.startX : m.startX - size;
          top = dy >= 0 ? m.startY : m.startY - size;
        } else {
          w = Math.abs(dx);
          h = Math.abs(dy);
          left = Math.min(m.startX, pointer.x);
          top = Math.min(m.startY, pointer.y);
        }
        (m.obj as fabric.Ellipse).set({ left, top, rx: w / 2, ry: h / 2 });
      } else if (m.shape === 'lasso') {
        m.points = [...m.points, { x: pointer.x, y: pointer.y }];
        replaceMarqueePolyline(canvas, m, marqueeColorRef.current);
      }
      // `requestRenderAll` queues a render at next rAF and coalesces
      // multiple calls within the same frame. Pointer events fire at
      // up to the device's native rate (often >60Hz on modern mice);
      // a synchronous `renderAll` per event redraws the full canvas
      // multiple times per frame, which on a large doc + the
      // marquee's dashed stroke piles up enough work to trigger
      // perceptible drag stutter.
      canvas.requestRenderAll();
    });

    canvas.on('mouse:up', () => {
      const m = marqueeRef.current;
      if (!m.isDrawing || !m.obj) return;
      if (m.shape === 'polygon') return; // polygon stays open until explicit close

      if (m.shape === 'rect' || m.shape === 'ellipse') {
        m.isDrawing = false;
        const w =
          m.shape === 'rect'
            ? (m.obj as fabric.Rect).width || 0
            : ((m.obj as fabric.Ellipse).rx || 0) * 2;
        const h =
          m.shape === 'rect'
            ? (m.obj as fabric.Rect).height || 0
            : ((m.obj as fabric.Ellipse).ry || 0) * 2;
        if (w < 3 && h < 3) {
          canvas.remove(m.obj);
          m.obj = null;
        }
      } else if (m.shape === 'lasso') {
        m.isDrawing = false;
        if (m.points.length < 3) {
          canvas.remove(m.obj);
          m.obj = null;
          m.points = [];
        } else {
          canvas.remove(m.obj);
          const closed = new fabric.Polygon(m.points, MARQUEE_STYLE);
          tagAsMarquee(closed);
          applyMarqueeColor(closed, marqueeColorRef.current);
          applyMarqueeStroke(canvas, closed);
          canvas.add(closed);
          m.obj = closed;
        }
      }
      canvas.renderAll();
      refreshMarqueeSelectionState();
    });

    // Tier 2 Group F sub-piece #5 — Slice tool. Drag on empty
    // canvas creates a new slice; clicks on an existing slice
    // overlay let Fabric route to its native selection + resize
    // controls (we don't intercept those — the `findTarget` check
    // below mirrors the marquee tool's "click on existing object
    // doesn't start a new region" semantics).
    //
    // The minimum-size threshold matches the marquee tool's
    // 3-pixel cutoff — a single tap with no drag shouldn't create
    // a degenerate slice. Negative drag (right-to-left or bottom-
    // to-top) is normalized via Math.min/abs the same way the
    // marquee rect handler does, so the user can drag in any
    // direction.
    canvas.on('mouse:down', (opt) => {
      if (activeToolRef.current !== 'slice') return;
      if (!opt.e) return;
      // Fabric v7 findTarget returns a `FullTargetsInfoWithContainer`
      // wrapper (truthy even on miss) — read `.target` for the
      // v6 hit semantics, same pattern as the marquee handler.
      // Hits on existing slice overlays mean the user is starting
      // a drag/resize — let Fabric handle it.
      if (canvas.findTarget(opt.e).target) return;

      const s = sliceStateRef.current;
      // Defensive cleanup if a prior drag was orphaned (e.g. the
      // user mouse:down'd and switched tools mid-drag). Without
      // this, a stale pending rect could accumulate on canvas.
      if (s.pending.obj) {
        canvas.remove(s.pending.obj);
        s.pending.obj = null;
      }

      const pointer = canvas.getScenePoint(opt.e);
      const obj = new fabric.Rect({
        ...SLICE_PENDING_STYLE,
        left: pointer.x,
        top: pointer.y,
        width: 0,
        height: 0,
      });
      canvas.add(obj);
      // The pending rect must NOT carry the slice-overlay tag —
      // it's not committed yet, so the tool-lock loops and the
      // object:modified path must not treat it as a real slice.
      s.pending = {
        obj,
        startX: pointer.x,
        startY: pointer.y,
        isDrawing: true,
      };
      canvas.renderAll();
    });

    canvas.on('mouse:move', (opt) => {
      const s = sliceStateRef.current;
      if (!s.pending.isDrawing || !s.pending.obj || !opt.e) return;
      if (activeToolRef.current !== 'slice') return;
      const pointer = canvas.getScenePoint(opt.e);
      const shiftHeld = !!(opt.e as MouseEvent).shiftKey;
      const dx = pointer.x - s.pending.startX;
      const dy = pointer.y - s.pending.startY;
      let width: number;
      let height: number;
      let left: number;
      let top: number;
      // Shift constrains to square — matches Photoshop's slice
      // tool. Otherwise free aspect.
      if (shiftHeld) {
        const size = Math.max(Math.abs(dx), Math.abs(dy));
        width = size;
        height = size;
        left = dx >= 0 ? s.pending.startX : s.pending.startX - size;
        top = dy >= 0 ? s.pending.startY : s.pending.startY - size;
      } else {
        width = Math.abs(dx);
        height = Math.abs(dy);
        left = Math.min(s.pending.startX, pointer.x);
        top = Math.min(s.pending.startY, pointer.y);
      }
      s.pending.obj.set({ left, top, width, height });
      // requestRenderAll coalesces to one paint per frame; pointermove
      // fires far faster than the display refreshes, and a synchronous
      // renderAll per event redraws the whole canvas multiple times a
      // frame (the same stutter the pinch handler had).
      canvas.requestRenderAll();
    });

    canvas.on('mouse:up', () => {
      const s = sliceStateRef.current;
      if (!s.pending.isDrawing || !s.pending.obj) return;
      // Snapshot + clear pending state BEFORE the dispatch so a
      // synchronous reducer + slice-sync round-trip can't see a
      // stale pending rect.
      const pendingObj = s.pending.obj;
      const finalWidth = pendingObj.width ?? 0;
      const finalHeight = pendingObj.height ?? 0;
      const finalLeft = pendingObj.left ?? 0;
      const finalTop = pendingObj.top ?? 0;
      s.pending = { obj: null, startX: 0, startY: 0, isDrawing: false };
      canvas.remove(pendingObj);

      // Reject too-small (probably a stray click rather than a
      // drag). Threshold matches marquee.
      if (finalWidth < 3 || finalHeight < 3) {
        canvas.renderAll();
        return;
      }
      // Dispatch into React. The slice-sync effect in EditorPage
      // observes the new state.slices entry and re-mounts a real
      // overlay (with selection controls); the pending rect is
      // never the committed overlay.
      s.callbacks?.onCreate({
        x: Math.round(finalLeft),
        y: Math.round(finalTop),
        width: Math.round(finalWidth),
        height: Math.round(finalHeight),
      });
      canvas.renderAll();
    });

    // Slice overlay edits — Fabric's native drag / resize controls
    // fire `object:modified` after the user releases. The generic
    // `object:modified` listener at the top of the events effect
    // forwards into `onObjectModifiedRef`; we add a parallel
    // listener that recognizes slice overlays specifically and
    // routes their geometry back into reducer state.
    canvas.on('object:modified', (opt) => {
      const target = opt.target;
      if (!target || !isSliceOverlay(target)) return;
      const sliceId = getSliceIdFromObject(target);
      if (!sliceId) return;
      const next = normalizeSliceScaleIntoDimensions(target as fabric.Rect);
      sliceStateRef.current.callbacks?.onUpdate(sliceId, next);
      canvas.renderAll();
    });

    // Non-affine Transform handle drag (Tier 1 Group E Phase 1b).
    // Custom mouse handlers — handles are non-selectable visual
    // overlays, so Fabric's selection system doesn't route the
    // drag. Initial implementation used selectable Circles +
    // object:moving but Fabric's hit-test priorities (active
    // object eats clicks within its bbox even with
    // preserveObjectStacking=true) made handles silently
    // unresponsive. Hit-test now happens manually via scene-space
    // distance to each handle position; HANDLE_HIT_RADIUS is
    // bumped above the visual radius so the user doesn't need
    // pixel-perfect aim.
    const HANDLE_HIT_RADIUS = 12;
    canvas.on('mouse:down', (opt) => {
      const overlay = transformOverlayRef.current;
      if (!overlay || !opt.e) return;
      const point = canvas.getScenePoint(opt.e);
      const points: { x: number; y: number }[] =
        overlay.mode === 'warp' ? [...overlay.warpGrid] : [...overlay.distortQuad];
      let bestIdx = -1;
      let bestDist = Infinity;
      for (let i = 0; i < points.length; i++) {
        const dx = points[i].x - point.x;
        const dy = points[i].y - point.y;
        const d = Math.hypot(dx, dy);
        if (d < bestDist) {
          bestDist = d;
          bestIdx = i;
        }
      }
      if (bestIdx < 0 || bestDist > HANDLE_HIT_RADIUS) return;
      transformDragRef.current = { handleIndex: bestIdx };
    });

    canvas.on('mouse:move', (opt) => {
      const drag = transformDragRef.current;
      const overlay = transformOverlayRef.current;
      if (!drag || !overlay || !opt.e) return;
      const point = canvas.getScenePoint(opt.e);
      const idx = drag.handleIndex;
      const x = point.x;
      const y = point.y;
      if (overlay.mode === 'warp') {
        const next = [...overlay.warpGrid] as { x: number; y: number }[];
        next[idx] = { x, y };
        overlay.warpGrid = next as unknown as WarpGrid;
        const h = overlay.handles[idx];
        if (h) {
          h.set({ left: x, top: y });
          h.setCoords();
        }
      } else {
        const next = [...overlay.distortQuad] as { x: number; y: number }[];
        next[idx] = { x, y };
        const h = overlay.handles[idx];
        if (h) {
          h.set({ left: x, top: y });
          h.setCoords();
        }
        if (overlay.mode === 'perspective') {
          // Mirror co-lateral corner for 1-point perspective.
          // Pairs: tl(0) ↔ tr(1), bl(3) ↔ br(2).
          const initialQuad: ProjectiveQuad = [
            { x: overlay.bbox.left, y: overlay.bbox.top },
            { x: overlay.bbox.left + overlay.bbox.width, y: overlay.bbox.top },
            {
              x: overlay.bbox.left + overlay.bbox.width,
              y: overlay.bbox.top + overlay.bbox.height,
            },
            { x: overlay.bbox.left, y: overlay.bbox.top + overlay.bbox.height },
          ];
          const pairIdx = idx === 0 ? 1 : idx === 1 ? 0 : idx === 2 ? 3 : 2;
          const dx = x - initialQuad[idx].x;
          const dy = y - initialQuad[idx].y;
          next[pairIdx] = {
            x: initialQuad[pairIdx].x - dx,
            y: initialQuad[pairIdx].y + dy,
          };
          const peer = overlay.handles[pairIdx];
          if (peer) {
            peer.set({ left: next[pairIdx].x, top: next[pairIdx].y });
            peer.setCoords();
          }
        }
        overlay.distortQuad = next as unknown as ProjectiveQuad;
      }
      updateNonAffinePreviewRef.current?.();
    });

    canvas.on('mouse:up', () => {
      transformDragRef.current = null;
    });

    canvas.on('mouse:dblclick', () => {
      if (activeToolRef.current !== 'marquee') return;
      const m = marqueeRef.current;
      if (m.shape !== 'polygon' || !m.isDrawing) return;
      // The browser fires two mousedowns before dblclick, so the second
      // mousedown already added a vertex at the dblclick position. Pop it
      // so the closing edge runs to the previous vertex, not back to itself.
      if (m.points.length >= 2) {
        m.points = m.points.slice(0, -1);
      }
      if (m.points.length < 3) return;
      closePolygonMarquee(canvas, m, marqueeColorRef.current);
      canvas.renderAll();
      refreshMarqueeSelectionState();
    });

    // Quick Mask brush handlers (Tier 1 Group D). Active only when
    // the user has toggled into Quick Mask mode from the StatusBar
    // selection chip popover — the reducer flips `activeTool` to
    // `'quickmask'` atomically (see `useImageEditorState` →
    // `SET_QUICK_MASK_ACTIVE`). Strokes paint into the working
    // alpha mask via a soft radial-gradient dab; the visual overlay
    // is rebuilt from the mask after each stroke.
    canvas.on('mouse:down', (opt) => {
      if (activeToolRef.current !== 'quickmask') return;
      const qmCanvas = quickMaskCanvasRef.current;
      if (!qmCanvas || !opt.e) return;
      const point = canvas.getScenePoint(opt.e);
      const altPressed = (opt.e as MouseEvent).altKey === true;
      // Default mode from the panel (or status-bar) toggle; Alt
      // inverts for the duration of the stroke.
      const baseMode = quickMaskModeRef.current;
      const inverted = altPressed ? (baseMode === 'add' ? 'subtract' : 'add') : baseMode;
      // Allocate per-stroke working canvases at QM dims. `temp`
      // captures the stroke's spatial footprint at full alpha
      // (saturates at 1 with overlapping dabs); `baseline` snapshots
      // the QM state at stroke start so the per-tick composite
      // (`QM = baseline ± temp@opacity`) is stable regardless of
      // how the user wiggles around within the stroke.
      const temp = document.createElement('canvas');
      temp.width = qmCanvas.width;
      temp.height = qmCanvas.height;
      const baseline = document.createElement('canvas');
      baseline.width = qmCanvas.width;
      baseline.height = qmCanvas.height;
      const bCtx = baseline.getContext('2d');
      if (bCtx) bCtx.drawImage(qmCanvas, 0, 0);
      quickMaskStrokeRef.current = {
        mode: inverted,
        radius: Math.max(1, brushSizeRef.current / 2),
        // Soft brush — center 50% radius is fully opaque, outer
        // edge fades to 0. Matches Photoshop's default brush
        // hardness for QM (50%).
        hardness: 0.5,
        opacity: brushOpacityRef.current,
        lastX: point.x,
        lastY: point.y,
        temp,
        baseline,
        dirtyMinX: Infinity,
        dirtyMinY: Infinity,
        dirtyMaxX: -Infinity,
        dirtyMaxY: -Infinity,
      };
      paintQuickMaskDabRef.current?.(point.x, point.y);
      refreshQuickMaskOverlayRef.current?.();
    });

    canvas.on('mouse:move', (opt) => {
      if (activeToolRef.current !== 'quickmask') return;
      const stroke = quickMaskStrokeRef.current;
      if (!stroke || !opt.e) return;
      const point = canvas.getScenePoint(opt.e);
      const dx = point.x - stroke.lastX;
      const dy = point.y - stroke.lastY;
      const dist = Math.hypot(dx, dy);
      // Brush spacing = radius × 0.25 (Photoshop default). Cheap and
      // gives a continuous-feeling stroke without stamping every
      // pointer sample.
      const spacing = Math.max(1, stroke.radius * 0.25);
      if (dist < spacing) return;
      const steps = Math.ceil(dist / spacing);
      for (let i = 1; i <= steps; i++) {
        const t = i / steps;
        paintQuickMaskDabRef.current?.(stroke.lastX + dx * t, stroke.lastY + dy * t);
      }
      stroke.lastX = point.x;
      stroke.lastY = point.y;
      refreshQuickMaskOverlayRef.current?.();
    });

    canvas.on('mouse:up', () => {
      if (activeToolRef.current !== 'quickmask') return;
      // Final flush so QM holds the committed end-of-stroke state
      // before the working canvases are discarded — the overlay,
      // getQuickMaskCanvas, and any next-stroke baseline all read
      // QM as the source of truth.
      refreshQuickMaskOverlayRef.current?.();
      quickMaskStrokeRef.current = null;
    });

    // Two-finger pinch zoom on touch devices. Bound to the upper canvas so we get
    // raw touch events with full multi-touch info (Fabric's mouse:* events only
    // surface the first finger). passive:false lets us preventDefault to suppress
    // the browser's native pinch-zoom of the page.
    const upperEl = canvas.upperCanvasEl as HTMLCanvasElement | undefined;

    // ── Palm rejection ──────────────────────────────────────────
    // While a stylus is in use, a resting palm registers as touch
    // points that would otherwise trigger the two-finger pinch/pan.
    // We track the timestamp of the last pen event (capture phase on
    // the wrapper so we see it before Fabric) and suppress touch
    // gestures within a short grace window after it — covering both
    // active strokes (pen pointermove keeps refreshing the timestamp)
    // and the beat a palm lingers after the nib lifts. Using a decaying
    // timestamp rather than a sticky "pen down" flag makes this
    // self-healing: a missed/strayed pointerup can't wedge gestures off
    // permanently — the window simply ages out. pointerType 'pen'
    // covers Apple Pencil and S-Pen; mouse/finger are unaffected.
    const PALM_GRACE_MS = 400;
    let lastPenActivityTs = 0;
    const stylusSuppressesTouch = () => Date.now() - lastPenActivityTs < PALM_GRACE_MS;
    const onPenActivity = (e: PointerEvent) => {
      if (e.pointerType === 'pen') lastPenActivityTs = Date.now();
    };
    wrapper.addEventListener('pointerdown', onPenActivity, true);
    wrapper.addEventListener('pointermove', onPenActivity, true);
    wrapper.addEventListener('pointerup', onPenActivity, true);
    wrapper.addEventListener('pointercancel', onPenActivity, true);

    const touchDistance = (e: TouchEvent) => {
      const a = e.touches[0];
      const b = e.touches[1];
      return Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
    };
    const touchMidpoint = (e: TouchEvent, el: HTMLElement) => {
      const a = e.touches[0];
      const b = e.touches[1];
      const rect = el.getBoundingClientRect();
      return new fabric.Point(
        (a.clientX + b.clientX) / 2 - rect.left,
        (a.clientY + b.clientY) / 2 - rect.top,
      );
    };
    // True from the first 2-finger touch until EVERY finger lifts — a
    // superset of `isPinching` (which needs exactly 2 fingers down).
    // Pointer suppression is gated on this, not `isPinching`: otherwise
    // lifting one finger of a two-finger pan while the other keeps moving
    // clears `isPinching` mid-gesture, un-suppresses the lingering
    // finger's pointer events, and Fabric re-engages the tool — painting a
    // stray stroke along the tail of the gesture.
    let gestureActive = false;
    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 2 || !upperEl) return;
      // Palm rejection — ignore touch gestures while a stylus is active.
      if (stylusSuppressesTouch()) return;
      e.preventDefault();
      gestureActive = true;
      const vt = canvas.viewportTransform;
      pinchRef.current = {
        isPinching: true,
        startDist: touchDistance(e),
        startZoom: vt ? vt[0] : 1,
      };
      // Seed the pan reference so two-finger drag translates the canvas
      // (the midpoint delta is applied as a relativePan each frame).
      lastPinchMidpoint = touchMidpoint(e, upperEl);
      panRef.current.isPanning = false;
      // The first finger already engaged the active tool (Fabric routes
      // its single tracked pointer to the tool, so a brush/clone stamp or
      // a layer grab is already in flight). Now that a second finger has
      // landed we know this is a pan/pinch, not a stroke — discard that
      // nascent interaction so it leaves no mark, and clear the per-tool
      // pixel-stroke refs so their `mouse:up` finalizers no-op. The
      // capture-phase `suppressPointerDuringGesture` listener below then
      // keeps the first finger's continued movement from re-engaging any
      // tool for the rest of the gesture (Fabric receives no further
      // pointer events while pinching).
      abandonInFlightCanvasInteraction(canvas);
      onAbandonStroke();
      // Cancel any deferred tap-tool action the first finger armed (text /
      // shape / fill / polygon-vertex / wand) so the pan doesn't commit it
      // on release.
      pendingTapTool = null;
      // A polygon marquee is built click-by-click and must SURVIVE a
      // two-finger pan — the user pans to reposition, then keeps placing
      // vertices. (Its tap-add was deferred above and just cancelled, so
      // no phantom vertex was added.) Every other marquee state is either
      // a half-finished press-drag (rect / ellipse / lasso) or a one-shot
      // selection (wand) with nothing in flight to preserve, so cancel it
      // — including wand contours (extraObjs) and the cached wand mask.
      const m = marqueeRef.current;
      const buildingPolygon = m.shape === 'polygon' && m.isDrawing;
      if (!buildingPolygon && (m.obj || m.extraObjs.length > 0)) {
        if (m.obj) canvas.remove(m.obj);
        for (const o of m.extraObjs) canvas.remove(o);
        m.obj = null;
        m.extraObjs = [];
        m.points = [];
        m.wandMaskCanvas = null;
        m.isDrawing = false;
      }
    };
    // Pinch was janky because every `touchmove` (which fires far more
    // often than the display refreshes) ran a synchronous full
    // `renderAll()` plus two React state updates (zoom %, viewport
    // info). We now coalesce to one apply per animation frame: a
    // touchmove only records the latest target zoom + midpoint, and a
    // single rAF flush applies the zoom, requests one batched render,
    // and fires the state updates once. Multiple moves within a frame
    // collapse into one cheap update.
    let pinchRafId: number | null = null;
    let pendingPinch: { zoom: number; point: fabric.Point } | null = null;
    // Last applied two-finger midpoint (canvas-element px). The frame-to-
    // frame delta is panned so the canvas follows the fingers — a pure
    // two-finger drag (distance constant → zoom unchanged) pans, and a
    // pinch zooms + pans together.
    let lastPinchMidpoint: fabric.Point | null = null;
    const flushPinch = () => {
      pinchRafId = null;
      const next = pendingPinch;
      pendingPinch = null;
      if (!next) return;
      if (lastPinchMidpoint) {
        const dx = next.point.x - lastPinchMidpoint.x;
        const dy = next.point.y - lastPinchMidpoint.y;
        if (dx !== 0 || dy !== 0) canvas.relativePan(new fabric.Point(dx, dy));
      }
      lastPinchMidpoint = next.point;
      canvas.zoomToPoint(next.point, next.zoom);
      if (marqueeRef.current.obj) {
        applyMarqueeStroke(canvas, marqueeRef.current.obj);
        for (const o of marqueeRef.current.extraObjs) applyMarqueeStroke(canvas, o);
      }
      applySliceStrokes(canvas, sliceStateRef.current.objects);
      canvas.requestRenderAll();
      onZoomChangedRef.current?.(next.zoom);
      updateViewportInfoRef.current();
    };
    const onTouchMove = (e: TouchEvent) => {
      if (!pinchRef.current.isPinching || e.touches.length !== 2 || !upperEl) return;
      e.preventDefault();
      const newDist = touchDistance(e);
      if (pinchRef.current.startDist === 0) return;
      const factor = newDist / pinchRef.current.startDist;
      const newZoom = Math.max(0.1, Math.min(10, pinchRef.current.startZoom * factor));
      // `newZoom` is absolute (startZoom × current/startDist), so keeping
      // only the latest target is correct — no incremental drift.
      pendingPinch = { zoom: newZoom, point: touchMidpoint(e, upperEl) };
      if (pinchRafId === null) pinchRafId = requestAnimationFrame(flushPinch);
    };
    const onTouchEnd = (e: TouchEvent) => {
      if (e.touches.length < 2) {
        // Pinch zoom/pan needs two fingers, so stop it now…
        pinchRef.current.isPinching = false;
        // Apply any frame still pending so the final zoom isn't dropped.
        if (pinchRafId !== null) {
          cancelAnimationFrame(pinchRafId);
          pinchRafId = null;
          flushPinch();
        }
      }
      // …but keep suppressing pointer input until the LAST finger lifts, so
      // a finger still on the glass after the pinch can't bleed into a
      // tool stroke. Re-abandon any nascent interaction defensively.
      if (e.touches.length === 0) {
        gestureActive = false;
      } else {
        abandonInFlightCanvasInteraction(canvas);
        onAbandonStroke();
      }
    };
    if (upperEl) {
      upperEl.addEventListener('touchstart', onTouchStart, { passive: false });
      upperEl.addEventListener('touchmove', onTouchMove, { passive: false });
      upperEl.addEventListener('touchend', onTouchEnd);
      upperEl.addEventListener('touchcancel', onTouchEnd);
    }

    // While a two-finger pan/pinch is active, swallow pointer events
    // before they reach Fabric's upper-canvas listeners. Fabric drives
    // tool input (free-draw brush + every `mouse:*`-based tool) off
    // pointer events and only ever tracks ONE pointer, so without this
    // it would keep routing the first finger's movement to the active
    // tool — painting a smear along the whole pan path. Capture phase on
    // `wrapper` (an ancestor of the canvases) lets us stopPropagation
    // before the descendant upper canvas sees the event, the same
    // technique the Alt+right resize gesture below relies on. Touch
    // events are a separate stream, so the pinch handler above (bound to
    // raw `touchmove`) is unaffected; single-finger input passes straight
    // through because `gestureActive` only spans a multi-finger gesture
    // (from the 2-finger touchstart until every finger lifts — see
    // `gestureActive`, which outlives `isPinching` through the 2→1 tail).
    const suppressPointerDuringGesture = (e: PointerEvent) => {
      if (gestureActive) e.stopPropagation();
    };
    wrapper.addEventListener('pointerdown', suppressPointerDuringGesture, true);
    wrapper.addEventListener('pointermove', suppressPointerDuringGesture, true);
    wrapper.addEventListener('pointerup', suppressPointerDuringGesture, true);

    // Alt + right-button horizontal drag → live-resize the active
    // size-based tool's tip (Photoshop's on-canvas brush resize).
    // Bound in the CAPTURE phase on `wrapper` (an ancestor of the
    // Fabric canvases) so we can stopPropagation before Fabric's
    // upper-canvas mousedown fires. Fabric ignores the right button
    // for its own drag/draw, but it still emits the `mouse:down`
    // EVENT — and the Retouch clone/heal handler sets the clone
    // source on any Alt+down regardless of button, so without this
    // interception Alt+right-drag would also stamp a clone source.
    // We listen on `window` for move/up so the gesture survives the
    // pointer leaving the canvas. The size delta scales by tool
    // because the ranges differ (Brush/Eraser 1–100 vs Retouch
    // 1–500). React state updates are rAF-coalesced (same reasoning
    // as the pinch handler) so a fast drag doesn't flood re-renders.
    // Desktop/mouse only.
    const RESIZE_FACTOR_BY_TOOL: Record<string, number> = {
      draw: 0.5,
      eraser: 0.5,
      retouch: 2,
    };
    const resizeStartSizeForTool = (tool: string): number | null => {
      if (tool === 'retouch') return retouchSettingsRef.current?.size ?? null;
      if (tool === 'draw' || tool === 'eraser') return brushSizeRef.current;
      return null;
    };
    let toolResize: { startX: number; startSize: number; factor: number } | null = null;
    let resizeRafId: number | null = null;
    let pendingResizeSize: number | null = null;
    let suppressContextMenu = false;
    const flushResize = () => {
      resizeRafId = null;
      if (pendingResizeSize === null) return;
      const size = pendingResizeSize;
      pendingResizeSize = null;
      onAdjustToolSizeRef.current?.(size);
    };
    const onResizePointerMove = (e: PointerEvent) => {
      if (!toolResize) return;
      e.preventDefault();
      const dx = e.clientX - toolResize.startX;
      pendingResizeSize = toolResize.startSize + dx * toolResize.factor;
      if (resizeRafId === null) resizeRafId = requestAnimationFrame(flushResize);
    };
    const endResize = () => {
      if (!toolResize) return;
      toolResize = null;
      if (resizeRafId !== null) {
        cancelAnimationFrame(resizeRafId);
        resizeRafId = null;
        flushResize();
      }
      window.removeEventListener('pointermove', onResizePointerMove, true);
      window.removeEventListener('pointerup', onResizePointerUp, true);
      // Tell EditorPage the resize ended so it restores the clone /
      // heal source-pick cursor state (suppressed during the drag).
      onToolResizeChangeRef.current?.(false);
    };
    const onResizePointerUp = () => endResize();
    const onResizePointerDown = (e: PointerEvent) => {
      if (e.button !== 2 || !e.altKey) return;
      const factor = RESIZE_FACTOR_BY_TOOL[activeToolRef.current];
      if (factor === undefined) return;
      const startSize = resizeStartSizeForTool(activeToolRef.current);
      if (startSize === null) return;
      e.preventDefault();
      // Capture-phase stopPropagation: halt the pointer event before
      // it reaches Fabric's upper-canvas listener, so no `mouse:down`
      // event fires. Fabric uses pointer events, so intercepting
      // `pointerdown` (not `mousedown`) is what actually prevents the
      // Retouch clone/heal source-set, the pan, and any stroke.
      e.stopPropagation();
      // Swallow the contextmenu that the browser raises after a
      // right-button release so the menu doesn't flash at gesture end.
      suppressContextMenu = true;
      toolResize = { startX: e.clientX, startSize, factor };
      window.addEventListener('pointermove', onResizePointerMove, true);
      window.addEventListener('pointerup', onResizePointerUp, true);
      // While resizing, suppress the clone/heal "set source" cursor so
      // the live tip-size circle shows (and tracks) instead of the
      // alt-held crosshair.
      onToolResizeChangeRef.current?.(true);
    };
    const onResizeContextMenu = (e: MouseEvent) => {
      // Only swallow the menu that follows an actual resize gesture
      // (suppressContextMenu is set when one starts). Don't suppress
      // every Alt+right-click — a non-size tool should keep the native
      // context menu.
      if (suppressContextMenu) {
        suppressContextMenu = false;
        e.preventDefault();
      }
    };
    // Capture phase on `wrapper` so we run before Fabric's pointer
    // listeners on the descendant upper canvas (see comment above).
    wrapper.addEventListener('pointerdown', onResizePointerDown, true);
    wrapper.addEventListener('contextmenu', onResizeContextMenu, true);

    // Abandon any in-flight retouch stroke if the window loses
    // focus (alt-tab, browser switcher) or the pointer release
    // happens outside the canvas — `mouse:up` won't fire in
    // either case and the per-stroke ref + `appliedMask` would
    // leak. The didClean guard prevents double-firing
    // onObjectModified on a normal mouse:up release (Fabric's
    // mouse:up runs alongside window pointerup; whichever runs
    // second sees null refs and skips the call).
    const onAbandonStroke = () => {
      let didClean = false;
      if (cloneStrokeRef.current) {
        cloneStrokeRef.current = null;
        didClean = true;
      }
      if (smudgeStrokeRef.current) {
        smudgeStrokeRef.current = null;
        didClean = true;
      }
      if (blurStrokeRef.current) {
        blurStrokeRef.current = null;
        didClean = true;
      }
      if (sharpenStrokeRef.current) {
        sharpenStrokeRef.current = null;
        didClean = true;
      }
      if (dodgeStrokeRef.current) {
        dodgeStrokeRef.current = null;
        didClean = true;
      }
      if (burnStrokeRef.current) {
        burnStrokeRef.current = null;
        didClean = true;
      }
      if (spongeStrokeRef.current) {
        spongeStrokeRef.current = null;
        didClean = true;
      }
      if (didClean) onObjectModifiedRef.current?.();
    };
    window.addEventListener('blur', onAbandonStroke);
    window.addEventListener('pointerup', onAbandonStroke);

    return () => {
      observer.disconnect();
      if (cursorRafId !== null) {
        cancelAnimationFrame(cursorRafId);
        cursorRafId = null;
      }
      if (pinchRafId !== null) {
        cancelAnimationFrame(pinchRafId);
        pinchRafId = null;
      }
      if (resizeRafId !== null) {
        cancelAnimationFrame(resizeRafId);
        resizeRafId = null;
      }
      window.removeEventListener('pointermove', onResizePointerMove, true);
      window.removeEventListener('pointerup', onResizePointerUp, true);
      wrapper.removeEventListener('pointerdown', onResizePointerDown, true);
      wrapper.removeEventListener('contextmenu', onResizeContextMenu, true);
      wrapper.removeEventListener('pointerdown', onPenActivity, true);
      wrapper.removeEventListener('pointermove', onPenActivity, true);
      wrapper.removeEventListener('pointerup', onPenActivity, true);
      wrapper.removeEventListener('pointercancel', onPenActivity, true);
      wrapper.removeEventListener('pointerdown', suppressPointerDuringGesture, true);
      wrapper.removeEventListener('pointermove', suppressPointerDuringGesture, true);
      wrapper.removeEventListener('pointerup', suppressPointerDuringGesture, true);
      if (upperEl) {
        upperEl.removeEventListener('touchstart', onTouchStart);
        upperEl.removeEventListener('touchmove', onTouchMove);
        upperEl.removeEventListener('touchend', onTouchEnd);
        upperEl.removeEventListener('touchcancel', onTouchEnd);
      }
      window.removeEventListener('blur', onAbandonStroke);
      window.removeEventListener('pointerup', onAbandonStroke);
      // Drop any cursor subscribers that survived their host
      // component's unmount. StrictMode mount-unmount-mount and
      // any future consumer that forgets to unsubscribe would
      // otherwise pile up in the Set — defense in depth.
      cursorListenersRef.current.clear();
      canvas.dispose();
      canvasRef.current = null;
    };
  }, []);

  /** Zoom and pan the viewport so the image fits centered in the wrapper */
  const zoomToFit = useCallback(() => {
    const canvas = canvasRef.current;
    const wrapper = wrapperRef.current;
    if (!canvas || !wrapper) return 1;

    const vpW = wrapper.clientWidth;
    const vpH = wrapper.clientHeight;
    const imgW = imageDimsRef.current.width;
    const imgH = imageDimsRef.current.height;
    if (!imgW || !imgH || !vpW || !vpH) return 1;

    // Ensure canvas element matches wrapper
    canvas.setDimensions({ width: vpW, height: vpH });

    const padding = 40;
    const zoom = Math.min((vpW - padding * 2) / imgW, (vpH - padding * 2) / imgH, 1);

    const panX = (vpW - imgW * zoom) / 2;
    const panY = (vpH - imgH * zoom) / 2;

    canvas.setViewportTransform([zoom, 0, 0, zoom, panX, panY]);
    if (marqueeRef.current.obj) {
      applyMarqueeStroke(canvas, marqueeRef.current.obj);
      for (const o of marqueeRef.current.extraObjs) applyMarqueeStroke(canvas, o);
    }
    applySliceStrokes(canvas, sliceStateRef.current.objects);
    canvas.renderAll();
    updateViewportInfo();
    return zoom;
  }, [wrapperRef, updateViewportInfo]);

  /** Load an image onto the canvas. Returns the fit zoom level. */
  const loadImage = useCallback(
    async (src: string): Promise<number> => {
      const canvas = canvasRef.current;
      if (!canvas) return 1;

      const img = await fabric.FabricImage.fromURL(src, { crossOrigin: 'anonymous' });
      backgroundImageRef.current = img;
      tagAsBackground(img);

      const imgW = img.width || 1;
      const imgH = img.height || 1;
      imageDimsRef.current = { width: imgW, height: imgH };

      // Replace the loaded HTMLImageElement with a same-sized canvas so the
      // bg becomes a paintable raster surface.
      const bgCanvas = ensureCanvasElement(
        img.getElement() as HTMLImageElement | HTMLCanvasElement,
        imgW,
        imgH,
      );
      img.setElement(bgCanvas);

      img.set({
        left: 0,
        top: 0,
        // Fabric v7 changed the default origin from 'left'/'top' to
        // 'center'/'center'. The bg image is positioned by its
        // top-left corner at (0, 0), so we restore the v6 default
        // explicitly. Without this, half the image renders into
        // negative coords ("only the bottom half visible in the
        // upper-left").
        originX: 'left',
        originY: 'top',
        scaleX: 1,
        scaleY: 1,
        selectable: false,
        evented: false,
        erasable: false,
        // Disable Fabric's per-object cache so live edits to the underlying
        // canvas show up on the next render without manual invalidation.
        objectCaching: false,
      } as Partial<fabric.FabricImage>);

      // The paint-surface map is keyed by layerId, which the caller sets via
      // `setBackgroundLayerId`. Reset any prior entries here; the new bg will
      // be registered when its layerId is assigned.
      paintSurfacesRef.current.clear();

      // Canvas Size's Extension fill is per-document; reset the ref so the
      // previous doc's fill color doesn't pre-select in the next Canvas Size
      // dialog. `canvas.clear()` below also drops the fill rect itself.
      canvasFillColorRef.current = null;

      // Clear any marquee left over from the previous image. `canvas.clear()`
      // below removes the marquee's fabric.Rect from the canvas, but our
      // marqueeRef would still hold an orphaned reference whose stored
      // width/height/coords are in the OLD image's scene space. A subsequent
      // Smart Create / Smart Delete would then crop at coordinates that no
      // longer match the active layer's pixel grid, producing an empty
      // source for the model and an invisible bake.
      marqueeRef.current.obj = null;
      marqueeRef.current.extraObjs = [];
      marqueeRef.current.points = [];
      marqueeRef.current.wandMaskCanvas = null;
      marqueeRef.current.isDrawing = false;
      setHasMarqueeSelection(false);

      // Drop any pointer-position state carried over from the previous
      // document — a click captured against the prior doc's scene space
      // would otherwise centerAt-paste a new image at a scene coord
      // that's meaningless (and frequently off-canvas) in the new doc.
      lastClickSceneRef.current = null;

      canvas.clear();
      canvas.add(img);
      canvas.sendObjectToBack(img);
      // Re-install the canvas-bounds chrome (outline + clip region)
      // AFTER `canvas.clear()` — otherwise the outline gets wiped
      // along with everything else from the prior tab and the user
      // sees no canvas border until something else (e.g. a canvas
      // resize) re-triggers the install.
      ensureCanvasBoundsOverlay(canvas, imgW, imgH);

      // Wait for layout to settle, then fit
      await new Promise((r) => requestAnimationFrame(r));
      const zoom = zoomToFit();

      onCanvasDimensions?.(imgW, imgH);
      return zoom;
    },
    [onCanvasDimensions, zoomToFit],
  );

  /** Initialize an empty document at the given dimensions — no background
   *  image, no objects. Used by multi-layer document loads (PSD import,
   *  potentially other future formats) where the caller will add each
   *  layer separately via `addImageLayer` rather than treating one image
   *  as the document.
   *
   *  Mirrors `loadImage`'s cleanup pattern (marquee + paint surfaces +
   *  marqueeRef state) so the editor doesn't inherit half-baked Fabric
   *  state from a prior document. Returns the fit zoom so the caller can
   *  drive viewport reset the same way `loadImage` does. */
  const initEmptyCanvas = useCallback(
    async (width: number, height: number): Promise<number> => {
      const canvas = canvasRef.current;
      if (!canvas) return 1;

      backgroundImageRef.current = null;
      imageDimsRef.current = { width, height };
      paintSurfacesRef.current.clear();
      canvasFillColorRef.current = null;
      // Drop pointer state from any prior document — see loadImage for why.
      lastClickSceneRef.current = null;

      marqueeRef.current.obj = null;
      marqueeRef.current.extraObjs = [];
      marqueeRef.current.points = [];
      marqueeRef.current.wandMaskCanvas = null;
      marqueeRef.current.isDrawing = false;
      setHasMarqueeSelection(false);

      canvas.clear();
      // Outline rect re-added AFTER canvas.clear() removed everything.
      ensureCanvasBoundsOverlay(canvas, width, height);

      await new Promise((r) => requestAnimationFrame(r));
      const zoom = zoomToFit();
      onCanvasDimensions?.(width, height);
      return zoom;
    },
    [onCanvasDimensions, zoomToFit],
  );

  /** Combine a hex color and 0–1 opacity into a Fabric-friendly rgba string. */
  const composeBrushColor = (hex: string, opacity: number): string => {
    const r = parseInt(hex.slice(1, 3), 16) || 0;
    const g = parseInt(hex.slice(3, 5), 16) || 0;
    const b = parseInt(hex.slice(5, 7), 16) || 0;
    const a = Math.max(0, Math.min(1, opacity));
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  };

  /** Enable free drawing mode */
  const enableDrawMode = useCallback((settings: BrushSettings) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.isDrawingMode = true;
    const brush = new fabric.PencilBrush(canvas);
    brush.color = composeBrushColor(settings.color, settings.opacity);
    brush.width = settings.width;
    canvas.freeDrawingBrush = brush;
    brushSizeRef.current = settings.width;
    brushOpacityRef.current = settings.opacity;
    brushFeatherRef.current = settings.feather;
  }, []);

  /**
   * Mutate the active free-drawing brush's color/width/opacity in place. Use
   * this on settings changes while the Draw tool is active so the next stroke
   * picks up the new values without re-toggling tools. For Eraser, only the
   * width is updated (the color is hard-coded for destination-out semantics).
   */
  const updateBrush = useCallback((settings: BrushSettings) => {
    // Always mirror opacity into the eraser ref — even when the active tool
    // is Draw, the user might switch to Eraser later expecting the slider
    // they just set to apply.
    eraserOpacityRef.current = settings.opacity;
    // Mirror width + opacity for tools that don't go through Fabric's
    // freeDrawingBrush (Quick Mask).
    brushSizeRef.current = settings.width;
    brushOpacityRef.current = settings.opacity;
    brushFeatherRef.current = settings.feather;
    const canvas = canvasRef.current;
    if (!canvas || !canvas.freeDrawingBrush) return;
    if (activeToolRef.current === 'draw') {
      canvas.freeDrawingBrush.color = composeBrushColor(settings.color, settings.opacity);
    }
    if (activeToolRef.current === 'draw' || activeToolRef.current === 'eraser') {
      canvas.freeDrawingBrush.width = settings.width;
    }
  }, []);

  /** Disable drawing mode */
  const disableDrawMode = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.isDrawingMode = false;
  }, []);

  // Tier 2 Group B — Fabric factory the styling helper needs.
  // Closed over fabric the same way the rest of this hook is.
  // Memoized once so identity is stable across re-renders.
  // Inside stroke is handled by the `_renderText` override
  // installed via `ensureInsideStrokeRenderOverride`, not by a
  // factory here.
  const textStylingDeps: TextStylingDeps = useMemo(
    () => ({
      createShadow: (spec) =>
        new fabric.Shadow({
          color: spec.color,
          offsetX: spec.offsetX,
          offsetY: spec.offsetY,
          blur: spec.blur,
        }),
    }),
    [],
  );

  /** Add a text object to the canvas center (in image coordinates) */
  const addText = useCallback(
    (
      text: string,
      settings: TextSettings,
      layerId?: string,
      position?: { x: number; y: number },
    ) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const imgW = imageDimsRef.current.width;
      const imgH = imageDimsRef.current.height;

      const textObj = new fabric.IText(text, {
        left: position ? position.x : imgW / 2,
        top: position ? position.y : imgH / 2,
        fontFamily: settings.fontFamily,
        fontSize: settings.fontSize,
        fontWeight: settings.fontWeight,
        fontStyle: settings.fontStyle,
        underline: settings.underline,
        textAlign: settings.textAlign,
        fill: settings.fill,
        originX: 'center',
        originY: 'center',
      });
      // Apply Tier 2 Group B styling (tracking, leading, stroke,
      // drop shadow). Path / arch stay below since they depend on
      // the IText's measured width after Fabric mounts the glyphs.
      // Install the inside-stroke render override BEFORE the set
      // so the first paint already honors the flag if Inside is
      // active.
      ensureInsideStrokeRenderOverride(textObj);
      textObj.set(resolveTextStyleProps(settings, textStylingDeps));

      (textObj as fabric.IText & { id: string }).id = `text_${Date.now()}`;
      if (layerId) setObjectLayerId(textObj, layerId);
      canvas.add(textObj);
      canvas.setActiveObject(textObj);
      // Stash the arch slider value on the object for later round-trip; build
      // the path now that Fabric has measured the text's width. Force a
      // re-init so width reflects the tracking applied via
      // resolveTextStyleProps above — without this, the constructor's
      // measurement (taken before charSpacing landed) yields the
      // tracking-zero width and the arch path would be too short for the
      // tracked glyphs, producing the jumbled-character look users see
      // when arch is touched after tracking is non-zero.
      const initText = textObj as fabric.IText & { initDimensions?: () => void };
      if (typeof initText.initDimensions === 'function') initText.initDimensions();
      (textObj as fabric.IText & { archValue: number }).archValue = settings.arch;
      if (settings.arch !== 0) {
        const path = buildTextArchPath(textObj.width || 0, settings.arch);
        if (path) textObj.set({ path });
      }
      // Drop straight into edit mode with the placeholder text selected so
      // the user can type to replace it.
      textObj.enterEditing();
      textObj.selectAll();
      canvas.renderAll();
      // Snapshot is the caller's responsibility (see addShape comment).
    },
    [textStylingDeps],
  );

  /** Apply TextSettings to the active IText. No-op if active isn't an IText. */
  const updateActiveText = useCallback(
    (settings: TextSettings) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const active = canvas.getActiveObject();
      if (!(active instanceof fabric.IText)) return;
      // Tier 2 Group B — single helper covers fontFamily/size/style
      // + tracking + leading + stroke + drop shadow. The helper does
      // not touch the `.path` slot (managed below) or `archValue`
      // (a custom object property, not a Fabric prop).
      ensureInsideStrokeRenderOverride(active);
      active.set(resolveTextStyleProps(settings, textStylingDeps));
      // Tracking (`charSpacing`) and leading (`lineHeight`) don't
      // auto-invalidate Fabric IText's measured-glyph cache
      // (`__charBounds` / `_textLines`) — without this re-init the
      // sliders only take visible effect after another edit (font
      // size, typing, etc.) forces a re-layout. `setTextPathBinding`
      // uses the same pair for the same reason.
      const itext = active as fabric.IText & { initDimensions?: () => void };
      if (typeof itext.initDimensions === 'function') itext.initDimensions();
      active.dirty = true;
      // Persist arch value even when a vector path binding is active —
      // unbinding later should restore the previous arch curvature.
      (active as fabric.IText & { archValue: number }).archValue = settings.arch;

      // Path slot ownership:
      //   - When this text is bound to a vector layer's path, the
      //     `.path` slot is managed by EditorPage's text-path-sync
      //     effect (via `setTextPathBinding`). We must NOT touch it
      //     here — overwriting with an arch path would visibly
      //     break the binding until the next sync tick.
      //   - When unbound, fall back to the original arch logic. The
      //     temp Text re-measures the natural width without a path
      //     so the arch geometry tracks the text width even when the
      //     previous path was already shaping the glyphs.
      const layerId = getObjectLayerId(active);
      const isBoundToVectorPath = layerId ? textPathBoundLayerIdsRef.current.has(layerId) : false;
      if (!isBoundToVectorPath) {
        if (settings.arch === 0) {
          active.set({ path: undefined });
        } else {
          // Tier 2 Group B (2026-05-14) — include charSpacing in
          // the measurement. Without it, the natural width is
          // computed AS IF tracking were 0, but the live IText has
          // the user's tracking applied. The arch path ends up
          // shorter than the glyphs need, and Fabric distributes
          // the glyphs along it cycling-style, producing the
          // jumbled-character look the user reported. lineHeight
          // doesn't affect width on a single-line measurement so
          // it's omitted for symmetry with the path-builder, but
          // keeping it in lock-step is harmless if added later.
          const measureText = new fabric.Text(active.text || '', {
            fontFamily: settings.fontFamily,
            fontSize: settings.fontSize,
            fontWeight: settings.fontWeight,
            fontStyle: settings.fontStyle,
            charSpacing: settings.letterSpacing,
          });
          const naturalWidth = measureText.width || active.width || 0;
          const path = buildTextArchPath(naturalWidth, settings.arch);
          if (path) active.set({ path });
        }
      }
      active.setCoords();
      canvas.requestRenderAll();
    },
    [textStylingDeps],
  );

  /** Bind or unbind a text layer's `fabric.IText.path` to a vector
   *  layer's geometry. Caller is EditorPage's text-path-sync effect;
   *  it computes the SVG path-data string from the bound vector
   *  layer's `VectorPath` via `pathToSvgData` and hands it in here.
   *  `null` unbinds — the IText's path slot reverts to the arch
   *  path if `archValue !== 0`, or clears entirely.
   *
   *  The `textPathBoundLayerIdsRef` mirror is updated synchronously
   *  so `updateActiveText` (which may fire in the same render pass
   *  for a different reason — e.g. font-size tweak while bound)
   *  sees the binding state and skips its arch path logic.
   *
   *  Returns true when a matching FabricImage IText was found and
   *  updated; false otherwise (text layer doesn't exist on the
   *  canvas yet — caller should retry once the layer is realized).
   *
   *  TODO(text-on-path): refresh-desync follow-up. After save + page
   *  reload, the IText occasionally renders against pre-edit path
   *  geometry until the user manually unbinds + rebinds. The cross-
   *  rAF split below mimics the manual recovery path for the LIVE
   *  rebind case (and works there), but it doesn't survive
   *  `canvas.loadFromJSON`'s post-load `renderAll` — Fabric's IText
   *  layout cache (`__charBounds` / `_textLines`) is filled against
   *  the saved path during load and our subsequent rebind doesn't
   *  always invalidate it. Root cause is on Fabric's side; needs a
   *  proper invalidation entry-point. Once fixed, this split can
   *  collapse back to a synchronous `set({ path })` + a clean
   *  contract ("binding is applied when this returns"). */
  const setTextPathBinding = useCallback(
    (textLayerId: string, vectorPathSvgData: string | null): boolean => {
      const canvas = canvasRef.current;
      if (!canvas) return false;
      if (vectorPathSvgData) textPathBoundLayerIdsRef.current.add(textLayerId);
      else textPathBoundLayerIdsRef.current.delete(textLayerId);
      for (const obj of canvas.getObjects()) {
        if (getObjectLayerId(obj) !== textLayerId) continue;
        if (!(obj instanceof fabric.IText)) continue;
        const itext = obj as fabric.IText & { initDimensions?: () => void };
        if (vectorPathSvgData) {
          const path = new fabric.Path(vectorPathSvgData, {
            fill: '',
            stroke: '',
            objectCaching: false,
            visible: false,
          });
          // Cross-frame clear → set. Synchronously calling
          // `set({ path: undefined })` then `set({ path: newPath })`
          // in the same tick lets Fabric coalesce the transitions
          // away — IText's cached layout (computed against whatever
          // path was attached when the prior render ran) survives
          // both sets, so the new path's geometry isn't honored.
          // Manual unbind+rebind (two separate setTextPathBinding
          // calls across React renders) works because there's a
          // render commit between the cleared-state and new-state
          // sets. The split below mimics that behavior in a single
          // public call: frame 1 commits the cleared state; frame
          // 2 attaches the new path against a freshly cleared
          // IText cache. requestAnimationFrame chains keep the
          // sequence ordered relative to Fabric's render loop.
          obj.set({ path: undefined });
          obj.dirty = true;
          obj.setCoords();
          canvas.requestRenderAll();
          requestAnimationFrame(() => {
            const stillOnCanvas = canvas.getObjects().includes(obj);
            if (!stillOnCanvas) return;
            obj.set({ path });
            if (typeof itext.initDimensions === 'function') itext.initDimensions();
            obj.dirty = true;
            obj.setCoords();
            canvas.requestRenderAll();
          });
        } else {
          const archValue = (obj as fabric.IText & { archValue?: number }).archValue ?? 0;
          if (archValue !== 0) {
            const archPath = buildTextArchPath(obj.width ?? 0, archValue);
            obj.set({ path: undefined });
            if (archPath) obj.set({ path: archPath });
          } else {
            obj.set({ path: undefined });
          }
          if (typeof itext.initDimensions === 'function') itext.initDimensions();
          obj.dirty = true;
          obj.setCoords();
          canvas.requestRenderAll();
        }
        return true;
      }
      return false;
    },
    [],
  );

  /** Read the active IText's properties as a TextSettings snapshot.
   *
   *  Tier 2 Group B (2026-05-14) — accepts an optional `layerId`
   *  fallback. When the canvas active object isn't an IText (e.g.
   *  immediately after `loadFromJSON`, which clears selection), the
   *  helper searches the canvas for an IText tagged with that
   *  layer id and reads from it. This is what makes the
   *  PropertiesPanel populate with the saved text-styling values
   *  on session restore — without it, the panel showed defaults
   *  until the user clicked off the layer and back. */
  const getActiveTextSettings = useCallback((layerId?: string | null): TextSettings | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    let active: fabric.Object | null | undefined = canvas.getActiveObject();
    if (!(active instanceof fabric.IText)) {
      if (!layerId) return null;
      active = canvas
        .getObjects()
        .find((o) => o instanceof fabric.IText && getObjectLayerId(o) === layerId) as
        | fabric.IText
        | undefined;
      if (!(active instanceof fabric.IText)) return null;
    }
    const fontWeightRaw = active.fontWeight;
    const fontWeight: 'normal' | 'bold' =
      fontWeightRaw === 'bold' || fontWeightRaw === 700 ? 'bold' : 'normal';
    const fontStyleRaw = active.fontStyle;
    const fontStyle: 'normal' | 'italic' = fontStyleRaw === 'italic' ? 'italic' : 'normal';
    const textAlignRaw = active.textAlign;
    const textAlign: 'left' | 'center' | 'right' =
      textAlignRaw === 'center' || textAlignRaw === 'right' ? textAlignRaw : 'left';
    const archValue = (active as fabric.IText & { archValue?: number }).archValue ?? 0;
    // Tier 2 Group B — read back the new styling fields when
    // present. `charSpacing` may be undefined on Fabric IText for
    // legacy sessions; default to 0. `lineHeight` defaults to
    // Fabric's natural 1.16. Stroke and shadow round-trip via the
    // helper-applied props; absent → undefined in TextSettings.
    const strokeColor = typeof active.stroke === 'string' ? active.stroke : null;
    const strokeWidthRaw = active.strokeWidth ?? 0;
    const paintFirst = (active as fabric.IText & { paintFirst?: string }).paintFirst ?? 'fill';
    const insideStrokeFlag = !!(active as fabric.IText & { _fiasInsideStroke?: boolean })
      ._fiasInsideStroke;
    const strokePosition: 'outside' | 'center' | 'inside' = insideStrokeFlag
      ? 'inside'
      : paintFirst === 'stroke'
        ? 'outside'
        : 'center';
    // strokeWidth on the IText is now always the user-set width
    // regardless of position — the inside-stroke render override
    // does the 2× doubling at render time, not in the stored
    // property. So no division here.
    const stroke =
      strokeColor && strokeWidthRaw > 0
        ? { color: strokeColor, width: strokeWidthRaw, position: strokePosition }
        : undefined;
    const shadowRaw = (active as fabric.IText & { shadow?: unknown }).shadow as
      | { color?: string; offsetX?: number; offsetY?: number; blur?: number }
      | null
      | undefined;
    const dropShadow = shadowRaw
      ? {
          color: typeof shadowRaw.color === 'string' ? shadowRaw.color : '#000000',
          offsetX: shadowRaw.offsetX ?? 0,
          offsetY: shadowRaw.offsetY ?? 0,
          blur: shadowRaw.blur ?? 0,
        }
      : undefined;
    return {
      fontFamily: active.fontFamily ?? 'Arial',
      fontSize: active.fontSize ?? 24,
      fontWeight,
      fontStyle,
      underline: active.underline ?? false,
      textAlign,
      fill: typeof active.fill === 'string' ? active.fill : '#000000',
      arch: archValue,
      letterSpacing: (active as fabric.IText & { charSpacing?: number }).charSpacing ?? 0,
      lineHeight: (active as fabric.IText & { lineHeight?: number }).lineHeight ?? 1.16,
      stroke,
      dropShadow,
    };
  }, []);

  /** Add a shape at the canvas center (in image coordinates), or at a given
   *  scene-space `position` if provided. */
  const addShape = useCallback(
    (
      shapeType: ShapeType,
      settings: ShapeSettings,
      layerId?: string,
      position?: { x: number; y: number },
    ) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const imgW = imageDimsRef.current.width;
      const imgH = imageDimsRef.current.height;
      const center = position
        ? { left: position.x, top: position.y }
        : { left: imgW / 2, top: imgH / 2 };
      let shape: fabric.FabricObject;

      switch (shapeType) {
        case 'rect':
          shape = new fabric.Rect({
            ...center,
            width: 150,
            height: 100,
            fill: resolveShapeFill(settings.fill),
            stroke: settings.stroke,
            strokeWidth: settings.strokeWidth,
            rx: settings.cornerRadius,
            ry: settings.cornerRadius,
            originX: 'center',
            originY: 'center',
          });
          break;
        case 'circle':
          shape = new fabric.Circle({
            ...center,
            radius: 60,
            fill: resolveShapeFill(settings.fill),
            stroke: settings.stroke,
            strokeWidth: settings.strokeWidth,
            originX: 'center',
            originY: 'center',
          });
          break;
        case 'triangle':
          shape = new fabric.Triangle({
            ...center,
            width: 120,
            height: 120,
            fill: resolveShapeFill(settings.fill),
            stroke: settings.stroke,
            strokeWidth: settings.strokeWidth,
            originX: 'center',
            originY: 'center',
          });
          break;
        case 'line':
          shape = new fabric.Line([center.left - 75, center.top, center.left + 75, center.top], {
            stroke: settings.stroke,
            strokeWidth: settings.strokeWidth,
          });
          break;
        default:
          return;
      }

      (shape as fabric.FabricObject & { id: string }).id = `shape_${Date.now()}`;
      if (layerId) setObjectLayerId(shape, layerId);
      canvas.add(shape);
      canvas.setActiveObject(shape);
      canvas.renderAll();
      // Snapshot is the caller's responsibility (see addText comment).
    },
    [],
  );

  /**
   * Live-update the active shape with new shape settings. If the type matches,
   * mutate fill/stroke/strokeWidth/cornerRadius in place. If the type changed,
   * replace the active shape with a fresh one at the same position +
   * layerId, then make the new shape active. No-op if the active object isn't
   * a shape (or there's no active object).
   */
  const updateActiveShape = useCallback((settings: ShapeSettings) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const active = canvas.getActiveObject();
    if (!active) return;

    const currentType: ShapeType | null =
      active instanceof fabric.Rect
        ? 'rect'
        : active instanceof fabric.Circle
          ? 'circle'
          : active instanceof fabric.Triangle
            ? 'triangle'
            : active instanceof fabric.Line
              ? 'line'
              : null;
    if (!currentType) return;

    if (currentType === settings.type) {
      const updates: Partial<fabric.FabricObjectProps> = {
        fill: resolveShapeFill(settings.fill),
        stroke: settings.stroke,
        strokeWidth: settings.strokeWidth,
      };
      active.set(updates);
      if (active instanceof fabric.Rect) {
        active.set({ rx: settings.cornerRadius, ry: settings.cornerRadius });
      }
      active.setCoords();
      canvas.requestRenderAll();
      return;
    }

    // Type changed — recreate at the same position and re-tag with the same
    // layerId so the layer association is preserved.
    const layerId = getObjectLayerId(active);
    const left = active.left;
    const top = active.top;
    const originX = active.originX;
    const originY = active.originY;

    let next: fabric.FabricObject;
    switch (settings.type) {
      case 'rect':
        next = new fabric.Rect({
          left,
          top,
          originX,
          originY,
          width: 150,
          height: 100,
          fill: resolveShapeFill(settings.fill),
          stroke: settings.stroke,
          strokeWidth: settings.strokeWidth,
          rx: settings.cornerRadius,
          ry: settings.cornerRadius,
        });
        break;
      case 'circle':
        next = new fabric.Circle({
          left,
          top,
          originX,
          originY,
          radius: 60,
          fill: resolveShapeFill(settings.fill),
          stroke: settings.stroke,
          strokeWidth: settings.strokeWidth,
        });
        break;
      case 'triangle':
        next = new fabric.Triangle({
          left,
          top,
          originX,
          originY,
          width: 120,
          height: 120,
          fill: resolveShapeFill(settings.fill),
          stroke: settings.stroke,
          strokeWidth: settings.strokeWidth,
        });
        break;
      case 'line': {
        const cx = left ?? 0;
        const cy = top ?? 0;
        next = new fabric.Line([cx - 75, cy, cx + 75, cy], {
          stroke: settings.stroke,
          strokeWidth: settings.strokeWidth,
        });
        break;
      }
      default:
        return;
    }

    canvas.remove(active);
    canvas.discardActiveObject();
    (next as fabric.FabricObject & { id: string }).id = `shape_${Date.now()}`;
    if (layerId) setObjectLayerId(next, layerId);
    canvas.add(next);
    canvas.setActiveObject(next);
    canvas.requestRenderAll();
  }, []);

  // ─── Pen tool (Tier 2 Group A) ───────────────────────────────────
  //
  // Construction-time state lives in `penStateRef` and is rebuilt
  // into a preview Fabric.Path on every relevant change. Finalize
  // emits a snapshot through `onPenToolFinalize` and tears down the
  // preview; EditorPage creates the persistent vector layer.

  /** Mirror EditorPage's `state.penSettings` so the canvas-side
   *  mouse handlers see the latest variant + stroke + fill without
   *  re-binding the listeners. Calling pattern matches the
   *  retouch-settings ref mirror. */
  /** Stroke/fill values resolved from the pen settings into the
   *  shape Fabric expects. Vector fill reuses the same gradient-
   *  capable picker as Shape settings. */
  const applyPenStyle = useCallback((target: fabric.Path, settings: PenSettings): void => {
    const fillResolved = resolveShapeFill(settings.fill);
    target.set({
      stroke: settings.strokeColor,
      strokeWidth: settings.strokeWidth,
      strokeUniform: true,
      // Fabric's fill prop accepts strings + gradient objects; the
      // resolver returns whichever matches the input value.
      fill: fillResolved,
      // Preview should ignore selection / events while drawing —
      // clicks need to fall through to the canvas-level mouse
      // handlers which manage the path-creation flow.
      selectable: false,
      evented: false,
      objectCaching: false,
    });
  }, []);

  /** Build a `fabric.Path` from the current penStateRef.anchors.
   *  The constructed object is meant for the in-progress preview
   *  only — `selectable: false`, `evented: false`, tagged with
   *  `_isPenPreview: true` so `applyLayerOrder` keeps it on top
   *  and `cancelPenPath` can sweep stale instances. */
  const buildPenPreviewObject = useCallback(
    (anchors: VectorAnchor[], closed: boolean, settings: PenSettings): fabric.Path | null => {
      if (anchors.length === 0) return null;
      const data = pathToSvgData({ subPaths: [{ closed, anchors }] });
      if (!data) return null;
      const obj = new fabric.Path(data, {
        // Fabric's default originX/originY for Path is 'left'/'top',
        // and it parses absolute path-data coords as scene coords.
        // Keep that — the SVG `M` command already encodes our
        // anchor positions in scene space.
        originX: 'left',
        originY: 'top',
      });
      applyPenStyle(obj, settings);
      (obj as fabric.Path & { _isPenPreview?: boolean })._isPenPreview = true;
      return obj;
    },
    [applyPenStyle],
  );

  /** Replace the preview object on the canvas with one built from
   *  the latest `penStateRef.anchors`. Idempotent; safe to call
   *  multiple times per session. */
  const updatePenPreview = useCallback((): void => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const state = penStateRef.current;
    if (!state) return;
    // Always remove the prior preview first — replacing
    // `_element` would be cheaper but Fabric.Path doesn't expose a
    // mutating API for its path data; reconstruction is the
    // supported shape. The construction-time cost is bounded
    // (≤ small N anchors per session).
    if (state.previewObj) {
      canvas.remove(state.previewObj);
      state.previewObj = null;
    }
    const obj = buildPenPreviewObject(state.anchors, false, penSettingsRef.current);
    if (obj) {
      canvas.add(obj);
      state.previewObj = obj;
    }
    canvas.requestRenderAll();
  }, [buildPenPreviewObject]);

  /** Discard any in-progress pen state and clean up the preview.
   *  Called from `cancelPenPath`, the Escape keyboard handler,
   *  and the tool-change path (switching away from pen mid-draw). */
  /** Mirror EditorPage's `state.penSettings` so the canvas-side
   *  mouse handlers see the latest variant + stroke + fill without
   *  re-binding the listeners. Calling pattern matches the
   *  retouch-settings ref mirror; also re-renders the in-progress
   *  preview when a session is active so style changes mid-draw
   *  show up immediately. */
  const setPenSettings = useCallback(
    (settings: PenSettings) => {
      penSettingsRef.current = settings;
      // Re-render the preview with the new stroke / fill if a path
      // is currently being drawn. Without this, changing a slider
      // mid-session would leave the preview using the OLD style
      // until the next click — which the user perceives as the
      // path "disappearing then coming back."
      if (penStateRef.current && penStateRef.current.anchors.length > 0) {
        updatePenPreview();
      }
    },
    [updatePenPreview],
  );

  const clearPenState = useCallback((): void => {
    const canvas = canvasRef.current;
    // Always sweep `_isPenPreview` objects, even if the ref's
    // `previewObj` slot is empty. The mouse:down handler can be
    // mid-write to the state when a tool-switch effect races
    // through cancelPenPath; without the unconditional sweep an
    // orphan preview survives, and `applyLayerOrder` (which now
    // brings `_isPenPreview` to front alongside the marquee /
    // crop-rect overlays) will faithfully resurface it on the
    // next state.document tick — making the path "reappear"
    // after the user clicks with the new tool.
    if (canvas) {
      for (const obj of canvas.getObjects()) {
        const tagged = obj as fabric.Object & { _isPenPreview?: boolean };
        if (tagged._isPenPreview) canvas.remove(obj);
      }
      canvas.requestRenderAll();
    }
    penStateRef.current = null;
  }, []);

  /** Public cancel — drops the preview, fires no callback. */
  const cancelPenPath = useCallback((): void => {
    clearPenState();
  }, [clearPenState]);

  /** Public finalize — emits the path via `onPenToolFinalize` and
   *  clears the preview. Caller (EditorPage) is responsible for
   *  creating the vector LayerNode + mounting the persistent
   *  Fabric.Path. A path with fewer than 2 anchors is treated as
   *  empty and silently discarded (matches Photoshop's
   *  "click-once-then-Escape = no-op" behavior). */
  const finalizePenPath = useCallback(
    (closed: boolean): boolean => {
      const state = penStateRef.current;
      if (!state || state.anchors.length < 2) {
        clearPenState();
        return false;
      }
      const path: VectorPath = {
        subPaths: [{ closed, anchors: state.anchors.slice() }],
        stroke: {
          color: penSettingsRef.current.strokeColor,
          width: penSettingsRef.current.strokeWidth,
        },
        fill: penSettingsRef.current.fill,
      };
      // Clear state BEFORE invoking the callback so the editor's
      // own re-render via the document-change effect (which may
      // call back into here via setTool / addVectorObject) sees a
      // clean slate.
      clearPenState();
      onPenToolFinalizeRef.current?.(path, { closed });
      return true;
    },
    [clearPenState],
  );

  /** Mount a persistent `fabric.IText` for a text LayerNode
   *  materialized by the SVG importer's native text-mapping path
   *  (Tier 2 Group F SVG round-trip polish, PR-2 phase D).
   *  Mirrors `addVectorObject` for the text-layer case: parses
   *  the descriptor's `(x, y)` as the top-left bbox origin (NOT
   *  the SVG baseline — the importer has already done the
   *  baseline → top adjustment), wires through the IText style
   *  helper so tracking / leading / stroke / shadow honor the
   *  parsed settings, and tags the Fabric object with `layerId`
   *  so every layer-aware helper treats it like any other layer
   *  object. */
  const addTextLayer = useCallback(
    (layerId: string, parsed: ParsedSvgTextLayer): void => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const textObj = new fabric.IText(parsed.text, {
        left: parsed.x,
        top: parsed.y,
        originX: 'left',
        originY: 'top',
        fontFamily: parsed.settings.fontFamily,
        fontSize: parsed.settings.fontSize,
        fontWeight: parsed.settings.fontWeight,
        fontStyle: parsed.settings.fontStyle,
        underline: parsed.settings.underline,
        textAlign: parsed.settings.textAlign,
        fill: parsed.settings.fill,
      });
      ensureInsideStrokeRenderOverride(textObj);
      textObj.set(resolveTextStyleProps(parsed.settings, textStylingDeps));
      (textObj as fabric.IText & { id: string }).id =
        `text_${Date.now()}_${Math.floor(Math.random() * 1e6)}`;
      setObjectLayerId(textObj, layerId);
      (textObj as fabric.IText & { archValue: number }).archValue = parsed.settings.arch;
      canvas.add(textObj);
      canvas.requestRenderAll();
      onObjectModifiedRef.current?.();
    },
    [textStylingDeps],
  );

  /** Mount a persistent `fabric.Path` for an existing vector
   *  LayerNode. Called from EditorPage after `addLayer`
   *  dispatches; tags the Fabric object with the layer id so
   *  every layer-aware helper (selection, opacity, ordering,
   *  effects) treats it like any other layer object. */
  const addVectorObject = useCallback((layerId: string, path: VectorPath): void => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const data = pathToSvgData(path);
    if (!data) return;
    const obj = new fabric.Path(data, {
      originX: 'left',
      originY: 'top',
      stroke: path.stroke?.color ?? '#000000',
      strokeWidth: path.stroke?.width ?? 2,
      strokeUniform: true,
      fill: resolveShapeFill(path.fill ?? 'transparent'),
      // Keep Fabric's default object caching enabled — the
      // per-object offscreen cache is what makes
      // `globalCompositeOperation` blend modes work. Without it,
      // stroke pixels render directly to the main canvas and most
      // blend modes silently no-op for transparent-fill paths.
    });
    setObjectLayerId(obj, layerId);
    canvas.add(obj);
    canvas.requestRenderAll();
    // Snapshot for history. Pen-tool creation is one atomic
    // history entry from the user's POV.
    onObjectModifiedRef.current?.();
  }, []);

  /** Update the stroke / strokeWidth / fill of the persistent
   *  `fabric.Path` bound to `layerId` without touching the path
   *  geometry. Used by the shape-settings effect to sync color /
   *  width / fill changes onto a vector layer that DOESN'T qualify
   *  for full regenerate (custom-edited primitives + pen-drawn
   *  paths). Returns true on success. */
  const updateVectorStyle = useCallback(
    (
      layerId: string,
      style: { stroke: string; strokeWidth: number; fill: string | ShapeFillValue },
    ): boolean => {
      const canvas = canvasRef.current;
      if (!canvas) return false;
      const obj = canvas
        .getObjects()
        .find((o): o is fabric.Path => o instanceof fabric.Path && getObjectLayerId(o) === layerId);
      if (!obj) return false;
      obj.set({
        stroke: style.stroke,
        strokeWidth: style.strokeWidth,
        fill: resolveShapeFill(style.fill),
      });
      obj.setCoords();
      canvas.requestRenderAll();
      return true;
    },
    [],
  );

  /** Read the stroke / strokeWidth / fill of the `fabric.Path` bound
   *  to `layerId`. Returns null when no matching path object is on
   *  the canvas. Counterpart to `updateVectorStyle` — used by the
   *  pen-tool panel sync so changing PenPanel sliders while a
   *  pen-drawn vector layer is selected edits THAT layer, not just
   *  the defaults for the next draw. `getActiveShapeSettings` only
   *  knows the four primitive shape classes, not `fabric.Path`,
   *  which is why pen-drawn paths fall through to here. */
  const getVectorLayerStyle = useCallback(
    (
      layerId: string,
    ): { stroke: string; strokeWidth: number; fill: string | ShapeFillValue } | null => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      const obj = canvas
        .getObjects()
        .find((o): o is fabric.Path => o instanceof fabric.Path && getObjectLayerId(o) === layerId);
      if (!obj) return null;
      return {
        stroke: typeof obj.stroke === 'string' ? obj.stroke : '#000000',
        strokeWidth: obj.strokeWidth ?? 0,
        fill: readShapeFill(obj.fill),
      };
    },
    [],
  );

  /** Synthesize a `VectorPath` from a primitive shape (Rect / Circle /
   *  Triangle) bound to `layerId`. PSD export uses this to round-trip
   *  primitive shape layers parametrically — without it, shapes
   *  flatten to raster and lose corner-radius / dimensions / stroke
   *  editability after save → load.
   *
   *  Returns null when the layer isn't on the canvas or isn't a
   *  primitive we can express as a single sub-path (e.g. line: PSD
   *  shape layers expect closed regions, so we fall back to raster).
   *  Corner radius on rectangles is ignored in v1 — emitted as sharp
   *  corners; a follow-up can add Bezier-corner anchors when the
   *  shape-tool's rx/ry preservation matters in practice.
   *
   *  All output coordinates are in scene space, derived via the
   *  object's `calcTransformMatrix()` so position / scale / rotation
   *  all round-trip. Stroke and fill come from the Fabric object's
   *  current properties — what the user sees IS what we emit. */
  const getShapeVectorPath = useCallback((layerId: string): VectorPath | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const obj = canvas.getObjects().find((o) => getObjectLayerId(o) === layerId);
    if (!obj) return null;
    return fabricShapeToVectorPath(obj, readShapeFill);
  }, []);

  /** Return the scene-space center + bbox of the `fabric.Path`
   *  bound to `layerId`, or null when no matching layer / object.
   *  Used by the shape-settings regenerate effect to position the
   *  rebuilt primitive at the same scene center as the old one. */
  const getActiveVectorBounds = useCallback(
    (
      layerId: string,
    ): {
      center: { x: number; y: number };
      bbox: { left: number; top: number; width: number; height: number };
    } | null => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      const obj = canvas
        .getObjects()
        .find((o): o is fabric.Path => o instanceof fabric.Path && getObjectLayerId(o) === layerId);
      if (!obj) return null;
      const c = obj.getCenterPoint();
      const r = obj.getBoundingRect();
      return {
        center: { x: c.x, y: c.y },
        bbox: { left: r.left, top: r.top, width: r.width, height: r.height },
      };
    },
    [],
  );

  /** Bake the layer's current Fabric transform into its path data,
   *  producing a `VectorPath` in scene-space coordinates. The
   *  source `path` is the raw (unbaked) `LayerNode.path`; the
   *  matrix + pathOffset come from the Fabric object on the
   *  canvas. Used by Boolean Ops so paper.js sees the actual
   *  on-canvas geometry — without this, two layers with different
   *  Fabric translations but identical raw path-data would be
   *  treated as overlapping in the same local frame, and the
   *  result of Subtract/Intersect/Exclude would be nonsense
   *  (empty result or geometry placed at the path-data origin
   *  instead of the visible center). Returns null when no
   *  matching layer is on the canvas. */
  const getBakedVectorPath = useCallback((layerId: string, path: VectorPath): VectorPath | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const obj = canvas
      .getObjects()
      .find((o): o is fabric.Path => o instanceof fabric.Path && getObjectLayerId(o) === layerId);
    if (!obj) return null;
    const matrix = obj.calcTransformMatrix();
    const pathOffset = obj.pathOffset ?? { x: 0, y: 0 };
    return bakeVectorPath(path, matrix, pathOffset);
  }, []);

  /** Replace the `fabric.Path` bound to `layerId` with a fresh one
   *  built from `path`. Used by the ShapePanel-slider regenerate
   *  flow for primitive vector layers — sides / inner / head-size
   *  / stroke / fill changes rebuild the path at the previous
   *  scene-space center so the layer feels like it's "morphing"
   *  in place rather than jumping. Selection is preserved when
   *  the replaced object was active. Returns the new fabric.Path
   *  or null when no matching layer / invalid path. */
  const replaceVectorObject = useCallback(
    (layerId: string, path: VectorPath): fabric.Path | null => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      const old = canvas
        .getObjects()
        .find((o): o is fabric.Path => o instanceof fabric.Path && getObjectLayerId(o) === layerId);
      if (!old) return null;
      const data = pathToSvgData(path);
      if (!data) return null;
      const wasActive = canvas.getActiveObject() === old;
      // Preserve scene-space center across the swap so the layer
      // visibly "morphs" in place. The new fabric.Path's
      // constructor computes its own pathOffset / left / top from
      // the new bbox; we then translate the result so the new
      // bbox center matches the old one.
      const oldCenter = old.getCenterPoint();
      const next = new fabric.Path(data, {
        originX: 'left',
        originY: 'top',
        stroke: path.stroke?.color ?? (old.stroke as string) ?? '#000000',
        strokeWidth: path.stroke?.width ?? old.strokeWidth ?? 2,
        strokeUniform: true,
        fill: resolveShapeFill(path.fill ?? 'transparent'),
        // Preserve any layer-effect shadow + blend mode from the
        // old object so toggling effects + then editing the shape
        // doesn't lose either.
        shadow: old.shadow,
        globalCompositeOperation: (
          old as unknown as { globalCompositeOperation?: GlobalCompositeOperation }
        ).globalCompositeOperation,
        opacity: old.opacity,
        visible: old.visible,
      });
      setObjectLayerId(next, layerId);
      canvas.add(next);
      const newCenter = next.getCenterPoint();
      next.set({
        left: (next.left ?? 0) + (oldCenter.x - newCenter.x),
        top: (next.top ?? 0) + (oldCenter.y - newCenter.y),
      });
      next.setCoords();
      canvas.remove(old);
      if (wasActive) canvas.setActiveObject(next);
      canvas.requestRenderAll();
      return next;
    },
    [],
  );

  // ─── Path Edit mode (Tier 2 Group A PR-B) ────────────────────────
  //
  // On-canvas anchor / handle manipulation for an existing vector
  // layer. Modal session mirroring Free Transform Phase 1b:
  //   - `pathEditOverlayRef` holds the working path + Fabric overlay
  //     objects + a snapshot for Cancel restoration.
  //   - During the session the layer's persistent Fabric.Path is
  //     hidden; a separate "working" Fabric.Path renders the live
  //     state and gets rebuilt on every mutation.
  //   - `activeToolRef` is forced to `'ai'` so every other tool
  //     handler short-circuits — same trick as the non-affine
  //     transform overlay.
  //   - Apply commits via `SET_VECTOR_PATH` (dispatched from
  //     EditorPage); Cancel restores the snapshot.

  /** Tag for every Fabric object that belongs to the Path Edit
   *  overlay (anchors, handles, handle lines, the working path).
   *  Pattern matches `_isMarquee` / `_isPenPreview` etc. so the
   *  layer-order helper keeps overlays on top and teardown can
   *  sweep orphans defensively. */
  const tagPathEditOverlay = (obj: fabric.Object): void => {
    (obj as unknown as { _isPathEditOverlay?: boolean })._isPathEditOverlay = true;
    obj.set({
      selectable: false,
      evented: false,
      hasControls: false,
      hasBorders: false,
      objectCaching: false,
    });
  };

  const pathEditOverlayRef = useRef<{
    layerId: string;
    /** What the session is editing — the layer's own path (vector
     *  layer) or the layer's mask's path (vector mask, Tier 2
     *  Group A PR-D). Drives Apply / Cancel commit shape and
     *  whether originalPathObj is set. */
    mode: 'layer' | 'mask';
    /** Snapshot of the path at session start. Cancel restores by
     *  re-mounting the original Fabric.Path with this data. */
    initialPath: VectorPath;
    /** Working state — mutated by mouse handlers during drag.
     *  Apply commits via `SET_VECTOR_PATH(layerId, workingPath)`. */
    workingPath: VectorPath;
    /** Style captured from the original Fabric.Path so the working
     *  path renders identically while the user edits. */
    style: { stroke: string; strokeWidth: number; fill: fabric.Path['fill'] };
    /** The layer's persistent Fabric.Path — hidden during the
     *  session, restored on Cancel, removed on Apply. Null for
     *  mask-mode sessions (mask vector path has no Fabric.Path
     *  on the canvas; it lives only in state.document). */
    originalPathObj: fabric.Path | null;
    /** Working Fabric.Path that mirrors workingPath. Rebuilt on
     *  every mutation. */
    workingPathObj: fabric.Path | null;
    anchorObjs: fabric.Rect[];
    handleObjs: { in: fabric.Circle | null; out: fabric.Circle | null };
    handleLineObjs: { in: fabric.Line | null; out: fabric.Line | null };
    drag: { kind: 'anchor' | 'handleIn' | 'handleOut'; anchorIndex: number } | null;
    selectedAnchorIndex: number | null;
    savedActiveTool: EditorTool;
    savedSelection: boolean;
  } | null>(null);

  /** Forward-ref pattern (TS TDZ) so mouse handlers can call the
   *  updater before its declaration. Mirrors `setupNonAffineOverlay`
   *  in the non-affine transform block. */
  const updatePathEditOverlayRef = useRef<(() => void) | null>(null);

  // Overlay handles + anchors render at a CONSTANT SCREEN size.
  // The SCENE-pixel constants (`PATH_EDIT_*_SCENE`) are the visual
  // target at 100% zoom; we divide by the current zoom so a
  // zoomed-in 4K image doesn't render anchors at fist-sized
  // squares and a zoomed-out preview doesn't reduce them to
  // single pixels. The overlay is refreshed on zoom changes via
  // an EditorPage effect that calls `refreshPathEditOverlay`.
  const buildAnchorObj = (a: VectorAnchor, isSelected: boolean): fabric.Rect => {
    const zoom = canvasRef.current?.getZoom() ?? 1;
    const size = PATH_EDIT_ANCHOR_SIZE_SCENE / zoom;
    const r = new fabric.Rect({
      left: a.x - size / 2,
      top: a.y - size / 2,
      width: size,
      height: size,
      fill: isSelected ? PATH_EDIT_OVERLAY_COLOR : '#ffffff',
      stroke: PATH_EDIT_OVERLAY_COLOR,
      strokeWidth: 1,
      strokeUniform: true,
      originX: 'left',
      originY: 'top',
    });
    tagPathEditOverlay(r);
    return r;
  };

  const buildHandleObj = (
    anchorX: number,
    anchorY: number,
    dx: number,
    dy: number,
  ): fabric.Circle => {
    const zoom = canvasRef.current?.getZoom() ?? 1;
    const radius = PATH_EDIT_HANDLE_RADIUS_SCENE / zoom;
    const hx = anchorX + dx;
    const hy = anchorY + dy;
    const c = new fabric.Circle({
      left: hx - radius,
      top: hy - radius,
      radius,
      fill: '#ffffff',
      stroke: PATH_EDIT_OVERLAY_COLOR,
      strokeWidth: 1,
      strokeUniform: true,
      originX: 'left',
      originY: 'top',
    });
    tagPathEditOverlay(c);
    return c;
  };

  const buildHandleLineObj = (
    anchorX: number,
    anchorY: number,
    dx: number,
    dy: number,
  ): fabric.Line => {
    const l = new fabric.Line([anchorX, anchorY, anchorX + dx, anchorY + dy], {
      stroke: PATH_EDIT_OVERLAY_COLOR,
      strokeWidth: 1,
      strokeDashArray: [2, 2],
      strokeUniform: true,
    });
    tagPathEditOverlay(l);
    return l;
  };

  const buildWorkingPathObj = (
    path: VectorPath,
    style: { stroke: string; strokeWidth: number; fill: fabric.Path['fill'] },
  ): fabric.Path | null => {
    const data = pathToSvgData(path);
    if (!data) return null;
    const obj = new fabric.Path(data, {
      originX: 'left',
      originY: 'top',
      stroke: style.stroke,
      strokeWidth: style.strokeWidth,
      strokeUniform: true,
      fill: style.fill,
    });
    tagPathEditOverlay(obj);
    return obj;
  };

  /** Rebuild every overlay Fabric object from the current
   *  workingPath + selectedAnchorIndex. Called after every
   *  mutation (anchor drag, handle drag, selection change). */
  const updatePathEditOverlay = useCallback((): void => {
    const canvas = canvasRef.current;
    const overlay = pathEditOverlayRef.current;
    if (!canvas || !overlay) return;
    // Sweep prior overlay objects. Snapshot the filter result
    // BEFORE removing — `canvas.getObjects()` returns the
    // internal `_objects` array directly in Fabric v7, so
    // mutating it (remove) while iterating skips entries and
    // leaves orphans behind. On a drag tick that orphan reads
    // as "two paths overlaid" — the symptom the QA pass caught.
    const stale = canvas
      .getObjects()
      .filter((o) => (o as fabric.Object & { _isPathEditOverlay?: boolean })._isPathEditOverlay);
    for (const obj of stale) canvas.remove(obj);
    overlay.anchorObjs = [];
    overlay.handleObjs = { in: null, out: null };
    overlay.handleLineObjs = { in: null, out: null };
    overlay.workingPathObj = null;

    // Build the working Fabric.Path first so anchors render on top.
    const pathObj = buildWorkingPathObj(overlay.workingPath, overlay.style);
    if (pathObj) {
      canvas.add(pathObj);
      overlay.workingPathObj = pathObj;
    }

    // Anchor squares.
    const subPath = overlay.workingPath.subPaths[0];
    if (subPath) {
      for (let i = 0; i < subPath.anchors.length; i++) {
        const a = subPath.anchors[i];
        const isSelected = i === overlay.selectedAnchorIndex;
        const obj = buildAnchorObj(a, isSelected);
        canvas.add(obj);
        overlay.anchorObjs.push(obj);
      }
      // Handles for the selected anchor — only render when an
      // anchor is selected (Photoshop UX). Skips a handle when
      // its dx/dy are both undefined (corner anchor with no
      // tangent on that side).
      const idx = overlay.selectedAnchorIndex;
      if (idx !== null) {
        const sel = subPath.anchors[idx];
        if (sel) {
          if (sel.inX !== undefined || sel.inY !== undefined) {
            const dx = sel.inX ?? 0;
            const dy = sel.inY ?? 0;
            const line = buildHandleLineObj(sel.x, sel.y, dx, dy);
            const circ = buildHandleObj(sel.x, sel.y, dx, dy);
            canvas.add(line);
            canvas.add(circ);
            overlay.handleLineObjs.in = line;
            overlay.handleObjs.in = circ;
          }
          if (sel.outX !== undefined || sel.outY !== undefined) {
            const dx = sel.outX ?? 0;
            const dy = sel.outY ?? 0;
            const line = buildHandleLineObj(sel.x, sel.y, dx, dy);
            const circ = buildHandleObj(sel.x, sel.y, dx, dy);
            canvas.add(line);
            canvas.add(circ);
            overlay.handleLineObjs.out = line;
            overlay.handleObjs.out = circ;
          }
        }
      }
    }
    canvas.requestRenderAll();
  }, []);
  updatePathEditOverlayRef.current = updatePathEditOverlay;

  /** Open Path Edit mode on a vector layer OR vector mask.
   *  Returns true on success; false otherwise. Caller is expected
   *  to have already dispatched `BEGIN_PATH_EDIT` against the
   *  reducer.
   *
   *  Two modes:
   *
   *  - `mode: 'layer'` (default) — edits the layer's own
   *    `fabric.Path`. Snapshots the path's transform + pathOffset
   *    and bakes them into the working path so the overlay
   *    aligns with the on-canvas geometry. Removes the original
   *    object during the session; Cancel re-adds it, Apply
   *    replaces it with a fresh `fabric.Path` built from the
   *    working data.
   *
   *  - `mode: 'mask'` (Tier 2 Group A PR-D) — edits a vector
   *    mask's `vectorPath`. The mask FabricImage is
   *    `absolutePositioned: true` and lives in canvas coordinates,
   *    so no transform-bake step is needed (identity matrix +
   *    zero pathOffset). The original mask clipPath stays
   *    attached during the session — its rasterized RAW continues
   *    to render the layer's masked appearance unchanged; on
   *    Apply, EditorPage's vector-mask-sync effect picks up the
   *    new `vectorPath` and re-rasterizes RAW. Cancel needs no
   *    canvas-side rollback (nothing was removed). */
  const enterPathEditMode = useCallback(
    (layerId: string, path: VectorPath, opts?: { mode?: 'layer' | 'mask' }): boolean => {
      const mode = opts?.mode ?? 'layer';
      const canvas = canvasRef.current;
      if (!canvas) return false;
      // If a previous session is still active, tear it down with
      // no commit so the new session starts clean.
      if (pathEditOverlayRef.current) {
        updatePathEditOverlayRef.current = null;
        pathEditOverlayRef.current = null;
      }

      let bakedPath: VectorPath;
      let style: { stroke: string; strokeWidth: number; fill: fabric.Path['fill'] };
      let originalPathObj: fabric.Path | null = null;

      if (mode === 'mask') {
        // Vector mask edits operate directly in canvas coords —
        // no Fabric.Path to look up, no transform to bake. The
        // working path IS the mask's vectorPath. Style is fixed
        // (no on-canvas appearance during edit; the user sees
        // the unchanged masked layer + the anchor overlay).
        bakedPath = path;
        style = {
          stroke: '#3b82f6',
          strokeWidth: 1,
          fill: 'rgba(59, 130, 246, 0.08)',
        };
      } else {
        // Find the layer's persistent Fabric.Path. The vector-
        // layer creation pipeline (addVectorObject + onPenTool
        // Finalize) tags the path with the layer id; without it
        // Path Edit has nothing to operate on.
        for (const obj of canvas.getObjects()) {
          if (!(obj instanceof fabric.Path)) continue;
          if (getObjectLayerId(obj) !== layerId) continue;
          originalPathObj = obj;
          break;
        }
        if (!originalPathObj) return false;

        style = {
          stroke: (originalPathObj.stroke as string) ?? '#000000',
          strokeWidth: originalPathObj.strokeWidth ?? 2,
          fill: originalPathObj.fill,
        };

        // Bake the target's current transform (left / top / scale /
        // rotation) + pathOffset into the working path's anchor
        // coordinates. Without this, the anchor overlay renders at
        // the path-data origin (where it was when the layer was
        // first created), while the path itself renders wherever
        // the user moved / scaled / rotated it — anchors detached
        // from the visible curve. On Apply, the new fabric.Path is
        // constructed from this baked path-data; Fabric assigns it
        // a fresh pathOffset + bbox-aligned `left`/`top`, so the
        // visible result is identical but the path-data now lives
        // in the same coord space as the on-canvas geometry.
        //
        // Coordinate chain for the bake math (verified against
        // Fabric v7's `Path.mjs`):
        //   - `pathOffset` is set to `(bbox.left + width/2,
        //     bbox.top + height/2)` — i.e. the POSITIVE bbox-center
        //     in path-data coords.
        //   - Rendering does `ctx.translate(-pathOffset.x,
        //     -pathOffset.y)` before drawing the path-data, so a
        //     path-data point (px, py) ends up at center-relative
        //     local (px - pathOffset.x, py - pathOffset.y).
        //   - `calcTransformMatrix()` then maps that center-relative
        //     local point to scene coords.
        // So: scene = matrix * (pathData − pathOffset).
        const matrix = originalPathObj.calcTransformMatrix();
        const pathOffset = originalPathObj.pathOffset ?? { x: 0, y: 0 };
        bakedPath = bakeVectorPath(path, matrix, pathOffset);

        // Remove the original from the canvas entirely (rather than
        // `visible: false`). The visibility-cascade effect in
        // EditorPage walks every object on every state.document
        // change and resets `visible` from the layer's flag — a
        // mid-session document change (e.g. SET_PATH_EDIT_ANCHOR)
        // would un-hide the original and the user would see the
        // un-edited path bleeding through alongside the working
        // overlay. Cancel adds the original back; Apply doesn't
        // (the new path replaces it).
        canvas.remove(originalPathObj);
      }

      pathEditOverlayRef.current = {
        layerId,
        mode,
        initialPath: bakedPath,
        // Deep-ish copy so mutations during the session can't
        // leak back into the caller's snapshot. Sub-paths +
        // anchors are slice'd; nested objects rebuilt fresh.
        workingPath: {
          ...bakedPath,
          subPaths: bakedPath.subPaths.map((sp) => ({
            ...sp,
            anchors: sp.anchors.map((a) => ({ ...a })),
          })),
        },
        style,
        originalPathObj,
        workingPathObj: null,
        anchorObjs: [],
        handleObjs: { in: null, out: null },
        handleLineObjs: { in: null, out: null },
        drag: null,
        selectedAnchorIndex: null,
        savedActiveTool: activeToolRef.current,
        savedSelection: canvas.selection,
      };

      // Force-suppress every other tool's mouse handlers during
      // the session. Same trick used by the non-affine transform
      // overlay. Also disable Fabric's group-select rubber-band.
      activeToolRef.current = 'ai';
      canvas.selection = false;
      canvas.discardActiveObject();

      updatePathEditOverlay();
      return true;
    },
    [updatePathEditOverlay],
  );

  /** Close Path Edit mode. `apply: true` commits the working path
   *  to the canvas (replaces the persistent Fabric.Path with one
   *  built from workingPath); `apply: false` restores the
   *  original. Caller (EditorPage) is responsible for dispatching
   *  the corresponding `SET_VECTOR_PATH` (apply) or NOT (cancel)
   *  + the `END_PATH_EDIT` reducer action. */
  const exitPathEditMode = useCallback((apply: boolean): VectorPath | null => {
    const canvas = canvasRef.current;
    const overlay = pathEditOverlayRef.current;
    if (!canvas || !overlay) return null;

    // Sweep every overlay object. Snapshot first — see the
    // matching note in `updatePathEditOverlay`.
    const stale = canvas
      .getObjects()
      .filter((o) => (o as fabric.Object & { _isPathEditOverlay?: boolean })._isPathEditOverlay);
    for (const obj of stale) canvas.remove(obj);

    if (overlay.mode === 'mask') {
      // Vector mask edit: no Fabric.Path swap. The caller
      // (EditorPage) reads the applied path from our return and
      // dispatches SET_MASK_VECTOR_PATH; the vector-mask-sync
      // effect picks it up and re-rasterizes RAW. Cancel needs
      // no canvas-side rollback because nothing was removed.
    } else if (apply) {
      // Replace the layer's persistent Fabric.Path with one built
      // from the (scene-coord) workingPath. The new fabric.Path's
      // constructor computes its own pathOffset / left / top from
      // the path-data bbox, so the result renders at the same
      // scene position the user was editing.
      const data = pathToSvgData(overlay.workingPath);
      if (data) {
        const next = new fabric.Path(data, {
          originX: 'left',
          originY: 'top',
          stroke: overlay.style.stroke,
          strokeWidth: overlay.style.strokeWidth,
          strokeUniform: true,
          fill: overlay.style.fill,
        });
        setObjectLayerId(next, overlay.layerId);
        canvas.add(next);
      }
    } else if (overlay.originalPathObj) {
      // Cancel (layer mode) — re-add the (untouched) original
      // Fabric.Path so the layer's on-canvas appearance returns
      // exactly to its pre-session state.
      canvas.add(overlay.originalPathObj);
    }

    // Restore canvas-side state.
    activeToolRef.current = overlay.savedActiveTool;
    canvas.selection = overlay.savedSelection;
    const applied = apply ? overlay.workingPath : null;
    pathEditOverlayRef.current = null;
    canvas.requestRenderAll();
    if (apply) onObjectModifiedRef.current?.();
    return applied;
  }, []);

  /** Set the selected-anchor highlight. Called from the canvas
   *  mouse:down handler (after a hit-test) and from EditorPage
   *  via React state mirror. */
  const setPathEditSelectedAnchor = useCallback(
    (index: number | null): void => {
      const overlay = pathEditOverlayRef.current;
      if (!overlay) return;
      if (overlay.selectedAnchorIndex === index) return;
      overlay.selectedAnchorIndex = index;
      updatePathEditOverlay();
    },
    [updatePathEditOverlay],
  );

  /** Rebuild the Path Edit overlay using the current zoom level.
   *  Called by EditorPage on `state.zoom` changes so anchors +
   *  handles re-render at constant SCREEN size across zoom levels.
   *  No-op when no session is active. */
  const refreshPathEditOverlay = useCallback((): void => {
    if (pathEditOverlayRef.current) updatePathEditOverlay();
  }, [updatePathEditOverlay]);

  /** Set the type of the currently-selected anchor and refresh.
   *  Called from EditPathPanel's type radio. */
  const setPathEditAnchorType = useCallback(
    (type: VectorAnchor['type']): void => {
      const overlay = pathEditOverlayRef.current;
      if (overlay === null || overlay.selectedAnchorIndex === null) return;
      const sub = overlay.workingPath.subPaths[0];
      if (!sub) return;
      const idx = overlay.selectedAnchorIndex;
      const a = sub.anchors[idx];
      if (!a) return;
      let next: VectorAnchor;
      if (type === 'corner') {
        // Corners have no tangent handles. Drop any existing ones
        // so the user sees a sharp transition AND the handle UI
        // disappears.
        next = { ...a, type, inX: undefined, inY: undefined, outX: undefined, outY: undefined };
      } else if (type === 'smooth' && a.inX === undefined && a.outX === undefined) {
        // Smooth anchor with no existing handles — infer tangent
        // direction from prev/next neighbors so the user can
        // actually drag handles. Logic extracted to
        // `lib/vector-path.populateSmoothAnchorHandles` and tested
        // there.
        next = populateSmoothAnchorHandles(sub.anchors, idx, sub.closed);
      } else {
        // 'asymmetric', OR 'smooth' on an anchor that already has
        // handles — change the type label, leave handles intact.
        next = { ...a, type };
      }
      sub.anchors[idx] = next;
      updatePathEditOverlay();
    },
    [updatePathEditOverlay],
  );

  /** Return the currently-selected anchor's type (or null when no
   *  anchor is selected / no session active). Used by EditPathPanel
   *  to display the type radio's current value. */
  const getPathEditSelectedAnchorType = useCallback((): VectorAnchor['type'] | null => {
    const overlay = pathEditOverlayRef.current;
    if (!overlay || overlay.selectedAnchorIndex === null) return null;
    const a = overlay.workingPath.subPaths[0]?.anchors[overlay.selectedAnchorIndex];
    return a?.type ?? null;
  }, []);

  /**
   * Read the active shape's properties as a `ShapeSettings` snapshot. Used to
   * mirror canvas selection state into the Shape panel so changing the panel
   * sliders edits the *currently selected* shape, not whatever was last
   * touched. Returns null if the active object isn't a shape (or absent).
   */
  const getActiveShapeSettings = useCallback((): ShapeSettings | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const active = canvas.getActiveObject();
    if (!active) return null;

    let type: ShapeType;
    let cornerRadius = 0;
    if (active instanceof fabric.Rect) {
      type = 'rect';
      cornerRadius = (active as fabric.Rect).rx ?? 0;
    } else if (active instanceof fabric.Circle) {
      type = 'circle';
    } else if (active instanceof fabric.Triangle) {
      type = 'triangle';
    } else if (active instanceof fabric.Line) {
      type = 'line';
    } else {
      return null;
    }

    const fill = readShapeFill(active.fill);
    const stroke = typeof active.stroke === 'string' ? active.stroke : '#000000';
    return {
      type,
      fill,
      stroke,
      strokeWidth: active.strokeWidth ?? 0,
      cornerRadius,
    };
  }, []);

  /** Delete pixels within marquee selection, or remove selected objects */
  const deleteSelected = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const m = marqueeRef.current;

    // If there's a finalized marquee selection, clip-clear pixels inside it
    // on the *active layer's* paint surface only, and remove any non-paint
    // overlay objects on the active layer whose bounding box overlaps. This
    // matches Photoshop's behavior — marquee delete operates on the active
    // layer, not every layer.
    if (m.obj && !m.isDrawing) {
      const isWand = m.shape === 'wand' && !!m.wandMaskCanvas;
      const wandMask = m.wandMaskCanvas;
      const activeLayerId = activeLayerIdRef.current;

      const clipClearSurface = (surface: {
        canvas: HTMLCanvasElement;
        fabricImg: fabric.FabricImage;
      }) => {
        const ctx = surface.canvas.getContext('2d');
        if (!ctx) return;
        // Marquee/wand geometry is in scene coords. Pass (surfaceLeft,
        // surfaceTop) to `applyMarqueeClipToContext` so it produces a
        // surface-local clip path, and shift the wand mask by the same
        // amount. The earlier `ctx.translate(-surfaceLeft, -surfaceTop)
        // + clearRect(0, 0, W, H)` pattern was broken for any non-zero
        // surface offset — the clearRect rect ended up outside the
        // surface's pixel bounds, so most of the marquee delete fell
        // through. See the matching note in `cutSelection`.
        const surfaceLeft = surface.fabricImg.left ?? 0;
        const surfaceTop = surface.fabricImg.top ?? 0;
        if (isWand && wandMask) {
          ctx.save();
          ctx.globalCompositeOperation = 'destination-out';
          ctx.drawImage(wandMask, -surfaceLeft, -surfaceTop);
          ctx.restore();
          return;
        }
        ctx.save();
        const clipped = applyMarqueeClipToContext(ctx, m, surfaceLeft, surfaceTop);
        if (clipped) ctx.clearRect(0, 0, surface.canvas.width, surface.canvas.height);
        ctx.restore();
      };

      // Find vector overlays (shape/text/pasted-image) on the active layer
      // whose bbox intersects the marquee. These will be rasterized onto the
      // layer's paint surface so the subsequent clip-clear cuts a hole
      // through them — matching Photoshop's "marquee delete cuts through
      // everything on the active layer" behavior.
      const marqueeBounds = (() => {
        if (!m.obj) return null;
        let b = m.obj.getBoundingRect();
        for (const extra of m.extraObjs) {
          const eb = extra.getBoundingRect();
          const left = Math.min(b.left, eb.left);
          const top = Math.min(b.top, eb.top);
          const right = Math.max(b.left + b.width, eb.left + eb.width);
          const bottom = Math.max(b.top + b.height, eb.top + eb.height);
          b = { left, top, width: right - left, height: bottom - top };
        }
        return b;
      })();
      const paintFabricImgs = new Set<fabric.Object>();
      for (const s of paintSurfacesRef.current.values()) paintFabricImgs.add(s.fabricImg);
      const overlaysToRasterize: fabric.Object[] = [];
      if (activeLayerId && marqueeBounds && marqueeBounds.width > 0 && marqueeBounds.height > 0) {
        const mLeft = marqueeBounds.left;
        const mTop = marqueeBounds.top;
        const mRight = mLeft + marqueeBounds.width;
        const mBottom = mTop + marqueeBounds.height;
        // Iterate canvas objects in z-order (back-to-front) so rasterization
        // preserves stacking order on the paint surface.
        for (const o of canvas.getObjects()) {
          if (paintFabricImgs.has(o)) continue;
          if (isMarqueeObject(o)) continue;
          if ((o as unknown as { _isCropRect?: boolean })._isCropRect) continue;
          if ((o as unknown as { _isQuickMaskOverlay?: boolean })._isQuickMaskOverlay) continue;
          if (o === m.obj || m.extraObjs.includes(o)) continue;
          if (getObjectLayerId(o) !== activeLayerId) continue;
          const ob = o.getBoundingRect();
          const intersects =
            ob.left < mRight &&
            ob.left + ob.width > mLeft &&
            ob.top < mBottom &&
            ob.top + ob.height > mTop;
          if (intersects) overlaysToRasterize.push(o);
        }
      }

      // Empty-region guard: if there are no vector overlays to rasterize AND
      // the marquee region on the active layer's surface is fully transparent,
      // the clip-clear would touch zero pixels. Surface a toast (so the user
      // doesn't think Delete silently failed) and bail before any state
      // mutation — leaving the marquee selection in place so the user can
      // adjust and try again.
      if (overlaysToRasterize.length === 0 && activeLayerId && marqueeBounds) {
        const activeSurface = paintSurfacesRef.current.get(activeLayerId);
        if (activeSurface) {
          const surfaceLeft = activeSurface.fabricImg.left ?? 0;
          const surfaceTop = activeSurface.fabricImg.top ?? 0;
          const sx = Math.max(0, Math.round(marqueeBounds.left - surfaceLeft));
          const sy = Math.max(0, Math.round(marqueeBounds.top - surfaceTop));
          const ex = Math.min(
            activeSurface.canvas.width,
            Math.round(marqueeBounds.left + marqueeBounds.width - surfaceLeft),
          );
          const ey = Math.min(
            activeSurface.canvas.height,
            Math.round(marqueeBounds.top + marqueeBounds.height - surfaceTop),
          );
          let hasPixels = false;
          if (ex > sx && ey > sy) {
            const probeCtx = activeSurface.canvas.getContext('2d');
            if (probeCtx) {
              const probe = probeCtx.getImageData(sx, sy, ex - sx, ey - sy).data;
              for (let i = 3; i < probe.length; i += 4) {
                if (probe[i] !== 0) {
                  hasPixels = true;
                  break;
                }
              }
            }
          }
          if (!hasPixels) {
            onMarqueeActionNoPixelsRef.current?.('delete');
            return;
          }
        }
      }

      // Rasterize intersecting overlays onto the active layer's paint surface,
      // then remove their vector representations from the main canvas. The
      // subsequent clip-clear will erase the marquee region from the surface,
      // including these freshly-rasterized pixels.
      if (activeLayerId && overlaysToRasterize.length > 0) {
        const surface = ensurePaintSurfaceRef.current?.(activeLayerId) ?? null;
        if (surface) {
          const ctx = surface.canvas.getContext('2d');
          if (ctx) {
            const w = surface.canvas.width;
            const h = surface.canvas.height;
            const tempEl = document.createElement('canvas');
            tempEl.width = w;
            tempEl.height = h;
            const tempCanvas = new fabric.StaticCanvas(tempEl, {
              width: w,
              height: h,
              enableRetinaScaling: false,
              renderOnAddRemove: false,
              backgroundColor: 'transparent',
            });
            for (const o of overlaysToRasterize) {
              canvas.remove(o);
              tempCanvas.add(o);
            }
            tempCanvas.renderAll();
            const offsetX = -(surface.fabricImg.left ?? 0);
            const offsetY = -(surface.fabricImg.top ?? 0);
            ctx.save();
            ctx.translate(offsetX, offsetY);
            ctx.drawImage(tempEl, 0, 0);
            ctx.restore();
            tempCanvas.dispose();
            surface.fabricImg.setElement(surface.canvas);
            surface.fabricImg.dirty = true;
            surface.fabricImg.setCoords();
          }
        }
      }

      // Apply clip-clear to the active layer's paint surface only — now
      // containing both pre-existing strokes and any just-rasterized overlay
      // pixels. Use ensure (not .get) so a freshly-added empty layer
      // vivifies a surface and clip-clear runs against it; otherwise this
      // would silently no-op the way cut/copy used to.
      if (activeLayerId) {
        const activeSurface = ensurePaintSurfaceRef.current?.(activeLayerId) ?? null;
        if (activeSurface) {
          clipClearSurface(activeSurface);
          // Force Fabric to re-bind via `setElement` — `dirty = true` alone
          // doesn't propagate canvas mutations reliably.
          activeSurface.fabricImg.setElement(activeSurface.canvas);
          activeSurface.fabricImg.dirty = true;
          activeSurface.fabricImg.setCoords();
        }
      }

      // If the active layer is the Background and bg has filters, re-run them
      // so the filter pipeline picks up the cleared pixels. `setElement`
      // resets `_element` to the (un-filtered) canvas; `applyFilters` rebuilds
      // `_filteredEl` from that.
      const bg = getBackgroundImage();
      if (bg && activeLayerId === getObjectLayerId(bg) && bg.filters && bg.filters.length > 0) {
        bg.applyFilters();
      }

      canvas.discardActiveObject();
      canvas.remove(m.obj);
      for (const o of m.extraObjs) canvas.remove(o);
      m.obj = null;
      m.extraObjs = [];
      m.points = [];
      m.wandMaskCanvas = null;
      setHasMarqueeSelection(false);
      canvas.renderAll();
      onObjectModified?.();
      return;
    }

    // Otherwise, delete selected Fabric objects
    const active = canvas.getActiveObjects();
    if (active.length === 0) return;
    const bg = getBackgroundImage();
    active.forEach((obj) => {
      if (obj === bg) return;
      canvas.remove(obj);
    });
    canvas.discardActiveObject();
    canvas.renderAll();
    onObjectModified?.();
  }, [onObjectModified, getBackgroundImage]);

  /** Set canvas zoom level, keeping content centered */
  const setZoom = useCallback(
    (zoom: number) => {
      const canvas = canvasRef.current;
      const wrapper = wrapperRef.current;
      if (!canvas || !wrapper) return;

      const vpW = wrapper.clientWidth;
      const vpH = wrapper.clientHeight;
      const imgW = imageDimsRef.current.width;
      const imgH = imageDimsRef.current.height;

      const panX = (vpW - imgW * zoom) / 2;
      const panY = (vpH - imgH * zoom) / 2;
      canvas.setViewportTransform([zoom, 0, 0, zoom, panX, panY]);
      if (marqueeRef.current.obj) {
        applyMarqueeStroke(canvas, marqueeRef.current.obj);
        for (const o of marqueeRef.current.extraObjs) applyMarqueeStroke(canvas, o);
      }
      applySliceStrokes(canvas, sliceStateRef.current.objects);
      canvas.renderAll();
      updateViewportInfo();
    },
    [wrapperRef, updateViewportInfo],
  );

  /** Fit canvas to container (re-center and scale to fit) */
  const fitToContainer = useCallback(() => {
    return zoomToFit();
  }, [zoomToFit]);

  /** Hard zoom limits — mirror the wheel-zoom clamp so keyboard /
   *  StatusBar paths can't exceed what wheel can reach. 0.1 = 10%,
   *  10 = 1000%. */
  const ZOOM_MIN = 0.1;
  const ZOOM_MAX = 10;

  /** Step-zoom anchored to the viewport center. Used by Ctrl+= /
   *  Ctrl+- shortcuts and StatusBar +/- buttons. Distinct from the
   *  setZoom(z) path (which re-centers the image): zoomBy preserves
   *  the user's current pan so navigating with the keyboard during
   *  a detail edit doesn't jump them back to the canvas center.
   *  Wheel zoom stays cursor-anchored via the existing mouse:wheel
   *  handler — both anchoring styles are useful. */
  const zoomBy = useCallback(
    (factor: number) => {
      const canvas = canvasRef.current;
      const wrapper = wrapperRef.current;
      if (!canvas || !wrapper) return;
      const vt = canvas.viewportTransform;
      if (!vt) return;
      const currentZoom = vt[0];
      const newZoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, currentZoom * factor));
      if (newZoom === currentZoom) return;
      // Anchor to wrapper center in canvas-element coords (same
      // coordinate space `zoomToPoint` expects — see the wheel-zoom
      // handler's note about double-transforming).
      const cx = wrapper.clientWidth / 2;
      const cy = wrapper.clientHeight / 2;
      canvas.zoomToPoint(new fabric.Point(cx, cy), newZoom);
      if (marqueeRef.current.obj) {
        applyMarqueeStroke(canvas, marqueeRef.current.obj);
        for (const o of marqueeRef.current.extraObjs) applyMarqueeStroke(canvas, o);
      }
      canvas.renderAll();
      onZoomChangedRef.current?.(newZoom);
      updateViewportInfo();
    },
    [wrapperRef, updateViewportInfo],
  );

  /** Zoom in one step (default 1.25× — finer than wheel zoom's 1.1×
   *  so keyboard repeats don't overshoot too fast). */
  const zoomIn = useCallback(
    (factor = 1.25) => {
      zoomBy(factor);
    },
    [zoomBy],
  );

  /** Zoom out one step. Inverse of `zoomIn`'s default. */
  const zoomOut = useCallback(
    (factor = 1 / 1.25) => {
      zoomBy(factor);
    },
    [zoomBy],
  );

  /** Photoshop's Ctrl+1 — snap to 1:1 (100%) zoom, recenter on the
   *  canvas. Distinct from `fitToContainer` which scales to fit. */
  const zoomToActual = useCallback(() => {
    setZoom(1);
  }, [setZoom]);

  /** Close any in-flight pointer interaction on the canvas — used by
   *  `setSpaceHeld(true)` to prevent stuck brush-stroke state when
   *  the user grabs Space mid-stroke. Wraps two Fabric internals so
   *  the dependency is centralized in one named function:
   *    - `canvas._isCurrentlyDrawing`: a boolean Fabric checks in its
   *      `__onMouseMove` / `__onMouseUp` to decide whether to extend
   *      a brush stroke. If left `true` while `isDrawingMode` is
   *      flipped off, releasing Space later resumes the stroke on
   *      the next `mouse:move` (no click required).
   *    - `canvas.freeDrawingBrush.onMouseUp`: commits the partial
   *      brush path as a real `fabric.Path` object so the user
   *      doesn't lose their in-flight stroke.
   *  Tested against Fabric v7.x; both fields are documented in
   *  fabric/src/canvas/Canvas.ts in upstream but not in the public
   *  TypeScript types. A wrapping try/catch fails open: if a future
   *  Fabric upgrade removes these, we log a dev-mode warning and
   *  fall through (brush would still resume on Space release — the
   *  original bug — but the editor wouldn't crash).
   *
   *  Also broadcasts `mouse:up` on Fabric's event bus so per-tool
   *  stroke handlers (retouch clone/heal/smudge per-stroke refs)
   *  can clean up. Every registered handler in this codebase gates
   *  on its own ref, so the broadcast is a no-op when no stroke is
   *  active. */
  function commitInFlightCanvasInteraction(canvas: fabric.Canvas): void {
    try {
      const internal = canvas as unknown as {
        _isCurrentlyDrawing?: boolean;
        freeDrawingBrush?: { onMouseUp?: (opts: { e: Event }) => unknown };
        fire?: (name: string, opts: { e: Event; isClick: boolean }) => void;
      };
      const syntheticEvent = new MouseEvent('mouseup') as unknown as Event;
      if (internal._isCurrentlyDrawing && internal.freeDrawingBrush?.onMouseUp) {
        internal.freeDrawingBrush.onMouseUp({ e: syntheticEvent });
        internal._isCurrentlyDrawing = false;
      }
      internal.fire?.('mouse:up', { e: syntheticEvent, isClick: false });
    } catch (err) {
      if (import.meta.env.DEV) {
        console.warn(
          '[image-editor] commitInFlightCanvasInteraction failed — Fabric internals may have changed in a recent upgrade. Brush-stroke continuation guard is disabled.',
          err,
        );
      }
    }
  }

  /** Sibling of `commitInFlightCanvasInteraction` that DISCARDS the
   *  in-flight pointer interaction instead of committing it. Used when a
   *  two-finger pan/pinch begins mid-stroke: Fabric tracks a single
   *  pointer and routes the first finger to the active tool, so by the
   *  time the second finger lands the tool has already begun a stroke or
   *  grabbed a layer. We want that nascent interaction gone, not
   *  materialized as a stray mark.
   *
   *  Unlike the commit path this deliberately does NOT call
   *  `freeDrawingBrush.onMouseUp` (which would bake the partial path into
   *  a real `fabric.Path`) nor fire `mouse:up` (which would let per-tool
   *  handlers finalize). Instead it:
   *    - clears `_isCurrentlyDrawing` and wipes the live brush preview off
   *      `contextTop` so the abandoned free-draw stroke leaves nothing;
   *    - nulls `_currentTransform` so the first finger's grab stops
   *      dragging/scaling/rotating the active layer.
   *  Per-tool pixel-stroke refs (clone/heal/smudge/blur/…) are cleared
   *  separately by the caller via the effect-scoped `onAbandonStroke`.
   *  Same Fabric-internals caveat and fail-open behavior as the commit
   *  helper. */
  function abandonInFlightCanvasInteraction(canvas: fabric.Canvas): void {
    try {
      const internal = canvas as unknown as {
        _isCurrentlyDrawing?: boolean;
        _currentTransform?: unknown;
        contextTop?: CanvasRenderingContext2D | null;
        freeDrawingBrush?: { _reset?: () => void };
        clearContext?: (ctx: CanvasRenderingContext2D) => void;
      };
      if (internal._isCurrentlyDrawing) {
        internal._isCurrentlyDrawing = false;
        internal.freeDrawingBrush?._reset?.();
        if (internal.contextTop && internal.clearContext) {
          internal.clearContext(internal.contextTop);
        }
      }
      internal._currentTransform = null;
      canvas.requestRenderAll();
    } catch (err) {
      if (import.meta.env.DEV) {
        console.warn(
          '[image-editor] abandonInFlightCanvasInteraction failed — Fabric internals may have changed in a recent upgrade. Two-finger-pan stroke-abandon guard is disabled.',
          err,
        );
      }
    }
  }

  /** Subscribe to snap-match events fired by the `object:moving`
   *  handler. The Guides overlay uses this to briefly highlight
   *  the matched guide for visual feedback. Returns an
   *  unsubscribe; lifecycle managed by the caller (typical:
   *  inside a `useEffect`). */
  const subscribeToSnapMatch = useCallback((cb: (m: SnapMatchEvent) => void): (() => void) => {
    snapMatchListenersRef.current.add(cb);
    return () => {
      snapMatchListenersRef.current.delete(cb);
    };
  }, []);

  /** Subscribe to smart-snap match events (a dragged layer
   *  aligning to another layer's edge/center). The SmartGuides
   *  overlay reads `source` + `matchX`/`matchY` + `targets` to
   *  render magenta indicator lines + distance labels for
   *  `SNAP_FLASH_MS`. Returns an unsubscribe. */
  const subscribeToSmartSnapMatch = useCallback(
    (cb: (m: SmartSnapMatchEvent) => void): (() => void) => {
      smartSnapMatchListenersRef.current.add(cb);
      return () => {
        smartSnapMatchListenersRef.current.delete(cb);
      };
    },
    [],
  );

  /** Mirror `state.viewSettings.smartGuidesEnabled` into the
   *  smart-snap config ref. Fires from EditorPage on every
   *  toggle change. Doesn't touch the per-drag `targets`
   *  snapshot — that's cleared separately at mouse:up. */
  const setSmartSnapEnabled = useCallback((enabled: boolean) => {
    smartSnapConfigRef.current.enabled = enabled;
  }, []);

  /** Mirror window-level Ctrl-held state into the smart-snap
   *  config ref. EditorPage's global keyboard tracker calls this
   *  on Ctrl keydown/keyup. While true, smart-snap is short-
   *  circuited mid-drag so the user can drop a layer without
   *  alignment fighting their intent. Existing guide / grid snap
   *  continues to fire — Ctrl only disables the layer-to-layer
   *  pass. */
  const setSmartSnapCtrlDisabled = useCallback((disabled: boolean) => {
    smartSnapConfigRef.current.ctrlDisabled = disabled;
  }, []);

  /** Look up the scene-space bounding boxes of the Fabric
   *  objects matching the supplied layer ids. Skipped overlay-
   *  type objects (marquee / crop / clone reticle) are silently
   *  dropped. Used by the Align & Distribute panel to seed the
   *  pure align/distribute math from `lib/layer-align`. */
  const getLayerBBoxes = useCallback((layerIds: string[]): LayerBBox[] => {
    const canvas = canvasRef.current;
    if (!canvas) return [];
    const out: LayerBBox[] = [];
    const wanted = new Set(layerIds);
    for (const obj of canvas.getObjects()) {
      const tagged = obj as unknown as {
        layerId?: string;
        _isMarquee?: boolean;
        _isCropRect?: boolean;
        _isCloneReticle?: boolean;
        _isQuickMaskOverlay?: boolean;
        _isTransformOutline?: boolean;
        _isPaintOverlay?: boolean;
      };
      const id = tagged.layerId;
      if (!id || !wanted.has(id)) continue;
      if (
        tagged._isMarquee ||
        tagged._isCropRect ||
        tagged._isCloneReticle ||
        tagged._isQuickMaskOverlay ||
        tagged._isTransformOutline ||
        tagged._isPaintOverlay
      ) {
        continue;
      }
      obj.setCoords();
      const br = obj.getBoundingRect();
      out.push({ id, left: br.left, top: br.top, width: br.width, height: br.height });
    }
    return out;
  }, []);

  /** Apply a batch of per-layer translation deltas (from
   *  `computeAlignDeltas` / `computeDistributeDeltas`). Each
   *  delta moves the matching Fabric object's origin by `dx`/`dy`.
   *  Fires a single `onObjectModified` callback at the end (so
   *  history captures the alignment as one undo step rather than
   *  one per layer) and runs `canvas.requestRenderAll()` once. */
  const applyLayerOffsets = useCallback(
    (deltas: ReadonlyArray<{ id: string; dx: number; dy: number }>): void => {
      const canvas = canvasRef.current;
      if (!canvas || deltas.length === 0) return;
      let anyMoved = false;
      const byId = new Map(deltas.map((d) => [d.id, d]));
      for (const obj of canvas.getObjects()) {
        const tagged = obj as unknown as { layerId?: string };
        const id = tagged.layerId;
        if (!id) continue;
        const d = byId.get(id);
        if (!d || (d.dx === 0 && d.dy === 0)) continue;
        obj.set({
          left: (obj.left ?? 0) + d.dx,
          top: (obj.top ?? 0) + d.dy,
        });
        obj.setCoords();
        anyMoved = true;
      }
      if (!anyMoved) return;
      canvas.requestRenderAll();
      onObjectModifiedRef.current?.();
    },
    [],
  );

  /** Mirror `state.viewSettings` snap fields into the
   *  `snapConfigRef` consumed by the `object:moving` handler.
   *  Called from an EditorPage effect that re-fires whenever
   *  the relevant view-settings fields change. Reading via a ref
   *  inside the Fabric event handler avoids re-binding the
   *  listener on every viewSettings change. */
  const setSnapConfig = useCallback(
    (config: {
      snapToGuides: boolean;
      snapToGrid: boolean;
      gridSize: number;
      guides: import('../types/image-editor.types').Guide[];
    }) => {
      snapConfigRef.current = {
        ...snapConfigRef.current,
        snapToGuides: config.snapToGuides,
        snapToGrid: config.snapToGrid,
        gridSize: config.gridSize,
        guides: config.guides,
      };
    },
    [],
  );

  /** Space-drag pan — Tier 2 Group D. EditorPage calls
   *  `setSpaceHeld(true)` on keydown (skipping when typing in inputs)
   *  and `setSpaceHeld(false)` on keyup/blur. The setter:
   *    - snapshots the prior tool / isDrawingMode / cursor /
   *      selection state so they can be restored on release
   *    - overrides `activeToolRef.current` to 'space-pan' so the
   *      space-pan mouse:down handler claims drag input and every
   *      tool-gated handler short-circuits via its existing
   *      `activeToolRef.current !== '<tool>'` guard
   *    - flips `isDrawingMode` off so a click-drag won't start a
   *      brush stroke under the pan
   *    - sets cursor to `grab` (and the click handler sets
   *      `grabbing` on mouse:down)
   *  Reentrant-safe: a second `setSpaceHeld(true)` while already
   *  held is a no-op (the snapshot is preserved). */
  const setSpaceHeld = useCallback((held: boolean) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (held) {
      if (preSpacePanRef.current !== null) return;
      // Commit any in-flight brush stroke + notify per-tool stroke
      // refs (retouch / clone / heal / smudge) before flipping the
      // tool override on. See `commitInFlightCanvasInteraction`'s
      // docstring for the failure modes this guards against and the
      // Fabric-internals it relies on.
      commitInFlightCanvasInteraction(canvas);

      preSpacePanRef.current = {
        tool: activeToolRef.current,
        isDrawingMode: canvas.isDrawingMode ?? false,
        defaultCursor: canvas.defaultCursor ?? 'default',
        selection: canvas.selection ?? false,
      };
      activeToolRef.current = 'space-pan';
      canvas.isDrawingMode = false;
      canvas.selection = false;
      canvas.defaultCursor = 'grab';
      canvas.setCursor('grab');
    } else {
      const prev = preSpacePanRef.current;
      if (!prev) return;
      preSpacePanRef.current = null;
      // If `activeToolRef` is no longer 'space-pan', something
      // external has already moved the canvas to a new tool — most
      // commonly the React `state.activeTool` mirror effect firing
      // because the user pressed a tool shortcut (B, M, V, etc.)
      // while Space was held. In that case the new tool's `setTool`
      // call has already configured isDrawingMode / cursor /
      // selection appropriately, and forcing them back to the
      // snapshot would clobber the user's choice. Skip the restore;
      // just clear the pan flag.
      if (activeToolRef.current === 'space-pan') {
        activeToolRef.current = prev.tool;
        canvas.isDrawingMode = prev.isDrawingMode;
        canvas.selection = prev.selection;
        canvas.defaultCursor = prev.defaultCursor;
        canvas.setCursor(prev.defaultCursor);
      }
      // If the user releases Space mid-drag, end the pan cleanly so
      // a stale `isPanning: true` doesn't follow the next click.
      panRef.current.isPanning = false;
    }
  }, []);

  /** Rotate the entire canvas by degrees */
  const rotateCanvas = useCallback(
    (degrees: number) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.getObjects().forEach((obj) => {
        const currentAngle = obj.angle || 0;
        obj.rotate(currentAngle + degrees);
      });
      canvas.renderAll();
      onObjectModified?.();
    },
    [onObjectModified],
  );

  /** Flip canvas objects */
  const flipCanvas = useCallback(
    (direction: 'horizontal' | 'vertical') => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.getObjects().forEach((obj) => {
        if (direction === 'horizontal') {
          obj.set('flipX', !obj.flipX);
        } else {
          obj.set('flipY', !obj.flipY);
        }
      });
      canvas.renderAll();
      onObjectModified?.();
    },
    [onObjectModified],
  );

  /** Resize the canvas without resampling image pixels — the
   *  Photoshop "Image → Canvas Size" semantic. Distinct from a Crop
   *  (which extracts a region into a new bg) and from an Image Size
   *  resample (which stretches pixels):
   *   - Pixels are untouched. Every Fabric object shifts by the
   *     anchor-derived (dx, dy) so the image stays anchored to the
   *     same edge / corner / center of the new canvas.
   *   - Layers extending beyond the new bounds aren't destructively
   *     cropped; Fabric naturally clips them at render / export time.
   *     Undoing the resize brings full pixels back.
   *   - Paint-surface offsets are updated in lock-step with the
   *     Fabric image positions so subsequent strokes target the
   *     correct surface pixel.
   *
   *  Caller is responsible for guarding against active modal modes
   *  (crop / transform / quick mask / path edit) — those overlays
   *  would get shifted alongside scene objects and end up
   *  desynchronized from their targets. Returns the applied
   *  (dx, dy) so the caller can shift downstream state (slices,
   *  selections) by the same amount; returns null on invalid input
   *  or when no document is loaded. */
  const setCanvasSize = useCallback(
    (
      newWidth: number,
      newHeight: number,
      /** Horizontal anchor as a 0..1 factor — 0 = left, 0.5 = center,
       *  1 = right. The new canvas grows / shrinks from the opposite
       *  edge (anchor=left expands to the right). */
      anchorX: number,
      anchorY: number,
    ): { dx: number; dy: number } | null => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      const oldW = imageDimsRef.current.width;
      const oldH = imageDimsRef.current.height;
      if (oldW <= 0 || oldH <= 0) return null;
      const w = Math.round(newWidth);
      const h = Math.round(newHeight);
      if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null;
      // Clamp anchor to [0, 1] so a misuse can't fling objects off
      // the canvas by negative or >1 factors.
      const ax = Math.max(0, Math.min(1, anchorX));
      const ay = Math.max(0, Math.min(1, anchorY));
      const dx = Math.round((w - oldW) * ax);
      const dy = Math.round((h - oldH) * ay);

      if (dx !== 0 || dy !== 0) {
        canvas.getObjects().forEach((obj) => {
          // Skip ALL canvas chrome — outline AND fill rect both sit
          // at canvas bounds and get re-positioned by the
          // ensureCanvasBoundsOverlay call below, not by the
          // anchor-derived shift. Without the fill exclusion the
          // rect lands at (dx, dy) instead of (0, 0) and the
          // anchor-side of the canvas ends up uncovered.
          if (isCanvasChrome(obj)) return;
          obj.set({ left: (obj.left ?? 0) + dx, top: (obj.top ?? 0) + dy });
          obj.setCoords();
        });
        // Paint-surface offsets mirror the FabricImage's left/top —
        // shift them together so writes at scene (sx, sy) still land
        // at the right (sx − offsetX, sy − offsetY) on the offscreen
        // canvas.
        paintSurfacesRef.current.forEach((surface) => {
          surface.offsetX += dx;
          surface.offsetY += dy;
        });
      }

      imageDimsRef.current = { width: w, height: h };
      ensureCanvasBoundsOverlay(canvas, w, h);
      onCanvasDimensions?.(w, h);
      zoomToFit();
      canvas.renderAll();
      onObjectModified?.();
      return { dx, dy };
    },
    [onCanvasDimensions, onObjectModified, zoomToFit],
  );

  /** Set the Canvas Size dialog's "Extension fill" color. Manages a
   *  tagged Fabric.Rect at scene (0, 0, W, H) sent to the back —
   *  scoped to canvas bounds by the rect's own geometry (no clipPath
   *  trick needed). `null` removes the rect. Non-destructive: the
   *  bg layer's pixel buffer is untouched, so shrinking the canvas
   *  hides those pixels visually but doesn't lose them — selecting
   *  and dragging the bg reveals the rest.
   *
   *  Distinct from a Fabric `canvas.backgroundColor` which paints
   *  across the entire DOM canvas (the wrapper viewport), bleeding
   *  into the pasteboard. The chrome rect approach is scoped by
   *  construction. */
  const setCanvasFillColor = useCallback(
    (color: string | null) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvasFillColorRef.current = color;
      const w = imageDimsRef.current.width;
      const h = imageDimsRef.current.height;
      ensureCanvasFillOverlay(canvas, w, h, color);
      restackCanvasChrome(canvas);
      canvas.renderAll();
      onObjectModified?.();
    },
    [onObjectModified],
  );

  /** Build the toDataURL options that capture exactly the image
   *  region at native resolution (imgW × imgH), regardless of the
   *  current zoom / pan / canvas-DOM size.
   *
   *  Fabric's `toCanvasElement` formula (StaticCanvas.mjs):
   *    scaledWidth   = width * multiplier
   *    newZoom       = zoom * multiplier
   *    newVp.tx      = (vp.tx - left) * multiplier
   *  We want output dimensions = imgW × imgH, newZoom = 1, newVp = 0
   *  so the rendered region is exactly scene (0, 0)-(imgW, imgH) at
   *  1:1 scale. Solving:
   *    multiplier = 1 / zoom
   *    width      = imgW * zoom    (so scaledWidth = imgW)
   *    height     = imgH * zoom
   *    left       = vp.tx          (so newVp.tx = 0)
   *    top        = vp.ty          (so newVp.ty = 0)
   *
   *  Without these options, `toDataURL` captures the Fabric canvas
   *  DOM at its native size (the wrapper size under canvas-as-
   *  viewport architecture) — a 3024×4032 image saved at "same
   *  dimensions" would come out as ~833×790 with the image scaled
   *  and centered inside transparent padding.
   *
   *  Custom output dimensions are handled separately via
   *  `resizeDataUrlToTarget` because Fabric's `toCanvasElement` only
   *  supports a uniform scalar zoom — non-aspect-preserving scaling
   *  would distort if forced through it. */
  const buildNativeExportOptions = useCallback(() => {
    const canvas = canvasRef.current;
    const imgW = imageDimsRef.current.width;
    const imgH = imageDimsRef.current.height;
    if (!canvas || !imgW || !imgH) return { multiplier: 1 } as const;
    const vp = canvas.viewportTransform;
    const zoom = (vp && vp[0]) || 1;
    const tx = (vp && vp[4]) || 0;
    const ty = (vp && vp[5]) || 0;
    return {
      multiplier: 1 / zoom,
      width: imgW * zoom,
      height: imgH * zoom,
      left: tx,
      top: ty,
    };
  }, []);

  /** Resize a native-resolution data URL to arbitrary target
   *  dimensions via an offscreen canvas. Supports both aspect-
   *  preserving (when target matches source aspect) and non-uniform
   *  (e.g. user types 1500×3100 for a 3024×4032 source) scaling.
   *  Smoothing on for downscales — keeps detail without aliasing. */
  const resizeDataUrlToTarget = useCallback(
    (
      dataUrl: string,
      format: CanvasNativeExportFormat,
      quality: number,
      targetW: number,
      targetH: number,
    ): Promise<Blob | null> =>
      new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          const out = document.createElement('canvas');
          out.width = Math.max(1, Math.round(targetW));
          out.height = Math.max(1, Math.round(targetH));
          const ctx = out.getContext('2d');
          if (!ctx) {
            resolve(null);
            return;
          }
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, out.width, out.height);
          out.toBlob((blob) => resolve(blob), `image/${format}`, quality);
        };
        img.onerror = () => resolve(null);
        img.src = dataUrl;
      }),
    [],
  );

  /** Export canvas to data URL at the source image's native pixel
   *  resolution. Custom-dimension exports go through `exportToBlob`
   *  (async) since arbitrary resizing requires an offscreen canvas
   *  pass that doesn't fit the synchronous `toDataURL` contract.
   *
   *  Fabric's `ImageFormat` union is `'jpeg' | 'png' | 'webp'` — but
   *  internally Fabric just builds `image/${format}` and calls the
   *  native canvas `toDataURL`, which accepts any mime the browser
   *  supports. AVIF works at runtime when the browser supports it
   *  (feature-detected at the UI layer via `SUPPORTS_AVIF`), so we
   *  cast to Fabric's narrower type at the boundary. */
  const exportToDataURL = useCallback(
    (format: CanvasNativeExportFormat = 'png', quality = 1) => {
      const canvas = canvasRef.current;
      if (!canvas) return '';
      return runWithoutCanvasBoundsOverlay(canvas, () =>
        canvas.toDataURL({
          format: format as 'png',
          quality,
          ...buildNativeExportOptions(),
        }),
      );
    },
    [buildNativeExportOptions],
  );

  /** Export canvas to blob. Output dims default to the source image's
   *  native pixel resolution; pass `target` to resize. Aspect ratios
   *  are NOT preserved when both target axes are specified — the
   *  output exactly matches the requested dimensions. To preserve
   *  aspect, omit one axis (the other will be filled in).
   *
   *  AVIF runtime-supported via the same boundary cast as
   *  `exportToDataURL` (see that function's note). */
  const exportToBlob = useCallback(
    async (
      format: CanvasNativeExportFormat = 'png',
      quality = 1,
      target?: { width?: number; height?: number },
    ): Promise<Blob | null> => {
      const canvas = canvasRef.current;
      if (!canvas) return null;

      const imgW = imageDimsRef.current.width;
      const imgH = imageDimsRef.current.height;
      const wantW = target?.width;
      const wantH = target?.height;

      // TIFF — browsers don't natively encode TIFF, so we render
      // the canvas to a 2D context at the desired output dimensions
      // and hand the raw RGBA off to utif. The output is lossless
      // regardless of the `quality` arg (TIFF tag 259 = 1, no
      // compression); the arg is accepted for signature symmetry.
      // Tier 2 Group F sub-piece #7.
      //
      // Memory note: peak usage is ~ `finalW * finalH * 4` bytes for
      // the offscreen canvas + the same again inside utif's RGBA
      // working buffer + a temporary `<img>` decode of the PNG
      // snapshot. For a 4K canvas (3840 × 2160) that's ~33 MB ×
      // 2–3 transient allocations. Acceptable on desktop / iPad;
      // 8K canvases would land in ~135 MB territory and need a
      // streaming pass if we ever raise the upper limit.
      if (format === 'tiff') {
        const finalW =
          wantW ?? (wantH && imgW && imgH ? Math.round(imgW * ((wantH as number) / imgH)) : imgW);
        const finalH =
          wantH ?? (wantW && imgW && imgH ? Math.round(imgH * ((wantW as number) / imgW)) : imgH);
        if (!finalW || !finalH) return null;
        // Render the Fabric scene to an offscreen canvas at the
        // desired output dims via `canvas.toCanvasElement` so the
        // background-handling + clip-region options stay consistent
        // with the toDataURL path. Fall back to the toDataURL ->
        // image -> drawImage round-trip when toCanvasElement isn't
        // available (older Fabric builds; defensive).
        const offscreen = document.createElement('canvas');
        offscreen.width = finalW;
        offscreen.height = finalH;
        const offCtx = offscreen.getContext('2d');
        if (!offCtx) return null;
        const sourceDataUrl = runWithoutCanvasBoundsOverlay(canvas, () =>
          canvas.toDataURL({
            format: 'png',
            quality: 1,
            ...buildNativeExportOptions(),
          }),
        );
        const img = await new Promise<HTMLImageElement | null>((resolve) => {
          const el = new Image();
          el.onload = () => resolve(el);
          el.onerror = () => resolve(null);
          el.src = sourceDataUrl;
        });
        if (!img) return null;
        offCtx.imageSmoothingEnabled = true;
        offCtx.imageSmoothingQuality = 'high';
        offCtx.drawImage(img, 0, 0, finalW, finalH);
        const { encodeCanvasAsTiffBlob } = await import('../lib/tiff-export');
        return encodeCanvasAsTiffBlob(offscreen);
      }

      const nativeDataUrl = runWithoutCanvasBoundsOverlay(canvas, () =>
        canvas.toDataURL({
          format: format as 'png',
          quality,
          ...buildNativeExportOptions(),
        }),
      );

      // No target → return native blob.
      if ((!wantW && !wantH) || !imgW || !imgH) {
        try {
          const res = await fetch(nativeDataUrl);
          return await res.blob();
        } catch {
          return null;
        }
      }

      // Fill in a missing axis from the other (aspect-preserving),
      // round to integer pixel dims, and skip the resize round-trip
      // when target == native.
      const finalW = wantW ?? Math.round(imgW * ((wantH as number) / imgH));
      const finalH = wantH ?? Math.round(imgH * ((wantW as number) / imgW));
      if (Math.round(finalW) === imgW && Math.round(finalH) === imgH) {
        try {
          const res = await fetch(nativeDataUrl);
          return await res.blob();
        } catch {
          return null;
        }
      }

      return resizeDataUrlToTarget(nativeDataUrl, format, quality, finalW, finalH);
    },
    [buildNativeExportOptions, resizeDataUrlToTarget],
  );

  /** Snapshot the scene WITHOUT editor-only overlays (marquee
   *  outline + slice rectangles + transform handles / outlines).
   *  Used by Region exports (Selection / Visible Layers) — the
   *  marquee is the *selection* the user wants to crop to, so it
   *  must NOT bake into the exported pixels. Slice overlays and
   *  transform handles are similarly screen-only chrome.
   *
   *  Implementation: walk the canvas object list once, flip every
   *  overlay's `visible` to false (remembering which were on),
   *  call `toDataURL`, then restore. `requestRenderAll` is skipped
   *  — `toDataURL` triggers a synchronous render on its own, and
   *  the restore happens before any rAF tick so the user never
   *  sees the overlays blink. */
  const exportToDataURLExcludingOverlays = useCallback(
    (format: CanvasNativeExportFormat = 'png', quality = 1): string => {
      const canvas = canvasRef.current;
      if (!canvas) return '';
      // Gather every overlay object we need to hide. Marquee +
      // extras come from marqueeRef; slice overlays are tagged via
      // `_isSliceOverlay`; transform handles + outlines live on
      // `transformOverlayRef.current`. Quick-mask overlay is a
      // single FabricImage stored on quickMaskOverlayRef.
      const m = marqueeRef.current;
      const transformOverlay = transformOverlayRef.current;
      const overlays: fabric.Object[] = [];
      if (m.obj) overlays.push(m.obj);
      for (const o of m.extraObjs) overlays.push(o);
      for (const o of canvas.getObjects()) {
        if (isSliceOverlay(o)) overlays.push(o);
        // The canvas-bounds outline is chrome — its visible stroke
        // would bake a 1px gray border into the exported pixels.
        if (isCanvasBoundsOverlay(o)) overlays.push(o);
      }
      if (transformOverlay) {
        for (const h of transformOverlay.handles) overlays.push(h);
        for (const ol of transformOverlay.outlines) overlays.push(ol);
      }
      // Snapshot the prior visibility flags so we can restore each
      // object exactly as it was (some overlays might already be
      // hidden by other code paths; we only flip ones that were
      // visible).
      const wasVisible: boolean[] = overlays.map((o) => o.visible !== false);
      try {
        for (let i = 0; i < overlays.length; i++) {
          if (wasVisible[i]) overlays[i].visible = false;
        }
        return canvas.toDataURL({
          format: format as 'png',
          quality,
          ...buildNativeExportOptions(),
        });
      } finally {
        for (let i = 0; i < overlays.length; i++) {
          if (wasVisible[i]) overlays[i].visible = true;
        }
      }
    },
    [buildNativeExportOptions],
  );

  /** Serialize canvas to JSON, optionally including custom property names. */
  const toJSON = useCallback((propertiesToInclude?: string[]) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    // Fabric's `toJSON()` is typed without args, but `toObject` accepts the
    // properties array and produces the same JSON-serializable structure.
    return propertiesToInclude && propertiesToInclude.length > 0
      ? canvas.toObject(propertiesToInclude)
      : canvas.toJSON();
  }, []);

  /**
   * Load canvas from JSON. Sets background image refs and dimensions but does
   * NOT modify the viewport transform — the caller decides whether to fit
   * (`fitToContainer`) or restore a previously saved transform
   * (`setViewportTransform`).
   */
  const loadFromJSON = useCallback(
    async (json: object): Promise<void> => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      // Tier 2 Group B (2026-05-14) — pre-load Google Fonts for any
      // IText objects in the saved JSON BEFORE Fabric synthesizes
      // them. Fabric IText's constructor calls `initDimensions()`
      // which measures glyph widths against whatever font is
      // currently resolvable on the page. If we let it run with
      // only the fallback font available, the IText's `width` is
      // computed too small (Archivo Black is much wider than
      // sans-serif fallback), and the post-construction width is
      // permanent for the visible bbox. Even calling
      // `initDimensions()` later from the `whenFontReady` callback
      // can't fully recover — Fabric's `_textLines` / `__charBounds`
      // caching and the cache canvas don't always recompute.
      //
      // Awaiting the fonts here means the IText is born already
      // measured against Archivo Black, so the saved width/height
      // round-trip correctly without any post-load patch-up.
      await preloadFontsFromJSON(json);
      await canvas.loadFromJSON(json);

      // The just-restored canvas is a different document than whatever
      // was previously open (tab switch / session restore). Drop any
      // pointer state captured against the prior doc's scene space so
      // paste-at-click doesn't drop the next image at a stale (and
      // often off-canvas) coord.
      lastClickSceneRef.current = null;

      const objects = canvas.getObjects();
      const bgImg = objects.find(
        (o) =>
          o instanceof fabric.FabricImage &&
          (isBackgroundImage(o) || !(o as fabric.FabricImage & { selectable: boolean }).selectable),
      ) as fabric.FabricImage | undefined;

      if (bgImg) {
        // Tag for future lookups, normalize to non-selectable until the user
        // explicitly clicks the Background layer.
        tagAsBackground(bgImg);
        bgImg.set({ selectable: false, evented: false });
        backgroundImageRef.current = bgImg;
        const imgW = bgImg.width || 1;
        const imgH = bgImg.height || 1;
        imageDimsRef.current = { width: imgW, height: imgH };
        ensureCanvasBoundsOverlay(canvas, imgW, imgH);
        canvas.sendObjectToBack(bgImg);
        onCanvasDimensions?.(imgW, imgH);
      }

      // Convert every layer-tagged FabricImage's underlying element to a
      // canvas so it's paintable, and register the surfaces in the map.
      // (Restored sessions have HTMLImageElements after deserialization;
      // we need HTMLCanvasElements to draw onto.) Disable object caching so
      // live writes to the canvas reflect without manual invalidation.
      paintSurfacesRef.current.clear();
      // Fill rect is excludeFromExport so it isn't in the restored JSON;
      // ref tracks the visual rect so reset it to match. A future session-
      // restore enhancement could re-apply a persisted fill color here.
      canvasFillColorRef.current = null;
      for (const obj of canvas.getObjects()) {
        if (!(obj instanceof fabric.FabricImage)) continue;
        const layerId = getObjectLayerId(obj);
        if (!layerId) continue;
        const w = obj.width ?? 0;
        const h = obj.height ?? 0;
        const surfaceCanvas = ensureCanvasElement(
          obj.getElement() as HTMLImageElement | HTMLCanvasElement,
          w,
          h,
        );
        if (obj.getElement() !== surfaceCanvas) obj.setElement(surfaceCanvas);
        obj.set({ objectCaching: false });
        installPaintSurfaceSerializer(obj);
        paintSurfacesRef.current.set(layerId, {
          canvas: surfaceCanvas,
          fabricImg: obj,
          offsetX: obj.left ?? 0,
          offsetY: obj.top ?? 0,
        });
      }

      // Separate pass for clipPath (mask) rehydration. The previous
      // pass only walks FabricImage layers, but masks attach to
      // ANY layer-tagged object (text / shape / paint surface / image)
      // — see addLayerMask. Without rehydrating clipPaths on
      // non-image layers too, mask painting on a text or shape
      // layer silently breaks after refresh because
      // `bakeStrokeOntoMask` calls `getContext('2d')` on the mask
      // element and an `HTMLImageElement` doesn't support that.
      //
      // ALSO restores the raw/effective split after a serialize→
      // load round-trip. `installMaskClipPathSerializer` makes
      // `toObject` save RAW as the element; after load, the canvas
      // element IS raw. We allocate a fresh EFFECTIVE canvas + copy
      // raw → effective identity (so the visible state matches the
      // saved state immediately), attach raw, install the serializer
      // on the new instance. The mask-sync effect in EditorPage
      // fires after this and re-materializes with the live mask
      // config from `state.document`, which catches feather/invert/
      // density correctly (effective is just an identity copy at
      // this point — config gets applied in materializeMaskClipPath).
      for (const obj of canvas.getObjects()) {
        // Tier 2 Group B — reinstall the inside-stroke `_renderText`
        // override on every IText that came through loadFromJSON.
        // The override is a per-instance method assignment; Fabric
        // rebuilds each object from its base class during load, so
        // the patch is gone after rehydration. Reinstall now so
        // the saved `_fiasInsideStroke` flag (preserved by Fabric's
        // serializer alongside other custom props) actually drives
        // rendering. Idempotent — `ensureInsideStrokeRenderOverride`
        // bails when the tag is already present.
        //
        // Same place we (re-)inject Google Fonts `@font-face` for
        // each IText. The picker only injects the link when the
        // user opens / hovers a font; an IText loaded from a saved
        // session has its `fontFamily` preserved (e.g. "Archivo
        // Black"), but if the font's CSS hasn't been fetched on
        // this page load, canvas2d falls back to a system font for
        // rendering. The user sees the text rendered in a fallback
        // font and assumes the fontFamily change "didn't persist"
        // even though the property round-tripped correctly.
        // `ensureFontLoaded` is a no-op for system fonts and for
        // already-injected families.
        if (obj instanceof fabric.IText) {
          ensureInsideStrokeRenderOverride(obj);
          if (typeof obj.fontFamily === 'string') {
            const family = obj.fontFamily;
            const size = obj.fontSize ?? 16;
            // Inject the @font-face link immediately so the bytes
            // start fetching. The Promise resolves once they're
            // ready — at which point Fabric IText's measured-glyph
            // cache (computed at construction time using whatever
            // font was available, likely the fallback) is stale.
            // Force a re-init + re-render so the visible text
            // reflects the real glyph metrics + family.
            ensureFontLoaded(family);
            void whenFontReady(family, size).then(() => {
              // Stale-reference guard. `loadFromJSON` can fire
              // again before the font resolves (tab switch, undo
              // / redo replay), in which case `obj` is detached
              // from `canvas` and mutating it does nothing visible
              // but wastes the requestRenderAll. The new objects
              // get their own `whenFontReady` from the fresh
              // applyLayerOrder walk.
              const objCanvas = (obj as fabric.IText & { canvas?: unknown }).canvas;
              if (objCanvas !== canvas) return;
              const itext = obj as fabric.IText & {
                initDimensions?: () => void;
              };
              if (typeof itext.initDimensions === 'function') itext.initDimensions();
              obj.dirty = true;
              canvas.requestRenderAll();
            });
          }
        }

        const cp = (obj as fabric.FabricObject).clipPath;
        if (!(cp instanceof fabric.FabricImage)) continue;
        const cpW = cp.width ?? obj.width ?? 0;
        const cpH = cp.height ?? obj.height ?? 0;
        const maskCanvas = ensureCanvasElement(
          cp.getElement() as HTMLImageElement | HTMLCanvasElement,
          cpW,
          cpH,
        );
        if (cp.getElement() !== maskCanvas) cp.setElement(maskCanvas);

        // Raw/effective split rehydration. Skip when raw is already
        // attached — applyLayerOrder runs on every layer ordering
        // change, not just session-load; idempotency keeps the post-
        // load state stable.
        if (!readRawMaskCanvas(cp)) {
          const raw = cp.getElement() as HTMLCanvasElement;
          const effective = document.createElement('canvas');
          effective.width = raw.width;
          effective.height = raw.height;
          const ectx = effective.getContext('2d', { willReadFrequently: true });
          if (ectx) ectx.drawImage(raw, 0, 0);
          attachRawMaskCanvas(cp, raw);
          cp.setElement(effective);
          installMaskClipPathSerializer(cp);
        }
      }

      canvas.renderAll();
    },
    [onCanvasDimensions],
  );

  /** Apply a saved [a, b, c, d, e, f] viewport transform. Returns the resulting zoom. */
  const setViewportTransform = useCallback(
    (vt: number[]): number => {
      const canvas = canvasRef.current;
      if (!canvas || vt.length !== 6) return 1;
      canvas.setViewportTransform([vt[0], vt[1], vt[2], vt[3], vt[4], vt[5]]);
      if (marqueeRef.current.obj) {
        applyMarqueeStroke(canvas, marqueeRef.current.obj);
        for (const o of marqueeRef.current.extraObjs) applyMarqueeStroke(canvas, o);
      }
      canvas.renderAll();
      updateViewportInfo();
      return vt[0];
    },
    [updateViewportInfo],
  );

  /** Snapshot the current viewport transform for persistence. */
  const getViewportTransform = useCallback((): number[] | null => {
    const canvas = canvasRef.current;
    const vt = canvas?.viewportTransform;
    return vt ? [...vt] : null;
  }, []);

  /** Clear the active marquee selection (any shape) if present */
  const clearMarquee = useCallback(() => {
    const canvas = canvasRef.current;
    const m = marqueeRef.current;
    if (!canvas) return;
    if (m.obj || m.extraObjs.length > 0) {
      if (m.obj) canvas.remove(m.obj);
      for (const o of m.extraObjs) canvas.remove(o);
      m.obj = null;
      m.extraObjs = [];
      m.points = [];
      m.wandMaskCanvas = null;
      m.isDrawing = false;
      canvas.renderAll();
      setHasMarqueeSelection(false);
    }
  }, []);

  /** Expand or contract the active marquee by `deltaPx` pixels on
   *  every side (negative for shrink). Three implementation paths
   *  depending on the marquee shape:
   *
   *    - **Rect / ellipse**: closed-form math on the Fabric object's
   *      own dimensions. Stays the original shape after the op.
   *    - **Lasso / polygon**: rasterize the current vertices into a
   *      binary mask, dilate or erode by `|deltaPx|` via a Euclidean
   *      kernel, re-trace contours back to polygons. The marquee
   *      transitions to a `wand`-style multi-contour selection
   *      (single contour for simple shapes, multiple if shrinking
   *      a thin shape splits it). Loses precise vertex positions
   *      but preserves the selected area faithfully.
   *    - **Wand**: same morphology applied to the cached
   *      `wandMaskCanvas`, then re-traced.
   *
   *  `deltaPx` is in scene-space pixels (so it scales naturally with
   *  image resolution, not viewport zoom). The result is clamped to
   *  the image bounds. */
  const growMarqueeBy = useCallback(
    (deltaPx: number) => {
      const canvas = canvasRef.current;
      const m = marqueeRef.current;
      if (!canvas || !m.obj || deltaPx === 0) return;
      const imgW = imageDimsRef.current.width;
      const imgH = imageDimsRef.current.height;

      if (m.shape === 'rect') {
        const r = m.obj as fabric.Rect;
        const left = (r.left ?? 0) - deltaPx;
        const top = (r.top ?? 0) - deltaPx;
        const width = (r.width ?? 0) + 2 * deltaPx;
        const height = (r.height ?? 0) + 2 * deltaPx;
        if (width < 1 || height < 1) return;
        const clampedLeft = Math.max(0, Math.min(imgW - 1, left));
        const clampedTop = Math.max(0, Math.min(imgH - 1, top));
        const clampedW = Math.max(1, Math.min(imgW - clampedLeft, width));
        const clampedH = Math.max(1, Math.min(imgH - clampedTop, height));
        r.set({ left: clampedLeft, top: clampedTop, width: clampedW, height: clampedH });
        r.setCoords();
        canvas.renderAll();
        return;
      }

      if (m.shape === 'ellipse') {
        const e = m.obj as fabric.Ellipse;
        const left = (e.left ?? 0) - deltaPx;
        const top = (e.top ?? 0) - deltaPx;
        const rx = (e.rx ?? 0) + deltaPx;
        const ry = (e.ry ?? 0) + deltaPx;
        if (rx < 1 || ry < 1) return;
        e.set({
          left: Math.max(0, left),
          top: Math.max(0, top),
          rx: Math.max(1, Math.min(imgW / 2, rx)),
          ry: Math.max(1, Math.min(imgH / 2, ry)),
        });
        e.setCoords();
        canvas.renderAll();
        return;
      }

      // Mask-based path for lasso / polygon / wand.
      if (imgW <= 0 || imgH <= 0) return;

      let mask: Uint8Array | null = null;
      if (m.shape === 'lasso' || m.shape === 'polygon') {
        // Source vertices for fill. `m.points` holds the polygon
        // points authoritatively (Fabric Polygon's `points` may be
        // re-centered after mounts; keep the canonical list).
        if (m.points.length < 3) return;
        mask = polygonPointsToMask(m.points, imgW, imgH);
      } else if (m.shape === 'wand' && m.wandMaskCanvas) {
        const result = canvasToBinaryMask(m.wandMaskCanvas);
        mask = result.mask;
      }
      if (!mask) return;

      const radius = Math.abs(Math.round(deltaPx));
      if (radius === 0) return;
      const next =
        deltaPx > 0 ? dilateMask(mask, imgW, imgH, radius) : erodeMask(mask, imgW, imgH, radius);

      // If erosion wiped the whole selection, treat it as a deselect
      // — anything else (a few stray pixels, etc.) would be confusing.
      let any = 0;
      for (let i = 0; i < next.length; i++)
        if (next[i]) {
          any = 1;
          break;
        }
      if (!any) {
        clearMarquee();
        return;
      }

      // Replace the marquee with wand-style contours from the new
      // mask. Drop existing fabric polys + cached wand canvas first.
      if (m.obj) canvas.remove(m.obj);
      for (const o of m.extraObjs) canvas.remove(o);
      m.obj = null;
      m.extraObjs = [];
      m.points = [];

      m.wandMaskCanvas = binaryMaskToCanvas(next, imgW, imgH);
      m.shape = 'wand';

      const contours = traceContours(next, imgW, imgH);
      const path = buildMarqueePath(contours);
      if (!path) {
        clearMarquee();
        return;
      }
      tagAsMarquee(path);
      applyMarqueeColor(path, marqueeColorRef.current);
      applyMarqueeStroke(canvas, path);
      canvas.add(path);
      m.obj = path;
      m.extraObjs = [];
      m.isDrawing = false;
      canvas.renderAll();
      refreshMarqueeSelectionState();
    },
    [clearMarquee, refreshMarqueeSelectionState],
  );

  /** True when the active marquee supports grow / shrink. Now true
   *  for every shape with a finalized selection — rect / ellipse use
   *  closed-form math; lasso / polygon / wand go through the mask
   *  morphology path (and transition to a wand-style selection). */
  const canGrowMarquee = useCallback((): boolean => {
    const m = marqueeRef.current;
    if (!m.obj || m.isDrawing) return false;
    if (m.shape === 'rect' || m.shape === 'ellipse') return true;
    if (m.shape === 'wand' && m.wandMaskCanvas) return true;
    if ((m.shape === 'lasso' || m.shape === 'polygon') && m.points.length >= 3) return true;
    return false;
  }, []);

  /** Install a pre-built binary mask as a wand-style marquee. The
   *  mask canvas is expected to be alpha-mask format (alpha > 127
   *  means "selected"). Sized to the source image dimensions —
   *  callers responsible for upstream resampling if their mask
   *  came from a differently-sized provider response. Used by the
   *  AI Select Subject route, which returns a 1-channel mask sized
   *  to the source image.
   *
   *  Behaves like the magic-wand path: traces contours into Fabric
   *  Polygons, marks `m.shape` as `'wand'`, refreshes the cached
   *  mask canvas the eraser path reads. Subsequent grow / shrink
   *  operations work the same as they do for any wand selection. */
  const setSelectionFromMask = useCallback(
    (maskCanvas: HTMLCanvasElement): boolean => {
      const canvas = canvasRef.current;
      if (!canvas) return false;
      const imgW = imageDimsRef.current.width;
      const imgH = imageDimsRef.current.height;
      if (imgW <= 0 || imgH <= 0) return false;

      // If the supplied mask doesn't match the image dims, draw it
      // scaled into a properly-sized buffer so traceContours emits
      // points in scene space rather than provider-output space.
      let normalized = maskCanvas;
      if (maskCanvas.width !== imgW || maskCanvas.height !== imgH) {
        const tmp = document.createElement('canvas');
        tmp.width = imgW;
        tmp.height = imgH;
        const tCtx = tmp.getContext('2d', { willReadFrequently: true });
        if (!tCtx) return false;
        tCtx.imageSmoothingEnabled = false;
        tCtx.drawImage(maskCanvas, 0, 0, imgW, imgH);
        normalized = tmp;
      }

      const { mask } = canvasToBinaryMask(normalized);
      // Reject empty masks — the provider returned something with
      // no foreground at all (rare, but possible on solid-color
      // images or when segmentation can't find a subject).
      let any = 0;
      for (let i = 0; i < mask.length; i++)
        if (mask[i]) {
          any = 1;
          break;
        }
      if (!any) return false;

      // Drop any existing marquee — including wand contours and
      // their cached mask — before installing the new selection.
      const m = marqueeRef.current;
      if (m.obj) canvas.remove(m.obj);
      for (const o of m.extraObjs) canvas.remove(o);
      m.obj = null;
      m.extraObjs = [];
      m.points = [];
      m.wandMaskCanvas = null;
      m.isDrawing = false;

      m.shape = 'wand';
      m.wandMaskCanvas = binaryMaskToCanvas(mask, imgW, imgH);

      const contours = traceContours(mask, imgW, imgH);
      const path = buildMarqueePath(contours);
      if (!path) return false;
      tagAsMarquee(path);
      applyMarqueeColor(path, marqueeColorRef.current);
      applyMarqueeStroke(canvas, path);
      canvas.add(path);
      m.obj = path;
      m.extraObjs = [];

      canvas.renderAll();
      refreshMarqueeSelectionState();
      return true;
    },
    [refreshMarqueeSelectionState],
  );

  /** Internal: rasterize the marqueeRef.current state into a
   *  Uint8Array sized to the image. Delegates per-shape rules to the
   *  pure `selectionToBinaryMask` helper. Returns an all-zero mask
   *  when there's no selection (callers can branch on
   *  `isMaskNonEmpty`). */
  const marqueeToBinaryMask = useCallback(
    (m: typeof marqueeRef.current, imgW: number, imgH: number): Uint8Array => {
      if (!m.obj) return new Uint8Array(imgW * imgH);
      if (m.shape === 'rect') {
        const r = m.obj as fabric.Rect;
        return selectionToBinaryMask(
          {
            shape: 'rect',
            left: r.left ?? 0,
            top: r.top ?? 0,
            width: r.width ?? 0,
            height: r.height ?? 0,
            scaleX: r.scaleX ?? 1,
            scaleY: r.scaleY ?? 1,
          },
          imgW,
          imgH,
        );
      }
      if (m.shape === 'ellipse') {
        const e = m.obj as fabric.Ellipse;
        return selectionToBinaryMask(
          {
            shape: 'ellipse',
            left: e.left ?? 0,
            top: e.top ?? 0,
            rx: e.rx ?? 0,
            ry: e.ry ?? 0,
            scaleX: e.scaleX ?? 1,
            scaleY: e.scaleY ?? 1,
          },
          imgW,
          imgH,
        );
      }
      if (m.shape === 'lasso' || m.shape === 'polygon') {
        return selectionToBinaryMask({ shape: m.shape, points: m.points }, imgW, imgH);
      }
      if (m.shape === 'wand' && m.wandMaskCanvas) {
        return selectionToBinaryMask({ shape: 'wand', maskCanvas: m.wandMaskCanvas }, imgW, imgH);
      }
      return new Uint8Array(imgW * imgH);
    },
    [],
  );

  /** Return the current marquee rasterized as an alpha-mask canvas
   *  sized to the image. White (alpha=255) where selected,
   *  transparent elsewhere. Used by Quick Mask to seed the edit
   *  canvas, by Color Range to constrain sampling, and by Refine
   *  Edge to populate the initial working mask. Returns `null` when
   *  there is no active selection or the image dims are unknown. */
  const getSelectionAsMaskCanvas = useCallback((): HTMLCanvasElement | null => {
    const m = marqueeRef.current;
    if (!m.obj || m.isDrawing) return null;
    const imgW = imageDimsRef.current.width;
    const imgH = imageDimsRef.current.height;
    if (imgW <= 0 || imgH <= 0) return null;
    const mask = marqueeToBinaryMask(m, imgW, imgH);
    if (!isMaskNonEmpty(mask)) return null;
    return binaryMaskToCanvas(mask, imgW, imgH);
  }, [marqueeToBinaryMask]);

  // === Quick Mask (Tier 1 Group D) ===========================
  // The QM canvas is the source of truth for the working alpha mask;
  // the overlay FabricImage is a presentation-only red tint built
  // from it. Brush handlers paint into the QM canvas via the
  // helpers below; `enterQuickMask` / `exitQuickMask` are the only
  // surface React touches.

  /** Stamp a single brush dab into the in-flight stroke's TEMP
   *  canvas at scene-space (x, y). Soft radial-gradient falloff at
   *  FULL alpha (the slider opacity is applied later when temp is
   *  composited onto QM — see `flushQuickMaskStrokeToQM`). Within a
   *  stroke, the temp's alpha saturates at 1 along the brush path
   *  regardless of how many dabs overlap, which is what enforces
   *  the "one stroke = one application at slider opacity" semantic.
   *
   *  Mode is irrelevant for the temp stamp itself — both add and
   *  subtract write a coverage mask. Mode determines how the temp
   *  composites onto QM during flush. */
  const paintQuickMaskDab = useCallback((x: number, y: number) => {
    const stroke = quickMaskStrokeRef.current;
    if (!stroke) return;
    const ctx = stroke.temp.getContext('2d');
    if (!ctx) return;
    const { radius, hardness } = stroke;
    // Inner-radius = (1 - hardness inverted) — at hardness 0 the
    // gradient is fully soft (center at 0%); at hardness 1 the
    // gradient is hard-edged (inner = outer). We use 0.5 by default
    // in QM, which gives a tight Photoshop-like brush.
    const innerR = radius * hardness;
    const grad = ctx.createRadialGradient(x, y, innerR, x, y, radius);
    grad.addColorStop(0, 'rgba(255, 255, 255, 1)');
    grad.addColorStop(1, 'rgba(255, 255, 255, 0)');
    ctx.globalCompositeOperation = 'source-over';
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fillStyle = grad;
    ctx.fill();
    // Expand the dirty bbox so the next flush re-composites only
    // this region (and anything else dabbed since the last flush).
    if (x - radius < stroke.dirtyMinX) stroke.dirtyMinX = x - radius;
    if (y - radius < stroke.dirtyMinY) stroke.dirtyMinY = y - radius;
    if (x + radius > stroke.dirtyMaxX) stroke.dirtyMaxX = x + radius;
    if (y + radius > stroke.dirtyMaxY) stroke.dirtyMaxY = y + radius;
  }, []);
  paintQuickMaskDabRef.current = paintQuickMaskDab;

  /** Re-derive QM = baseline ± temp@opacity over the dirty bbox.
   *  Called from `refreshQuickMaskOverlay` before the tint composite
   *  so the overlay reflects the latest in-flight stroke state.
   *  Bbox-bounded so per-tick cost is proportional to brush
   *  movement, not canvas size. */
  const flushQuickMaskStrokeToQM = useCallback(() => {
    const qm = quickMaskCanvasRef.current;
    const stroke = quickMaskStrokeRef.current;
    if (!qm || !stroke) return;
    if (stroke.dirtyMaxX <= stroke.dirtyMinX) return;
    const ctx = qm.getContext('2d');
    if (!ctx) return;
    // Clip the dirty bbox to canvas bounds. Integer-aligned so
    // drawImage's source/dest rects match pixel-for-pixel.
    const w = qm.width;
    const h = qm.height;
    const rx = Math.max(0, Math.floor(stroke.dirtyMinX));
    const ry = Math.max(0, Math.floor(stroke.dirtyMinY));
    const rmx = Math.min(w, Math.ceil(stroke.dirtyMaxX));
    const rmy = Math.min(h, Math.ceil(stroke.dirtyMaxY));
    const rw = rmx - rx;
    const rh = rmy - ry;
    if (rw <= 0 || rh <= 0) {
      stroke.dirtyMinX = Infinity;
      stroke.dirtyMinY = Infinity;
      stroke.dirtyMaxX = -Infinity;
      stroke.dirtyMaxY = -Infinity;
      return;
    }
    // Reset QM region to baseline. `copy` mode is destination-clearing,
    // so a single drawImage replaces the region's pixels exactly
    // (no leftover alpha from prior in-stroke composites).
    ctx.save();
    ctx.beginPath();
    ctx.rect(rx, ry, rw, rh);
    ctx.clip();
    ctx.globalCompositeOperation = 'copy';
    ctx.drawImage(stroke.baseline, rx, ry, rw, rh, rx, ry, rw, rh);
    // Apply the stroke contribution at slider opacity.
    ctx.globalAlpha = stroke.opacity;
    ctx.globalCompositeOperation = stroke.mode === 'add' ? 'source-over' : 'destination-out';
    ctx.drawImage(stroke.temp, rx, ry, rw, rh, rx, ry, rw, rh);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
    ctx.restore();
    stroke.dirtyMinX = Infinity;
    stroke.dirtyMinY = Infinity;
    stroke.dirtyMaxX = -Infinity;
    stroke.dirtyMaxY = -Infinity;
  }, []);

  /** Rebuild the red-tinted overlay image from the current QM
   *  canvas. The overlay is a single FabricImage on top of every
   *  layer — its element is a tint canvas whose RGB is solid red
   *  and whose alpha is `0.5 * (1 - qmMaskAlpha/255)` so SELECTED
   *  pixels show through and UN-SELECTED pixels get a 50% red
   *  matte. (Photoshop's Quick Mask is configurable; we ship with
   *  the default "Masked Areas" color.)
   *
   *  Implementation: native canvas composites, not a per-pixel JS
   *  loop. Fires on every QM mouse:move at 60+ Hz, so the cost of a
   *  full-canvas `getImageData` + JS iteration + `putImageData`
   *  (the previous implementation) made painting unusable on large
   *  canvases — drawing visibly lagged a full second behind the
   *  cursor on 4K documents because each move spent ~100–200ms in
   *  the loop on the main thread.
   *
   *  The composite pipeline:
   *    1. Clear the tint canvas
   *    2. Fill with `rgba(255, 0, 0, 0.5)` — solid 50% red, full canvas
   *    3. `destination-out` + drawImage(qm) — subtracts QM's alpha
   *       channel from the tint's alpha, leaving `tint_alpha =
   *       0.5 × (1 − qm_alpha)`, exactly the matte formula above
   *
   *  Only the QM canvas's alpha channel matters here; destination-out
   *  ignores the source's RGB and keeps the destination's RGB
   *  unchanged (so tint stays red). Browser executes these in C and
   *  may GPU-accelerate them — orders of magnitude faster than the
   *  JS loop. */
  const refreshQuickMaskOverlay = useCallback(() => {
    const overlay = quickMaskOverlayRef.current;
    const qm = quickMaskCanvasRef.current;
    const canvas = canvasRef.current;
    if (!overlay || !qm || !canvas) return;
    // If a stroke is in flight, push any pending temp→QM composite
    // through first so the tint reflects the latest dabs. No-op when
    // not mid-stroke or when nothing has been dabbed since last flush.
    flushQuickMaskStrokeToQM();
    const w = qm.width;
    const h = qm.height;
    const tint = (overlay.getElement() as HTMLCanvasElement) ?? document.createElement('canvas');
    if (tint.width !== w || tint.height !== h) {
      tint.width = w;
      tint.height = h;
    }
    const tctx = tint.getContext('2d');
    if (!tctx) return;
    tctx.globalCompositeOperation = 'source-over';
    tctx.clearRect(0, 0, w, h);
    tctx.fillStyle = 'rgba(255, 0, 0, 0.5)';
    tctx.fillRect(0, 0, w, h);
    tctx.globalCompositeOperation = 'destination-out';
    tctx.drawImage(qm, 0, 0);
    // Restore default so any later draw on this context behaves as
    // expected. The QM canvas is reused across strokes; leaking a
    // non-default compositeOperation would silently break the next
    // refresh.
    tctx.globalCompositeOperation = 'source-over';
    overlay.setElement(tint);
    overlay.dirty = true;
    canvas.requestRenderAll();
  }, []);
  refreshQuickMaskOverlayRef.current = refreshQuickMaskOverlay;

  /** Mirror the panel-set QM brush mode into the canvas-side ref
   *  read by the brush handler. */
  const setQuickMaskMode = useCallback((mode: 'add' | 'subtract') => {
    quickMaskModeRef.current = mode;
  }, []);

  /** Enter Quick Mask mode. Seeds the QM working canvas from the
   *  current selection (or starts blank if no selection), installs
   *  the visual overlay, and clears the marquee from the Fabric
   *  canvas (the mask is now the source of truth — the marquee
   *  would otherwise show stale boundary lines that don't reflect
   *  the user's brush edits).
   *
   *  The reducer flips `activeTool` to `'quickmask'` BEFORE calling
   *  this — see EditorPage's `quickMaskActive` effect. The two are
   *  expected to stay in sync.
   *
   *  Returns true on success, false when the canvas isn't ready
   *  (no image loaded). */
  const enterQuickMask = useCallback((): boolean => {
    const canvas = canvasRef.current;
    if (!canvas) return false;
    const imgW = imageDimsRef.current.width;
    const imgH = imageDimsRef.current.height;
    if (imgW <= 0 || imgH <= 0) return false;

    // Idempotency: sweep any pre-existing QM overlays so a re-entry
    // (effect re-fires while quickMaskActive stays true, or a
    // missed exit) doesn't stack red mattes. Each call into this
    // fn produces a fresh overlay regardless of prior state.
    for (const o of canvas.getObjects()) {
      if ((o as unknown as { _isQuickMaskOverlay?: boolean })._isQuickMaskOverlay) {
        canvas.remove(o);
      }
    }
    quickMaskOverlayRef.current = null;

    // Build the working alpha mask. Seed from the current selection
    // if there is one; otherwise preserve the prior in-flight QM
    // canvas (so a stray re-entry doesn't wipe the user's painted
    // strokes), else start fully empty.
    const seed = getSelectionAsMaskCanvas();
    let qm = quickMaskCanvasRef.current;
    if (!qm || qm.width !== imgW || qm.height !== imgH || seed) {
      qm = document.createElement('canvas');
      qm.width = imgW;
      qm.height = imgH;
      const qctx = qm.getContext('2d', { willReadFrequently: true });
      if (!qctx) return false;
      if (seed) qctx.drawImage(seed, 0, 0);
      quickMaskCanvasRef.current = qm;
    }

    // Drop the existing marquee from the Fabric canvas. The mask
    // we just captured carries the same data, and a lingering
    // marching-ants outline would be misleading during QM editing.
    const m = marqueeRef.current;
    if (m.obj) canvas.remove(m.obj);
    for (const o of m.extraObjs) canvas.remove(o);
    m.obj = null;
    m.extraObjs = [];
    m.points = [];
    m.wandMaskCanvas = null;
    m.isDrawing = false;

    // Build the overlay's backing canvas and FabricImage. The
    // backing is sized to the image; the FabricImage anchors at
    // (0, 0) so the overlay aligns 1:1 with paint-layer pixels.
    const tint = document.createElement('canvas');
    tint.width = imgW;
    tint.height = imgH;
    const overlay = new fabric.FabricImage(tint, {
      left: 0,
      top: 0,
      originX: 'left',
      originY: 'top',
      selectable: false,
      evented: false,
      excludeFromExport: true,
      hoverCursor: 'default',
      objectCaching: false,
    });
    // Tag for filter-out paths (similar to `_isMarquee`,
    // `_isCloneReticle`). Layer-order helpers skip tagged overlays.
    (overlay as unknown as { _isQuickMaskOverlay: boolean })._isQuickMaskOverlay = true;
    canvas.add(overlay);
    canvas.bringObjectToFront(overlay);
    quickMaskOverlayRef.current = overlay;

    refreshQuickMaskOverlay();
    refreshMarqueeSelectionState();
    return true;
  }, [getSelectionAsMaskCanvas, refreshMarqueeSelectionState, refreshQuickMaskOverlay]);

  /** Exit Quick Mask mode. Converts the working alpha mask back
   *  into a wand-style selection via `setSelectionFromMask`,
   *  removes the overlay, clears the refs. Returns whether a
   *  selection was installed — an all-empty mask (the user
   *  cleared everything while in QM) returns false and leaves the
   *  canvas with no selection. */
  /** Peek the current Quick Mask alpha canvas without exiting QM.
   *  Returns a CLONE so the caller can hand it to downstream
   *  consumers (layer-mask attach, etc.) without worrying about
   *  the QM session mutating it from under them. Null when QM
   *  isn't active. */
  const getQuickMaskCanvas = useCallback((): HTMLCanvasElement | null => {
    const qm = quickMaskCanvasRef.current;
    if (!qm) return null;
    const clone = document.createElement('canvas');
    clone.width = qm.width;
    clone.height = qm.height;
    const ctx = clone.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(qm, 0, 0);
    return clone;
  }, []);

  /** Clean up Quick Mask state WITHOUT installing the painted mask
   *  as a marquee. Used by the "Apply as Layer Mask" exit path —
   *  the caller has already applied the mask elsewhere and just
   *  wants to dismiss the overlay. Idempotent. */
  const discardQuickMask = useCallback((): void => {
    const canvas = canvasRef.current;
    if (canvas) {
      const stale: fabric.FabricObject[] = [];
      for (const o of canvas.getObjects()) {
        if ((o as unknown as { _isQuickMaskOverlay?: boolean })._isQuickMaskOverlay) {
          stale.push(o);
        }
      }
      for (const o of stale) canvas.remove(o);
      canvas.requestRenderAll();
    }
    quickMaskOverlayRef.current = null;
    quickMaskCanvasRef.current = null;
    quickMaskStrokeRef.current = null;
  }, []);

  /** Attach a layer mask to a layer using a caller-supplied alpha
   *  canvas as the mask source. Sibling to `addLayerMask` (which
   *  fills the mask reveal-all or from the active marquee). Used
   *  by Quick Mask's "Apply as Layer Mask" output path. */
  const applyLayerMaskFromCanvas = useCallback(
    (layerId: string, sourceMaskCanvas: HTMLCanvasElement): LayerMask | null => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      const imgW = imageDimsRef.current.width;
      const imgH = imageDimsRef.current.height;
      if (imgW <= 0 || imgH <= 0) return null;

      // Find target object (or lazily create paint surface for empty
      // layers — same fallback path `addLayerMask` uses).
      let targetObj: fabric.Object | null = null;
      for (const o of canvas.getObjects()) {
        if (getObjectLayerId(o) === layerId) {
          targetObj = o;
          break;
        }
      }
      if (!targetObj) {
        const surface = ensurePaintSurfaceRef.current?.(layerId);
        targetObj = surface?.fabricImg ?? null;
      }
      if (!targetObj) return null;

      // Build the RAW alpha-mask canvas at image dims. Normalize via
      // a scale-blit if the supplied mask doesn't match (defensive —
      // QM hands us same-dim canvases today, but Color Range and
      // future callers may not). See `addLayerMask` for the rationale
      // behind the raw/effective split.
      const rawCanvas = document.createElement('canvas');
      rawCanvas.width = imgW;
      rawCanvas.height = imgH;
      const rCtx = rawCanvas.getContext('2d', { willReadFrequently: true });
      if (!rCtx) return null;
      rCtx.imageSmoothingEnabled = sourceMaskCanvas.width === imgW;
      rCtx.drawImage(sourceMaskCanvas, 0, 0, imgW, imgH);

      const effectiveCanvas = document.createElement('canvas');
      effectiveCanvas.width = imgW;
      effectiveCanvas.height = imgH;

      const initialMaskConfig: LayerMask = {
        id: `lmask_${layerId}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        enabled: true,
        inverted: false,
        linked: true,
        density: 1,
        feather: 0,
        fabricObjectId: '',
      };
      materializeEffectiveMaskInto(
        effectiveCanvas,
        rawCanvas,
        maskMaterializationConfig(initialMaskConfig),
        imgW,
        imgH,
      );

      const maskImg = new fabric.FabricImage(effectiveCanvas, {
        left: 0,
        top: 0,
        originX: 'left',
        originY: 'top',
        selectable: false,
        evented: false,
      });
      const maskFabricId = `fmask_${layerId}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      (maskImg as fabric.FabricImage & { id: string }).id = maskFabricId;
      (maskImg as fabric.FabricObject & { absolutePositioned: boolean }).absolutePositioned = true;
      attachRawMaskCanvas(maskImg, rawCanvas);
      installMaskClipPathSerializer(maskImg);

      targetObj.clipPath = maskImg;
      canvas.requestRenderAll();

      initialMaskConfig.fabricObjectId = maskFabricId;
      maskConfigsRef.current.set(layerId, initialMaskConfig);
      return initialMaskConfig;
    },
    [],
  );

  const exitQuickMask = useCallback((): boolean => {
    const canvas = canvasRef.current;
    const qm = quickMaskCanvasRef.current;
    // Sweep every tagged QM overlay rather than relying on the
    // single tracked ref. Defensive against the re-entry edge case
    // where an old overlay was orphaned by an enter call that
    // overwrote the ref without removing the prior FabricImage.
    if (canvas) {
      const stale: fabric.FabricObject[] = [];
      for (const o of canvas.getObjects()) {
        if ((o as unknown as { _isQuickMaskOverlay?: boolean })._isQuickMaskOverlay) {
          stale.push(o);
        }
      }
      for (const o of stale) canvas.remove(o);
    }
    quickMaskOverlayRef.current = null;
    quickMaskStrokeRef.current = null;
    let installed = false;
    if (qm && canvas) {
      installed = setSelectionFromMask(qm);
    }
    quickMaskCanvasRef.current = null;
    if (canvas) canvas.requestRenderAll();
    return installed;
  }, [setSelectionFromMask]);

  // === Free Transform (Tier 1 Group E Phase 1) ==================
  // Captured pre-transform state so Cancel can revert the layer
  // exactly. For affine modes (Free/Skew) we only need the matrix
  // properties; for non-affine modes (Distort/Perspective/Warp)
  // we also stash the original element so a baked-pixel apply can
  // be reversed.
  const transformInitialRef = useRef<{
    layerId: string;
    scaleX: number;
    scaleY: number;
    left: number;
    top: number;
    angle: number;
    skewX: number;
    skewY: number;
    /** Origin can shift during Fabric's wrapWithFixedAnchor flow —
     *  the action handlers temporarily swap originX/Y to keep the
     *  opposite corner pinned during drag. Capturing + restoring
     *  these eliminates a class of "Cancel doesn't fully revert"
     *  bugs where the bbox was technically correct but the layer's
     *  origin had shifted. */
    originX: number | string;
    originY: number | string;
    flipX: boolean;
    flipY: boolean;
    originalElement: HTMLImageElement | HTMLCanvasElement | null;
    /** Captured lock flags + controls map so exitTransformMode
     *  can restore the layer's pre-transform config exactly.
     *  `applyTransformControls` mutates these on every mode change
     *  (e.g. Skew mode swaps in custom mid-edge controls); without
     *  the snapshot, a subsequent tool pickup would see stale
     *  transform-only state. */
    lockRotation: boolean;
    lockScalingFlip: boolean;
    lockSkewingX: boolean;
    lockSkewingY: boolean;
    /** Pre-transform interaction flags for the TARGET. Capturing
     *  these makes exit honor the layer's actual original state
     *  rather than hard-coding selectable=true / evented=true.
     *  Paint surfaces (rasterized layers, the bg image acting as
     *  the paint target) come in non-selectable / non-evented so
     *  brush/retouch tools intercept clicks; unconditionally
     *  flipping both to true on exit broke that. */
    selectable: boolean;
    evented: boolean;
    controls: Record<string, fabric.Control>;
    /** Pre-lock selectable/evented state for every sibling object
     *  enterTransformMode mutated. Free Transform doesn't change
     *  `activeTool`, so EditorPage's tool-sync effect doesn't re-
     *  fire on exit — without this snapshot, siblings stay locked
     *  (non-selectable, non-evented) past the session, and the
     *  user can't click them on the canvas until they switch
     *  tools. Per-sibling capture is exact: it survives whatever
     *  the current tool's lock convention is (e.g. Marquee locks
     *  every sibling; Move leaves them all evented). */
    siblings: { obj: fabric.FabricObject; selectable: boolean; evented: boolean }[];
  } | null>(null);

  /** Non-affine transform working state (Tier 1 Group E Phase 1b).
   *  Set up by `setupNonAffineOverlay` when the user picks Distort,
   *  Perspective, or Warp from the mode picker. Holds:
   *    - the current dst-corner quad (Distort/Perspective) or 9-point
   *      mesh (Warp), in canvas pixel coords;
   *    - the Fabric overlay objects we render to show those points
   *      (handles + a dashed outline);
   *    - the live-preview source canvas and target canvas — drag
   *      rasterizes the source through the projective/warp math into
   *      the target, swapping the layer's element so the user sees
   *      the deformation under the handles.
   *
   *  The `previewSource` is a downscaled copy (≤ PREVIEW_LONGEST_PX)
   *  so per-drag rasterization stays interactive on 4K layers; the
   *  full-res bake happens once on Apply.
   *
   *  Coordinate system: handles live in the canvas's scene coords —
   *  same space the layer's `left/top` use. The quad/grid points
   *  reference handle positions directly. */
  const transformOverlayRef = useRef<{
    mode: 'distort' | 'perspective' | 'warp';
    /** Saved active-tool ref + drawing-mode flag so teardown can
     *  restore whatever tool the user was on before entering the
     *  non-affine session. During the session, `activeToolRef` is
     *  forced to `'ai'` — a tool whose mouse handlers we never
     *  registered. Every other tool handler gates on
     *  `activeToolRef.current === 'X'` and early-returns when not
     *  matching, so this single ref-override globally suppresses
     *  tool behaviors that would otherwise compete with handle
     *  drags (Marquee drawing a rect, Move panning, brush painting,
     *  etc.). */
    savedActiveTool: EditorTool;
    savedDrawingMode: boolean;
    /** Pre-session `canvas.selection` flag. Forced to `false` for
     *  the duration of the session so Fabric's group-selection
     *  rubber-band (a transient overlay rect drawn from drag-start
     *  to cursor when `canvas.selection === true` and the click
     *  misses all targets) cannot fire. */
    savedSelection: boolean;
    /** Current dst-corner positions for Distort/Perspective.
     *  Order: top-left, top-right, bottom-right, bottom-left. */
    distortQuad: ProjectiveQuad;
    /** Current 9-point grid for Warp. Order row-major: tl-mt-tr,
     *  ml-c-mr, bl-mb-br. */
    warpGrid: WarpGrid;
    /** Fabric handle circles in same order as `distortQuad` (4) or
     *  `warpGrid` (9). */
    handles: fabric.FabricObject[];
    /** Dashed quad/mesh outline rendered behind the handles. */
    outlines: fabric.FabricObject[];
    /** Layer pixel source at enter time, full resolution.
     *  Captured via `target.toCanvasElement()` so any prior affine
     *  matrix the user applied in Free/Skew is baked into the
     *  source pixels — the non-affine session composes on top of
     *  whatever the affine session did. */
    sourceFull: HTMLCanvasElement;
    /** Downscaled preview source — read by each drag tick's
     *  rasterizer. Sized so the longest side ≤ PREVIEW_LONGEST_PX. */
    sourcePreview: HTMLCanvasElement;
    /** Pre-overlay bounding box in scene coords — used only for
     *  the initial handle positions (handles seed at the bbox
     *  corners) and the bake-step source pixel density
     *  calculation. The session itself doesn't constrain the
     *  deformation to this bbox. */
    bbox: { left: number; top: number; width: number; height: number };
  } | null>(null);

  /** Forward-ref to the latest `updateNonAffinePreview` so the
   *  mount-time mouse-event handlers can invoke the current
   *  closure (rather than the one captured at registration time).
   *  Same pattern as `paintQuickMaskDabRef` etc. */
  const updateNonAffinePreviewRef = useRef<(() => void) | null>(null);

  /** Active-handle drag state. Set on mouse:down when the user
   *  clicks within hit-radius of a handle; cleared on mouse:up.
   *  Stores the index in the overlay's handles array so the
   *  mouse:move handler can route updates to the right quad/grid
   *  slot. */
  const transformDragRef = useRef<{ handleIndex: number } | null>(null);

  /** Forward-refs to the overlay setup / teardown helpers so
   *  `applyTransformControls` (declared earlier in this hook)
   *  can call them without hitting the TS temporal-dead-zone for
   *  the corresponding useCallback declarations below. */
  const setupNonAffineOverlayRef = useRef<
    ((mode: 'distort' | 'perspective' | 'warp') => void) | null
  >(null);
  const teardownNonAffineOverlayRef = useRef<(() => void) | null>(null);
  const bakeNonAffineOverlayRef = useRef<(() => void) | null>(null);

  /** Find the layer's primary Fabric object. For paint surfaces
   *  this is the wrapped FabricImage; for image layers it's the
   *  loaded image; for text/shape it's the IText/Rect/etc. */
  const findLayerObject = useCallback((layerId: string): fabric.FabricObject | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    for (const o of canvas.getObjects()) {
      if (getObjectLayerId(o) === layerId) return o;
    }
    return null;
  }, []);

  /** Resolve a layer's current rendered pixels + bounding-box origin
   *  for export paths that need raw raster data (PSD export's
   *  raster-emission branch, future format encoders). Returns `null`
   *  for layers with no pixel content (empty placeholders, vector
   *  layers without a rasterized cache, layers not on the canvas).
   *
   *  Priority of pixel sources:
   *    1. `paintSurfacesRef` entry — the live paint canvas, kept up
   *       to date by every brush / retouch / fill stroke.
   *    2. Fabric object's element — for image layers that were added
   *       but never painted on, the original `HTMLCanvasElement` (or
   *       wrapped from `HTMLImageElement`).
   *
   *  Position comes from `obj.getBoundingRect()` which handles every
   *  Fabric origin / transform combination correctly (the v7 default
   *  is origin-center, but background images use origin-top-left;
   *  the bounding rect normalizes this). Returns top-left of the
   *  layer's visible bbox in document scene coordinates. */
  const getLayerExportPayload = useCallback(
    (layerId: string): { canvas: HTMLCanvasElement; left: number; top: number } | null => {
      // 1. Paint-surface path — has the most-recent live pixels.
      const surface = paintSurfacesRef.current.get(layerId);
      if (surface) {
        // Refresh aCoords before reading the bounding rect — Fabric's
        // drag handler sets left/top WITHOUT calling setCoords(), so
        // the cached rect is one frame stale during in-progress
        // transforms. Same precedent as the snap handler at line ~2114.
        // Calling setCoords() unconditionally is cheap when the cache
        // is already current.
        surface.fabricImg.setCoords();
        const rect = surface.fabricImg.getBoundingRect();
        return { canvas: surface.canvas, left: rect.left, top: rect.top };
      }
      // 2. Fabric-object element fallback.
      const obj = findLayerObject(layerId);
      if (!obj) return null;
      obj.setCoords();
      const rect = obj.getBoundingRect();
      if (obj instanceof fabric.FabricImage) {
        const el = obj.getElement() as HTMLCanvasElement | HTMLImageElement;
        if (el instanceof HTMLCanvasElement) {
          return { canvas: el, left: rect.left, top: rect.top };
        }
        // HTMLImageElement — wrap in a fresh canvas so ag-psd can read
        // its pixels. Dimension priority: Fabric's intrinsic width/height,
        // fall back to the element's naturalWidth/Height.
        const w = (obj.width as number) || el.naturalWidth || el.width || 0;
        const h = (obj.height as number) || el.naturalHeight || el.height || 0;
        // Refuse to wrap a broken element. A 1×1 fallback would silently
        // emit a 1×1 raster layer with no signal that the layer's actual
        // pixels were lost (broken image src, CORS failure, mid-load).
        // Returning null counts the layer as `skipped` in the export
        // summary, which the user sees in the post-save toast.
        if (w < 2 || h < 2) {
          console.warn(
            `[image-editor] getLayerExportPayload: layer ${layerId} has degenerate dimensions (${w}×${h}); skipping export.`,
          );
          return null;
        }
        const wrapped = document.createElement('canvas');
        wrapped.width = w;
        wrapped.height = h;
        const ctx = wrapped.getContext('2d');
        if (!ctx) return null;
        ctx.drawImage(el, 0, 0, w, h);
        return { canvas: wrapped, left: rect.left, top: rect.top };
      }
      // 3. Non-image fabric object (IText, Path, Group, …) — rasterize
      //    via Fabric's per-object `toCanvasElement` so PSD export has
      //    a pixel fallback when the native chunk path declines.
      //    Concrete trigger: text-on-path. `getLayerTextPayload` returns
      //    null for path-bound IText (the native PSD text record can't
      //    carry the curve), so without this fallback the layer is
      //    skipped entirely and the text vanishes on save → re-open.
      //    Vector paths get a native vector chunk via `buildPsdVectorChunks`
      //    in `psd-export.ts` and never reach this branch in normal flow;
      //    this just keeps the contract clean if any future emitter
      //    falls through here.
      // Fabric's `obj.getBoundingRect()` returns the object's bbox in
      // scene coords for path-bound IText too (the path-follow layout
      // pass updates IText's width/height via `initDimensions`). But
      // arched text can extend ABOVE the path's top by ascender height
      // and below by descender — both fall outside the unwarped bbox
      // and get clipped on raster. Pad generously (font size on each
      // side) so the apex of an arc and the descenders both fit.
      // Don't try to combine with `path.getBoundingRect()` — the
      // path's coords are in IText-local space (path is attached to
      // the IText as a child, not laid out on the canvas directly),
      // and mixing them with scene coords yields wrong offsets.
      try {
        const pad = obj instanceof fabric.IText ? (obj.fontSize ?? 16) * 2 : 0;
        const targetLeft = rect.left - pad;
        const targetTop = rect.top - pad;
        const targetWidth = rect.width + pad * 2;
        const targetHeight = rect.height + pad * 2;
        const rasterized = document.createElement('canvas');
        rasterized.width = Math.max(2, Math.ceil(targetWidth));
        rasterized.height = Math.max(2, Math.ceil(targetHeight));
        const rctx = rasterized.getContext('2d');
        if (!rctx) return null;
        // Translate so scene point (targetLeft, targetTop) lands at
        // buffer (0, 0). `obj.render(rctx)` will then apply the
        // object's own transform on top, placing the object at its
        // correct scene position within our buffer.
        rctx.save();
        rctx.translate(-targetLeft, -targetTop);
        (obj as fabric.FabricObject & { render: (ctx: CanvasRenderingContext2D) => void }).render(
          rctx,
        );
        rctx.restore();
        return { canvas: rasterized, left: targetLeft, top: targetTop };
      } catch (err) {
        console.warn(
          `[image-editor] getLayerExportPayload: toCanvasElement failed for layer ${layerId}; skipping export.`,
          err,
        );
        return null;
      }
    },
    [findLayerObject],
  );

  /** Query the Fabric IText bound to `layerId` and produce a
   *  payload for native `<text>` emission on SVG export
   *  (Tier 2 Group F SVG round-trip polish, PR-2 phase E).
   *
   *  Returns null when:
   *   - No IText is bound to the layer (not a text layer, or the
   *     object was removed)
   *   - The IText carries `archValue !== 0` (arch curvature)
   *   - The IText is bound to a path (text-on-path)
   *   - The IText's text content has a literal newline
   *
   *  These constraints mirror the import-side rejection set in
   *  `svg-text-import.ts` so round-trip fidelity is symmetric. */
  const getLayerTextPayload = useCallback(
    (layerId: string): SvgExportTextPayload | null => {
      const obj = findLayerObject(layerId);
      if (!(obj instanceof fabric.IText)) return null;
      // Arch — native `<text>` can't carry the curvature without
      // `<textPath>` machinery (out of v1 scope). Fall back to raster.
      const archValue = (obj as fabric.IText & { archValue?: number }).archValue ?? 0;
      if (archValue !== 0) return null;
      // Path-bound text now goes through native emission and a
      // marker-based rebinding scheme on the PSD side (the path
      // remains its own vector layer; export + import tag both
      // layer names with a shared token). SVG export already had
      // `<textPath>` machinery that consumed `node.textPath`; the
      // previous null-return blocked it from running. Letting path-
      // bound text reach this code unlocks both formats.
      const text = obj.text ?? '';
      if (/[\n\r]/.test(text)) return null;
      obj.setCoords();
      const rect = obj.getBoundingRect();
      const fontSize = obj.fontSize ?? 16;
      const charSpacing = obj.charSpacing ?? 0;
      // Coerce Fabric's permissive prop types into our narrow
      // TextSettings types.
      const fwRaw: unknown = obj.fontWeight;
      const fontWeight: 'normal' | 'bold' =
        fwRaw === 'bold' ||
        fwRaw === 'bolder' ||
        (typeof fwRaw === 'number' && fwRaw >= 600) ||
        (typeof fwRaw === 'string' && parseInt(fwRaw, 10) >= 600)
          ? 'bold'
          : 'normal';
      const fsRaw: unknown = obj.fontStyle;
      const fontStyle: 'normal' | 'italic' = fsRaw === 'italic' ? 'italic' : 'normal';
      const taRaw: unknown = obj.textAlign;
      const textAlign: 'left' | 'center' | 'right' =
        taRaw === 'center' ? 'center' : taRaw === 'right' ? 'right' : 'left';
      const fillRaw: unknown = obj.fill;
      const fill = typeof fillRaw === 'string' ? fillRaw : '#000000';
      const lineHeight = obj.lineHeight ?? 1.16;
      const underline = obj.underline === true;
      // Stroke: present when both stroke (non-null/non-'none')
      // and strokeWidth (> 0). The renderer's
      // INSIDE_STROKE_FLAG / paintFirst extras are runtime-only;
      // emit as a centered stroke matching the original SVG-
      // canonical position.
      const strokeColor: unknown = obj.stroke;
      const strokeWidth = obj.strokeWidth ?? 0;
      const stroke =
        typeof strokeColor === 'string' && strokeColor !== '' && strokeWidth > 0
          ? { color: strokeColor, width: strokeWidth, position: 'center' as const }
          : undefined;
      // Drop shadow: Fabric stores it as a `Shadow` instance or a
      // string. Convert back to the editor's TextShadowSettings
      // shape when present.
      const shadowRaw: unknown = obj.shadow;
      let dropShadow: import('../types').TextShadowSettings | undefined;
      if (
        shadowRaw &&
        typeof shadowRaw === 'object' &&
        'offsetX' in shadowRaw &&
        'offsetY' in shadowRaw
      ) {
        const sh = shadowRaw as {
          offsetX?: number;
          offsetY?: number;
          blur?: number;
          color?: string;
        };
        dropShadow = {
          offsetX: sh.offsetX ?? 0,
          offsetY: sh.offsetY ?? 0,
          blur: sh.blur ?? 0,
          color: sh.color ?? '#000000',
        };
      }
      return {
        text,
        x: rect.left,
        y: rect.top,
        settings: {
          fontFamily: obj.fontFamily ?? 'serif',
          fontSize,
          fontWeight,
          fontStyle,
          underline,
          textAlign,
          fill,
          arch: 0,
          letterSpacing: charSpacing,
          lineHeight,
          ...(stroke ? { stroke } : {}),
          ...(dropShadow ? { dropShadow } : {}),
        },
      };
    },
    [findLayerObject],
  );

  /** Rasterize a non-image Fabric object (vector path / shape /
   *  text) into a `FabricImage` and swap it into the canvas in
   *  place. Used by the rasterize-before-warp prompt: Distort,
   *  Perspective and Warp's underlying renderer
   *  (`commitNonAffineDeformation` + `setElement`) is image-only,
   *  so when the user accepts the prompt we convert the layer to
   *  pixels first.
   *
   *  The result is a normal **image layer** (NOT a paint surface):
   *    - bbox-sized canvas positioned at the source's scene-space
   *      bbox.left/bbox.top — so Move tool's resize handles land
   *      on the visible content, not the full document edge
   *    - `selectable: true`, `evented: true` — Move tool can
   *      translate / resize it like any other image layer
   *    - identity scaleX/scaleY/angle: `toCanvasElement` already
   *      baked the source's affine into the pixels, so applying
   *      it again would double-transform
   *    - `objectCaching: false`: matches every other FabricImage
   *      we mount (Fabric's per-object cache mis-renders some
   *      filter / blend combos)
   *
   *  Why NOT a paint surface: brush / retouch / clone tools work
   *  on image layers via `ensurePaintSurface`, which lazily mounts
   *  a doc-sized transparent overlay tagged with the same layerId
   *  on top of the image. The image stays visible underneath, with
   *  its own selectable bbox. Making the rasterized layer itself
   *  doc-sized + non-selectable (like a bg paint surface) was the
   *  wrong default — it took away the user's resize handles and
   *  routed retouch through Move-tool because of stale selectable
   *  flags during transform exit.
   *
   *  Returns true when the swap succeeded. False on:
   *    - canvas not mounted
   *    - no Fabric object for `layerId`
   *    - the object is ALREADY a `FabricImage` (defensive)
   *    - `toCanvasElement` unavailable or produced an empty canvas */
  const rasterizeLayerToImage = useCallback(
    (layerId: string): boolean => {
      const canvas = canvasRef.current;
      if (!canvas) return false;
      const old = findLayerObject(layerId);
      if (!old) return false;
      if (old instanceof fabric.FabricImage) return false;
      const objWithToCanvas = old as fabric.FabricObject & {
        toCanvasElement?: () => HTMLCanvasElement;
      };
      if (!objWithToCanvas.toCanvasElement) return false;
      const rendered = objWithToCanvas.toCanvasElement();
      if (!rendered || rendered.width === 0 || rendered.height === 0) return false;

      const br = old.getBoundingRect();
      const wasActive = canvas.getActiveObject() === old;
      const newImg = new fabric.FabricImage(rendered, {
        left: br.left,
        top: br.top,
        originX: 'left',
        originY: 'top',
        scaleX: 1,
        scaleY: 1,
        angle: 0,
        flipX: false,
        flipY: false,
        // Inherit interaction flags from the source. The two callers
        // have different needs: rasterize-before-warp calls this
        // while the layer is `selectable: true` (Free Transform's
        // entry set it), and `ensurePaintSurface`'s auto-rasterize
        // calls this during a retouch session where the layer is
        // `selectable: false` (setTool('retouch') locked it). Either
        // way the new FabricImage should match the old's flags so
        // the active tool's lock pattern stays intact across the
        // swap.
        selectable: old.selectable ?? true,
        evented: old.evented ?? true,
        opacity: old.opacity ?? 1,
        visible: old.visible !== false,
      });
      newImg.set({ objectCaching: false } as Partial<fabric.FabricImage>);
      setObjectLayerId(newImg, layerId);

      canvas.remove(old);
      canvas.add(newImg);
      if (wasActive) {
        canvas.setActiveObject(newImg);
      }

      // If a transform session is currently captured against THIS
      // layer (rasterize-before-warp path), refresh the snapshot so
      // Cancel rolls back to the rasterized image's matrix, not the
      // long-gone vector/shape's. Mirror every captured field —
      // including selectable / evented — so exitTransformMode's
      // restore reflects the new FabricImage's interaction flags
      // and the layer doesn't end up with stale selectable/evented
      // state from the pre-rasterize object.
      const sessionInitial = transformInitialRef.current;
      if (sessionInitial && sessionInitial.layerId === layerId) {
        sessionInitial.scaleX = newImg.scaleX ?? 1;
        sessionInitial.scaleY = newImg.scaleY ?? 1;
        sessionInitial.left = newImg.left ?? 0;
        sessionInitial.top = newImg.top ?? 0;
        sessionInitial.angle = newImg.angle ?? 0;
        sessionInitial.skewX = newImg.skewX ?? 0;
        sessionInitial.skewY = newImg.skewY ?? 0;
        sessionInitial.originX = newImg.originX ?? 'left';
        sessionInitial.originY = newImg.originY ?? 'top';
        sessionInitial.flipX = newImg.flipX ?? false;
        sessionInitial.flipY = newImg.flipY ?? false;
        sessionInitial.selectable = newImg.selectable ?? true;
        sessionInitial.evented = newImg.evented ?? true;
        sessionInitial.originalElement =
          (newImg.getElement() as HTMLImageElement | HTMLCanvasElement) ?? null;
      }

      canvas.requestRenderAll();
      return true;
    },
    [findLayerObject],
  );

  /** Crop a layer's backing pixels to the axis-aligned bounding box
   *  of its non-transparent content — Photoshop's "Trim Transparent
   *  Pixels" semantics applied per-layer rather than to the document.
   *
   *  Returns:
   *   - `{ ok: true, dx, dy }` — pixels were trimmed. `(dx, dy)` is
   *     the new top-left in scene coords (useful to keep callers in
   *     sync if they cache layer positions).
   *   - `{ ok: false, reason }` — refused. Reasons:
   *     • `not-found`       — no such layer on the canvas.
   *     • `background`      — bg layer; trimming it would shrink the
   *                            document, which is `image.trim` not
   *                            `layer.trim`. Out of scope for v1.
   *     • `not-image`       — text / shape / vector / group /
   *                            adjustment layer. Their bounding box
   *                            is already tight to content; raster
   *                            trim doesn't apply.
   *     • `has-mask`        — the layer carries a per-object clipPath
   *                            (layer mask). Trimming the pixels
   *                            without trimming the mask would
   *                            desync the two surfaces. Refuse — the
   *                            user can apply or delete the mask
   *                            first.
   *     • `transformed`     — the layer has a non-identity transform
   *                            (rotation, skew, or flip). Trim is
   *                            defined in image-local space; mapping
   *                            the bbox back to scene-space under a
   *                            non-axis-aligned matrix is not
   *                            supported in v1. The user can apply
   *                            Free Transform first to bake the
   *                            transform into pixels.
   *     • `empty`           — every pixel is fully transparent.
   *                            Nothing to trim.
   *     • `already-tight`   — the alpha bbox already matches the
   *                            full source canvas. No-op.
   *
   *  The legal transform set for v1 (intersection of the inverse of
   *  every refusal reason): angle === 0, skewX/Y === 0, flipX/Y ===
   *  false. Scale is permitted — the new layer position is shifted
   *  by `bbox.x * scaleX` so the trimmed content stays at the same
   *  scene location regardless of scale.
   *
   *  Both `originX='left'/'top'` and `originX='center'/'center'`
   *  origins are handled; other combinations fall through to the
   *  `center` branch because the conversion uses the matrix delta
   *  between old and new image-center, not the raw `.left`/`.top`. */
  const trimLayer = useCallback(
    (
      layerId: string,
    ):
      | { ok: true; dx: number; dy: number }
      | {
          ok: false;
          reason:
            | 'not-found'
            | 'background'
            | 'not-image'
            | 'has-mask'
            | 'transformed'
            | 'empty'
            | 'already-tight';
        } => {
      const canvas = canvasRef.current;
      if (!canvas) return { ok: false, reason: 'not-found' };
      const obj = findLayerObject(layerId);
      if (!obj) return { ok: false, reason: 'not-found' };
      if (isBackgroundImage(obj)) return { ok: false, reason: 'background' };
      if (!(obj instanceof fabric.FabricImage)) return { ok: false, reason: 'not-image' };
      // Layer mask presence: per-object clipPath is reused for the
      // canvas-bounds enforcement rectangle, so distinguish by type
      // — only FabricImage clipPaths are layer masks (matches the
      // convention used in the auto-applied-clipPath effect above).
      if (obj.clipPath instanceof fabric.FabricImage) return { ok: false, reason: 'has-mask' };
      // Non-axis-aligned transforms — see jsdoc for rationale.
      const angle = obj.angle ?? 0;
      const skewX = obj.skewX ?? 0;
      const skewY = obj.skewY ?? 0;
      const flipX = obj.flipX ?? false;
      const flipY = obj.flipY ?? false;
      if (angle !== 0 || skewX !== 0 || skewY !== 0 || flipX || flipY) {
        return { ok: false, reason: 'transformed' };
      }

      const src = obj.getElement() as HTMLImageElement | HTMLCanvasElement;
      const srcW = (src as HTMLCanvasElement).width ?? (src as HTMLImageElement).naturalWidth ?? 0;
      const srcH =
        (src as HTMLCanvasElement).height ?? (src as HTMLImageElement).naturalHeight ?? 0;
      if (srcW <= 0 || srcH <= 0) return { ok: false, reason: 'empty' };

      // Read pixels via an off-DOM canvas — works uniformly for
      // HTMLImageElement and HTMLCanvasElement source elements.
      const probe = document.createElement('canvas');
      probe.width = srcW;
      probe.height = srcH;
      const pctx = probe.getContext('2d', { willReadFrequently: true });
      if (!pctx) return { ok: false, reason: 'empty' };
      pctx.drawImage(src, 0, 0);
      const imgData = pctx.getImageData(0, 0, srcW, srcH);
      const data = imgData.data;

      // Alpha-bbox scan. Treat alpha > 0 as "content"; matches
      // Photoshop's Trim default ("Based on Transparent Pixels").
      let minX = srcW;
      let minY = srcH;
      let maxX = -1;
      let maxY = -1;
      for (let y = 0; y < srcH; y++) {
        for (let x = 0; x < srcW; x++) {
          const a = data[(y * srcW + x) * 4 + 3];
          if (a !== 0) {
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
          }
        }
      }
      if (maxX < 0) return { ok: false, reason: 'empty' };
      const bboxW = maxX - minX + 1;
      const bboxH = maxY - minY + 1;
      if (bboxW === srcW && bboxH === srcH) return { ok: false, reason: 'already-tight' };

      // Copy the trimmed region into a tightly-sized canvas.
      const trimmed = document.createElement('canvas');
      trimmed.width = bboxW;
      trimmed.height = bboxH;
      const tctx = trimmed.getContext('2d');
      if (!tctx) return { ok: false, reason: 'empty' };
      tctx.drawImage(src, -minX, -minY);

      // Scene-space repositioning. With angle=0/skew=0/flip=false,
      // the only active transform components are `(left, top)`,
      // `scaleX`, `scaleY` and the origin choice.
      //
      // For originX='left'/'top':
      //    scene(image-local (x, y)) = (left + x*sx, top + y*sy)
      //  → newLeft so new image-local (0,0) lands at old (minX, minY)
      //  → newLeft = left + minX*sx
      //
      // For originX='center'/'center' (or any centered origin):
      //    The center of the IMAGE stays at fabric.left/top; we want
      //    the trimmed image's center to coincide with the scene
      //    point that the trimmed REGION's center currently occupies.
      //    Old region-center in image-local coords = (minX + bboxW/2,
      //    minY + bboxH/2); scene coords =
      //    (left + (minX + bboxW/2 − srcW/2)*sx,
      //     top  + (minY + bboxH/2 − srcH/2)*sy).
      //    That becomes the new (left, top) for a centered origin.
      const sx = obj.scaleX ?? 1;
      const sy = obj.scaleY ?? 1;
      const oldLeft = obj.left ?? 0;
      const oldTop = obj.top ?? 0;
      const originX = obj.originX ?? 'left';
      const originY = obj.originY ?? 'top';
      let newLeft: number;
      let newTop: number;
      if (originX === 'left') {
        newLeft = oldLeft + minX * sx;
      } else {
        // center / right share the formula via "delta of image-center";
        // see comment above.
        newLeft = oldLeft + (minX + bboxW / 2 - srcW / 2) * sx;
      }
      if (originY === 'top') {
        newTop = oldTop + minY * sy;
      } else {
        newTop = oldTop + (minY + bboxH / 2 - srcH / 2) * sy;
      }

      // Swap the FabricImage's backing element. `setElement` rewires
      // the source + intrinsic size in one call; setting `left`/`top`
      // afterward repositions the object so the trimmed pixels stay
      // anchored at the same scene location.
      obj.setElement(trimmed);
      obj.set({
        left: newLeft,
        top: newTop,
        // setElement clears cached width/height; re-set them so any
        // downstream code reading `obj.width` (export, bbox, etc.)
        // sees the trimmed bounds instead of falling back to stale
        // values.
        width: bboxW,
        height: bboxH,
      });
      // Filter cache must be regenerated against the new element.
      if (obj.filters && obj.filters.length > 0) {
        try {
          obj.applyFilters();
        } catch {
          // Some filters fail on small canvases; swallow so the
          // trim itself still applies.
        }
      }

      // If a paint surface entry exists, update it to point at the
      // new canvas. Without this, the next brush stroke would write
      // into the old (full-size, transparent-bordered) canvas while
      // the visible image is the trimmed one — strokes would appear
      // outside the layer bounds or get clipped.
      const surface = paintSurfacesRef.current.get(layerId);
      if (surface) {
        paintSurfacesRef.current.set(layerId, {
          canvas: trimmed,
          fabricImg: obj,
          offsetX: newLeft,
          offsetY: newTop,
        });
      }

      canvas.requestRenderAll();
      onObjectModifiedRef.current?.();
      return { ok: true, dx: newLeft, dy: newTop };
    },
    [findLayerObject],
  );

  /** Composite the rendered pixels of every layer in `ids` into a
   *  single canvas. Returns the new canvas + its scene-space
   *  top-left, OR `null` when:
   *   - any id isn't on the canvas
   *   - no id has a Fabric object with non-zero rendered area
   *   - the union bounding box is empty
   *
   *  Compositing semantics:
   *   - Z-order: bottom-to-top is the order returned by
   *     `canvas.getObjects()` (Fabric's intrinsic stacking). The
   *     caller's `ids` order is IGNORED — z-order is the source of
   *     truth so a multi-select pick from the panel doesn't change
   *     stacking on merge.
   *   - Per-layer transform / opacity / filters are baked in via
   *     `toCanvasElement()`. Blend mode is read from each Fabric
   *     object's `globalCompositeOperation` (mirrored from the
   *     LayerNode's `blendMode`) and applied via the merge ctx's
   *     `globalCompositeOperation` before drawing.
   *   - The bottom-most layer composites with `source-over` against
   *     a transparent canvas regardless of its own blend mode — a
   *     "Multiply" layer at the bottom of the merge stack has
   *     nothing below it inside the merge to multiply against, so
   *     the merge result becomes a plain raster of its pixels.
   *     This matches Photoshop's "merge collapses inter-layer
   *     blends" semantic.
   *
   *  Does NOT mutate the canvas — adding the merged layer and
   *  removing the originals is the caller's responsibility, so the
   *  React document state and the Fabric scene can be transitioned
   *  in lockstep. */
  const mergeLayers = useCallback(
    (ids: readonly string[]): { canvas: HTMLCanvasElement; left: number; top: number } | null => {
      const fc = canvasRef.current;
      if (!fc) return null;
      if (ids.length === 0) return null;

      // Resolve Fabric objects in z-order (bottom-to-top). Filter
      // out any ids that don't map to a current object — those
      // would be group / adjustment layers (no Fabric proxy) or
      // a stale id from a recent removal.
      const wantedIds = new Set(ids);
      const ordered: fabric.FabricObject[] = [];
      for (const o of fc.getObjects()) {
        const layerId = getObjectLayerId(o);
        if (layerId !== undefined && wantedIds.has(layerId)) ordered.push(o);
      }
      if (ordered.length === 0) return null;

      // Union bounding box in scene coords. Skip objects with
      // zero-area bounds (degenerate or fully off-canvas) — they
      // shouldn't extend the merge canvas.
      let unionL = Infinity;
      let unionT = Infinity;
      let unionR = -Infinity;
      let unionB = -Infinity;
      const bboxes: {
        obj: fabric.FabricObject;
        bbox: { left: number; top: number; width: number; height: number };
      }[] = [];
      for (const obj of ordered) {
        const bbox = obj.getBoundingRect();
        if (bbox.width <= 0 || bbox.height <= 0) continue;
        if (bbox.left < unionL) unionL = bbox.left;
        if (bbox.top < unionT) unionT = bbox.top;
        if (bbox.left + bbox.width > unionR) unionR = bbox.left + bbox.width;
        if (bbox.top + bbox.height > unionB) unionB = bbox.top + bbox.height;
        bboxes.push({ obj, bbox });
      }
      if (bboxes.length === 0) return null;
      const unionW = Math.ceil(unionR - unionL);
      const unionH = Math.ceil(unionB - unionT);
      if (unionW <= 0 || unionH <= 0) return null;

      const merged = document.createElement('canvas');
      merged.width = unionW;
      merged.height = unionH;
      const mctx = merged.getContext('2d');
      if (!mctx) return null;

      // Bottom-most layer composites with the default — anything else
      // would multiply / screen against a transparent backdrop and
      // produce nonsense. Subsequent layers honor their own blend
      // mode against the accumulating merge.
      let isFirst = true;
      for (const { obj, bbox } of bboxes) {
        const renderedEl = (
          obj as fabric.FabricObject & {
            toCanvasElement?: () => HTMLCanvasElement;
          }
        ).toCanvasElement?.();
        if (!renderedEl) continue;
        const composite = isFirst
          ? 'source-over'
          : ((obj as unknown as { globalCompositeOperation?: GlobalCompositeOperation })
              .globalCompositeOperation ?? 'source-over');
        mctx.globalCompositeOperation = composite as GlobalCompositeOperation;
        mctx.drawImage(renderedEl, bbox.left - unionL, bbox.top - unionT);
        isFirst = false;
      }
      mctx.globalCompositeOperation = 'source-over';
      return { canvas: merged, left: unionL, top: unionT };
    },
    [],
  );

  /** Dialog-driver pair for the rasterize-before-paint confirmation.
   *  EditorPage holds the dialog state and calls one of these on
   *  user confirm / cancel.
   *
   *  Confirm: swaps the shape/text/vector Fabric object for a
   *  FabricImage of its rendered pixels via `rasterizeLayerToImage`,
   *  then fires `onLayerConvertedToPaint` so EditorPage can dispatch
   *  `convertLayerToPaint(layerId)` and the LayerNode.type catches
   *  up to the on-canvas reality. If a brush / eraser stroke was
   *  captured as a replay closure (path:created handler queues this
   *  when its bake aborts due to the gate), runs it now — the
   *  user's drag becomes paint without having to retrace it.
   *  Single-click / multi-event paint ops (fill, retouch, AI) don't
   *  queue a replay; those users redo their action manually.
   *
   *  Cancel: clears the pending flag AND drops any queued replay so
   *  the rejected stroke doesn't ghost. No on-canvas state changes.
   *
   *  Either function is safe to call when no prompt is pending —
   *  the pending-set delete is a no-op and rasterize bails on
   *  already-image layers. */
  const approveLayerForPaint = useCallback(
    (layerId: string) => {
      paintPromptPendingRef.current.delete(layerId);
      const ok = rasterizeLayerToImage(layerId);
      onLayerConvertedToPaintRef.current?.(layerId);
      const replay = pendingPaintReplayRef.current.get(layerId);
      pendingPaintReplayRef.current.delete(layerId);
      // Replay only when rasterize actually succeeded — otherwise
      // the Fabric object is still vector/shape/text and the gate
      // would re-fire, re-opening the dialog in a loop.
      if (ok && replay) replay();
    },
    [rasterizeLayerToImage],
  );

  const cancelLayerForPaint = useCallback((layerId: string) => {
    paintPromptPendingRef.current.delete(layerId);
    pendingPaintReplayRef.current.delete(layerId);
  }, []);

  /** Enter Free Transform on `layerId`. Captures the layer's
   *  current Fabric matrix so Cancel can revert. Sets the layer as
   *  the active object so Fabric's native scale/rotate/skew
   *  controls render. Locks other objects so the user can't
   *  accidentally drag siblings while transforming.
   *
   *  Also captures `lockRotation` / `lockScalingFlip` / the
   *  per-instance `controls` ref — `applyTransformControls` may
   *  override these (Skew mode swaps in custom mid-edge controls;
   *  affine modes unlock rotation), and exit must restore them
   *  exactly so the next tool pickup sees the layer in its
   *  original config.
   *
   *  Returns true on success. False when no layer matches the id
   *  (e.g. the layer was deleted between BEGIN dispatch and effect
   *  firing — shouldn't happen but guard defensively). */
  const enterTransformMode = useCallback(
    (layerId: string): boolean => {
      const canvas = canvasRef.current;
      if (!canvas) return false;
      const target = findLayerObject(layerId);
      if (!target) return false;

      // Idempotency guard: don't re-capture if the same session is
      // already active. The active-flag effect in EditorPage
      // depends on `canvas` identity, which can re-fire mid-drag
      // when ANY useCallback inside useFabricCanvas's giant memo
      // dep array changes — a re-capture would clobber the
      // pre-drag snapshot with the in-progress drag state, and
      // Cancel would then revert to the drag state instead of
      // the true initial (visible symptom: Cancel does nothing).
      if (transformInitialRef.current?.layerId === layerId) {
        return true;
      }

      transformInitialRef.current = {
        layerId,
        scaleX: target.scaleX ?? 1,
        scaleY: target.scaleY ?? 1,
        left: target.left ?? 0,
        top: target.top ?? 0,
        angle: target.angle ?? 0,
        skewX: target.skewX ?? 0,
        skewY: target.skewY ?? 0,
        originX: target.originX ?? 'left',
        originY: target.originY ?? 'top',
        flipX: target.flipX ?? false,
        flipY: target.flipY ?? false,
        originalElement:
          target instanceof fabric.FabricImage
            ? ((target.getElement() as HTMLImageElement | HTMLCanvasElement) ?? null)
            : null,
        lockRotation: target.lockRotation ?? false,
        lockScalingFlip: target.lockScalingFlip ?? false,
        lockSkewingX: target.lockSkewingX ?? false,
        lockSkewingY: target.lockSkewingY ?? false,
        // Capture happens BEFORE the post-snapshot mutation that
        // sets `selectable: true / evented: true` on the target
        // (so transform handles render). That mutation is
        // intentional — `exitTransformMode` restores from this
        // snapshot, which means it restores to whatever the
        // PREVIOUS tool's lock pattern was (e.g. retouch leaves
        // image layers `selectable: false` so brush handlers
        // intercept). Do not "improve" by capturing after the
        // mutation — that would lock layers to `true` forever
        // post-transform, re-introducing the Move-tool-during-
        // retouch drag bug.
        selectable: target.selectable ?? true,
        evented: target.evented ?? true,
        controls: target.controls,
        siblings: [],
      };

      // Lock siblings, activate target. Bg image stays
      // non-selectable per the editor's locked-Background
      // convention (mirrors the marquee tool's bg lock) UNLESS the
      // user is transforming the bg layer itself — in which case
      // we have to make it selectable so Fabric's transform
      // handles render. Same exception applies to the brush /
      // crop locks once those land in the workspace pattern.
      //
      // Capture each sibling's pre-lock selectable/evented so
      // exitTransformMode can restore them exactly. Free Transform
      // doesn't change `activeTool`, so EditorPage's tool-sync
      // effect doesn't re-fire on Apply/Cancel — explicit per-
      // sibling restore is required to avoid leaving them locked.
      const bg = getBackgroundImage();
      const siblings: { obj: fabric.FabricObject; selectable: boolean; evented: boolean }[] = [];
      canvas.getObjects().forEach((o) => {
        if (o === target) {
          o.set({ selectable: true, evented: true });
        } else if (o !== bg && !isMarqueeObject(o)) {
          siblings.push({ obj: o, selectable: o.selectable ?? true, evented: o.evented ?? true });
          o.set({ selectable: false, evented: false });
        }
      });
      transformInitialRef.current.siblings = siblings;
      // Unlock rotation + scaling-flip regardless of mode. Some
      // layer types (notably the bg image) ship with lockRotation
      // or lockScalingFlip set; Transform mode needs both off to
      // honor the rotate handle and to allow negative-scale flips
      // when the user drags a corner past the opposite side.
      target.lockRotation = false;
      target.lockScalingFlip = false;
      canvas.setActiveObject(target);
      canvas.defaultCursor = 'default';
      canvas.requestRenderAll();
      return true;
    },
    [findLayerObject, getBackgroundImage],
  );

  /** Configure Fabric corner-control visibility + skew unlocks
   *  per Transform sub-mode. Called whenever the user switches
   *  modes via the Transform panel's mode picker (and once at
   *  BEGIN to set the initial Free-mode controls).
   *
   *  - free: default Fabric controls — corners scale, mid-edges
   *    scale (skew requires Shift modifier, matching Photoshop's
   *    Free Transform).
   *  - skew: corners still scale; mid-edges have their action
   *    handler SWAPPED to `skewHandlerX/Y` so bare drag skews
   *    without needing Shift. The pre-transform `controls` map
   *    was snapshotted in `transformInitialRef.controls` so exit
   *    restores the originals.
   *  - distort / perspective / warp: hide Fabric handles entirely;
   *    custom on-canvas overlays handle the interaction (1b). */
  const applyTransformControls = useCallback(
    (mode: 'free' | 'skew' | 'distort' | 'perspective' | 'warp'): void => {
      const canvas = canvasRef.current;
      const initial = transformInitialRef.current;
      if (!canvas || !initial) return;
      const target = findLayerObject(initial.layerId);
      if (!target) return;

      const isAffine = mode === 'free' || mode === 'skew';
      target.setControlsVisibility({
        tl: isAffine,
        tr: isAffine,
        bl: isAffine,
        br: isAffine,
        ml: isAffine,
        mr: isAffine,
        mt: isAffine,
        mb: isAffine,
        mtr: isAffine,
      });
      target.lockRotation = false;
      target.lockScalingFlip = false;
      if (mode === 'skew') {
        // Swap mid-edge controls to skew-only. Fabric's default
        // `scaleSkewX/Y` action conditionally skews on Shift; for
        // dedicated Skew sub-mode we want bare drag = skew, so we
        // replace the controls with ones bound directly to the
        // `skewHandlerX/Y` action handlers.
        //
        // `controls` is normally a shared object on the prototype;
        // assigning a fresh map to the instance overrides it for
        // this layer only. The snapshot restore in exit /
        // mode-switch puts the prototype reference back so we
        // don't leak the per-instance map across the layer's life.
        target.controls = {
          ...initial.controls,
          ml: buildSkewMidEdgeControl('x-', initial.controls.ml),
          mr: buildSkewMidEdgeControl('x+', initial.controls.mr),
          mt: buildSkewMidEdgeControl('y-', initial.controls.mt),
          mb: buildSkewMidEdgeControl('y+', initial.controls.mb),
        };
        target.lockSkewingX = false;
        target.lockSkewingY = false;
      } else {
        // Free (or any non-skew affine path) — restore the
        // snapshotted control map and re-lock skewing so corners
        // and mid-edges fall back to default scale behavior.
        target.controls = initial.controls;
        target.lockSkewingX = true;
        target.lockSkewingY = true;
      }
      // Non-affine modes: tear down any prior overlay then build a
      // fresh one for the new mode. Switching affine→non-affine,
      // non-affine→affine, or within non-affine modes all hit the
      // same setup/teardown calls — keeps the lifecycle uniform.
      if (mode === 'distort' || mode === 'perspective' || mode === 'warp') {
        setupNonAffineOverlayRef.current?.(mode);
      } else {
        teardownNonAffineOverlayRef.current?.();
        // Going from non-affine back to affine: re-enable layer
        // interaction so Fabric's native scale/skew/rotate handles
        // work again. The setup path made it non-selectable so
        // overlay handles could receive clicks; reverse here.
        target.selectable = true;
        target.evented = true;
        canvas.setActiveObject(target);
      }
      canvas.requestRenderAll();
    },
    [findLayerObject],
  );

  /** Read the live Fabric matrix off the transform target. Used by
   *  the TransformPanel to keep its numeric inputs in sync as the
   *  user drags handles on the canvas. Returns null when transform
   *  isn't active or the target was lost. */
  const getTransformMatrix = useCallback((): {
    x: number;
    y: number;
    width: number;
    height: number;
    rotation: number;
    skewX: number;
    skewY: number;
  } | null => {
    const initial = transformInitialRef.current;
    if (!initial) return null;
    const target = findLayerObject(initial.layerId);
    if (!target) return null;
    const sX = target.scaleX ?? 1;
    const sY = target.scaleY ?? 1;
    return {
      x: target.left ?? 0,
      y: target.top ?? 0,
      width: (target.width ?? 0) * sX,
      height: (target.height ?? 0) * sY,
      rotation: target.angle ?? 0,
      skewX: target.skewX ?? 0,
      skewY: target.skewY ?? 0,
    };
  }, [findLayerObject]);

  /** Write back numeric Transform-panel values to the Fabric
   *  matrix. Patch shape matches `getTransformMatrix` output;
   *  width/height map to scale factors via the layer's intrinsic
   *  width/height. */
  const setTransformMatrix = useCallback(
    (patch: {
      x?: number;
      y?: number;
      width?: number;
      height?: number;
      rotation?: number;
      skewX?: number;
      skewY?: number;
    }): void => {
      const canvas = canvasRef.current;
      const initial = transformInitialRef.current;
      if (!canvas || !initial) return;
      const target = findLayerObject(initial.layerId);
      if (!target) return;
      const intrinsicW = target.width ?? 1;
      const intrinsicH = target.height ?? 1;
      const next: Partial<fabric.FabricObjectProps> = {};
      if (patch.x !== undefined) next.left = patch.x;
      if (patch.y !== undefined) next.top = patch.y;
      if (patch.width !== undefined && intrinsicW > 0) {
        next.scaleX = patch.width / intrinsicW;
      }
      if (patch.height !== undefined && intrinsicH > 0) {
        next.scaleY = patch.height / intrinsicH;
      }
      if (patch.rotation !== undefined) next.angle = patch.rotation;
      if (patch.skewX !== undefined) next.skewX = patch.skewX;
      if (patch.skewY !== undefined) next.skewY = patch.skewY;
      target.set(next);
      target.setCoords();
      canvas.requestRenderAll();
    },
    [findLayerObject],
  );

  // === Non-affine Transform overlay (Phase 1b) =================
  // Distort, Perspective, and Warp deform pixels in ways canvas2d
  // can't render natively (projective for the first two, mesh for
  // the third). The shared rendering path: snapshot the layer at
  // session start, then on each handle drag re-rasterize a
  // downscaled preview into a canvas that becomes the layer's
  // element. Apply re-rasterizes at full source resolution and
  // swaps the element for the bake; Cancel restores the original
  // element via the `transformInitialRef` snapshot.

  const PREVIEW_LONGEST_PX = 768;

  /** Tear down the overlay (remove handles + outlines from canvas,
   *  clear the working ref). Doesn't touch the layer's element —
   *  the apply / cancel paths handle that. Idempotent. */
  const teardownNonAffineOverlay = useCallback((): void => {
    const canvas = canvasRef.current;
    const overlay = transformOverlayRef.current;
    if (!canvas) {
      transformOverlayRef.current = null;
      return;
    }
    // Always re-enable hit-test on teardown so subsequent tool
    // interactions work normally. Set first so even an early
    // return (no overlay ref) restores the canvas state.
    canvas.skipTargetFind = false;
    if (!overlay) {
      transformOverlayRef.current = null;
      return;
    }
    // Remove the preview overlay + handles + outlines from the
    // canvas. Restore the hidden layer's opacity so the user sees
    // it again (whether or not it was just baked over — bake calls
    // setElement before teardown, so the restored layer shows the
    // baked deformation if Apply ran, or the original pixels if
    // Cancel ran).
    for (const h of overlay.handles) canvas.remove(h);
    for (const o of overlay.outlines) canvas.remove(o);
    // No previewOverlay or opacity to restore — the target stays
    // visible throughout the session because the live preview
    // drives `target.setElement` + matrix directly. Cancel
    // restoration of the target's element + matrix is handled by
    // `exitTransformMode(false)`.
    //
    // Restore the saved tool ref + drawing-mode + selection flag
    // so the user's previous tool resumes normal behavior after
    // the session.
    activeToolRef.current = overlay.savedActiveTool;
    canvas.isDrawingMode = overlay.savedDrawingMode;
    canvas.selection = overlay.savedSelection;
    transformDragRef.current = null;
    transformOverlayRef.current = null;
    canvas.requestRenderAll();
  }, []);
  teardownNonAffineOverlayRef.current = teardownNonAffineOverlay;

  /** Re-render the preview from the current quad/grid. Called on
   *  every handle-drag tick. Reads handle positions live, computes
   *  the AABB of the deformed quad/grid (so the layer's bbox
   *  tracks the visible deformation instead of staying at the
   *  pre-transform original bbox — the latter produced the
   *  "image scales down with deformation" artifact where
   *  transparent margins inside the original bbox made the layer
   *  look faded), and rasterizes the deformation into the preview
   *  element in coords local to the new AABB. */
  const updateNonAffinePreview = useCallback((): void => {
    const overlay = transformOverlayRef.current;
    const canvas = canvasRef.current;
    const initial = transformInitialRef.current;
    if (!overlay || !canvas || !initial) return;
    const target = findLayerObject(initial.layerId);
    if (!target || !(target instanceof fabric.FabricImage)) return;

    // Live preview: rasterize at the downscaled-source density into
    // a fresh canvas and commit to the target. The Apply-time bake
    // calls the same helper with `sourceFull` and higher subdivision
    // counts. Replacing target.setElement + matrix in one shot is
    // what made the live preview work for non-bg layers (the earlier
    // separate-previewOverlay approach had unresolved Fabric refresh
    // issues for non-bg targets). Cancel restoration is handled by
    // `exitTransformMode(false)` which calls
    // `target.setElement(initial.originalElement)` + restores the
    // initial matrix.
    commitNonAffineDeformation(target, overlay.sourcePreview, overlay, {
      projective: 12,
      warp: 20,
    });

    // Rebuild outlines from current handle positions so the dashed
    // visual tracks the deformed quad/mesh.
    for (const o of overlay.outlines) canvas.remove(o);
    const newOutlines = buildNonAffineOutlines(
      overlay.mode,
      overlay.mode === 'warp' ? overlay.warpGrid : overlay.distortQuad,
    );
    for (const o of newOutlines) {
      canvas.add(o);
      canvas.bringObjectToFront(o);
    }
    // Handles must stay above outlines.
    for (const h of overlay.handles) canvas.bringObjectToFront(h);
    overlay.outlines = newOutlines;

    canvas.renderAll();
  }, [findLayerObject]);
  updateNonAffinePreviewRef.current = updateNonAffinePreview;

  /** Set up the on-canvas overlay for Distort / Perspective / Warp.
   *  Snapshots the layer's current pixel content into a full-res
   *  source canvas and a downscaled preview source; resets the
   *  layer's Fabric transform to identity-with-current-bbox so
   *  subsequent renders show the deformation; builds + adds the
   *  handle + outline Fabric objects. */
  const setupNonAffineOverlay = useCallback(
    (mode: 'distort' | 'perspective' | 'warp'): void => {
      const canvas = canvasRef.current;
      const initial = transformInitialRef.current;
      if (!canvas || !initial) return;
      const target = findLayerObject(initial.layerId);
      if (!target) return;

      // Idempotency guard: if the overlay is already set up for
      // this same mode AND the same layer, do nothing. Without
      // this, the EditorPage mode-mirror effect (which depends on
      // `canvas` identity) could re-fire during a handle drag —
      // any useCallback inside the giant useFabricCanvas memo
      // changing identity churns `canvas`, the effect re-runs,
      // applyTransformControls re-calls this function, which would
      // tear down + rebuild the handles at INITIAL positions and
      // "snap" the user's in-progress drag back. With the guard,
      // mode-switches still work (different mode → setup runs) but
      // spurious re-fires are no-op.
      if (transformOverlayRef.current?.mode === mode && transformOverlayRef.current?.bbox) {
        return;
      }

      // Switching modes tears down any prior overlay so handles
      // and the preview overlay don't stack.
      teardownNonAffineOverlay();

      // Snapshot the layer's current rendered state — captures any
      // affine matrix the user applied in Free/Skew. Non-affine
      // sessions compose on top of that.
      const objWithToCanvas = target as fabric.FabricObject & {
        toCanvasElement?: () => HTMLCanvasElement;
      };
      const sourceFull = objWithToCanvas.toCanvasElement ? objWithToCanvas.toCanvasElement() : null;
      if (!sourceFull) return;

      // Scene-space bbox of the layer at session start. Initial
      // handles seed at the corners of this bbox; the deformation
      // is unconstrained afterward.
      const br = target.getBoundingRect();
      const bbox = {
        left: br.left,
        top: br.top,
        width: br.width,
        height: br.height,
      };

      // Scene canvas dims — used here only to derive the preview
      // scale factor (per-drag rasterization runs at this density
      // for interactive performance; the bake on Apply uses the
      // full `sourceFull` density).
      const sceneWidth = imageDimsRef.current.width || canvas.getWidth();
      const sceneHeight = imageDimsRef.current.height || canvas.getHeight();
      const sceneLongest = Math.max(sceneWidth, sceneHeight);
      const previewScale =
        sceneLongest > PREVIEW_LONGEST_PX ? PREVIEW_LONGEST_PX / sceneLongest : 1;
      const pw = Math.max(1, Math.round(sceneWidth * previewScale));
      const ph = Math.max(1, Math.round(sceneHeight * previewScale));

      // Downscaled rasterization source — read by each drag tick.
      // The bake step reads `sourceFull` for final pixel density.
      const sourcePreview = document.createElement('canvas');
      sourcePreview.width = Math.max(
        1,
        Math.round(sourceFull.width * (pw / sceneWidth) * (sceneWidth / bbox.width)),
      );
      sourcePreview.height = Math.max(
        1,
        Math.round(sourceFull.height * (ph / sceneHeight) * (sceneHeight / bbox.height)),
      );
      const spCtx = sourcePreview.getContext('2d');
      if (spCtx) {
        spCtx.imageSmoothingEnabled = true;
        spCtx.imageSmoothingQuality = 'high';
        spCtx.drawImage(sourceFull, 0, 0, sourcePreview.width, sourcePreview.height);
      }

      // Initial deformation = bbox corners.
      const initialQuad: ProjectiveQuad = [
        { x: bbox.left, y: bbox.top },
        { x: bbox.left + bbox.width, y: bbox.top },
        { x: bbox.left + bbox.width, y: bbox.top + bbox.height },
        { x: bbox.left, y: bbox.top + bbox.height },
      ];
      const initialGrid = defaultWarpGridForBbox(bbox);

      // No separate preview overlay — `updateNonAffinePreview`
      // drives the target's element directly each tick (same
      // mechanism as `bakeNonAffineOverlay`, which has shipped
      // working). Cancel restoration is handled by
      // `exitTransformMode(false)` which `target.setElement`s the
      // initial element back and restores the initial matrix.

      // Build handles + outlines that float on top of the layer.
      const positions: { x: number; y: number }[] =
        mode === 'warp' ? [...initialGrid] : [...initialQuad];
      const handles = positions.map((p) => {
        const h = new fabric.Circle({
          left: p.x,
          top: p.y,
          radius: 7,
          fill: '#ffffff',
          stroke: '#0078d7',
          strokeWidth: 2,
          originX: 'center',
          originY: 'center',
          hasControls: false,
          hasBorders: false,
          selectable: false,
          evented: false,
          hoverCursor: 'move',
          objectCaching: false,
          excludeFromExport: true,
        });
        return h as unknown as fabric.FabricObject;
      });
      for (const h of handles) canvas.add(h);

      const outlines = buildNonAffineOutlines(mode, mode === 'warp' ? initialGrid : initialQuad);
      for (const o of outlines) canvas.add(o);

      // Z-order: outlines above target, handles above outlines.
      for (const o of outlines) canvas.bringObjectToFront(o);
      for (const h of handles) canvas.bringObjectToFront(h);

      // Suppress tool-specific mouse handlers for the duration of
      // the session by overriding activeToolRef to 'ai' (a tool
      // with no registered mouse handlers). Every brush / marquee
      // / retouch / fill / shape / text handler gates on
      // `activeToolRef.current === 'X'` and early-returns; 'ai'
      // doesn't match anything so they all early-return. Also
      // force isDrawingMode off so the Draw / Eraser brush won't
      // paint during a handle drag. Pre-session values are saved
      // and restored in teardown.
      const savedActiveTool = activeToolRef.current;
      const savedDrawingMode = canvas.isDrawingMode;
      const savedSelection = canvas.selection;
      activeToolRef.current = 'ai';
      canvas.isDrawingMode = false;
      // Force Fabric's group-selection rubber-band OFF. Otherwise
      // every handle-miss click falls through to Fabric's "draw a
      // selection rectangle from drag-start to cursor" path, which
      // renders a transient axis-aligned rect overlay during the
      // drag.
      canvas.selection = false;

      transformOverlayRef.current = {
        mode,
        savedActiveTool,
        savedDrawingMode,
        savedSelection,
        distortQuad: initialQuad,
        warpGrid: initialGrid,
        handles,
        outlines,
        sourceFull,
        sourcePreview,
        bbox,
      };

      // Disable Fabric's hit-test entirely for the duration of the
      // session — our custom mouse:down handler does the hit-test
      // against the handle positions directly. Without this,
      // clicks miss-near-handles can fall through to Fabric's
      // selection system and start a drag on whatever the
      // top-most evented object happens to be (the preview
      // overlay is non-evented, but other layers may be evented
      // depending on the active tool).
      canvas.skipTargetFind = true;
      // Cancel any in-progress canvas pan from the Move tool.
      // While the Move tool's mouse:* handlers are gated on
      // `!transformOverlayRef.current` for new pans, this clears
      // a pan that was mid-gesture when the user opened the
      // panel and clicked a mode.
      panRef.current.isPanning = false;

      updateNonAffinePreview();
    },
    [findLayerObject, teardownNonAffineOverlay, updateNonAffinePreview],
  );
  setupNonAffineOverlayRef.current = setupNonAffineOverlay;

  /** Bake the current non-affine deformation at full source
   *  resolution. Replaces the layer's element with the baked
   *  canvas; the Fabric transform stays at identity-with-bbox. */
  const bakeNonAffineOverlay = useCallback((): void => {
    const overlay = transformOverlayRef.current;
    const initial = transformInitialRef.current;
    if (!overlay || !initial) return;
    const target = findLayerObject(initial.layerId);
    if (!target || !(target instanceof fabric.FabricImage)) {
      teardownNonAffineOverlay();
      return;
    }
    // Bake at the deformed AABB sized to keep source pixel density.
    // Same helper used by the live preview tick — the bake just
    // hands in `sourceFull` (full-resolution captured layer pixels)
    // and higher subdivision counts so the final commit is
    // visually clean at any zoom level.
    commitNonAffineDeformation(target, overlay.sourceFull, overlay, {
      projective: 24,
      warp: 32,
    });

    // Sync the paint-surface cache with the new element. The bake
    // calls `target.setElement(dest)` with a fresh canvas — without
    // this update, `paintSurfacesRef[layerId].canvas` still points
    // at the PRE-warp canvas and subsequent brush / retouch / clone
    // strokes write to a hidden canvas that the FabricImage no
    // longer displays. Stale-cache symptom: tool cursor is correct,
    // no marks appear on screen, undo/redo does nothing visible.
    // The cache is only refreshed when an entry already exists for
    // this layer — otherwise we'd be creating ghost entries for
    // image layers that were never used as paint targets.
    const cached = paintSurfacesRef.current.get(initial.layerId);
    if (cached && cached.fabricImg === target) {
      const newElement = target.getElement();
      if (newElement instanceof HTMLCanvasElement) {
        paintSurfacesRef.current.set(initial.layerId, {
          canvas: newElement,
          fabricImg: target,
          offsetX: target.left ?? 0,
          offsetY: target.top ?? 0,
        });
      }
    }

    teardownNonAffineOverlay();
    // Tier 2 Group F — non-affine deformation (Distort / Perspective /
    // Warp Apply) bakes new pixels into the layer's element. The
    // export path must emit this layer as raster rather than restoring
    // from the PSD stash.
    onLayerRasterModifiedRef.current?.(initial.layerId);
  }, [findLayerObject, teardownNonAffineOverlay]);
  bakeNonAffineOverlayRef.current = bakeNonAffineOverlay;

  /** Apply a Warp preset (Arc / Bulge / Twist / Wave) to the
   *  current overlay. Re-seeds the 9 grid points + updates the
   *  Fabric handle positions + re-renders the preview. No-op when
   *  Warp isn't the active mode or the overlay isn't set up. */
  const applyWarpPresetToOverlay = useCallback(
    (preset: WarpPreset, intensity: number = 1): void => {
      const overlay = transformOverlayRef.current;
      if (!overlay || overlay.mode !== 'warp') return;
      // Compute the new grid in BBOX-LOCAL coords, then offset to
      // scene-space (same as `defaultWarpGridForBbox`).
      const local = applyWarpPreset(overlay.bbox.width, overlay.bbox.height, preset, intensity);
      const scene = local.map((p) => ({
        x: p.x + overlay.bbox.left,
        y: p.y + overlay.bbox.top,
      })) as unknown as WarpGrid;
      overlay.warpGrid = scene;
      // Move the handle Fabric objects to track the new grid.
      for (let i = 0; i < 9; i++) {
        const h = overlay.handles[i];
        if (h) {
          h.set({ left: scene[i].x, top: scene[i].y });
          h.setCoords();
        }
      }
      updateNonAffinePreview();
    },
    [updateNonAffinePreview],
  );

  /** Exit Free Transform. On apply, the Fabric matrix stays as-is
   *  for affine modes (Free/Skew) — Fabric's native rendering
   *  already shows the transformed result, so "applying" is just
   *  leaving the matrix and dropping the active-object handles.
   *  Non-affine modes that baked pixels via a separate path are
   *  also no-op here (the bake already replaced the element).
   *
   *  On cancel, the snapshotted initial state is restored and any
   *  baked element is reverted. */
  const exitTransformMode = useCallback(
    (apply: boolean): void => {
      const canvas = canvasRef.current;
      const initial = transformInitialRef.current;
      if (!canvas || !initial) return;
      const target = findLayerObject(initial.layerId);
      // Non-affine overlay path: on Apply, bake the deformation at
      // full source resolution; on Cancel, just tear down (the
      // captured original element + matrix get restored below).
      if (transformOverlayRef.current) {
        if (apply) {
          bakeNonAffineOverlayRef.current?.();
        } else {
          teardownNonAffineOverlayRef.current?.();
        }
      }
      if (!apply && target) {
        // Restore the captured matrix BEFORE discardActiveObject —
        // Fabric reads transform props during the active-object
        // teardown for its bounding-rect cache. Order:
        //   1. set() the matrix props in one batch.
        //   2. setCoords() to refresh the bbox.
        //   3. dirty=true to invalidate any per-object cache
        //      (some FabricImage configs use objectCaching=true
        //      even when ours don't — defensive).
        //   4. setElement() for FabricImage to undo any non-affine
        //      bake that swapped the underlying element (1b path).
        //
        // origin{X,Y} and flip{X,Y} get restored too — Fabric's
        // `wrapWithFixedAnchor` flow temporarily swaps origin to
        // pin the opposite corner during a drag, and a corner-flip
        // (drag past the opposite side) toggles flipX/flipY.
        // Without restoring these the visual bbox is right but the
        // layer can land "inside out" relative to its pre-transform
        // state.
        target.set({
          scaleX: initial.scaleX,
          scaleY: initial.scaleY,
          left: initial.left,
          top: initial.top,
          angle: initial.angle,
          skewX: initial.skewX,
          skewY: initial.skewY,
          originX: initial.originX,
          originY: initial.originY,
          flipX: initial.flipX,
          flipY: initial.flipY,
        });
        target.setCoords();
        target.dirty = true;
        if (target instanceof fabric.FabricImage && initial.originalElement) {
          target.setElement(initial.originalElement);
        }
      }
      // Restore the snapshotted locks + controls map so the next
      // tool pickup sees the layer exactly as we found it. The
      // controls reference may be the prototype-shared default
      // (most layers) OR a per-instance override (e.g. bg image
      // with lockRotation) — either way `initial.controls` is the
      // right thing to put back.
      if (target) {
        // Reset every control's visibility to true. The previous
        // empty-object call was a no-op (setControlsVisibility
        // only mutates NAMED controls), so non-affine modes —
        // which hide all corners + mid-edges + rotator via
        // applyTransformControls — left the layer with no resize
        // handles after Apply / Cancel. Naming every default
        // control here ensures Move tool's standard 8-handle +
        // rotator UI is restored.
        target.setControlsVisibility({
          tl: true,
          tr: true,
          bl: true,
          br: true,
          ml: true,
          mr: true,
          mt: true,
          mb: true,
          mtr: true,
        });
        target.controls = initial.controls;
        target.lockRotation = initial.lockRotation;
        target.lockScalingFlip = initial.lockScalingFlip;
        target.lockSkewingX = initial.lockSkewingX;
        target.lockSkewingY = initial.lockSkewingY;
        // Restore the PRE-transform interaction flags. Layers
        // that came in selectable/evented (image, vector before
        // rasterize, etc.) get those flags back. Paint surfaces
        // (rasterized layers, bg-as-paint) came in non-selectable
        // / non-evented so brush handlers could intercept clicks
        // — they go back to that state so the next retouch /
        // brush stroke isn't swallowed by Move-tool selection.
        target.selectable = initial.selectable;
        target.evented = initial.evented;
      }
      // Restore every sibling's pre-lock selectable/evented
      // state. enterTransformMode locked them so the user
      // couldn't accidentally drag siblings while transforming;
      // since Free Transform doesn't change `activeTool`, the
      // EditorPage tool-sync effect doesn't re-fire on exit —
      // without this loop, siblings stay locked until the user
      // manually switches tools.
      for (const s of initial.siblings) {
        s.obj.set({ selectable: s.selectable, evented: s.evented });
      }
      canvas.discardActiveObject();
      transformInitialRef.current = null;
      // Sync renderAll() rather than requestRenderAll() — async
      // schedule was producing a brief window where React commits
      // (panel unmount) ran before the canvas redrew, leaving the
      // post-drag pixels on screen because the user perceives the
      // panel closing as "the operation completed" and stops
      // looking for further visual changes. Sync ensures the
      // revert is on screen the same tick we drop the panel.
      canvas.renderAll();
    },
    [findLayerObject],
  );

  /** Invert the current marquee — every pixel inside the selection
   *  becomes outside, and vice versa. Bounded by the image rect, so
   *  inverting an empty selection selects everything and inverting
   *  a full-image selection deselects.
   *
   *  Implementation: rasterize whatever shape currently exists into
   *  a binary mask, NOT the bits, install the result via the same
   *  wand-style polygon path. The output is always shape='wand'
   *  regardless of the input shape (the inverse of a rect is a
   *  frame, not a rect — closed-form per-shape inversion would be
   *  more state to carry without buying anything). */
  const invertMarquee = useCallback((): boolean => {
    const canvas = canvasRef.current;
    if (!canvas) return false;
    const imgW = imageDimsRef.current.width;
    const imgH = imageDimsRef.current.height;
    if (imgW <= 0 || imgH <= 0) return false;

    const m = marqueeRef.current;
    if (m.isDrawing) return false;

    // Rasterize the current selection into a mask the size of the
    // image, then NOT the bits. Empty selection → all-zeros → all-
    // ones (select everything).
    const mask = marqueeToBinaryMask(m, imgW, imgH);
    invertMaskInPlace(mask);
    if (!isMaskNonEmpty(mask)) {
      // Inverted selection covers nothing — that means the original
      // covered the whole image. Treat as deselect.
      clearMarquee();
      return true;
    }

    // Drop existing fabric polys + cached state.
    if (m.obj) canvas.remove(m.obj);
    for (const o of m.extraObjs) canvas.remove(o);
    m.obj = null;
    m.extraObjs = [];
    m.points = [];
    m.wandMaskCanvas = null;
    m.isDrawing = false;
    m.shape = 'wand';
    m.wandMaskCanvas = binaryMaskToCanvas(mask, imgW, imgH);

    const contours = traceContours(mask, imgW, imgH);
    const path = buildMarqueePath(contours);
    if (!path) {
      clearMarquee();
      return true;
    }
    tagAsMarquee(path);
    applyMarqueeColor(path, marqueeColorRef.current);
    applyMarqueeStroke(canvas, path);
    canvas.add(path);
    m.obj = path;
    m.extraObjs = [];

    canvas.renderAll();
    refreshMarqueeSelectionState();
    return true;
  }, [clearMarquee, refreshMarqueeSelectionState]);

  /**
   * Render the marqueed region of the active layer's paint surface to a
   * fresh HTMLCanvasElement sized to the marquee's bounding box. Used by
   * copySelection / cutSelection. Returns null if there's no finalized
   * marquee or no active layer. For a freshly-added empty layer with no
   * paint surface yet, the surface is auto-vivified — copy/cut on such a
   * layer yields a transparent region rather than a silent no-op.
   */
  const renderMarqueeRegion = useCallback((): {
    canvas: HTMLCanvasElement;
    left: number;
    top: number;
  } | null => {
    const m = marqueeRef.current;
    if (!m.obj || m.isDrawing) return null;
    const target = resolveActivePaintTarget(activeLayerIdRef, ensurePaintSurfaceRef);
    if (!target) return null;
    const { surface } = target;

    // Compute the bbox via the pure helper (intrinsic properties, not the
    // stale `getBoundingRect()` cache). See the helper's doc comment for
    // why and the unit tests in `lib/marquee-bounds.test.ts`.
    let bounds: { left: number; top: number; width: number; height: number } | null = (() => {
      if (m.shape === 'rect') {
        const r = m.obj as fabric.Rect;
        return computeMarqueeBounds({
          shape: 'rect',
          left: r.left ?? 0,
          top: r.top ?? 0,
          width: r.width ?? 0,
          height: r.height ?? 0,
        });
      }
      if (m.shape === 'ellipse') {
        const e = m.obj as fabric.Ellipse;
        return computeMarqueeBounds({
          shape: 'ellipse',
          left: e.left ?? 0,
          top: e.top ?? 0,
          rx: e.rx ?? 0,
          ry: e.ry ?? 0,
        });
      }
      if (m.shape === 'lasso' || m.shape === 'polygon') {
        return computeMarqueeBounds({ shape: m.shape, points: m.points });
      }
      if (m.shape === 'wand' && m.obj) {
        // Wand selections are now rendered as a single fabric.Path
        // with evenodd fill (see `buildMarqueePath`). The Path's
        // own bbox spans every contour in the mask, which is the
        // bounding rect we want for copy/cut/render. The actual
        // per-pixel masking happens below in step 2 via the cached
        // `m.wandMaskCanvas`.
        return {
          left: m.obj.left ?? 0,
          top: m.obj.top ?? 0,
          width: (m.obj.width ?? 0) * (m.obj.scaleX ?? 1),
          height: (m.obj.height ?? 0) * (m.obj.scaleY ?? 1),
        };
      }
      return null;
    })();
    if (!bounds) return null;

    // Clip the marquee bbox to the active surface's scene bbox before
    // sizing `out`. Without this, a marquee dragged from outside the
    // canvas across the image produced a clipboard canvas the size of
    // the marquee — INCLUDING the off-canvas regions where the source
    // layer has no pixels — and the pasted layer's bounding box ended
    // up larger than the actual content with asymmetric transparent
    // padding on the sides where the marquee overhung.
    const surfaceLeft = surface.fabricImg.left ?? 0;
    const surfaceTop = surface.fabricImg.top ?? 0;
    const surfaceRight = surfaceLeft + surface.canvas.width;
    const surfaceBottom = surfaceTop + surface.canvas.height;
    const clippedLeft = Math.max(bounds.left, surfaceLeft);
    const clippedTop = Math.max(bounds.top, surfaceTop);
    const clippedRight = Math.min(bounds.left + bounds.width, surfaceRight);
    const clippedBottom = Math.min(bounds.top + bounds.height, surfaceBottom);
    const clippedW = clippedRight - clippedLeft;
    const clippedH = clippedBottom - clippedTop;
    if (clippedW <= 0 || clippedH <= 0) return null;
    bounds = {
      left: clippedLeft,
      top: clippedTop,
      width: clippedW,
      height: clippedH,
    };

    const w = Math.round(bounds.width);
    const h = Math.round(bounds.height);
    if (w <= 0 || h <= 0) return null;

    const out = document.createElement('canvas');
    out.width = w;
    out.height = h;
    const ctx = out.getContext('2d');
    if (!ctx) return null;

    // Step 1: Copy the bbox region from the source surface into `out` using
    // the explicit 9-arg drawImage form. surface.canvas pixel (X,Y) =
    // scene (X + surfaceLeft, Y + surfaceTop), so the source rect in surface
    // pixels is (bounds.left - surfaceLeft, bounds.top - surfaceTop).
    const sx = Math.round(bounds.left - surfaceLeft);
    const sy = Math.round(bounds.top - surfaceTop);
    ctx.drawImage(surface.canvas, sx, sy, w, h, 0, 0, w, h);

    // Step 2: For non-rect marquees, mask out pixels outside the selection
    // shape. We render the marquee shape into a separate mask canvas in
    // out-local coords and apply it via destination-in compositing.
    if (m.shape !== 'rect') {
      const mask = document.createElement('canvas');
      mask.width = w;
      mask.height = h;
      const mCtx = mask.getContext('2d');
      if (mCtx) {
        mCtx.fillStyle = '#ffffff';
        if (m.shape === 'ellipse') {
          const e = m.obj as fabric.Ellipse;
          const rx = e.rx ?? 0;
          const ry = e.ry ?? 0;
          const cx = (e.left ?? 0) + rx - bounds.left;
          const cy = (e.top ?? 0) + ry - bounds.top;
          mCtx.beginPath();
          mCtx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
          mCtx.fill();
        } else if ((m.shape === 'lasso' || m.shape === 'polygon') && m.points.length >= 3) {
          mCtx.beginPath();
          mCtx.moveTo(m.points[0].x - bounds.left, m.points[0].y - bounds.top);
          for (let i = 1; i < m.points.length; i++) {
            mCtx.lineTo(m.points[i].x - bounds.left, m.points[i].y - bounds.top);
          }
          mCtx.closePath();
          mCtx.fill();
        } else if (m.shape === 'wand' && m.wandMaskCanvas) {
          // Crop the wand mask (full bg-sized) into the bbox region.
          mCtx.drawImage(
            m.wandMaskCanvas,
            Math.round(bounds.left),
            Math.round(bounds.top),
            w,
            h,
            0,
            0,
            w,
            h,
          );
        }
        ctx.globalCompositeOperation = 'destination-in';
        ctx.drawImage(mask, 0, 0);
        ctx.globalCompositeOperation = 'source-over';
      }
    }

    return { canvas: out, left: bounds.left, top: bounds.top };
  }, []);

  /** Copy the marqueed region of the active layer to the clipboard. Returns
   *  true on success. */
  const copySelection = useCallback((): boolean => {
    const result = renderMarqueeRegion();
    if (!result) return false;
    // The marquee region might be over a fully-transparent area of the
    // active layer — the rendered output is then an empty canvas. Skip
    // the clipboard write and surface a toast so the user doesn't think
    // Ctrl+C silently failed.
    if (isCanvasFullyTransparent(result.canvas)) {
      onMarqueeActionNoPixelsRef.current?.('copy');
      return false;
    }
    clipboardRef.current = result;
    setHasClipboard(true);
    return true;
  }, [renderMarqueeRegion]);

  /** Copy + clear: same as copySelection, then clip-clear the marqueed
   *  pixels from the active layer's paint surface. Returns true on success. */
  const cutSelection = useCallback((): boolean => {
    const result = renderMarqueeRegion();
    if (!result) return false;
    // Same empty-region guard as copySelection — see comment there.
    if (isCanvasFullyTransparent(result.canvas)) {
      onMarqueeActionNoPixelsRef.current?.('cut');
      return false;
    }
    clipboardRef.current = result;
    setHasClipboard(true);

    const canvas = canvasRef.current;
    const m = marqueeRef.current;
    if (!canvas) return true;
    const target = resolveActivePaintTarget(activeLayerIdRef, ensurePaintSurfaceRef);
    if (!target) return true;
    const { surface } = target;
    const ctx = surface.canvas.getContext('2d');
    if (!ctx) return true;

    // Map scene marquee geometry into the surface's local pixel space.
    // The earlier `ctx.translate(-surfaceLeft, -surfaceTop)` +
    // `clearRect(0, 0, W, H)` pattern was broken for any non-zero
    // surface offset: clearRect's rect, expressed in translated coords,
    // actually covered canvas pixels `(-surfaceLeft, -surfaceTop, W, H)`
    // — leaving most of the surface untouched on a moved pasted layer,
    // so cut silently failed for marquees outside the upper-left
    // quadrant of the surface. Now we pass `(surfaceLeft, surfaceTop)`
    // straight to `applyMarqueeClipToContext` (it subtracts them to
    // produce surface-local clip coords) and clear with no translate.
    const surfaceLeft = surface.fabricImg.left ?? 0;
    const surfaceTop = surface.fabricImg.top ?? 0;
    ctx.save();
    if (m.shape === 'wand' && m.wandMaskCanvas) {
      ctx.globalCompositeOperation = 'destination-out';
      // wandMaskCanvas is doc-sized in scene coords. Shifting by
      // -(surfaceLeft, surfaceTop) places its scene origin at the
      // surface's local origin.
      ctx.drawImage(m.wandMaskCanvas, -surfaceLeft, -surfaceTop);
    } else {
      const clipped = applyMarqueeClipToContext(ctx, m, surfaceLeft, surfaceTop);
      if (clipped) ctx.clearRect(0, 0, surface.canvas.width, surface.canvas.height);
    }
    ctx.restore();

    surface.fabricImg.setElement(surface.canvas);
    surface.fabricImg.dirty = true;
    surface.fabricImg.setCoords();
    canvas.renderAll();
    return true;
  }, [renderMarqueeRegion]);

  /** Read the clipboard payload — caller (EditorPage) is expected to
   *  create a new layer and place a fabric.FabricImage there. Returns
   *  null when the clipboard is empty. */
  const getClipboard = useCallback(() => clipboardRef.current, []);

  /** Most-recent canvas-click position in scene coords, or null if the
   *  user hasn't clicked the canvas yet this session. Callers (paste)
   *  use this to drop new content at the user's last interaction point. */
  const getLastClickPoint = useCallback((): { x: number; y: number } | null => {
    return lastClickSceneRef.current;
  }, []);

  /** True between fabric `mouse:down` and `mouse:up` — i.e. while the
   *  user holds a pointer on the canvas. Autosave consults this so a
   *  marquee drag (or any held-pointer interaction) defers the
   *  `canvas.toJSON()` serialization that would otherwise hitch the
   *  drag for tens of ms on a large doc. */
  const getIsCanvasInteracting = useCallback((): boolean => {
    return canvasInteractionRef.current;
  }, []);

  /** Clear Fabric's active object + re-render. Used by LayersPanel
   *  name-clicks in non-Move tools: the row click should set the
   *  active layer state (so the panel highlights / the brush targets
   *  the right surface) without leaving the transform handles
   *  visible — those are a Move-tool affordance. */
  const clearActiveSelection = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return;
    c.discardActiveObject();
    c.requestRenderAll();
  }, []);

  /** Bounding rect (scene coords) of the currently-finalized marquee, or
   *  null when there's no marquee in progress. Combines the primary
   *  marquee object with any extras so polygon / lasso multi-segment
   *  selections come back as a single rect. Used by Smart Create to know
   *  where to drop the generated image. */
  const getMarqueeBounds = useCallback(() => {
    const m = marqueeRef.current;
    if (!m.obj || m.isDrawing) return null;
    // Read intrinsic dimensions per shape type directly. Going through
    // `getBoundingRect()` was returning stroke-dominated values (the
    // marquee uses a `strokeUniform: true` border, and for shapes
    // where the intrinsic width somehow stayed near zero, the bbox
    // collapsed to roughly the stroke width — a 1.12px frame around
    // a 0×0 rect, not the user's actual selection). Reading the
    // stored geometry directly bypasses any stroke / transform
    // contamination.
    let bounds: { left: number; top: number; width: number; height: number } | null = null;
    if (m.shape === 'rect') {
      const r = m.obj as fabric.Rect;
      bounds = {
        left: r.left ?? 0,
        top: r.top ?? 0,
        width: (r.width ?? 0) * (r.scaleX ?? 1),
        height: (r.height ?? 0) * (r.scaleY ?? 1),
      };
    } else if (m.shape === 'ellipse') {
      const e = m.obj as fabric.Ellipse;
      const rx = (e.rx ?? 0) * (e.scaleX ?? 1);
      const ry = (e.ry ?? 0) * (e.scaleY ?? 1);
      bounds = {
        left: e.left ?? 0,
        top: e.top ?? 0,
        width: rx * 2,
        height: ry * 2,
      };
    } else if (m.shape === 'wand') {
      // Wand has no `m.points` (the shape lives in `m.wandMaskCanvas`
      // and the rendered Fabric Polygons). Union the polygon objects'
      // bboxes — each polygon's left/top/width/height is its
      // contour's bounding box in scene coords (originX/Y: left/top).
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      for (const o of [m.obj, ...m.extraObjs]) {
        if (!o) continue;
        const left = o.left ?? 0;
        const top = o.top ?? 0;
        const w = (o.width ?? 0) * (o.scaleX ?? 1);
        const h = (o.height ?? 0) * (o.scaleY ?? 1);
        if (left < minX) minX = left;
        if (top < minY) minY = top;
        if (left + w > maxX) maxX = left + w;
        if (top + h > maxY) maxY = top + h;
      }
      if (Number.isFinite(minX) && Number.isFinite(minY)) {
        bounds = { left: minX, top: minY, width: maxX - minX, height: maxY - minY };
      }
    } else if (m.points.length >= 2) {
      // Polygon / lasso contour — derive from the stored points
      // directly (they're authored in scene coords).
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      for (const p of m.points) {
        if (p.x < minX) minX = p.x;
        if (p.y < minY) minY = p.y;
        if (p.x > maxX) maxX = p.x;
        if (p.y > maxY) maxY = p.y;
      }
      if (Number.isFinite(minX) && Number.isFinite(minY)) {
        bounds = { left: minX, top: minY, width: maxX - minX, height: maxY - minY };
      }
    }
    if (!bounds || bounds.width <= 0 || bounds.height <= 0) return null;
    return bounds;
  }, []);

  /**
   * Run an inpaint pipeline (e.g. MI-GAN) over the active layer's marqueed
   * region and bake the result back onto the layer's paint surface.
   *
   *   • The injected `inpaint` function does the actual model inference —
   *     so the hook stays free of any onnxruntime / model-specific code.
   *     `lib/smart-delete.ts` ships the MI-GAN runtime; the AI tool's
   *     handler in EditorPage glues the two.
   *   • The result is composited only inside the marquee mask, so non-
   *     selected pixels stay byte-identical (avoids resize artifacts on
   *     pixels the user didn't ask us to touch).
   *   • Returns `true` on success, `false` when there's no active layer,
   *     no marquee, or the active layer doesn't have a paint surface yet.
   */
  const runSmartDeleteOnActiveLayer = useCallback(
    async (
      inpaint: (
        source: HTMLCanvasElement,
        mask: HTMLCanvasElement,
        /** Tight bounding rect of the masked region in mask-canvas
         *  coords (already clamped to mask bounds). Computed from
         *  shape geometry — no pixel scan — so callers can crop
         *  source / mask without an O(W·H) walk. */
        maskBbox: { left: number; top: number; width: number; height: number },
      ) => Promise<HTMLCanvasElement>,
    ): Promise<boolean> => {
      const m = marqueeRef.current;
      if (!m.obj || m.isDrawing) return false;
      const layerId = activeLayerIdRef.current;
      if (!layerId) return false;
      const canvas = canvasRef.current;
      if (!canvas) return false;

      // Lazy-register a paint surface so Inpaint works on an image
      // layer that's never been brush-touched (e.g. a fresh Smart
      // Create result). Same fallback as `runOnActiveLayerFullImage`.
      const surface = resolveOrRegisterPaintSurface(
        layerId,
        imageLayerCandidates(canvas),
        paintSurfacesRef.current,
      );
      if (!surface) return false;

      const w = surface.canvas.width;
      const h = surface.canvas.height;
      if (w <= 0 || h <= 0) return false;

      // Mask matches the surface's pixel grid. Marquee geometry is in
      // scene coords; for a paint surface positioned at scene (left, top)
      // we shift by -(left, top) so the mask lands on the right pixels.
      const mask = document.createElement('canvas');
      mask.width = w;
      mask.height = h;
      const mCtx = mask.getContext('2d');
      if (!mCtx) return false;
      const surfLeft = surface.fabricImg.left ?? 0;
      const surfTop = surface.fabricImg.top ?? 0;
      mCtx.save();
      mCtx.translate(-surfLeft, -surfTop);
      mCtx.fillStyle = '#ffffff';
      if (m.shape === 'wand' && m.wandMaskCanvas) {
        // Wand: wandMaskCanvas is already a binary alpha mask in scene
        // coords. Translate is already applied to the ctx.
        mCtx.drawImage(m.wandMaskCanvas, 0, 0);
      } else if (m.shape === 'rect') {
        const r = m.obj as fabric.Rect;
        mCtx.fillRect(r.left ?? 0, r.top ?? 0, r.width ?? 0, r.height ?? 0);
      } else if (m.shape === 'ellipse') {
        const e = m.obj as fabric.Ellipse;
        const rx = e.rx ?? 0;
        const ry = e.ry ?? 0;
        const cx = (e.left ?? 0) + rx;
        const cy = (e.top ?? 0) + ry;
        mCtx.beginPath();
        mCtx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
        mCtx.fill();
      } else if ((m.shape === 'lasso' || m.shape === 'polygon') && m.points.length >= 3) {
        mCtx.beginPath();
        mCtx.moveTo(m.points[0].x, m.points[0].y);
        for (let i = 1; i < m.points.length; i++) {
          mCtx.lineTo(m.points[i].x, m.points[i].y);
        }
        mCtx.closePath();
        mCtx.fill();
      } else {
        mCtx.restore();
        return false;
      }
      mCtx.restore();

      // Source: a fresh copy of the surface so the model has full image
      // context (inpainting works better when the model can sample from
      // outside the marquee, not just within it).
      const source = document.createElement('canvas');
      source.width = w;
      source.height = h;
      const srcCtx = source.getContext('2d');
      if (!srcCtx) return false;
      srcCtx.drawImage(surface.canvas, 0, 0);

      // Tight bbox of the masked region, in mask-canvas coords, derived
      // from the marquee's stored shape geometry — no pixel scan. The
      // mask was filled with `mCtx.translate(-surfLeft, -surfTop)` so
      // we apply the same shift here, then clamp to mask bounds.
      let sceneLeft = 0;
      let sceneTop = 0;
      let sceneRight = w;
      let sceneBottom = h;
      if (m.shape === 'rect') {
        const r = m.obj as fabric.Rect;
        sceneLeft = r.left ?? 0;
        sceneTop = r.top ?? 0;
        sceneRight = sceneLeft + (r.width ?? 0);
        sceneBottom = sceneTop + (r.height ?? 0);
      } else if (m.shape === 'ellipse') {
        const e = m.obj as fabric.Ellipse;
        const rx = e.rx ?? 0;
        const ry = e.ry ?? 0;
        sceneLeft = e.left ?? 0;
        sceneTop = e.top ?? 0;
        sceneRight = sceneLeft + rx * 2;
        sceneBottom = sceneTop + ry * 2;
      } else if ((m.shape === 'lasso' || m.shape === 'polygon') && m.points.length >= 3) {
        let minX = Infinity;
        let minY = Infinity;
        let maxX = -Infinity;
        let maxY = -Infinity;
        for (const p of m.points) {
          if (p.x < minX) minX = p.x;
          if (p.y < minY) minY = p.y;
          if (p.x > maxX) maxX = p.x;
          if (p.y > maxY) maxY = p.y;
        }
        sceneLeft = minX;
        sceneTop = minY;
        sceneRight = maxX;
        sceneBottom = maxY;
      }
      // Wand has no shape geometry to derive bbox from — its bounds
      // are the flood-filled pixels of `wandMaskCanvas`. Falling back
      // to the full mask is correct (the inpaint callback will still
      // get a valid rect, just larger than necessary). Accept the
      // looser crop for the rare wand+Blend case rather than scanning
      // a full-resolution alpha buffer.
      const maskBboxLeft = Math.max(0, Math.floor(sceneLeft - surfLeft));
      const maskBboxTop = Math.max(0, Math.floor(sceneTop - surfTop));
      const maskBboxRight = Math.min(w, Math.ceil(sceneRight - surfLeft));
      const maskBboxBottom = Math.min(h, Math.ceil(sceneBottom - surfTop));
      const maskBbox = {
        left: maskBboxLeft,
        top: maskBboxTop,
        width: Math.max(1, maskBboxRight - maskBboxLeft),
        height: Math.max(1, maskBboxBottom - maskBboxTop),
      };

      const result = await inpaint(source, mask, maskBbox);

      // Composite result onto surface, restricted to the mask region.
      const masked = document.createElement('canvas');
      masked.width = w;
      masked.height = h;
      const maskedCtx = masked.getContext('2d');
      if (!maskedCtx) return false;
      maskedCtx.drawImage(result, 0, 0, w, h);
      maskedCtx.globalCompositeOperation = 'destination-in';
      maskedCtx.drawImage(mask, 0, 0);

      const ctx = surface.canvas.getContext('2d');
      if (!ctx) return false;
      ctx.drawImage(masked, 0, 0);

      surface.fabricImg.setElement(surface.canvas);
      surface.fabricImg.dirty = true;
      surface.fabricImg.setCoords();
      canvas.renderAll();
      onObjectModifiedRef.current?.();
      // Tier 2 Group F — mask-aware AI op (Smart Delete / Inpaint /
      // Search-Replace) committed new pixels into the active layer's
      // surface. Export emits as raster, not stash-restored.
      onLayerRasterModifiedRef.current?.(layerId);
      return true;
    },
    [],
  );

  /** Replace the active layer's full image with a transformed copy. The
   *  caller's `transform` receives a fresh copy of the surface canvas
   *  and returns a new canvas (typically from an AI service that
   *  rewrites the whole image, not a mask-localized edit). The return
   *  value is drawn back onto the surface, scaled to fit the surface's
   *  original dimensions — so the layer's place on the Fabric canvas,
   *  bounding box, and z-order are all preserved.
   *
   *  Used by Search & Replace, where Stability auto-segments the
   *  search target and returns the entire image with the replacement
   *  composited. Smart Delete + Inpaint use the mask-aware
   *  `runSmartDeleteOnActiveLayer` instead. */
  const runOnActiveLayerFullImage = useCallback(
    async (
      transform: (source: HTMLCanvasElement) => Promise<HTMLCanvasElement>,
    ): Promise<boolean> => {
      const layerId = activeLayerIdRef.current;
      if (!layerId) return false;
      const canvas = canvasRef.current;
      if (!canvas) return false;

      // Lazy-register a paint surface when one doesn't exist yet
      // for this layer. Image layers added via `addImageLayer` and
      // the background-before-first-paint have a FabricImage but
      // no `paintSurfacesRef` entry — brush handlers create the
      // entry on first stroke, but pixel-level bake ops (Liquify
      // Apply, future filters) need to operate without requiring
      // a precursor stroke. The helper mirrors the fallback in
      // `getActiveAdjustmentTargets` so the read-side and write-
      // side stay symmetric.
      const surface: PaintSurface<fabric.FabricImage> | null = resolveOrRegisterPaintSurface(
        layerId,
        imageLayerCandidates(canvas),
        paintSurfacesRef.current,
      );
      if (!surface) return false;

      const w = surface.canvas.width;
      const h = surface.canvas.height;
      if (w <= 0 || h <= 0) return false;

      // Hand the transform a fresh canvas so it can't accidentally
      // mutate the live surface mid-flight.
      const source = document.createElement('canvas');
      source.width = w;
      source.height = h;
      const srcCtx = source.getContext('2d');
      if (!srcCtx) return false;
      srcCtx.drawImage(surface.canvas, 0, 0);

      const result = await transform(source);

      // Replace surface pixels in place, scaling the result to the
      // surface's native size. Clearing first prevents partial-alpha
      // result pixels from blending with stale surface content.
      const ctx = surface.canvas.getContext('2d');
      if (!ctx) return false;
      ctx.clearRect(0, 0, w, h);
      ctx.drawImage(result, 0, 0, result.width, result.height, 0, 0, w, h);

      surface.fabricImg.setElement(surface.canvas);
      surface.fabricImg.dirty = true;
      surface.fabricImg.setCoords();
      canvas.renderAll();
      onObjectModifiedRef.current?.();
      // Tier 2 Group F — full-image AI transform (Search-Replace /
      // Image-to-Image / Upscale / Replace-Background / Recolor) wrote
      // a fresh canvas back into the active layer's surface. Export
      // emits as raster, not stash-restored.
      onLayerRasterModifiedRef.current?.(layerId);
      return true;
    },
    [],
  );

  /** Live paint targets for an in-place pixel adjustment (Curves,
   *  Levels, etc.). Returns one entry per rasterizable layer the
   *  user is editing. Each entry exposes the live backing canvas
   *  (NOT a copy) so the caller can read+write pixels in place,
   *  plus the wrapping FabricImage so the caller can mark it
   *  `dirty` after writing.
   *
   *  Single layer → array of length 1. Group → array of one entry
   *  per rasterizable descendant (front-most first). Empty array
   *  when nothing in the active selection is rasterizable.
   *
   *  Per-layer resolution order:
   *    1. `paintSurfacesRef` — covers any layer that's been painted
   *       on (brush/eraser/retouch register entries here via
   *       `ensurePaintSurface`), and the background image once any
   *       paint has touched it.
   *    2. Direct FabricImage lookup by layerId. Catches image layers
   *       added via `addImageLayer` and the background before any
   *       paint stroke has triggered surface registration. Converts
   *       the element to a canvas (via `ensureCanvasElement`) and
   *       updates the FabricImage in place so subsequent reads stay
   *       coherent. */
  const getActiveAdjustmentTargets = useCallback(
    (
      doc: EditorDocument,
      // Explicit target overrides the active layer — used when Adjust /
      // Filter is launched from a specific layer row's overflow menu,
      // which may not be the active layer. Falls back to the active
      // layer (the picker / command path) when omitted.
      layerIdOverride?: string,
    ): Array<{ canvas: HTMLCanvasElement; fabricObj: fabric.FabricObject }> => {
      const fc = canvasRef.current;
      if (!fc) return [];
      const layerId = layerIdOverride ?? activeLayerIdRef.current;
      if (!layerId) return [];
      const node = doc.layers[layerId];
      if (!node) return [];

      const targets: Array<{ canvas: HTMLCanvasElement; fabricObj: fabric.FabricObject }> = [];
      const seen = new Set<string>();

      const tryAddLayer = (id: string) => {
        if (seen.has(id)) return;
        seen.add(id);
        const surface = paintSurfacesRef.current.get(id);
        if (surface && surface.canvas.width > 0 && surface.canvas.height > 0) {
          targets.push({ canvas: surface.canvas, fabricObj: surface.fabricImg });
          return;
        }
        // Direct FabricImage lookup.
        for (const obj of fc.getObjects()) {
          if (getObjectLayerId(obj) !== id) continue;
          if (!(obj instanceof fabric.FabricImage)) continue;
          const w = obj.width ?? 0;
          const h = obj.height ?? 0;
          if (w <= 0 || h <= 0) continue;
          const surfaceCanvas = ensureCanvasElement(
            obj.getElement() as HTMLImageElement | HTMLCanvasElement,
            w,
            h,
          );
          if (obj.getElement() !== surfaceCanvas) obj.setElement(surfaceCanvas);
          targets.push({ canvas: surfaceCanvas, fabricObj: obj });
          return;
        }
      };

      // Group: walk descendants depth-first, collect each rasterizable
      // child's target. `traverseDescendants` yields the group itself
      // first; skip non-rasterizable nodes (the group itself, nested
      // groups, text/shape/adjustment) — only image/paint can resolve.
      if (node.type === 'group') {
        for (const descendant of traverseDescendants(doc, layerId)) {
          if (descendant.type === 'image' || descendant.type === 'paint') {
            tryAddLayer(descendant.id);
          }
        }
        return targets;
      }

      // Single non-group layer. Only image/paint produce a target;
      // text/shape/adjustment/empty have no rasterizable surface.
      // Must stay aligned with `hasRasterizableTarget` in
      // `lib/layer-tree.ts` — that helper drives the Adjust
      // button's disabled state.
      if (node.type === 'image' || node.type === 'paint') {
        tryAddLayer(layerId);
      }
      return targets;
    },
    [],
  );

  /** Paint a single leaf layer's pixels onto an external target
   *  canvas at the layer's current transform. Used by the slow-path
   *  compositor in `render-pipeline.ts` to build the running
   *  composite below each adjustment layer. No-op when the layer
   *  has no FabricImage (e.g., text/shape — they render through
   *  Fabric in their normal z-order and aren't composited by the
   *  slow path in Phase 2).
   *
   *  Uses `fabricObj.render(ctx)` so the layer's full transform
   *  (position, scale, rotation, opacity) is applied. Skips
   *  invisible objects. */
  const paintLeafToCanvas = useCallback((target: HTMLCanvasElement, layerId: string): void => {
    const fc = canvasRef.current;
    if (!fc) return;
    const ctx = target.getContext('2d');
    if (!ctx) return;
    for (const obj of fc.getObjects()) {
      if (getObjectLayerId(obj) !== layerId) continue;
      if (!obj.visible) continue;
      // Fabric's `render` applies the object's full transform to
      // the target context. Save/restore so we don't leak the
      // object's matrix to subsequent paintLeaf calls.
      ctx.save();
      obj.render(ctx);
      ctx.restore();
    }
  }, []);

  /** Look up the backing canvas an adjustment layer's display
   *  FabricImage wraps. The slow-path compositor writes the
   *  post-adjustment pixels here; the caller marks the wrapping
   *  Fabric object dirty + `requestRenderAll` after the compositor
   *  returns.
   *
   *  Returns null when the layer isn't an adjustment, when no
   *  Fabric object exists yet for the layer, or when the Fabric
   *  object isn't wrapping a canvas element (shouldn't happen — the
   *  adjustment-create path in `EditorPage` allocates a canvas
   *  upfront). */
  const getAdjustmentBackingCanvas = useCallback((layerId: string): HTMLCanvasElement | null => {
    const fc = canvasRef.current;
    if (!fc) return null;
    for (const obj of fc.getObjects()) {
      if (getObjectLayerId(obj) !== layerId) continue;
      if (!(obj instanceof fabric.FabricImage)) continue;
      const el = obj.getElement();
      if (el instanceof HTMLCanvasElement) return el;
      return null;
    }
    return null;
  }, []);

  /** Non-mutating snapshot of the active layer for tools that produce a
   *  *new* layer rather than replacing the source pixels in place
   *  (e.g. Image to Image's "add as new layer" mode). Returns the
   *  layer's surface as a fresh canvas plus its scene-space bounds —
   *  width / height in scene units (i.e. already multiplied by the
   *  fabric image's scale) so callers can pass them straight into
   *  `addImageLayer`'s `fitTo`. The new image then lands exactly on
   *  top of the original, ready to move/hide/delete.
   *
   *  Returns null if there's no active layer or no surface for it. */
  /** Return a fresh CANVAS of the best sampling source for color-
   *  range / per-pixel selection ops, sized to the image rect.
   *  Prefers the active layer when it's an image; falls back to
   *  the background image. Returns null when no image is loaded.
   *
   *  IMPORTANT: returns a canvas rather than ImageData. For a 4K
   *  source ImageData is ~32MB and a 8K source is ~192MB; reading
   *  + passing that around forces expensive `putImageData` writes
   *  every time the consumer wants to render it. Consumers should
   *  use `drawImage` (GPU-accelerated) for any rendering and only
   *  call `getImageData` lazily at the points that genuinely need
   *  pixel data (sample-pick = 1×1; Apply = one-time full read).
   *
   *  Tier 1 Group D — Color Range and any future per-pixel
   *  selection tool funnel through here. */
  const getSourceCanvasForSampling = useCallback((): HTMLCanvasElement | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const imgW = imageDimsRef.current.width;
    const imgH = imageDimsRef.current.height;
    if (imgW <= 0 || imgH <= 0) return null;

    // Prefer the active image layer; otherwise the background.
    const bg = getBackgroundImage();
    const activeLayerId = activeLayerIdRef.current;
    let targetImg: fabric.FabricImage | null = null;
    if (activeLayerId) {
      for (const o of canvas.getObjects()) {
        if (getObjectLayerId(o) === activeLayerId && o instanceof fabric.FabricImage) {
          targetImg = o;
          break;
        }
      }
    }
    if (!targetImg) targetImg = bg;
    if (!targetImg) return null;

    const imgEl = targetImg.getElement() as HTMLImageElement | HTMLCanvasElement;
    const tmp = document.createElement('canvas');
    tmp.width = imgW;
    tmp.height = imgH;
    // `willReadFrequently: false` here — the typical consumer is
    // drawImage (GPU), not getImageData. Only the Apply path needs
    // pixel data and it allocates its own willReadFrequently canvas.
    const ctx = tmp.getContext('2d');
    if (!ctx) return null;
    // Anchor the source at its scene-space origin so sampling
    // coords line up with the canvas the user sees.
    const left = targetImg.left ?? 0;
    const top = targetImg.top ?? 0;
    const sX = targetImg.scaleX ?? 1;
    const sY = targetImg.scaleY ?? 1;
    const drawW = (targetImg.width ?? imgW) * sX;
    const drawH = (targetImg.height ?? imgH) * sY;
    ctx.drawImage(imgEl, left, top, drawW, drawH);
    return tmp;
  }, [getBackgroundImage]);

  const getActiveLayerSnapshot = useCallback((): {
    source: HTMLCanvasElement;
    bounds: { left: number; top: number; width: number; height: number };
  } | null => {
    const layerId = activeLayerIdRef.current;
    if (!layerId) return null;
    const canvas = canvasRef.current;
    if (!canvas) return null;

    // Lazy-register a paint surface for an image layer that has never
    // been painted on — e.g. a fresh Smart Create / Image-to-Image
    // result added via `addImageLayer`, which has a FabricImage but no
    // `paintSurfacesRef` entry until the first brush stroke. Without
    // this fallback every snapshot-based AI tool (Image to Image,
    // Replace Background, Upscale, Vectorize, Control) errored with
    // "needs an active paintable layer" when run on a generated layer.
    // Mirrors the same fallback in `runOnActiveLayerFullImage` so the
    // read-side and write-side stay symmetric.
    const surface = resolveOrRegisterPaintSurface(
      layerId,
      imageLayerCandidates(canvas),
      paintSurfacesRef.current,
    );
    if (!surface) return null;
    const w = surface.canvas.width;
    const h = surface.canvas.height;
    if (w <= 0 || h <= 0) return null;

    const source = document.createElement('canvas');
    source.width = w;
    source.height = h;
    const ctx = source.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(surface.canvas, 0, 0);

    const fab = surface.fabricImg;
    const scaleX = fab.scaleX ?? 1;
    const scaleY = fab.scaleY ?? 1;
    return {
      source,
      bounds: {
        left: fab.left ?? 0,
        top: fab.top ?? 0,
        width: w * scaleX,
        height: h * scaleY,
      },
    };
  }, []);

  /** Upscale the active layer in place — replaces the layer's
   *  FabricImage with one wrapping the transform's result canvas at
   *  scale 1, so the displayed image actually grows. If the active
   *  layer is the background, the editor canvas dimensions also
   *  grow to match the result and the viewport re-fits. For non-bg
   *  layers, the layer overflows the canvas visually until the user
   *  zooms out — same behavior Photoshop gives you when you scale a
   *  layer up beyond the document.
   *
   *  Mirrors `runOutpaintOnBackground`'s replace-the-FabricImage
   *  pattern but without any object shifting (upscale anchors the
   *  layer's top-left in scene space and grows from there). */
  const runUpscaleOnActiveLayer = useCallback(
    async (
      transform: (source: HTMLCanvasElement) => Promise<HTMLCanvasElement>,
    ): Promise<boolean> => {
      const layerId = activeLayerIdRef.current;
      if (!layerId) return false;
      const canvas = canvasRef.current;
      if (!canvas) return false;

      // Lazy-register a paint surface so Upscale applies to an image
      // layer that's never been brush-touched (e.g. a fresh Smart
      // Create result). Same fallback as `runOnActiveLayerFullImage`.
      const surface = resolveOrRegisterPaintSurface(
        layerId,
        imageLayerCandidates(canvas),
        paintSurfacesRef.current,
      );
      if (!surface) return false;

      const oldFab = surface.fabricImg;
      const oldW = surface.canvas.width;
      const oldH = surface.canvas.height;
      if (oldW <= 0 || oldH <= 0) return false;

      // Hand the transform a fresh canvas so it can't race with stroke
      // writes in flight on the live surface.
      const source = document.createElement('canvas');
      source.width = oldW;
      source.height = oldH;
      const sCtx = source.getContext('2d');
      if (!sCtx) return false;
      sCtx.drawImage(surface.canvas, 0, 0);

      const result = await transform(source);
      const newW = result.width;
      const newH = result.height;
      if (newW <= 0 || newH <= 0) return false;

      // Normalize onto a fresh canvas so downstream code (paint
      // surface, fabricImg.width) sees stable inputs.
      const offscreen = document.createElement('canvas');
      offscreen.width = newW;
      offscreen.height = newH;
      const ctx = offscreen.getContext('2d');
      if (!ctx) return false;
      ctx.drawImage(result, 0, 0);

      const isBg = isBackgroundImage(oldFab);

      // Build the replacement FabricImage. scaleX/Y stays at 1 so the
      // larger pixel grid translates to a larger displayed image.
      // Position pins to the original's left/top + origin so the layer
      // anchors at the same scene point.
      const newImg = new fabric.FabricImage(offscreen, {
        left: oldFab.left ?? 0,
        top: oldFab.top ?? 0,
        originX: oldFab.originX ?? 'left',
        originY: oldFab.originY ?? 'top',
        scaleX: 1,
        scaleY: 1,
        selectable: oldFab.selectable,
        evented: oldFab.evented,
        angle: oldFab.angle ?? 0,
        flipX: oldFab.flipX ?? false,
        flipY: oldFab.flipY ?? false,
      });
      if (isBg) {
        tagAsBackground(newImg);
        (newImg as fabric.FabricImage & { erasable: boolean }).erasable = false;
      }
      newImg.set({ objectCaching: false } as Partial<fabric.FabricImage>);
      newImg.filters = oldFab.filters ? [...oldFab.filters] : [];
      newImg.applyFilters();

      setObjectLayerId(newImg, layerId);
      paintSurfacesRef.current.set(layerId, {
        canvas: offscreen,
        fabricImg: newImg,
        offsetX: newImg.left ?? 0,
        offsetY: newImg.top ?? 0,
      });

      canvas.remove(oldFab);
      canvas.add(newImg);
      if (isBg) {
        canvas.sendObjectToBack(newImg);
        backgroundImageRef.current = newImg;
        // Grow the canvas/document dims so the upscaled bg has room.
        // Without this the new larger pixel grid extends past the
        // editor canvas edges and the user sees the dark editor
        // background bleeding through (the "black border" symptom).
        imageDimsRef.current = { width: newW, height: newH };
        ensureCanvasBoundsOverlay(canvas, newW, newH);
        onCanvasDimensions?.(newW, newH);
      }

      zoomToFit();
      canvas.renderAll();
      onObjectModifiedRef.current?.();
      return true;
    },
    [onCanvasDimensions, zoomToFit],
  );

  /** Outpaint the background image by extending its bounds in any
   *  combination of directions. The transform receives the current
   *  background surface and the requested edge extensions, and is
   *  expected to return a new canvas at the extended dimensions
   *  (oldW + left + right, oldH + up + down) with the original content
   *  composited at offset (left, up).
   *
   *  After the transform, the helper:
   *    • replaces the background FabricImage with a new one wrapping
   *      the returned canvas (mirroring `applyActiveCrop`'s pattern)
   *    • shifts every non-background object by (+left, +up) so the
   *      original content stays visually anchored
   *    • updates `imageDimsRef` and notifies the parent via
   *      `onCanvasDimensions`
   *    • re-zooms to fit the new image
   *
   *  Returns false if there is no background to outpaint or the
   *  extensions are all zero. */
  const runOutpaintOnBackground = useCallback(
    async (
      extensions: { left: number; right: number; up: number; down: number },
      transform: (
        source: HTMLCanvasElement,
        ext: { left: number; right: number; up: number; down: number },
      ) => Promise<HTMLCanvasElement>,
    ): Promise<boolean> => {
      const canvas = canvasRef.current;
      const bg = getBackgroundImage();
      if (!canvas || !bg) return false;

      const leftPx = Math.max(0, Math.floor(extensions.left));
      const rightPx = Math.max(0, Math.floor(extensions.right));
      const upPx = Math.max(0, Math.floor(extensions.up));
      const downPx = Math.max(0, Math.floor(extensions.down));
      if (leftPx + rightPx + upPx + downPx === 0) return false;

      const oldW = bg.width ?? 0;
      const oldH = bg.height ?? 0;
      if (oldW <= 0 || oldH <= 0) return false;

      // Snapshot the current bg surface into a fresh canvas the
      // transform owns. The element under bg may be the live
      // paint-surface canvas (stroke target); we don't want the
      // network call to race with stroke writes.
      const source = document.createElement('canvas');
      source.width = oldW;
      source.height = oldH;
      const sCtx = source.getContext('2d');
      if (!sCtx) return false;
      sCtx.drawImage(bg.getElement() as CanvasImageSource, 0, 0);

      const result = await transform(source, {
        left: leftPx,
        right: rightPx,
        up: upPx,
        down: downPx,
      });

      const newW = oldW + leftPx + rightPx;
      const newH = oldH + upPx + downPx;

      // Normalize the result onto a canvas at exactly newW × newH so
      // downstream code (paint surface, fabricImg.width) doesn't have
      // to deal with provider rounding or unexpected scaling.
      const offscreen = document.createElement('canvas');
      offscreen.width = newW;
      offscreen.height = newH;
      const ctx = offscreen.getContext('2d');
      if (!ctx) return false;
      ctx.drawImage(result, 0, 0, result.width, result.height, 0, 0, newW, newH);

      // Mirror applyActiveCrop's overlay-shift, but in the opposite
      // direction: original content has shifted right by leftPx and
      // down by upPx within the new canvas, so non-bg objects must
      // shift the same amount to stay visually anchored.
      canvas.getObjects().forEach((obj) => {
        if (obj === bg) return;
        obj.set({
          left: (obj.left ?? 0) + leftPx,
          top: (obj.top ?? 0) + upPx,
        });
        obj.setCoords();
      });

      const newImg = new fabric.FabricImage(offscreen, {
        left: 0,
        top: 0,
        originX: 'left',
        originY: 'top',
        scaleX: 1,
        scaleY: 1,
        selectable: false,
        evented: false,
      });
      (newImg as fabric.FabricImage & { erasable: boolean }).erasable = false;
      tagAsBackground(newImg);
      newImg.set({ objectCaching: false } as Partial<fabric.FabricImage>);
      newImg.filters = bg.filters ? [...bg.filters] : [];
      newImg.applyFilters();

      const bgLayerId = getObjectLayerId(bg);
      if (bgLayerId) {
        setObjectLayerId(newImg, bgLayerId);
        paintSurfacesRef.current.set(bgLayerId, {
          canvas: offscreen,
          fabricImg: newImg,
          offsetX: newImg.left ?? 0,
          offsetY: newImg.top ?? 0,
        });
      }

      canvas.remove(bg);
      canvas.add(newImg);
      canvas.sendObjectToBack(newImg);
      backgroundImageRef.current = newImg;
      imageDimsRef.current = { width: newW, height: newH };
      ensureCanvasBoundsOverlay(canvas, newW, newH);
      onCanvasDimensions?.(newW, newH);

      zoomToFit();
      canvas.renderAll();
      onObjectModifiedRef.current?.();
      return true;
    },
    [getBackgroundImage, onCanvasDimensions, zoomToFit],
  );

  /** Switch the marquee shape, clearing any in-progress selection. No-op if unchanged. */
  const setMarqueeShape = useCallback(
    (shape: MarqueeShape) => {
      if (marqueeRef.current.shape === shape) return;
      clearMarquee();
      marqueeRef.current.shape = shape;
    },
    [clearMarquee],
  );

  /** Tag the current background image with a layer id (used to associate the
   *  bg image with the user-facing "Background" layer entry). No-op if there's
   *  no bg yet. */
  const setBackgroundLayerId = useCallback(
    (layerId: string) => {
      const bg = getBackgroundImage();
      if (!bg) return;
      setObjectLayerId(bg, layerId);
      // Register the bg's underlying canvas as the Background layer's paint
      // surface so subsequent strokes route there.
      const w = bg.width ?? 0;
      const h = bg.height ?? 0;
      const surfaceCanvas = ensureCanvasElement(
        bg.getElement() as HTMLImageElement | HTMLCanvasElement,
        w,
        h,
      );
      if (bg.getElement() !== surfaceCanvas) bg.setElement(surfaceCanvas);
      paintSurfacesRef.current.set(layerId, {
        canvas: surfaceCanvas,
        fabricImg: bg,
        offsetX: bg.left ?? 0,
        offsetY: bg.top ?? 0,
      });
    },
    [getBackgroundImage],
  );

  /** Toggle visibility of every Fabric object tagged with the given layerId. */
  const setLayerVisibility = useCallback((layerId: string, visible: boolean) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let changed = false;
    for (const o of canvas.getObjects()) {
      if (getObjectLayerId(o) === layerId) {
        o.visible = visible;
        changed = true;
      }
    }
    if (changed) canvas.renderAll();
  }, []);

  /** Apply tree-aware effective visibility to every layer-tagged
   *  Fabric object. A layer is rendered only when its own
   *  `visible` flag is true AND every ancestor in the tree is
   *  visible too — so hiding a group cascades to all its
   *  descendants. Idempotent: only renders when something actually
   *  changes, so the useEffect that calls this on every document
   *  update doesn't burn frames when visibility didn't move. */
  const applyEffectiveVisibility = useCallback((doc: EditorDocument) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const eff = new Map<string, boolean>();
    for (const id of Object.keys(doc.layers)) {
      let visible = true;
      let cur: string | null = id;
      // Walk the parent chain — first false flag wins.
      while (cur !== null) {
        const node: import('../types').LayerNode | undefined = doc.layers[cur];
        if (!node || !node.visible) {
          visible = false;
          break;
        }
        cur = node.parentId;
      }
      eff.set(id, visible);
    }
    let changed = false;
    for (const o of canvas.getObjects()) {
      const layerId = getObjectLayerId(o);
      if (!layerId) continue;
      const newVis = eff.get(layerId) ?? true;
      if (o.visible !== newVis) {
        o.visible = newVis;
        changed = true;
      }
    }
    // `requestRenderAll` (rAF-coalesced) rather than the sync
    // `renderAll` so multiple state.document-driven effects in the
    // same React commit (applyEffectiveVisibility + applyLayerOrder
    // + slow-path) flush in one frame paint instead of N sync
    // paints — the latter compound on top of the slow-path
    // compositor's own per-pixel work and turn a 200ms recomposite
    // into a perceptible UI freeze.
    if (changed) canvas.requestRenderAll();
  }, []);

  /** Set opacity (0..1) on every Fabric object tagged with the given
   *  layerId. Idempotent — re-renders only when something actually
   *  changed so dragging the slider doesn't burn frames. */
  const setLayerOpacity = useCallback((layerId: string, opacity: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const clamped = Math.max(0, Math.min(1, opacity));
    let changed = false;
    for (const o of canvas.getObjects()) {
      if (getObjectLayerId(o) === layerId && o.opacity !== clamped) {
        o.set('opacity', clamped);
        changed = true;
      }
    }
    if (changed) canvas.renderAll();
  }, []);

  /** Set globalCompositeOperation (a.k.a. blend mode) on every Fabric
   *  object tagged with the given layerId. The string values match
   *  Canvas2D's composite operations directly so no translation is
   *  needed. Already in SERIALIZED_PROPS, so changes survive
   *  toJSON/loadFromJSON without extra plumbing. */
  const setLayerBlendMode = useCallback((layerId: string, blendMode: string) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let changed = false;
    for (const o of canvas.getObjects()) {
      if (
        getObjectLayerId(o) === layerId &&
        (o as unknown as { globalCompositeOperation?: string }).globalCompositeOperation !==
          blendMode
      ) {
        (o as unknown as { set: (k: string, v: string) => void }).set(
          'globalCompositeOperation',
          blendMode,
        );
        changed = true;
      }
    }
    if (changed) canvas.renderAll();
  }, []);

  /** Apply a layer's effect bundle (drop shadow, stroke) to every
   *  Fabric object tagged with the given layerId. Stroke dispatches
   *  by object type so it follows the *visible content* on raster
   *  layers (paint / image) via PixelStrokeFilter, and uses Fabric's
   *  native stroke on shape / text / path / line objects (where the
   *  Fabric object IS the rendered shape). Drop shadow uses Canvas2D's
   *  native shadow context, which follows alpha automatically. All
   *  three properties (.shadow, .stroke, .strokeWidth) are part of
   *  Fabric's default serialization, so per-layer effect values
   *  round-trip through toJSON / loadFromJSON. */
  const setLayerEffects = useCallback(
    (
      layerId: string,
      effects:
        | {
            dropShadow?: { color: string; blur: number; offsetX: number; offsetY: number };
            stroke?: { color: string; width: number };
            glow?: { color: string; blur: number; intensity: number };
          }
        | undefined,
    ) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ds = effects?.dropShadow;
      const st = effects?.stroke;
      const gl = effects?.glow;

      // Fabric only has one `shadow` slot per object. Drop shadow
      // gets first claim; glow falls back to that slot for non-image
      // layers (text/shape) when drop shadow isn't enabled. For
      // image layers we have a dedicated `PixelGlowFilter` that
      // composes alongside drop shadow + stroke.
      const dropShadowFabric = ds
        ? new fabric.Shadow({
            color: ds.color,
            blur: ds.blur,
            offsetX: ds.offsetX,
            offsetY: ds.offsetY,
          })
        : null;
      const glowAsShadowFabric = gl
        ? new fabric.Shadow({
            color: gl.color,
            blur: gl.blur,
            offsetX: 0,
            offsetY: 0,
          })
        : null;

      for (const o of canvas.getObjects()) {
        if (getObjectLayerId(o) !== layerId) continue;

        const isImage = (o as fabric.Object).type === 'image';
        if (isImage) {
          const img = o as fabric.FabricImage;
          // Image layers: shadow slot = drop shadow only; glow runs
          // through the pixel-aware filter so it tracks the alpha
          // silhouette (Fabric's native shadow on a transparent
          // canvas would draw a rectangle around the whole canvas).
          (img as unknown as { shadow: fabric.Shadow | null }).shadow = dropShadowFabric;

          const filters = img.filters as unknown as Array<{ type?: string }>;
          for (let i = filters.length - 1; i >= 0; i--) {
            const t = filters[i]?.type;
            if (t === PIXEL_STROKE_TYPE || t === PIXEL_GLOW_TYPE) filters.splice(i, 1);
          }
          // Order matters within the filter chain: glow first so it
          // sits behind the stroke ring in the composite.
          if (gl && gl.blur > 0 && gl.intensity > 0) {
            filters.push(
              new PixelGlowFilter({
                color: gl.color,
                blur: gl.blur,
                intensity: gl.intensity,
              }) as unknown as { type?: string },
            );
          }
          if (st && st.width > 0) {
            filters.push(
              new PixelStrokeFilter({
                color: st.color,
                width: st.width,
              }) as unknown as { type?: string },
            );
          }
          img.applyFilters();
          o.set({ strokeWidth: 0 });
        } else {
          // Non-image layers: drop shadow wins the shadow slot. If
          // only glow is enabled, glow gets the slot. If both are
          // enabled, drop shadow renders and glow is silently
          // dropped on this layer (text/shape layers can't host two
          // independent shadow passes without a bigger refactor).
          (o as unknown as { shadow: fabric.Shadow | null }).shadow =
            dropShadowFabric ?? glowAsShadowFabric;

          if (st && st.width > 0) {
            o.set({ stroke: st.color, strokeWidth: st.width });
          } else if (!(o instanceof fabric.Path)) {
            // Vector layers (fabric.Path) own their stroke via
            // `VectorPath.stroke` — leaving an effects-driven clear
            // to zero the strokeWidth would clobber the user's
            // chosen pen stroke. For shape/text the layer's stroke
            // is set elsewhere; clearing here is the historical
            // behavior we keep until a broader effects-vs-native
            // refactor.
            o.set({ strokeWidth: 0 });
          }
        }
      }
      canvas.renderAll();
    },
    [],
  );

  /** Rasterize the current marquee silhouette onto the given mask
   *  context, painting white where the selection covers. Returns
   *  true if the selection contributed any pixels. Used by the
   *  selection-to-mask path of `addLayerMask`. */
  const rasterizeMarqueeOntoMask = useCallback(
    (mCtx: CanvasRenderingContext2D, w: number, h: number): boolean => {
      const m = marqueeRef.current;
      if (!m.obj || m.isDrawing) return false;
      mCtx.fillStyle = '#ffffff';

      if (m.shape === 'rect') {
        const r = m.obj as fabric.Rect;
        const left = Math.max(0, r.left ?? 0);
        const top = Math.max(0, r.top ?? 0);
        const width = Math.min(w - left, (r.width ?? 0) * (r.scaleX ?? 1));
        const height = Math.min(h - top, (r.height ?? 0) * (r.scaleY ?? 1));
        if (width <= 0 || height <= 0) return false;
        mCtx.fillRect(left, top, width, height);
        return true;
      }
      if (m.shape === 'ellipse') {
        const e = m.obj as fabric.Ellipse;
        const rx = (e.rx ?? 0) * (e.scaleX ?? 1);
        const ry = (e.ry ?? 0) * (e.scaleY ?? 1);
        if (rx <= 0 || ry <= 0) return false;
        const cx = (e.left ?? 0) + rx;
        const cy = (e.top ?? 0) + ry;
        mCtx.beginPath();
        mCtx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
        mCtx.fill();
        return true;
      }
      if ((m.shape === 'lasso' || m.shape === 'polygon') && m.points.length >= 3) {
        mCtx.beginPath();
        mCtx.moveTo(m.points[0].x, m.points[0].y);
        for (let i = 1; i < m.points.length; i++) {
          mCtx.lineTo(m.points[i].x, m.points[i].y);
        }
        mCtx.closePath();
        mCtx.fill();
        return true;
      }
      if (m.shape === 'wand' && m.wandMaskCanvas) {
        // Draw the cached wand-mask canvas directly — its alpha
        // channel is already 1-bit "selected = opaque".
        mCtx.drawImage(m.wandMaskCanvas, 0, 0);
        return true;
      }
      return false;
    },
    [],
  );

  /** Attach a mask to a layer. The mask is implemented via Fabric's
   *  native `clipPath` (destination-in compositing under the hood,
   *  which is exactly mask semantics) — this is why we don't need
   *  a custom render-pipeline path for basic masks.
   *
   *  Three initial-fill modes (caller-selected via `opts`):
   *
   *  - `fromMarquee: true` — fill the RAW alpha from the active
   *    marquee silhouette (Photoshop's "Add Mask From Selection").
   *    No-op fallback to reveal-all if no marquee is active.
   *
   *  - `vectorPath` set — Tier 2 Group A PR-D. Rasterize the
   *    provided VectorPath into RAW so the mask starts shaped
   *    by the path geometry. Skips the wasted reveal-all → re-
   *    rasterize round-trip the vector-mask-sync effect would
   *    otherwise do on the first render. Caller is responsible
   *    for flagging the resulting LayerMask with
   *    `kind: 'vector'` + the same `vectorPath` before handing
   *    to the reducer.
   *
   *  - default — RAW is opaque white everywhere (reveal-all).
   *    Tier 0/1 behavior. Brush in mask mode paints the alpha.
   *
   *  Returns the LayerMask config the caller should hand to
   *  `setLayerMask` in the reducer, or null if no target layer was
   *  found. */
  const addLayerMask = useCallback(
    (
      layerId: string,
      opts?: { fromMarquee?: boolean; vectorPath?: VectorPath },
    ): LayerMask | null => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      const imgW = imageDimsRef.current.width;
      const imgH = imageDimsRef.current.height;
      if (imgW <= 0 || imgH <= 0) return null;

      // Find the layer's Fabric object. Empty layers don't have
      // one yet (the user hasn't painted into them) — for those we
      // lazily create a transparent paint surface, the same way
      // a brush stroke would. Without this fallback, "Add Mask"
      // on a fresh "+ Layer" silently no-ops, which is exactly
      // the bug reported during Step 4 QA.
      let targetObj: fabric.Object | null = null;
      for (const o of canvas.getObjects()) {
        if (getObjectLayerId(o) === layerId) {
          targetObj = o;
          break;
        }
      }
      if (!targetObj) {
        const surface = ensurePaintSurfaceRef.current?.(layerId);
        targetObj = surface?.fabricImg ?? null;
      }
      if (!targetObj) return null;

      // Build the RAW alpha-mask canvas (user paint target).
      // `bakeStrokeOntoMask` writes user strokes into this canvas;
      // `materializeMaskClipPath` derives the EFFECTIVE alpha (with
      // feather/invert/density baked in) into a separate canvas
      // wrapped by the Fabric clipPath. At creation time the mask
      // config is identity (enabled, !inverted, density=1, feather=0)
      // so effective ≡ raw — the first materialize is a single
      // drawImage. The split exists for subsequent config changes.
      const rawCanvas = document.createElement('canvas');
      rawCanvas.width = imgW;
      rawCanvas.height = imgH;
      const rCtx = rawCanvas.getContext('2d', { willReadFrequently: true });
      if (!rCtx) return null;

      const filledFromMarquee =
        opts?.fromMarquee === true && rasterizeMarqueeOntoMask(rCtx, imgW, imgH);
      const filledFromVectorPath = !filledFromMarquee && opts?.vectorPath !== undefined;
      if (filledFromVectorPath) {
        // Vector mask seed — rasterize the supplied path directly
        // into RAW. Eliminates the wasted reveal-all fill the
        // default branch below would otherwise do, plus the
        // matching re-rasterize that the vector-mask-sync effect
        // would queue for the first frame after this returns
        // (since RAW would mismatch the path's rasterized output).
        rasterizeVectorMaskInto(rawCanvas, opts!.vectorPath!, imgW, imgH);
      } else if (!filledFromMarquee) {
        // Reveal-all default — entirely opaque white.
        rCtx.fillStyle = '#ffffff';
        rCtx.fillRect(0, 0, imgW, imgH);
      }

      // EFFECTIVE canvas — the one Fabric's clipPath wraps. Identity
      // materialize at creation is just a drawImage; the split costs
      // ~32MB extra per masked layer at 4K, which the soft mask
      // feather/invert/density UX needs (otherwise toggling feather
      // would have nowhere to recover the original raw alpha from).
      const effectiveCanvas = document.createElement('canvas');
      effectiveCanvas.width = imgW;
      effectiveCanvas.height = imgH;

      const initialMaskConfig: LayerMask = {
        id: `lmask_${layerId}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        enabled: true,
        inverted: false,
        linked: true,
        density: 1,
        feather: 0,
        fabricObjectId: '',
      };
      materializeEffectiveMaskInto(
        effectiveCanvas,
        rawCanvas,
        maskMaterializationConfig(initialMaskConfig),
        imgW,
        imgH,
      );

      // objectCaching is left at Fabric's default (true). The mask
      // canvas is multi-megapixel; without caching, Fabric
      // rasterizes the mask FabricImage on every parent render,
      // which freezes the UI on full-size photos. We invalidate
      // the cache by setting `dirty = true` in `bakeStrokeOntoMask`
      // after each paint stroke, so caching is correct here.
      //
      // NOTE: do NOT set `excludeFromExport: true` on the mask.
      // Fabric's `toObject` checks `!clipPath.excludeFromExport`
      // before serializing it, so flagging the mask as
      // export-excluded silently drops it from undo snapshots and
      // the IndexedDB session JSON — which is what broke
      // mask persistence across refresh and undo. The mask is
      // attached via `clipPath` only (never `canvas.add`), so it
      // never appears in user PNG exports anyway, regardless of
      // this flag.
      const maskImg = new fabric.FabricImage(effectiveCanvas, {
        left: 0,
        top: 0,
        originX: 'left',
        originY: 'top',
        selectable: false,
        evented: false,
      });
      const maskFabricId = `fmask_${layerId}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      (maskImg as fabric.FabricImage & { id: string }).id = maskFabricId;
      // absolutePositioned: clip in canvas coords, not the layer's
      // local coords. Without this the mask would translate with
      // the layer's transform, which is the opposite of what users
      // expect for a typical "fixed canvas mask."
      (maskImg as fabric.FabricObject & { absolutePositioned: boolean }).absolutePositioned = true;
      attachRawMaskCanvas(maskImg, rawCanvas);
      installMaskClipPathSerializer(maskImg);

      targetObj.clipPath = maskImg;
      canvas.requestRenderAll();

      initialMaskConfig.fabricObjectId = maskFabricId;
      maskConfigsRef.current.set(layerId, initialMaskConfig);
      return initialMaskConfig;
    },
    [rasterizeMarqueeOntoMask],
  );

  /** Detach the mask from a layer. Reverses `addLayerMask` —
   *  removes the clipPath, drops the Fabric mask object reference.
   *  No-op if the layer has no mask. */
  const removeLayerMask = useCallback((layerId: string): void => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    for (const o of canvas.getObjects()) {
      if (getObjectLayerId(o) === layerId && o.clipPath) {
        o.clipPath = undefined;
      }
    }
    maskConfigsRef.current.delete(layerId);
    canvas.requestRenderAll();
  }, []);

  /**
   * Look up (or lazily create) a paint surface for the given layer.
   *
   * Three cases:
   *
   * 1. **Cached.** Return the cached entry if its fabricImg is still
   *    on the canvas.
   *
   * 2. **FabricImage-backed layer** (bg image, pasted image, prior
   *    rasterize result). The surface canvas IS the FabricImage's
   *    own canvas — strokes write directly onto its pixels so the
   *    bbox stays tight to the image content, retouch results move
   *    with the layer in Move tool, and the per-dab destSample
   *    already contains the underlying image (no separate baseline
   *    composite needed). When the image is at non-identity scale,
   *    rotation, or skew, we auto-rasterize first via
   *    `rasterizeLayerToImage` so the resulting FabricImage is at
   *    identity-scale with no rotation/skew — simple translate from
   *    scene → canvas suffices, no inverse matrix.
   *
   *    `offsetX/Y` = the layer's scene (left, top). Bg is at (0, 0).
   *    A pasted layer typically lands at the marquee's scene
   *    position with scaleX=1, scaleY=1, no rotation, so no auto-
   *    rasterize triggers.
   *
   * 3. **Empty / paint layer** (e.g. one added via the LayersPanel
   *    "+ Add Layer" button) — no FabricImage yet. Falls back to a
   *    fresh doc-sized transparent canvas wrapped in a FabricImage
   *    tagged with this layerId. Surface offset is (0, 0) since the
   *    overlay spans the document.
   *
   * Returns null on a hard failure (no canvas, no doc dims, no
   * usable target).
   */
  const ensurePaintSurface = useCallback(
    (layerId: string): LayerPaintSurface | null => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      const cached = paintSurfacesRef.current.get(layerId);
      if (cached && canvas.contains(cached.fabricImg)) return cached;

      // Case 2 — adopt a FabricImage tagged with this layerId as
      // the paint surface. Bg image AND non-bg image layers take
      // this path. Non-identity transforms trigger auto-rasterize
      // first.
      let imgObj: fabric.FabricImage | null = null;
      for (const obj of canvas.getObjects()) {
        if (!(obj instanceof fabric.FabricImage)) continue;
        if (getObjectLayerId(obj) !== layerId) continue;
        imgObj = obj;
        break;
      }
      if (imgObj) {
        const needsRasterize =
          (imgObj.scaleX ?? 1) !== 1 ||
          (imgObj.scaleY ?? 1) !== 1 ||
          (imgObj.angle ?? 0) !== 0 ||
          (imgObj.skewX ?? 0) !== 0 ||
          (imgObj.skewY ?? 0) !== 0 ||
          (imgObj.flipX ?? false) ||
          (imgObj.flipY ?? false);
        if (needsRasterize) {
          // FabricImage path: `rasterizeLayerToImage` refuses to
          // rasterize an existing FabricImage (it's the
          // shape/text/vector → image conversion helper). Bake the
          // transform into the underlying canvas ourselves so copy /
          // cut / paste capture the post-transform pixel state. The
          // user-reported symptom was: "if I transform a pasted
          // layer then copy/paste, it doesn't grab the
          // post-transformed pixels" — exactly because this branch
          // used to bail.
          const br = imgObj.getBoundingRect();
          const baked = (
            imgObj as fabric.FabricImage & {
              toCanvasElement: () => HTMLCanvasElement;
            }
          ).toCanvasElement();
          if (baked.width <= 0 || baked.height <= 0) {
            // `toCanvasElement` failed to produce a usable canvas
            // (zero-area, missing dimensions, etc.). For FabricImage
            // there's no further rasterize path —
            // `rasterizeLayerToImage` explicitly refuses image
            // instances — so bail. Callers (every `ensurePaintSurface`
            // consumer) already abort cleanly on null.
            return null;
          }
          imgObj.setElement(baked);
          // `toCanvasElement` renders the FabricImage through its
          // full pipeline INCLUDING filters and opacity, so `baked`
          // already contains those effects. The FabricImage's
          // `filters` array would then re-apply on the next render,
          // double-applying. Clear it so the rendered pixels match
          // what the user saw before the bake.
          imgObj.filters = [];
          imgObj.set({
            left: br.left,
            top: br.top,
            width: baked.width,
            height: baked.height,
            scaleX: 1,
            scaleY: 1,
            angle: 0,
            skewX: 0,
            skewY: 0,
            flipX: false,
            flipY: false,
            originX: 'left',
            originY: 'top',
          });
          imgObj.setCoords();
        }
      }
      if (imgObj) {
        const w = imgObj.width ?? 0;
        const h = imgObj.height ?? 0;
        if (w > 0 && h > 0) {
          const surfaceCanvas = ensureCanvasElement(
            imgObj.getElement() as HTMLImageElement | HTMLCanvasElement,
            w,
            h,
          );
          if (imgObj.getElement() !== surfaceCanvas) imgObj.setElement(surfaceCanvas);
          installPaintSurfaceSerializer(imgObj);
          const entry = {
            canvas: surfaceCanvas,
            fabricImg: imgObj,
            offsetX: imgObj.left ?? 0,
            offsetY: imgObj.top ?? 0,
          };
          paintSurfacesRef.current.set(layerId, entry);
          return entry;
        }
      }

      // Rasterize-before-paint gate. By the time we reach here, no
      // existing FabricImage on the canvas is tagged with this
      // layerId — so the layer is one of: `empty` (silent
      // transition — the user explicitly added a blank layer to
      // paint on), `shape` / `text` / `vector` (destructive — the
      // user is about to lose the layer's editable structure to a
      // raster surface). Only the destructive cases prompt. Empty
      // falls through to Case 3 and creates the surface silently.
      const layerType = getLayerTypeRef.current?.(layerId);
      if (layerType === 'shape' || layerType === 'text' || layerType === 'vector') {
        if (!paintPromptPendingRef.current.has(layerId)) {
          paintPromptPendingRef.current.add(layerId);
          const tool = activeToolRef.current;
          const action: 'brush' | 'eraser' | 'retouch' | 'fill' | 'ai' =
            tool === 'eraser'
              ? 'eraser'
              : tool === 'retouch'
                ? 'retouch'
                : tool === 'fill'
                  ? 'fill'
                  : tool === 'ai'
                    ? 'ai'
                    : 'brush';
          onRasterizeBeforePaintNeededRef.current?.({ layerId, sourceType: layerType, action });
        }
        // Abort the paint op. EditorPage's dialog will call
        // `approveLayerForPaint` on confirm; the user redoes their
        // action after that, at which point the layer is a
        // FabricImage and Case 2 above handles it.
        return null;
      }

      // Case 3 — empty layer with no FabricImage. Fresh doc-sized
      // transparent canvas + FabricImage.
      const dims = imageDimsRef.current;
      if (dims.width <= 0 || dims.height <= 0) return null;
      const surfaceCanvas = document.createElement('canvas');
      surfaceCanvas.width = dims.width;
      surfaceCanvas.height = dims.height;
      // Lock the canvas into CPU-readable mode at creation. The
      // Adjustments modal's source-capture path and the slow-path
      // compositor both call `getImageData` on paint surfaces; the
      // first getContext call decides the backing store policy, so
      // we set the flag here before anything else touches the
      // canvas. See the matching note in `ensureCanvasElement`.
      surfaceCanvas.getContext('2d', { willReadFrequently: true });
      const fabricImg = new fabric.FabricImage(surfaceCanvas, {
        left: 0,
        top: 0,
        originX: 'left',
        originY: 'top',
        scaleX: 1,
        scaleY: 1,
        selectable: false,
        evented: false,
        objectCaching: false,
      });
      (fabricImg as fabric.FabricImage & { erasable: boolean }).erasable = false;
      setObjectLayerId(fabricImg, layerId);
      installPaintSurfaceSerializer(fabricImg);
      canvas.add(fabricImg);

      const entry = { canvas: surfaceCanvas, fabricImg, offsetX: 0, offsetY: 0 };
      paintSurfacesRef.current.set(layerId, entry);
      return entry;
    },
    [rasterizeLayerToImage],
  );
  ensurePaintSurfaceRef.current = ensurePaintSurface;

  /**
   * Render a Fabric Path onto a paint surface's canvas, applying the active
   * marquee as a clip and the eraser's destination-out compositing if the
   * eraser tool was used.
   *
   * Implementation: render the path onto a *temporary* StaticCanvas to get a
   * clean Fabric rendering pipeline (transforms, brush settings, anti-
   * aliasing) with no shared state with the main canvas. Then `drawImage` the
   * resulting bitmap onto our surface with the appropriate compositing/clip.
   * This avoids cache contamination from rendering Path objects directly onto
   * the surface's 2D context.
   *
   * The path is removed from the main canvas before being added to the temp
   * canvas (Fabric objects belong to one canvas at a time).
   */
  const bakeStrokeOntoSurface = useCallback(
    (path: fabric.Object, layerId: string, isEraser: boolean) => {
      const surface = ensurePaintSurface(layerId);
      if (!surface) return false;
      const ctx = surface.canvas.getContext('2d');
      if (!ctx) return false;

      const w = surface.canvas.width;
      const h = surface.canvas.height;
      const surfaceLeft = surface.fabricImg.left ?? 0;
      const surfaceTop = surface.fabricImg.top ?? 0;

      // Render the path onto a fresh temp canvas via Fabric's pipeline. The
      // path is expected to already be removed from the main canvas by the
      // caller.
      //
      // Critical for non-BG layers: the path is authored in SCENE coords
      // (Fabric's freeDrawingBrush captures pointer scene positions). If we
      // left the temp canvas at the identity viewport, scene coords outside
      // the surface's `(0..w, 0..h)` pixel rect would render past the temp
      // viewport and get cropped to nothing — user-visible as "stroke
      // disappears on mouse-up" the moment the layer is moved off-origin or
      // the user paints near the edge. Shifting the viewport by
      // `-(surfaceLeft, surfaceTop)` makes scene coords map directly to
      // surface pixel coords on the temp, after which we composite onto the
      // surface with no destination translate.
      const tempEl = document.createElement('canvas');
      tempEl.width = w;
      tempEl.height = h;
      const tempCanvas = new fabric.StaticCanvas(tempEl, {
        width: w,
        height: h,
        enableRetinaScaling: false,
        renderOnAddRemove: false,
        backgroundColor: 'transparent',
      });
      tempCanvas.setViewportTransform([1, 0, 0, 1, -surfaceLeft, -surfaceTop]);
      tempCanvas.add(path);
      tempCanvas.renderAll();

      const m = marqueeRef.current;
      const hasMarquee = !!m.obj && !m.isDrawing;

      // Marquee-bound paint: if the stroke's scene bbox lies entirely
      // outside the marquee, the clip will eat every pixel and the user
      // sees a flash of live preview followed by nothing. Surface a
      // toast so they understand why. Same check applies to eraser
      // strokes (same bake path). Fires before any rendering so the
      // expensive paint pipeline below short-circuits.
      if (hasMarquee) {
        const pathBBox = path.getBoundingRect();
        const marqueeBBox = computeMarqueeSceneBBox(m);
        if (marqueeBBox && bboxesDisjoint(pathBBox, marqueeBBox)) {
          onMarqueeActionNoPixelsRef.current?.(isEraser ? 'eraser' : 'brush');
          tempCanvas.dispose();
          return false;
        }
      }

      // For eraser strokes, decouple the bake's alpha from the live preview
      // color (a fixed semi-transparent gray). Build a fresh mask where any
      // stroke pixel becomes opaque at the user's eraser-opacity setting,
      // so 1.0 fully erases and 0.5 reduces alpha by 50%, regardless of
      // the preview color's own alpha. For draw strokes we use tempEl
      // unchanged so opacity-aware blending keeps working.
      const stamp = isEraser ? buildEraserMask(tempEl, eraserOpacityRef.current) : tempEl;

      ctx.save();
      // Soft-edge feather. The fast path is a canvas2d
      // `filter: blur(Npx)` on the bake `drawImage` so the cost lives
      // in the browser's GPU compositor. But canvas `ctx.filter` is
      // unsupported on many mobile browsers (iOS Safari <16.4, various
      // WebViews), where it silently no-ops and the feather would have
      // no effect (crisp edges). So when it's unsupported we pre-blur
      // the drawn stamp in JS (premultiplied-alpha Gaussian, matching
      // the GPU result) instead. 0 = no feather (crisp anti-aliased
      // edge, matching the live preview); eraser strokes feather the
      // same way so subtracted alpha falls off softly. The
      // `ctx.restore()` below clears any filter so it can't taint
      // subsequent draws on this context.
      const featherPx = Math.max(0, brushFeatherRef.current);
      const cssFeather = featherPx > 0 && supportsCanvasFilterBlur();
      const jsFeather = featherPx > 0 && !supportsCanvasFilterBlur();
      if (cssFeather) ctx.filter = `blur(${featherPx}px)`;
      if (hasMarquee && m.shape === 'wand' && m.wandMaskCanvas) {
        const masked = document.createElement('canvas');
        masked.width = w;
        masked.height = h;
        const mCtx = masked.getContext('2d');
        if (mCtx) {
          mCtx.drawImage(stamp, 0, 0);
          mCtx.globalCompositeOperation = 'destination-in';
          // wandMaskCanvas is in scene coords (doc-sized). Shift by
          // -(surfaceLeft, surfaceTop) so its scene origin lands at the
          // surface's local origin.
          mCtx.drawImage(m.wandMaskCanvas, -surfaceLeft, -surfaceTop);
          if (isEraser) ctx.globalCompositeOperation = 'destination-out';
          ctx.drawImage(jsFeather ? featherStampCanvas(masked, featherPx) : masked, 0, 0);
        }
      } else {
        if (hasMarquee) applyMarqueeClipToContext(ctx, m, surfaceLeft, surfaceTop);
        if (isEraser) ctx.globalCompositeOperation = 'destination-out';
        ctx.drawImage(jsFeather ? featherStampCanvas(stamp, featherPx) : stamp, 0, 0);
      }
      ctx.restore();

      tempCanvas.dispose();

      // Force Fabric to re-bind the FabricImage to its (now-modified) source
      // canvas. setElement clears internal texture handles and runs
      // `_initConfig`, which is what makes new pixels show up reliably across
      // multiple consecutive bakes — `dirty = true` alone isn't sufficient.
      surface.fabricImg.setElement(surface.canvas);
      surface.fabricImg.dirty = true;
      surface.fabricImg.setCoords();
      return true;
    },
    [ensurePaintSurface],
  );

  /** Render a brush stroke onto the active layer's mask alpha
   *  instead of its pixel surface. Brush strokes paint white onto
   *  the mask (revealing the layer); eraser strokes erase mask
   *  alpha (hiding the layer). The "color" of the user's brush is
   *  ignored in mask mode — only the alpha shape of the stroke
   *  matters, which is the natural Photoshop-like behavior.
   *
   *  Marquee clipping (when a finalized selection is active) applies
   *  here too, so users can constrain mask edits to a region. The
   *  layer's Fabric object is marked dirty afterwards so its
   *  cached render re-evaluates the clipPath. */
  const bakeStrokeOntoMask = useCallback(
    (path: fabric.Object, layerId: string, isEraser: boolean): MaskBakeResult => {
      const canvas = canvasRef.current;
      if (!canvas) return 'no-mask';

      // Vector masks don't accept brush strokes — their alpha is
      // derived from `mask.vectorPath`, so painting would either
      // do nothing visible (next vector-mask-sync rasterize would
      // wipe it) or require a destructive conversion. Match
      // Photoshop / Affinity: brush is a no-op, caller toasts a
      // one-time hint pointing the user at "Convert to Raster
      // Mask." `maskConfigsRef` is the canvas-side mirror of
      // `LayerMask` config (synced from `state.document` via
      // `setMaskConfig`) — reading kind from here avoids a
      // dispatcher round-trip during stroke commit.
      const maskConfig = maskConfigsRef.current.get(layerId);
      if (maskConfig && maskConfig.kind === 'vector') {
        onMaskPaintBlockedRef.current?.(layerId);
        return 'vector-blocked';
      }

      // Locate the leaf's clipPath FabricImage. We paint into its RAW
      // canvas (the user-paint target) and re-materialize the
      // EFFECTIVE alpha (the clipPath element) afterwards so feather/
      // invert/density show in-stroke. Pre-materialization sessions
      // have only the effective canvas saved — `ensureRawMaskCanvas`
      // lazily copies it into a raw slot on first stroke.
      let targetFabric: fabric.FabricObject | null = null;
      let clipPath: fabric.FabricImage | null = null;
      for (const o of canvas.getObjects()) {
        if (getObjectLayerId(o) !== layerId) continue;
        const cp = (o as fabric.FabricObject).clipPath;
        if (!(cp instanceof fabric.FabricImage)) continue;
        targetFabric = o as fabric.FabricObject;
        clipPath = cp;
        break;
      }
      if (!targetFabric || !clipPath) return 'no-mask';
      const effectiveCanvas = clipPath.getElement();
      if (!(effectiveCanvas instanceof HTMLCanvasElement)) return 'no-mask';
      const rawCanvas = ensureRawMaskCanvas(clipPath);
      if (!rawCanvas) return 'no-mask';

      const w = rawCanvas.width;
      const h = rawCanvas.height;

      // Render path onto a temp canvas via Fabric. The user's brush
      // color is whatever they had set; we only care about the
      // stroke's alpha shape.
      const tempEl = document.createElement('canvas');
      tempEl.width = w;
      tempEl.height = h;
      const tempCanvas = new fabric.StaticCanvas(tempEl, {
        width: w,
        height: h,
        enableRetinaScaling: false,
        renderOnAddRemove: false,
        backgroundColor: 'transparent',
      });
      tempCanvas.add(path);
      tempCanvas.renderAll();

      const ctx = rawCanvas.getContext('2d');
      if (!ctx) {
        tempCanvas.dispose();
        return 'no-mask';
      }

      const m = marqueeRef.current;
      const hasMarquee = !!m.obj && !m.isDrawing;

      ctx.save();
      if (hasMarquee) applyMarqueeClipToContext(ctx, m);

      if (isEraser) {
        // Eraser in mask mode subtracts mask alpha. Same gotcha as
        // bakeStrokeOntoSurface: the eraser's live preview color is
        // a fixed semi-transparent gray (for visibility against any
        // background), so the rendered tempEl's pixels have alpha
        // ≪ 1 even at 100% eraser opacity. buildEraserMask
        // re-normalizes the stroke to fully-opaque at the user's
        // eraser-opacity setting, so 1.0 fully clears mask alpha
        // and 0.5 halves it, independent of the preview color.
        const stamp = buildEraserMask(tempEl, eraserOpacityRef.current);
        ctx.globalCompositeOperation = 'destination-out';
        ctx.drawImage(stamp, 0, 0);
      } else {
        // Brush in mask mode paints white. Recolor the temp canvas's
        // RGB to white via source-in (preserves alpha shape, replaces
        // RGB with #ffffff), then composite source-over onto the
        // mask. Result: existing mask alpha is unioned with the
        // stroke's alpha, RGB stays white throughout.
        const tempCtx = tempEl.getContext('2d');
        if (tempCtx) {
          tempCtx.globalCompositeOperation = 'source-in';
          tempCtx.fillStyle = '#ffffff';
          tempCtx.fillRect(0, 0, w, h);
        }
        ctx.globalCompositeOperation = 'source-over';
        ctx.drawImage(tempEl, 0, 0);
      }
      ctx.restore();
      tempCanvas.dispose();
      // RAW just changed — drop the cached dataURL so the next snapshot
      // re-encodes from the new RAW. The materialize below only touches
      // EFFECTIVE (which isn't what we serialize), so one invalidate
      // here covers the whole bake.
      invalidateMaskRawSrcCache(clipPath);

      // Materialize raw → effective using the latest mask config
      // mirrored from React state. Missing entry = identity (the
      // addLayerMask defaults), so the materialize is a single
      // drawImage — same perf as the pre-split fast path. The
      // earlier vector-mask guard already pulled config once;
      // re-read here so the materialize uses the very latest
      // value even if a parallel setMaskConfig snuck in between.
      const latestConfig = maskConfigsRef.current.get(layerId);
      const matConfig = latestConfig
        ? maskMaterializationConfig(latestConfig)
        : { enabled: true, inverted: false, density: 1, feather: 0 };
      materializeEffectiveMaskInto(effectiveCanvas, rawCanvas, matConfig, w, h);

      clipPath.dirty = true;
      targetFabric.dirty = true;
      canvas.requestRenderAll();
      return 'painted';
    },
    [],
  );

  /** Push the latest mask config for a layer into the canvas-side
   *  mirror. EditorPage calls this whenever `state.document`'s mask
   *  config changes — `bakeStrokeOntoMask` reads from the mirror to
   *  apply the right feather/invert/density during in-stroke
   *  materialization. `null` mask clears the entry. */
  const setMaskConfig = useCallback((layerId: string, mask: LayerMask | null): void => {
    if (mask) maskConfigsRef.current.set(layerId, mask);
    else maskConfigsRef.current.delete(layerId);
  }, []);

  /** Read a layer's RAW mask canvas (the user-paint target, source
   *  of truth for raster masks). Returns null when the layer has
   *  no mask or the mask isn't backed by a canvas yet.
   *
   *  Used by the raster→vector mask conversion path (Tier 2 Group
   *  A PR-D) — the trace operates on the alpha channel of RAW so
   *  the resulting vector path matches what the user painted.
   *  Reading from EFFECTIVE instead would bake feather / invert /
   *  density into the traced geometry, which isn't what users
   *  expect from a "convert mask kind" operation. */
  const getRawMaskCanvas = useCallback((layerId: string): HTMLCanvasElement | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    for (const obj of canvas.getObjects()) {
      if (getObjectLayerId(obj) !== layerId) continue;
      const cp = (obj as fabric.FabricObject).clipPath;
      if (!(cp instanceof fabric.FabricImage)) continue;
      return readRawMaskCanvas(cp);
    }
    return null;
  }, []);

  /** Re-rasterize a vector mask's path into RAW, then re-derive
   *  EFFECTIVE. Called by EditorPage's vector-mask-sync effect
   *  whenever a vector mask's `vectorPath` changes (Path Edit
   *  Apply on a mask, raster→vector seed, etc.). The path's
   *  filled region becomes opaque white in RAW; the materialize
   *  step then applies feather/invert/density/enabled on top.
   *
   *  Tier 2 Group A PR-D. Mirror of `materializeMaskClipPath` —
   *  same target lookup, same dirty-flag flow, but the input is
   *  a vector path instead of a config change.
   *
   *  Returns true when a clipPath was found and the path was
   *  rasterized + materialized. False on no-match (no mask for
   *  this layer, or mask is raster-kind). */
  const setMaskVectorPath = useCallback(
    (layerId: string, vectorPath: VectorPath, mask: LayerMask): boolean => {
      const canvas = canvasRef.current;
      if (!canvas) return false;
      for (const obj of canvas.getObjects()) {
        if (getObjectLayerId(obj) !== layerId) continue;
        const cp = (obj as fabric.FabricObject).clipPath;
        if (!(cp instanceof fabric.FabricImage)) continue;
        const effective = cp.getElement();
        if (!(effective instanceof HTMLCanvasElement)) continue;
        const raw = ensureRawMaskCanvas(cp);
        if (!raw) continue;
        rasterizeVectorMaskInto(raw, vectorPath, raw.width, raw.height);
        invalidateMaskRawSrcCache(cp);
        materializeEffectiveMaskInto(
          effective,
          raw,
          maskMaterializationConfig(mask),
          effective.width,
          effective.height,
        );
        cp.dirty = true;
        (obj as fabric.FabricObject).dirty = true;
        maskConfigsRef.current.set(layerId, mask);
        canvas.requestRenderAll();
        return true;
      }
      return false;
    },
    [],
  );

  /** Re-derive a layer's effective mask alpha (the clipPath element)
   *  from its raw mask canvas + supplied config. Called by EditorPage
   *  whenever a mask config field changes (Disable / Invert /
   *  Density / Feather) — feathered/inverted/density-attenuated alpha
   *  is baked into the effective canvas here, then Fabric's native
   *  destination-in clipPath compositing renders it correctly.
   *
   *  Returns true when a clipPath was found and materialized. */
  const materializeMaskClipPath = useCallback((layerId: string, mask: LayerMask): boolean => {
    const canvas = canvasRef.current;
    if (!canvas) return false;
    let didMaterialize = false;
    for (const obj of canvas.getObjects()) {
      if (getObjectLayerId(obj) !== layerId) continue;
      const cp = (obj as fabric.FabricObject).clipPath;
      if (!(cp instanceof fabric.FabricImage)) continue;
      const effective = cp.getElement();
      if (!(effective instanceof HTMLCanvasElement)) continue;
      const raw = ensureRawMaskCanvas(cp);
      if (!raw) continue;
      materializeEffectiveMaskInto(
        effective,
        raw,
        maskMaterializationConfig(mask),
        effective.width,
        effective.height,
      );
      cp.dirty = true;
      (obj as fabric.FabricObject).dirty = true;
      didMaterialize = true;
    }
    if (didMaterialize) {
      maskConfigsRef.current.set(layerId, mask);
      canvas.requestRenderAll();
    }
    return didMaterialize;
  }, []);

  /**
   * Reorder canvas objects to match the layer list. `orderedLayerIds[0]` is the
   * top-of-panel / front-of-canvas layer. Objects without a layerId keep their
   * relative positions; marquee and crop overlays are always brought to front.
   */
  const applyLayerOrder = useCallback((orderedLayerIds: readonly string[]) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Iterate from back-most to front-most. bringObjectToFront leaves the most
    // recently brought item on top, so the last id processed ends up at the top.
    for (let i = orderedLayerIds.length - 1; i >= 0; i--) {
      const layerId = orderedLayerIds[i];
      for (const obj of canvas.getObjects()) {
        if (getObjectLayerId(obj) === layerId) {
          canvas.bringObjectToFront(obj);
        }
      }
    }
    // Keep UI overlays above all layer content. The Clone Stamp
    // reticle bars belong here too — without this, every layers
    // re-render buries the reticle behind the active layer's
    // paint surface and the user only sees it for the brief
    // moment between renderCloneReticle and the next React tick.
    for (const obj of canvas.getObjects()) {
      const flags = obj as unknown as {
        _isMarquee?: boolean;
        _isCropRect?: boolean;
        _isCloneReticle?: boolean;
        _isQuickMaskOverlay?: boolean;
        _isPenPreview?: boolean;
        _isPathEditOverlay?: boolean;
        _isSliceOverlay?: boolean;
      };
      if (
        flags._isMarquee ||
        flags._isCropRect ||
        flags._isCloneReticle ||
        flags._isQuickMaskOverlay ||
        flags._isPenPreview ||
        flags._isPathEditOverlay ||
        flags._isSliceOverlay
      ) {
        canvas.bringObjectToFront(obj);
      }
    }
    // `requestRenderAll` rather than the sync `renderAll` — see the
    // matching note in `applyEffectiveVisibility`.
    canvas.requestRenderAll();
  }, []);

  /**
   * Select the canvas objects tagged with the given layerId. Single object →
   * setActiveObject; multiple → ActiveSelection. Forces target objects to be
   * selectable + evented so the transform handles render even if a previous
   * tool (marquee, crop) had locked them. The bg image is left non-selectable
   * to match Photoshop's locked-Background behavior.
   *
   * Exception: when the active tool needs layers LOCKED (marquee, retouch),
   * skip the unlock + setActiveObject entirely. Panel-side selection in those
   * tools is purely a state-level indicator (LayersPanel row highlight); the
   * tool's canvas-level mouse handlers don't want Fabric's hit-test running
   * Move-tool drag in parallel with their custom stamping/marquee handlers.
   */
  const selectLayerObjects = useCallback(
    (layerId: string) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const bg = getBackgroundImage();
      // Strokes are part of the layer but not individually selectable; skip them
      // so the active selection wraps only the user-movable objects. Paint
      // overlays (added on first brush/retouch touch by `ensurePaintSurface`)
      // are also filtered — they're doc-sized transparent UI artifacts, not
      // user-managed layer geometry, and wrapping them into an ActiveSelection
      // would inflate the bbox to the full canvas.
      const objs = canvas
        .getObjects()
        .filter((o) => getObjectLayerId(o) === layerId && !isPaintOverlay(o));

      if (TOOLS_REQUIRING_LOCKED_LAYERS.has(activeToolRef.current)) {
        // Lock-mode toggle. The tool's setTool branch already locked
        // every object so the marquee / retouch mouse handlers claim
        // every click; we keep `selectable: false, evented: false` on
        // the target here too. `setActiveObject` alone makes Fabric
        // render the transform controls, and because the object is
        // still non-evented, control rendering is purely visual —
        // clicks pass through to the active tool. Repeat icon-click
        // on the same single-object layer toggles the bbox off so the
        // user can dismiss it without switching tools.
        if (objs.length === 1) {
          if (canvas.getActiveObject() === objs[0]) {
            canvas.discardActiveObject();
          } else {
            canvas.setActiveObject(objs[0]);
          }
          canvas.requestRenderAll();
        }
        // Multi-object (group) and zero-object cases fall through —
        // wrapping a group in a non-evented ActiveSelection produces
        // a misleading combined bbox the user can't grab; better to
        // do nothing than mislead. LayersPanel still highlights the
        // row via `state.activeLayerId`.
        return;
      }

      canvas.discardActiveObject();

      // Toggle the bg image's interactivity based on whether the target layer
      // *is* the Background. Selecting Background unlocks it; selecting any
      // other layer re-locks it so it's not accidentally clicked on the canvas.
      if (bg) {
        const targetingBg = objs.includes(bg);
        bg.set({ selectable: targetingBg, evented: targetingBg });
      }

      for (const o of objs) {
        o.set({ selectable: true, evented: true });
      }

      if (objs.length === 1) {
        // Text layers stay in non-editing mode on panel select —
        // the user wanted a single click to set up move-with-
        // transform-handles, with editing reserved for an explicit
        // canvas double-click. Fabric's IText default handles the
        // dblclick → enterEditing transition.
        canvas.setActiveObject(objs[0]);
      } else if (objs.length > 1) {
        const sel = new fabric.ActiveSelection(objs, { canvas });
        canvas.setActiveObject(sel);
      }
      canvas.requestRenderAll();
    },
    [getBackgroundImage],
  );

  /** Multi-layer selection: select every Fabric object tagged
   *  with any of the given layer ids and wrap them in a single
   *  Fabric ActiveSelection. Used by panel-side group clicks
   *  (click a group row → select every descendant's Fabric
   *  object) and by Shift+click range selections, so dragging
   *  any one of the selected objects moves them all together —
   *  Fabric's built-in ActiveSelection drag handles this
   *  uniformly. Empty input or no matching objects clears the
   *  active selection.
   *
   *  Same lock-mode exception as `selectLayerObjects`: tools that
   *  need every object non-evented (marquee, retouch) skip the
   *  Fabric-side selection entirely. */
  const selectLayersObjects = useCallback(
    (layerIds: readonly string[]) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      if (TOOLS_REQUIRING_LOCKED_LAYERS.has(activeToolRef.current)) return;
      const bg = getBackgroundImage();
      const idSet = new Set(layerIds);
      const objs = canvas.getObjects().filter((o) => {
        const lid = getObjectLayerId(o);
        // Same paint-overlay exclusion as the single-layer path —
        // don't let `ensurePaintSurface`'s doc-sized helper image
        // inflate the ActiveSelection's bbox.
        return typeof lid === 'string' && idSet.has(lid) && !isPaintOverlay(o);
      });
      canvas.discardActiveObject();

      // Bg interactivity follows whether the bg is in the
      // selection — same rule as the single-id path so the user
      // can grab + drag the Background as part of a group.
      if (bg) {
        const targetingBg = objs.includes(bg);
        bg.set({ selectable: targetingBg, evented: targetingBg });
      }

      for (const o of objs) {
        o.set({ selectable: true, evented: true });
      }

      if (objs.length === 1) {
        canvas.setActiveObject(objs[0]);
      } else if (objs.length > 1) {
        const sel = new fabric.ActiveSelection(objs, { canvas });
        canvas.setActiveObject(sel);
      }
      canvas.requestRenderAll();
    },
    [getBackgroundImage],
  );

  /** Remove every Fabric object tagged with the given layerId, including its
   *  paint surface entry. */
  const removeLayerObjects = useCallback((layerId: string) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const toRemove = canvas.getObjects().filter((o) => getObjectLayerId(o) === layerId);
    paintSurfacesRef.current.delete(layerId);
    if (toRemove.length === 0) return;
    for (const o of toRemove) canvas.remove(o);
    canvas.discardActiveObject();
    canvas.renderAll();
    // DO NOT fire onObjectModified here. Every caller of
    // `removeLayerObjects` (handleDeleteLayer, handleMergeLayers)
    // pairs the imperative Fabric removal with a `removeLayer`
    // dispatch, then sets `layerSnapshotPendingRef.current = true`
    // so the post-commit effect captures a SINGLE consistent
    // snapshot (canvas post-delete + extra-state post-delete).
    //
    // Firing onObjectModified inline pushed an intermediate-bad
    // snapshot — canvas had the Fabric object removed but
    // extraAccessRef.get() still saw the PRE-dispatch React state
    // (the dispatch hadn't committed yet). On undo the engine
    // returned to that bad entry, so the user saw "layer node
    // reappears in the panel but the on-screen content is gone."
    // markDirty is also handled by the pending-flag effect, so no
    // dirty-tracking is lost.
  }, []);

  /** Add a FabricImage from a Blob (e.g. clipboard paste, AI Insert)
   *  tagged with the given layerId. Returns the created object or null.
   *
   *  Options:
   *    - `layerId` (required): the layer entry in `state.layers` that
   *      this fabric object should be associated with.
   *    - `fitTo` (optional): a scene-coord rect; the image is
   *      uniformly scaled (longest side fills the rect) and centered
   *      at the rect's center. Handy for the Smart Create Insert flow
   *      where the new layer should land inside the user's marquee.
   *      Without `fitTo`, the image lands at the photo's center at its
   *      natural size.
   *
   *  Either way, exactly one `renderAll` runs before the function
   *  returns, so the caller never needs to manually trigger a repaint
   *  after positioning. */
  const addImageLayer = useCallback(
    async (
      blob: Blob,
      options: {
        layerId: string;
        fitTo?: { left: number; top: number; width: number; height: number };
        /** Scene-coords point the new image's center should land on.
         *  When provided, overrides the default "center in document"
         *  placement. Used by paste so the image lands at the user's
         *  last canvas-click point in a single set() — without this,
         *  callers had to fix up position post-construction and the
         *  user saw a one-frame flicker as the image jumped from the
         *  doc center to the click point. Ignored when `fitTo` is
         *  provided (fitTo's centered-in-rect placement wins). */
        centerAt?: { x: number; y: number };
      },
    ): Promise<fabric.FabricImage | null> => {
      const { layerId, fitTo, centerAt } = options;
      const canvas = canvasRef.current;
      if (!canvas) return null;
      // Hand the blob directly to the browser via an object URL — far
      // faster than the old FileReader→data URL path, which copies the
      // entire blob into a base64 string (33% inflation, full string
      // allocation in JS heap) before the browser can decode it. Object
      // URLs are essentially free to mint.
      //
      // CRITICAL: rasterize the loaded `<img>` element to a canvas
      // BEFORE we revoke the object URL. Otherwise the FabricImage's
      // underlying `<img>` keeps the (now-revoked) blob URL as its
      // `src` — and any later code that re-reads the src (Fabric
      // serialization, browser bitmap re-decode after eviction) gets
      // `net::ERR_FILE_NOT_FOUND`. Rasterizing detaches the image
      // from the URL so the revoke is safe.
      const objectUrl = URL.createObjectURL(blob);
      let img: fabric.FabricImage;
      try {
        img = await fabric.FabricImage.fromURL(objectUrl, { crossOrigin: 'anonymous' });
        const loadedEl = img.getElement();
        if (loadedEl instanceof HTMLImageElement) {
          const w = loadedEl.naturalWidth || loadedEl.width || 1;
          const h = loadedEl.naturalHeight || loadedEl.height || 1;
          const detachedCanvas = document.createElement('canvas');
          detachedCanvas.width = w;
          detachedCanvas.height = h;
          const detachedCtx = detachedCanvas.getContext('2d', {
            willReadFrequently: true,
          });
          if (detachedCtx) {
            detachedCtx.drawImage(loadedEl, 0, 0);
            img.setElement(detachedCanvas);
          }
        }
      } finally {
        URL.revokeObjectURL(objectUrl);
      }

      // Read dimensions straight from the underlying `<img>` element
      // — `naturalWidth`/`naturalHeight` are guaranteed populated by
      // the time `FabricImage.fromURL`'s promise resolves. We've seen
      // Fabric's own `img.width` come back as 0 for blob-backed PNGs
      // (e.g. the bg-removal output), and even when it's non-zero
      // it's not always in sync with the element. So we *always*
      // overwrite Fabric's intrinsic width/height with what the
      // element reports — that's the source rect Fabric uses for the
      // actual drawImage call, so getting it right is critical.
      const el = img.getElement();
      const naturalW =
        (el instanceof HTMLImageElement
          ? el.naturalWidth || el.width
          : (el as HTMLCanvasElement).width) || 1;
      const naturalH =
        (el instanceof HTMLImageElement
          ? el.naturalHeight || el.height
          : (el as HTMLCanvasElement).height) || 1;
      img.set({ width: naturalW, height: naturalH });

      // Paint pipeline invariant: every FabricImage that participates
      // as a paint surface MUST be authored with `originX: 'left',
      // originY: 'top'` so `imgObj.left/top` is the scene position of
      // surface pixel (0, 0). cut/copy/delete/bake all read
      // `surface.fabricImg.left/top` live and assume this convention;
      // a center-origin image silently breaks every paint op on the
      // layer. Centering is done by adjusting left/top — NOT by
      // changing the origin.
      if (fitTo && fitTo.width > 0 && fitTo.height > 0) {
        const rawScale = Math.min(fitTo.width / naturalW, fitTo.height / naturalH);
        // Defensive: if naturalW/H were somehow NaN or the fit rect
        // was degenerate, `rawScale` could be NaN/Infinity/0 — any of
        // which would make the image disappear or render at native
        // size. Fall back to 1 so the user at least sees *something*
        // they can manually adjust, rather than a vertical sliver.
        const scale = Number.isFinite(rawScale) && rawScale > 0 ? rawScale : 1;
        const renderedW = naturalW * scale;
        const renderedH = naturalH * scale;
        img.set({
          left: fitTo.left + (fitTo.width - renderedW) / 2,
          top: fitTo.top + (fitTo.height - renderedH) / 2,
          originX: 'left',
          originY: 'top',
          scaleX: scale,
          scaleY: scale,
        });
        img.setCoords();
      } else {
        // centerAt wins over the default doc-center placement.
        const anchor = centerAt ?? {
          x: imageDimsRef.current.width / 2,
          y: imageDimsRef.current.height / 2,
        };
        img.set({
          left: anchor.x - naturalW / 2,
          top: anchor.y - naturalH / 2,
          originX: 'left',
          originY: 'top',
        });
        img.setCoords();
      }

      (img as fabric.FabricImage & { id: string }).id = `pasted_${Date.now()}`;
      setObjectLayerId(img, layerId);
      // Match the new image's hit-test policy to the current tool.
      // Otherwise a fresh AI / paste layer added while in marquee mode
      // covers the canvas with `evented: true` Fabric objects, which
      // trip `findTarget` on every click and silently abort the
      // marquee draw — symptom: "select tool stopped working after a
      // few Smart Creates." `setTool('marquee')` does this same
      // normalization for objects already on the canvas; this branch
      // covers objects added later.
      if (activeToolRef.current === 'marquee') {
        img.set({ selectable: false, evented: false });
      }
      canvas.add(img);
      // Don't auto-select the new image when the active tool is
      // marquee — the user is mid-selection workflow and a sudden
      // setActiveObject on a non-selectable image leaves Fabric in a
      // weird half-active state.
      if (activeToolRef.current !== 'marquee') {
        canvas.setActiveObject(img);
      }
      canvas.renderAll();
      // Snapshot is the caller's responsibility (see addText comment).
      return img;
    },
    [],
  );

  /** Synchronous sibling of `addImageLayer` for callers that already
   *  have an `HTMLCanvasElement` (e.g. the merge compositor). Skips
   *  the toBlob → URL → FabricImage.fromURL round-trip — the input
   *  canvas IS the source pixels, so wrapping it in a FabricImage
   *  is a one-line construct.
   *
   *  Critical for snapshot timing: the merge handler dispatches a
   *  reducer action that schedules a React render; the post-commit
   *  effect captures `history.saveSnapshot()`. If the new FabricImage
   *  isn't on the canvas by the time that effect fires, the saved
   *  snapshot has state-has-merged-layer but canvas-missing-FabricImage
   *  — and a subsequent Undo lands the user on that inconsistent
   *  frame (layer visible in the panel but no rendered pixels). The
   *  async toBlob path that addImageLayer uses is fundamentally
   *  unsafe for this caller; this helper closes the gap.
   *
   *  Tag conventions match `addImageLayer`: originX/Y='left'/'top',
   *  scaleX/Y=1, objectCaching off, layerId tag set. */
  const addImageLayerFromCanvas = useCallback(
    (
      source: HTMLCanvasElement,
      options: { layerId: string; left: number; top: number },
    ): fabric.FabricImage | null => {
      const fc = canvasRef.current;
      if (!fc) return null;
      const img = new fabric.FabricImage(source, {
        left: options.left,
        top: options.top,
        originX: 'left',
        originY: 'top',
        scaleX: 1,
        scaleY: 1,
        angle: 0,
        flipX: false,
        flipY: false,
        // Same caching policy as addImageLayer's output — needed
        // because the slow-path compositor writes into the backing
        // canvas after creation; `dirty = true` alone isn't enough
        // when `objectCaching = true` returns from the cache.
        objectCaching: false,
      });
      img.set({ width: source.width, height: source.height });
      setObjectLayerId(img, options.layerId);
      if (activeToolRef.current === 'marquee') {
        img.set({ selectable: false, evented: false });
      }
      fc.add(img);
      if (activeToolRef.current !== 'marquee') {
        fc.setActiveObject(img);
      }
      fc.renderAll();
      // Snapshot is the caller's responsibility — same contract as
      // addImageLayer.
      return img;
    },
    [],
  );

  /** Create a FabricImage that wraps a canvas-sized backing
   *  canvas for an adjustment layer's display. The slow-path
   *  compositor in `render-pipeline.ts` writes the cumulative
   *  post-adjustment pixels into this backing canvas; Fabric
   *  paints it in its z-order alongside the source layers.
   *
   *  The display object is:
   *  - tagged with `layerId` so `applyLayerOrder` /
   *    `getAdjustmentBackingCanvas` / removal flows can find it
   *  - positioned at (0,0) with no scale — full canvas, identity
   *    transform — so the backing canvas's pixels paint 1:1 onto
   *    the document
   *  - non-selectable / non-evented — adjustment layers are
   *    edited through the LayersPanel modal, not by clicking on
   *    canvas (clicks pass through to the layers below)
   *  - rendered with `objectCaching: false` so a `dirty = true`
   *    flag after the compositor writes is enough to pick up
   *    fresh pixels on the next render
   *
   *  Returns the created FabricImage. Caller is responsible for
   *  the subsequent `applyLayerOrder` so the object lands at the
   *  right z-position. */
  const addAdjustmentLayer = useCallback(
    (options: {
      layerId: string;
      /** Explicit document dimensions for the backing canvas. The hook's
       *  `imageDimsRef` is only written by paths that go through
       *  `initEmptyCanvas` / `loadImage`. Session-restore and PSD-import
       *  callers can populate the document via React state alone, in
       *  which case `imageDimsRef` is still `{0,0}` when the mount
       *  effect fires — falling back to the caller's known dimensions
       *  rescues the backing from being silently skipped. */
      width?: number;
      height?: number;
    }): fabric.FabricImage | null => {
      const fc = canvasRef.current;
      if (!fc) return null;
      const w = options.width && options.width > 0 ? options.width : imageDimsRef.current.width;
      const h = options.height && options.height > 0 ? options.height : imageDimsRef.current.height;
      if (w <= 0 || h <= 0) return null;
      const backing = document.createElement('canvas');
      backing.width = w;
      backing.height = h;
      // Pre-clear so the first render before the compositor runs
      // shows transparent (no flash of garbage). Allocating a fresh
      // canvas with `2d` gets transparent pixels by default but be
      // defensive — some browsers used to ship uninitialized backing
      // stores. The `willReadFrequently` hint locks the canvas into
      // CPU-readable mode at first-getContext time; the compositor's
      // adjustment-apply path writes here via `putImageData` and
      // composite-op blends drawImage it as a source — no
      // `getImageData` today, but downstream features (filters,
      // export, AI) may read it.
      const bctx = backing.getContext('2d', { willReadFrequently: true });
      if (bctx) bctx.clearRect(0, 0, w, h);
      const img = new fabric.FabricImage(backing, {
        left: 0,
        top: 0,
        originX: 'left',
        originY: 'top',
        scaleX: 1,
        scaleY: 1,
        selectable: false,
        evented: false,
        objectCaching: false,
        // Skip serialization. Adjustment FabricImages wrap full-
        // resolution backing canvases — `toObject`/`toJSON` would
        // otherwise stringify each as a data URL (~5-20 MB on 4K),
        // and every `history.saveSnapshot` fires that for the
        // entire stack. With 3 adjustments stacked the per-snapshot
        // cost ballooned to 60+ MB of allocations plus PNG-encode
        // CPU per call → sustained heap pressure, IDB `put` failing
        // with DataCloneError "out of memory" on session save, and
        // a noticeably bogged-down UI on every Fabric event that
        // triggers a snapshot (drag, resize, transform).
        //
        // The adjustment configs in `state.document` ARE persisted
        // (via the history-extra channel + session-store's
        // `document` field), so the pixels are recoverable: on undo
        // / session reload, the reconciliation effect in EditorPage
        // walks the document, creates any missing adjustment
        // FabricImage via `addAdjustmentLayer`, and the slow-path
        // compositor re-derives the pixels from config + lower
        // layers. The backings never need to be in the snapshot.
        excludeFromExport: true,
      });
      img.set({ width: w, height: h });
      setObjectLayerId(img, options.layerId);
      fc.add(img);
      fc.requestRenderAll();
      return img;
    },
    [],
  );

  /** Whether to force rect/ellipse marquee drawing to a square aspect ratio. */
  const setForceSquare = useCallback((force: boolean) => {
    forceSquareRef.current = force;
  }, []);

  /** Update the marquee outline color. Re-tints any active marquee object live. */
  const setMarqueeColor = useCallback((color: string) => {
    marqueeColorRef.current = color;
    const canvas = canvasRef.current;
    const m = marqueeRef.current;
    if (canvas && (m.obj || m.extraObjs.length)) {
      if (m.obj) applyMarqueeColor(m.obj, color);
      for (const o of m.extraObjs) applyMarqueeColor(o, color);
      canvas.renderAll();
    }
  }, []);

  /** Update magic-wand tolerance and contiguous mode. */
  const setWandSettings = useCallback((tolerance: number, contiguous: boolean) => {
    wandToleranceRef.current = tolerance;
    wandContiguousRef.current = contiguous;
  }, []);

  /** Tell the canvas which layer is currently active. New paint strokes get
   *  tagged with this layerId so they're associated with that layer in the
   *  layers panel and follow its visibility / order / deletion. */
  const setActiveLayerId = useCallback((id: string | null) => {
    activeLayerIdRef.current = id;
  }, []);

  /** Mirror the React-side Move-tool Auto-Select preference into the
   *  canvas-side ref. EditorPage calls this in a useEffect on
   *  `state.autoSelectLayers`. When `false`, the Move-tool mouse:down
   *  bypasses Fabric's grab-on-click and instead drags whichever
   *  layer is currently active — see the mouse:down handler block. */
  const setAutoSelectLayersInternal = useCallback((value: boolean) => {
    autoSelectLayersRef.current = value;
  }, []);

  /** Push the React-side edit-target map into the canvas hook. The
   *  brush-stroke listener is a Fabric event handler and reads
   *  outside the React render lifecycle, so we mirror the state
   *  via a ref. EditorPage calls this in a useEffect on
   *  `state.editTargets`. */
  const setEditTargets = useCallback((map: Record<string, 'layer' | 'mask'>) => {
    editTargetsRef.current = map;
  }, []);

  /** Null every per-variant stroke ref. Called on tool-switch,
   *  variant-switch (Shift+R), window blur, and component
   *  unmount — anywhere a stroke might be silently abandoned
   *  before its `mouse:up` fires. Without this, the stroke ref
   *  + its lazily-allocated `appliedMask` Uint8Array (4–16 MB
   *  per variant on a typical 4MP layer) leaks until the next
   *  stroke of that variant is committed. If any ref was
   *  non-null we also fire `onObjectModified` so the
   *  in-progress paint is committed to undo history. */
  const clearAllRetouchStrokes = useCallback(() => {
    let didClean = false;
    if (cloneStrokeRef.current) {
      cloneStrokeRef.current = null;
      didClean = true;
    }
    if (smudgeStrokeRef.current) {
      smudgeStrokeRef.current = null;
      didClean = true;
    }
    if (blurStrokeRef.current) {
      blurStrokeRef.current = null;
      didClean = true;
    }
    if (sharpenStrokeRef.current) {
      sharpenStrokeRef.current = null;
      didClean = true;
    }
    if (dodgeStrokeRef.current) {
      dodgeStrokeRef.current = null;
      didClean = true;
    }
    if (burnStrokeRef.current) {
      burnStrokeRef.current = null;
      didClean = true;
    }
    if (spongeStrokeRef.current) {
      spongeStrokeRef.current = null;
      didClean = true;
    }
    if (didClean) onObjectModifiedRef.current?.();
  }, []);

  /** Subscribe to cursor scene-coords. Returns an unsubscribe.
   *  Lets a leaf component (StatusBar) track the cursor without
   *  routing through the reducer — `mouse:move` fires at 60+ Hz
   *  and was previously dispatching SET_CURSOR_POSITION on every
   *  tick, re-rendering the entire EditorPage subtree. */
  const subscribeToCursorMove = useCallback((cb: (x: number, y: number) => void) => {
    const listeners = cursorListenersRef.current;
    listeners.add(cb);
    return () => {
      listeners.delete(cb);
    };
  }, []);

  /** Push the React-side RetouchSettings into the canvas hook so
   *  Fabric mouse handlers (which run outside the React render
   *  lifecycle) can read variant + per-variant config. EditorPage
   *  calls this in a useEffect on `state.retouchSettings`. If the
   *  variant changed mid-stroke (Shift+R cycle), abandon any in-
   *  flight stroke first — the new variant's mouse:up gate would
   *  otherwise miss the old ref and leak its `appliedMask`. */
  const setRetouchSettings = useCallback(
    (settings: RetouchSettings) => {
      const prev = retouchSettingsRef.current;
      retouchSettingsRef.current = settings;
      if (prev && prev.variant !== settings.variant) {
        clearAllRetouchStrokes();
      }
    },
    [clearAllRetouchStrokes],
  );

  /** Render or clear the Clone Stamp source reticle on the
   *  canvas. Reticle is a `+` (two crossed lines, no ring) drawn
   *  at the current sample position in scene coords — paired with
   *  the brush-circle SVG cursor in CanvasArea.tsx, which has a
   *  center `+` of its own at the destination. The two `+`s let
   *  the user see source ↔ destination as one gesture. The
   *  reticle is non-interactive and rides above all layers. The
   *  position passed in is OPTIONAL; if omitted, the reticle is
   *  placed at `cloneSourceRef` (default for fresh source-set or
   *  non-aligned post-stroke reset). The stamp loop and the
   *  hover-tracking mouse:move both call with explicit positions. */
  const renderCloneReticle = useCallback((overridePos?: { x: number; y: number }) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Tear down any existing bars before rebuilding.
    if (cloneReticleRef.current) {
      for (const bar of cloneReticleRef.current.bars) canvas.remove(bar);
      cloneReticleRef.current = null;
    }
    const src = overridePos ?? cloneSourceRef.current;
    if (!src) {
      canvas.requestRenderAll();
      return;
    }
    // Reticle: 4 fabric.Rect bars added directly to the canvas
    // (no Group wrapper). Two white halo bars sit underneath two
    // accent-colored bars to form a `+` with a white outline,
    // legible against both light and dark imagery. Sizing is in
    // scene units pre-divided by zoom so the on-screen size
    // stays stable across zoom levels.
    const zoom = canvas.getZoom() || 1;
    const crossLen = 18 / zoom;
    const haloThick = 5 / zoom;
    const accentThick = 2 / zoom;
    const buildBar = (width: number, height: number, color: string): fabric.Rect =>
      new fabric.Rect({
        left: src.x,
        top: src.y,
        width,
        height,
        fill: color,
        stroke: undefined,
        originX: 'center',
        originY: 'center',
        selectable: false,
        evented: false,
        excludeFromExport: true,
        objectCaching: false,
      });
    const bars = [
      buildBar(crossLen, haloThick, '#ffffff'),
      buildBar(haloThick, crossLen, '#ffffff'),
      buildBar(crossLen, accentThick, '#0078d7'),
      buildBar(accentThick, crossLen, '#0078d7'),
    ];
    for (const bar of bars) {
      (bar as fabric.Rect & { _isCloneReticle: boolean })._isCloneReticle = true;
      canvas.add(bar);
      canvas.bringObjectToFront(bar);
    }
    cloneReticleRef.current = { bars, left: src.x, top: src.y };
    canvas.requestRenderAll();
  }, []);
  renderCloneReticleRef.current = renderCloneReticle;

  /** Arm the next canvas tap to set the Clone source point. UI
   *  flow: user taps "Set Source" button → setCloneArmed(true) →
   *  next mouse:down records the source and disarms. Tapping
   *  again (or pressing Esc, handled by EditorPage) cancels. */
  const setCloneArmed = useCallback((armed: boolean) => {
    cloneArmedRef.current = armed;
  }, []);

  /** Push a transient cursor override onto Fabric's defaultCursor.
   *  Used by EditorPage to flip the cursor to a crosshair while
   *  Clone Stamp source-set mode is active (button armed or Alt
   *  held). Pass `null` to clear and restore whatever cursor was
   *  set by the active tool's setTool branch.
   *
   *  We snapshot the cursor at the moment the override is applied
   *  (rather than re-deriving from the active tool) so that
   *  restoration is correct regardless of which tool is active —
   *  setTool has too many cursor branches (`text`, `shape`,
   *  `fill`, etc.) to mirror in a separate switch. */
  const cursorOverrideStashRef = useRef<{ cursor: string; tool: EditorTool } | null>(null);
  const setCursorOverride = useCallback((cursor: string | null) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (cursor) {
      if (cursorOverrideStashRef.current === null) {
        cursorOverrideStashRef.current = {
          cursor: canvas.defaultCursor || 'default',
          tool: activeToolRef.current,
        };
      }
      canvas.defaultCursor = cursor;
    } else if (cursorOverrideStashRef.current !== null) {
      // Only restore the snapshot if the user is still on the
      // same tool that was active when the override was applied.
      // If they've switched tools, setTool will have already
      // installed the right cursor for the new tool — restoring
      // the old one would clobber it.
      if (cursorOverrideStashRef.current.tool === activeToolRef.current) {
        canvas.defaultCursor = cursorOverrideStashRef.current.cursor;
      }
      cursorOverrideStashRef.current = null;
    }
    canvas.requestRenderAll();
  }, []);

  /** Programmatic source-point setter — used by long-press on
   *  touch and by undo/redo. Fires the change callback so the
   *  panel mirrors the new state. */
  const setCloneSource = useCallback(
    (point: { x: number; y: number } | null) => {
      cloneSourceRef.current = point;
      // A new (or cleared) source resets any persisted Aligned
      // offset — same as the mouse:down armed/alt-click path.
      cloneAlignedOffsetRef.current = null;
      renderCloneReticle();
      onCloneSourceChangedRef.current?.(point);
    },
    [renderCloneReticle],
  );

  /** Push the latest brush/fill color so the fill tool can pick it up. */
  const setBrushColor = useCallback((color: string) => {
    brushColorRef.current = color;
  }, []);

  /** Set the active tool on the canvas */
  const setTool = useCallback(
    (tool: EditorTool, brushSettings?: BrushSettings) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const prevTool = activeToolRef.current;
      activeToolRef.current = tool;
      // Abandon any in-flight retouch stroke when switching away
      // from the retouch tool — its `mouse:up` would otherwise
      // never fire, leaking the per-stroke ref + `appliedMask`.
      // Also drop the clone source state: source point, armed flag,
      // aligned offset, and the on-canvas reticle. Otherwise the
      // reticle stays visible after switching tools, and a return
      // to Clone/Heal would silently inherit the stale source.
      if (prevTool === 'retouch' && tool !== 'retouch') {
        clearAllRetouchStrokes();
        // Order-dependent: zero the refs FIRST, then render the
        // reticle. renderCloneReticle reads `cloneSourceRef.current`
        // — if it still held a position, the reticle would
        // re-render once at the old source before the next render
        // tear-down. Same for armed/aligned-offset which gate
        // future stroke behavior. Do not reorder.
        cloneSourceRef.current = null;
        cloneArmedRef.current = false;
        cloneAlignedOffsetRef.current = null;
        renderCloneReticleRef.current?.();
        // Mirror to React state via the callback so the
        // PropertiesPanel "Set Source" button leaves armed state.
        onCloneSourceChangedRef.current?.(null);
      }

      // Preserve the active marquee for tools that operate on the selection
      // (Move, Draw, Eraser, Fill) plus the marquee tool itself.
      if (!MARQUEE_PRESERVING_TOOLS.has(tool)) {
        clearMarquee();
      }

      // Tear down the crop rect if leaving crop mode without applying.
      if (tool !== 'crop' && cropRectRef.current) {
        canvas.discardActiveObject();
        canvas.remove(cropRectRef.current);
        cropRectRef.current = null;
      }

      // Tier 2 Group F sub-piece #5 — abandon any in-flight slice
      // drag when leaving the slice tool. The pending rect is
      // ephemeral (not committed to state); without this cleanup
      // a half-drawn rect would persist on canvas in the next
      // tool's view. Slice overlay objects themselves are
      // unmounted via the EditorPage `syncSliceOverlays` effect
      // when `activeTool` flips off `'slice'`.
      if (prevTool === 'slice' && tool !== 'slice') {
        const s = sliceStateRef.current;
        if (s.pending.obj) {
          canvas.remove(s.pending.obj);
          s.pending.obj = null;
        }
        s.pending.isDrawing = false;
      }

      canvas.isDrawingMode = false;
      canvas.selection = false;
      canvas.defaultCursor = 'default';

      // Clear any pre-existing active object on a tool change. A layer
      // selected in Move mode leaves its bounding box + transform
      // handles drawn; switching to Brush / Marquee / Eraser / etc.
      // would otherwise leave those handles intercepting clicks
      // (Fabric's hit-test still consults the active object even
      // after the surrounding objects' `selectable` is flipped off),
      // making the new tool feel broken until the user clicks empty
      // canvas first. Tools that need an active object (crop sets a
      // crop rect below; canvas-event handlers like text-edit set
      // their target) re-establish it in the switch branches that
      // follow this discard. Skipped on same-tool re-entry so we don't
      // wipe the user's selection mid-session.
      //
      // Also lock every non-marquee object's selectable/evented flags
      // by default. Without this, switching from Move to Text / Shape
      // / Draw / etc. leaves the prior tool's `selectable: true` on
      // each layer — Fabric then renders the per-object `hoverCursor`
      // (default 'move') instead of the new tool's `defaultCursor`,
      // and click-to-place-text drags the underlying layer instead
      // of placing text. The `move` case below re-enables selectables
      // it needs; `crop` sets a fresh active rect; the marquee /
      // retouch / eraser branches still do their own equivalent
      // locking (now redundant but harmless).
      if (prevTool !== tool) {
        canvas.discardActiveObject();
        canvas.getObjects().forEach((o) => {
          if (isMarqueeObject(o)) return;
          o.set({ selectable: false, evented: false });
        });
      }

      switch (tool) {
        case 'move': {
          canvas.defaultCursor = 'grab';
          const bg = getBackgroundImage();
          canvas.getObjects().forEach((o) => {
            // Paint overlays AND canvas chrome (the doc-sized bounds
            // outline + Extension fill rect) must never become
            // selectable — they cover the whole canvas, so making them
            // evented lets a click grab the chrome and drag a
            // full-document bounding box instead of panning / moving a
            // real layer. This is why Move misbehaved only after a tool
            // round-trip: on first open setTool('move') runs before the
            // chrome exists, but switching away and back re-runs it with
            // the chrome present — without this guard it flipped the
            // outline to selectable.
            if (o !== bg && !isMarqueeObject(o) && !isPaintOverlay(o) && !isCanvasChrome(o)) {
              o.set({ selectable: true, evented: true });
            }
          });
          break;
        }
        case 'marquee': {
          canvas.defaultCursor = 'crosshair';
          const bg = getBackgroundImage();
          // Force the bg non-evented so its hit-test doesn't intercept
          // marquee mouse:down (otherwise selecting the Background layer
          // earlier would block subsequent marquee drawing).
          if (bg) bg.set({ selectable: false, evented: false });
          canvas.getObjects().forEach((o) => {
            if (o !== bg && !isMarqueeObject(o)) {
              o.set({ selectable: false, evented: false });
            }
          });
          break;
        }
        case 'draw':
          if (brushSettings) enableDrawMode(brushSettings);
          break;
        case 'retouch': {
          // Retouch variants each implement their own
          // mouse:down/move/up handlers gated on
          // `settings.variant`; none use Fabric's
          // freeDrawingBrush. Drawing mode stays off so a
          // brush-color line doesn't render on top of stamped
          // pixels.
          canvas.isDrawingMode = false;
          // Hide the OS arrow over the canvas — CanvasArea
          // renders an SVG brush-circle (with center cross for
          // the Clone variant) that fully replaces it. Source-set
          // mode flips this to 'crosshair' via setCursorOverride;
          // touch devices don't render the SVG cursor but also
          // don't show a system cursor, so 'none' is fine.
          canvas.defaultCursor = 'none';
          // Lock every object so Fabric's hit-test doesn't run
          // Move-tool drag in parallel with the custom retouch
          // mouse handlers. Without this, retouch on a
          // selectable image layer (e.g. a rasterized vector or
          // any non-bg image) drags the layer AND stamps pixels
          // simultaneously — the user sees the layer shift while
          // strokes accumulate on a lazy-mounted paint overlay
          // that stays put. Mirrors the marquee tool's lock
          // pattern. Switching to Move tool unlocks them again.
          const bg = getBackgroundImage();
          if (bg) bg.set({ selectable: false, evented: false });
          canvas.getObjects().forEach((o) => {
            if (o !== bg && !isMarqueeObject(o)) {
              o.set({ selectable: false, evented: false });
            }
          });
          break;
        }
        case 'eraser':
          canvas.isDrawingMode = true;
          canvas.freeDrawingBrush = new fabric.PencilBrush(canvas);
          // Semi-transparent neutral gray for the live preview — visible on
          // both light and dark backgrounds. The actual erase happens in
          // bakeStrokeOntoSurface via `globalCompositeOperation:
          // 'destination-out'`, so the preview color doesn't affect output.
          canvas.freeDrawingBrush.color = 'rgba(128, 128, 128, 0.5)';
          canvas.freeDrawingBrush.width = brushSettings?.width ?? 20;
          break;
        case 'crop': {
          canvas.defaultCursor = 'default';
          canvas.selection = true;
          const bg = getBackgroundImage();
          // Lock bg so its hit-test doesn't compete with the crop rect.
          if (bg) bg.set({ selectable: false, evented: false });
          canvas.getObjects().forEach((o) => {
            if (o !== bg && !isMarqueeObject(o)) {
              o.set({ selectable: false, evented: false });
            }
          });
          const imgW = imageDimsRef.current.width;
          const imgH = imageDimsRef.current.height;
          if (bg && imgW > 0 && imgH > 0) {
            const rect = buildCropRect(canvas, imgW, imgH);
            canvas.add(rect);
            canvas.bringObjectToFront(rect);
            canvas.setActiveObject(rect);
            cropRectRef.current = rect;
            canvas.requestRenderAll();
          }
          break;
        }
        case 'text': {
          canvas.defaultCursor = 'text';
          // Make existing text layers clickable so clicking an existing
          // IText selects (and edits) it instead of dropping a new text
          // box on top. The create handler bails when `findTarget` hits an
          // evented object — by only un-locking the IText layers here, a
          // click on empty canvas (or a non-text layer) still places new
          // text, while a click on existing text selects it. Overlays,
          // chrome and the background stay locked.
          const bg = getBackgroundImage();
          canvas.getObjects().forEach((o) => {
            if (o === bg) return;
            if (isMarqueeObject(o) || isPaintOverlay(o) || isCanvasChrome(o)) return;
            if (o instanceof fabric.IText) {
              o.set({ selectable: true, evented: true });
            }
          });
          break;
        }
        case 'shape':
          canvas.defaultCursor = 'crosshair';
          break;
        case 'pen':
          // Crosshair matches Photoshop's pen cursor. Clearing any
          // stale pen preview defensively here lets a fresh tool
          // selection always start with a clean slate, even if the
          // activeTool mirror effect in EditorPage hasn't fired yet.
          canvas.defaultCursor = 'crosshair';
          clearPenState();
          break;
        case 'slice': {
          // Tier 2 Group F sub-piece #5. Crosshair for drag-to-
          // create; existing slice overlays carry their own
          // controls so Fabric's native selection routes their
          // drag / resize. Lock every non-bg, non-marquee, non-
          // slice-overlay object so a click outside an existing
          // slice never accidentally starts dragging a layer
          // (the same lock-loop pattern marquee + retouch use).
          //
          // The slice overlay objects themselves are mounted /
          // updated / removed by the EditorPage `syncSliceOverlays`
          // effect — NOT here — so this case only handles cursor
          // + locking. Setting `canvas.selection = false` prevents
          // Fabric's group-marquee from kicking in on drag (we
          // own the drag semantics for slice creation).
          canvas.defaultCursor = 'crosshair';
          canvas.selection = false;
          const bg = getBackgroundImage();
          if (bg) bg.set({ selectable: false, evented: false });
          canvas.getObjects().forEach((o) => {
            if (o === bg) return;
            if (isMarqueeObject(o)) return;
            if (isSliceOverlay(o)) return;
            o.set({ selectable: false, evented: false });
          });
          break;
        }
        case 'fill':
          canvas.defaultCursor = FILL_CURSOR;
          break;
        case 'ai':
          // AI tool drives a Properties-panel UI (Smart Delete / Smart
          // Create), not pointer interactions on the canvas. Keep the
          // marquee bg-lock behavior so a finalized marquee remains
          // selectable as the AI op's target region.
          canvas.defaultCursor = 'default';
          break;
        case 'quickmask': {
          // Quick Mask: paint into the working alpha mask. Brush
          // strokes are handled by the dedicated mouse:down/move/up
          // listeners (registered in the canvas-events effect),
          // NOT by Fabric's freeDrawingBrush. We keep `cursor:
          // crosshair` so the user sees a brush-tip target. The
          // overlay FabricImage was added by `enterQuickMask` and
          // is non-selectable / non-evented, so layer hit-tests
          // pass through to nothing — exactly what we want
          // (pointer events go to the canvas's mouse handlers
          // unmediated). Bg + layers stay non-evented per the
          // marquee/crop convention so the brush hits the canvas
          // not an underlying layer.
          canvas.defaultCursor = 'crosshair';
          const bg = getBackgroundImage();
          if (bg) bg.set({ selectable: false, evented: false });
          canvas.getObjects().forEach((o) => {
            if (o !== bg && !isMarqueeObject(o)) {
              o.set({ selectable: false, evented: false });
            }
          });
          break;
        }
      }

      canvas.renderAll();
    },
    [clearAllRetouchStrokes, enableDrawMode, getBackgroundImage],
  );

  /** Tier 2 Group F sub-piece #5 — install / replace the slice
   *  tool's React-side callbacks. EditorPage calls this once
   *  (in a `useEffect` keyed off the dispatch helpers from
   *  `useImageEditorState`) to wire create + update into the
   *  reducer. The ref pattern means our canvas event handlers
   *  always read the freshest reference without re-registering. */
  const setSliceCallbacks = useCallback(
    (
      callbacks: {
        onCreate: (rect: { x: number; y: number; width: number; height: number }) => void;
        onUpdate: (id: string, patch: Partial<Omit<SliceRect, 'id'>>) => void;
      } | null,
    ) => {
      sliceStateRef.current.callbacks = callbacks;
    },
    [],
  );

  /** Tier 2 Group F sub-piece #5 — reconcile the on-canvas slice
   *  overlay set with the supplied target list. Idempotent diff:
   *    - new slice in target → mount a fresh overlay
   *    - removed slice → unmount the matching overlay
   *    - geometry changed → patch left / top / width / height in
   *      place (preserves Fabric object identity so a mid-drag
   *      sync that lands during user interaction doesn't tear
   *      down the object the user is currently dragging)
   *
   *  Calling with `[]` (or while `activeTool !== 'slice'` in the
   *  EditorPage caller) tears down every overlay — used both at
   *  tool-switch and on slice deletion. */
  const syncSliceOverlays = useCallback((slices: readonly SliceRect[]) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const s = sliceStateRef.current;
    s.current = slices;

    const targetIds = new Set(slices.map((sl) => sl.id));

    // Pass 1: drop any overlay whose slice was removed.
    for (const [id, obj] of Array.from(s.objects.entries())) {
      if (!targetIds.has(id)) {
        canvas.remove(obj);
        s.objects.delete(id);
      }
    }

    // Pass 2: mount missing, patch existing.
    for (const slice of slices) {
      const existing = s.objects.get(slice.id);
      if (!existing) {
        const rect = buildSliceOverlay(canvas, slice);
        tagAsSliceOverlay(rect, slice.id);
        canvas.add(rect);
        // Bring slice overlays to front so they always sit above
        // layer content — matching how Photoshop renders the
        // slice grid overlay.
        canvas.bringObjectToFront(rect);
        s.objects.set(slice.id, rect);
        continue;
      }
      // Identity preserved — patch the fields that diverge so
      // Fabric's internal coords cache stays warm. Skip writes
      // for fields that already match so a no-op tick doesn't
      // trigger a re-render.
      const patch: Partial<fabric.Rect> = {};
      if ((existing.left ?? 0) !== slice.x) patch.left = slice.x;
      if ((existing.top ?? 0) !== slice.y) patch.top = slice.y;
      if ((existing.width ?? 0) !== slice.width) patch.width = slice.width;
      if ((existing.height ?? 0) !== slice.height) patch.height = slice.height;
      // Reset any residual scaleX/scaleY from a previous resize
      // that the user-side normalize step somehow missed (defensive).
      if ((existing.scaleX ?? 1) !== 1) patch.scaleX = 1;
      if ((existing.scaleY ?? 1) !== 1) patch.scaleY = 1;
      if (Object.keys(patch).length > 0) {
        existing.set(patch);
        existing.setCoords();
      }
    }

    canvas.requestRenderAll();
  }, []);

  /** Apply crop to the canvas */
  const applyCrop = useCallback(
    (rect: { left: number; top: number; width: number; height: number }) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const clipRect = new fabric.Rect({
        left: rect.left,
        top: rect.top,
        // v7 default origin is center; crop rect is anchored by its
        // top-left corner so the clip area matches the user's drag.
        originX: 'left',
        originY: 'top',
        width: rect.width,
        height: rect.height,
        absolutePositioned: true,
      });

      canvas.getObjects().forEach((obj) => {
        obj.clipPath = clipRect;
      });

      canvas.renderAll();
      onObjectModified?.();
    },
    [onObjectModified],
  );

  /**
   * Apply the active crop rect: extract the chosen region into a new background
   * image, translate overlay objects to the new origin, update canvas dimensions,
   * and reset the crop rect to the new full-image bounds so the user can iterate.
   */
  const applyActiveCrop = useCallback((): boolean => {
    const canvas = canvasRef.current;
    const bg = getBackgroundImage();
    const cropRect = cropRectRef.current;
    if (!canvas || !bg || !cropRect) return false;

    const left = cropRect.left ?? 0;
    const top = cropRect.top ?? 0;
    const width = (cropRect.width ?? 0) * (cropRect.scaleX ?? 1);
    const height = (cropRect.height ?? 0) * (cropRect.scaleY ?? 1);

    const imgW = bg.width ?? 0;
    const imgH = bg.height ?? 0;
    const cropX = Math.max(0, Math.round(left));
    const cropY = Math.max(0, Math.round(top));
    const cropW = Math.min(imgW - cropX, Math.round(width));
    const cropH = Math.min(imgH - cropY, Math.round(height));
    if (cropW <= 0 || cropH <= 0) return false;

    const imgEl = bg.getElement() as HTMLImageElement | HTMLCanvasElement;
    const offscreen = document.createElement('canvas');
    offscreen.width = cropW;
    offscreen.height = cropH;
    const ctx = offscreen.getContext('2d');
    if (!ctx) return false;
    ctx.drawImage(imgEl, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);

    canvas.discardActiveObject();
    canvas.remove(cropRect);
    cropRectRef.current = null;

    canvas.getObjects().forEach((obj) => {
      if (obj === bg) return;
      obj.set({ left: (obj.left ?? 0) - cropX, top: (obj.top ?? 0) - cropY });
      obj.setCoords();
    });

    const newImg = new fabric.FabricImage(offscreen, {
      left: 0,
      top: 0,
      // v7 origin default flipped to center; the post-crop bg sits
      // at the canvas top-left.
      originX: 'left',
      originY: 'top',
      scaleX: 1,
      scaleY: 1,
      selectable: false,
      evented: false,
    });
    (newImg as fabric.FabricImage & { erasable: boolean }).erasable = false;
    tagAsBackground(newImg);
    newImg.set({ objectCaching: false } as Partial<fabric.FabricImage>);
    newImg.filters = bg.filters ? [...bg.filters] : [];
    newImg.applyFilters();

    // Carry the bg's layer id forward to the new image and re-register
    // its underlying canvas as the Background layer's paint surface.
    // Without this, draw / eraser / fill strokes would silently bake into
    // the orphaned old surface canvas and never reach the visible bg.
    const bgLayerId = getObjectLayerId(bg);
    if (bgLayerId) {
      setObjectLayerId(newImg, bgLayerId);
      paintSurfacesRef.current.set(bgLayerId, {
        canvas: offscreen,
        fabricImg: newImg,
        offsetX: newImg.left ?? 0,
        offsetY: newImg.top ?? 0,
      });
    }

    canvas.remove(bg);
    canvas.add(newImg);
    canvas.sendObjectToBack(newImg);
    backgroundImageRef.current = newImg;
    imageDimsRef.current = { width: cropW, height: cropH };
    ensureCanvasBoundsOverlay(canvas, cropW, cropH);
    onCanvasDimensions?.(cropW, cropH);

    zoomToFit();

    // Re-create crop rect at the new full bounds so the user can crop again.
    const fresh = buildCropRect(canvas, cropW, cropH);
    canvas.add(fresh);
    canvas.setActiveObject(fresh);
    cropRectRef.current = fresh;

    canvas.renderAll();
    onObjectModified?.();
    return true;
  }, [onCanvasDimensions, onObjectModified, zoomToFit, getBackgroundImage]);

  /**
   * Remove the background of the current image. `processBlob` hands the
   * source bytes to a server-side engine (Stability or Recraft) and
   * returns the transparent PNG; the layer-replacement logic stays here
   * in one place. It is required — the on-device imgly default this
   * hook used to fall back to was retired for licensing reasons.
   */
  const removeBackground = useCallback(
    async (opts: { processBlob: (sourceBlob: Blob) => Promise<Blob> }) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      // Find the target image. If the active layer is an image layer,
      // operate on it; otherwise fall back to the bg image (the original
      // behavior). This is the fix for "Remove Background only worked on
      // the actual background, not on a selected layer image."
      const bg = getBackgroundImage();
      const activeLayerId = activeLayerIdRef.current;
      let targetImg: fabric.FabricImage | null = null;
      if (activeLayerId) {
        for (const o of canvas.getObjects()) {
          if (getObjectLayerId(o) === activeLayerId && o instanceof fabric.FabricImage) {
            targetImg = o;
            break;
          }
        }
      }
      if (!targetImg) targetImg = bg;
      if (!targetImg) return;

      const isBg = targetImg === bg;
      const imgEl = targetImg.getElement() as HTMLImageElement | HTMLCanvasElement;
      const w = targetImg.width || 0;
      const h = targetImg.height || 0;
      if (!w || !h) return;

      const offscreen = document.createElement('canvas');
      offscreen.width = w;
      offscreen.height = h;
      const ctx = offscreen.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(imgEl, 0, 0);

      const sourceBlob: Blob | null = await new Promise((resolve) =>
        offscreen.toBlob(resolve, 'image/png'),
      );
      if (!sourceBlob) return;

      const resultBlob = await opts.processBlob(sourceBlob);
      const resultUrl = URL.createObjectURL(resultBlob);

      try {
        const newImg = await fabric.FabricImage.fromURL(resultUrl, { crossOrigin: 'anonymous' });
        // Preserve placement of the layer we're replacing. For the bg
        // layer this is always (0,0) at scale 1; for a non-bg image layer
        // we copy across the user's existing position / scale / rotation
        // so the replacement lands exactly where the original was.
        if (isBg) {
          newImg.set({
            left: 0,
            top: 0,
            // v7 origin default flipped to center; the bg-removed
            // replacement should still anchor at the canvas top-left.
            originX: 'left',
            originY: 'top',
            scaleX: 1,
            scaleY: 1,
            selectable: false,
            evented: false,
            objectCaching: false,
          } as Partial<fabric.FabricImage>);
          (newImg as fabric.FabricImage & { erasable: boolean }).erasable = false;
          tagAsBackground(newImg);
        } else {
          newImg.set({
            left: targetImg.left,
            top: targetImg.top,
            scaleX: targetImg.scaleX,
            scaleY: targetImg.scaleY,
            angle: targetImg.angle,
            originX: targetImg.originX,
            originY: targetImg.originY,
            flipX: targetImg.flipX,
            flipY: targetImg.flipY,
          } as Partial<fabric.FabricImage>);
        }

        // Convert the loaded <img> element to an HTMLCanvasElement so the
        // new image is paintable. Subsequent draw / eraser / fill strokes
        // need a canvas surface to bake into; without this swap they'd
        // silently no-op (the rendering flips to a non-canvas element
        // after the background-removal step).
        const newW = newImg.width || 1;
        const newH = newImg.height || 1;
        const surfaceCanvas = ensureCanvasElement(
          newImg.getElement() as HTMLImageElement | HTMLCanvasElement,
          newW,
          newH,
        );
        if (newImg.getElement() !== surfaceCanvas) newImg.setElement(surfaceCanvas);

        newImg.filters = targetImg.filters ? [...targetImg.filters] : [];
        newImg.applyFilters();

        // Carry the layer id forward to the replacement and refresh the
        // paint-surface registry to point at the new canvas. The old
        // entry (pointing to targetImg's now-orphaned element) would
        // otherwise eat all future strokes silently.
        const layerId = getObjectLayerId(targetImg);
        if (layerId) {
          setObjectLayerId(newImg, layerId);
          paintSurfacesRef.current.set(layerId, {
            canvas: surfaceCanvas,
            fabricImg: newImg,
            offsetX: newImg.left ?? 0,
            offsetY: newImg.top ?? 0,
          });
        }

        canvas.remove(targetImg);
        canvas.add(newImg);
        if (isBg) {
          canvas.sendObjectToBack(newImg);
          backgroundImageRef.current = newImg;
        } else {
          canvas.setActiveObject(newImg);
        }
        canvas.renderAll();
        onObjectModified?.();
      } finally {
        URL.revokeObjectURL(resultUrl);
      }
    },
    [onObjectModified, getBackgroundImage],
  );

  /**
   * Clear every object on the canvas and reset our internal references
   * (background image, paint surfaces, image dimensions, marquee). Used by
   * the multi-tab flow when switching to a freshly-created tab that hasn't
   * had a file loaded into it yet.
   */
  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.clear();
    // canvas.clear() removes every object, which takes their per-object
    // clipPaths with them. No additional clip-state reset needed —
    // the next loadImage / initEmptyCanvas re-applies clips to its
    // own objects via ensureCanvasClipRegion.
    backgroundImageRef.current = null;
    paintSurfacesRef.current.clear();
    canvasFillColorRef.current = null;
    imageDimsRef.current = { width: 0, height: 0 };
    marqueeRef.current.obj = null;
    marqueeRef.current.extraObjs = [];
    marqueeRef.current.points = [];
    marqueeRef.current.wandMaskCanvas = null;
    marqueeRef.current.isDrawing = false;
    // Mirror into React state so the toolbar's Cut / Copy buttons disable
    // when switching to a freshly-emptied tab. Without this, they'd stay
    // enabled (matching the previous tab's marquee) and silently no-op.
    setHasMarqueeSelection(false);
    canvas.renderAll();
  }, []);

  /** Select all objects (excluding background) */
  const selectAll = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const bg = getBackgroundImage();
    const objects = canvas.getObjects().filter((o) => o !== bg);
    if (objects.length > 0) {
      const selection = new fabric.ActiveSelection(objects, { canvas });
      canvas.setActiveObject(selection);
      canvas.renderAll();
    }
  }, [getBackgroundImage]);

  /** Translate the active object / selection by (dx, dy) scene
   *  pixels — the arrow-key nudge. Returns false (no snapshot) when
   *  nothing is selected, so the command is a safe no-op outside a
   *  selection. Snapshots via `onObjectModified` so each nudge is a
   *  discrete undo step, matching Fabric's drag-modified flow. */
  const nudgeActiveObject = useCallback((dx: number, dy: number): boolean => {
    const canvas = canvasRef.current;
    if (!canvas) return false;
    const active = canvas.getActiveObject();
    if (!active) return false;
    active.set({ left: (active.left ?? 0) + dx, top: (active.top ?? 0) + dy });
    active.setCoords();
    canvas.requestRenderAll();
    onObjectModifiedRef.current?.();
    return true;
  }, []);

  // Memoize the returned API so its identity is stable across
  // renders that didn't change any of the underlying callbacks or
  // state. Consumers commonly use the whole object as a useEffect
  // dependency (`[..., canvas]`); without this memo the literal
  // is a fresh object every render, re-firing every dependent
  // effect and clobbering canvas-side state pushed by other paths.
  // The regression in `handleInvertSelection` (where the
  // `setMarqueeShape` effect re-fired and called `clearMarquee` after
  // we'd just installed a wand-style selection) was caused by this
  // churn.
  return useMemo(
    () => ({
      canvasRef,
      viewport,
      loadImage,
      initEmptyCanvas,
      getLayerExportPayload,
      getLayerTextPayload,
      enableDrawMode,
      disableDrawMode,
      addText,
      addTextLayer,
      addShape,
      addVectorObject,
      replaceVectorObject,
      updateVectorStyle,
      getVectorLayerStyle,
      getShapeVectorPath,
      getActiveVectorBounds,
      getBakedVectorPath,
      rasterizeLayerToImage,
      approveLayerForPaint,
      cancelLayerForPaint,
      setPenSettings,
      cancelPenPath,
      finalizePenPath,
      enterPathEditMode,
      exitPathEditMode,
      setPathEditSelectedAnchor,
      setPathEditAnchorType,
      getPathEditSelectedAnchorType,
      refreshPathEditOverlay,
      updateActiveShape,
      getActiveShapeSettings,
      updateActiveText,
      getActiveTextSettings,
      setTextPathBinding,
      deleteSelected,
      setZoom,
      fitToContainer,
      zoomIn,
      zoomOut,
      zoomToActual,
      setSpaceHeld,
      setSnapConfig,
      subscribeToSnapMatch,
      subscribeToSmartSnapMatch,
      setSmartSnapEnabled,
      setSmartSnapCtrlDisabled,
      getLayerBBoxes,
      applyLayerOffsets,
      rotateCanvas,
      flipCanvas,
      setCanvasSize,
      setCanvasFillColor,
      trimLayer,
      mergeLayers,
      exportToDataURL,
      exportToBlob,
      exportToDataURLExcludingOverlays,
      toJSON,
      loadFromJSON,
      setTool,
      setSliceCallbacks,
      syncSliceOverlays,
      applyCrop,
      applyActiveCrop,
      selectAll,
      nudgeActiveObject,
      clearMarquee,
      growMarqueeBy,
      canGrowMarquee,
      setSelectionFromMask,
      getSelectionAsMaskCanvas,
      enterQuickMask,
      exitQuickMask,
      getQuickMaskCanvas,
      discardQuickMask,
      applyLayerMaskFromCanvas,
      setQuickMaskMode,
      enterTransformMode,
      exitTransformMode,
      applyTransformControls,
      getTransformMatrix,
      setTransformMatrix,
      applyWarpPresetToOverlay,
      invertMarquee,
      setMarqueeShape,
      setForceSquare,
      setMarqueeColor,
      setWandSettings,
      setActiveLayerId,
      setAutoSelectLayers: setAutoSelectLayersInternal,
      setBrushColor,
      updateBrush,
      setViewportTransform,
      setBackgroundLayerId,
      setLayerVisibility,
      applyEffectiveVisibility,
      setLayerOpacity,
      setLayerBlendMode,
      setLayerEffects,
      addLayerMask,
      removeLayerMask,
      setMaskConfig,
      materializeMaskClipPath,
      setMaskVectorPath,
      getRawMaskCanvas,
      setEditTargets,
      setRetouchSettings,
      subscribeToCursorMove,
      setCloneArmed,
      setCloneSource,
      setCursorOverride,
      applyLayerOrder,
      selectLayerObjects,
      selectLayersObjects,
      removeLayerObjects,
      addImageLayer,
      addImageLayerFromCanvas,
      addAdjustmentLayer,
      getViewportTransform,
      removeBackground,
      clearCanvas,
      copySelection,
      cutSelection,
      getClipboard,
      getLastClickPoint,
      getIsCanvasInteracting,
      clearActiveSelection,
      getMarqueeBounds,
      runSmartDeleteOnActiveLayer,
      runOnActiveLayerFullImage,
      runUpscaleOnActiveLayer,
      getActiveLayerSnapshot,
      getSourceCanvasForSampling,
      getActiveAdjustmentTargets,
      paintLeafToCanvas,
      getAdjustmentBackingCanvas,
      runOutpaintOnBackground,
      hasMarqueeSelection,
      hasClipboard,
    }),
    [
      viewport,
      hasMarqueeSelection,
      hasClipboard,
      loadImage,
      initEmptyCanvas,
      getLayerExportPayload,
      getLayerTextPayload,
      enableDrawMode,
      disableDrawMode,
      addText,
      addTextLayer,
      addShape,
      addVectorObject,
      replaceVectorObject,
      updateVectorStyle,
      getVectorLayerStyle,
      getShapeVectorPath,
      getActiveVectorBounds,
      getBakedVectorPath,
      rasterizeLayerToImage,
      approveLayerForPaint,
      cancelLayerForPaint,
      setPenSettings,
      cancelPenPath,
      finalizePenPath,
      enterPathEditMode,
      exitPathEditMode,
      setPathEditSelectedAnchor,
      setPathEditAnchorType,
      getPathEditSelectedAnchorType,
      refreshPathEditOverlay,
      updateActiveShape,
      getActiveShapeSettings,
      updateActiveText,
      getActiveTextSettings,
      setTextPathBinding,
      deleteSelected,
      setZoom,
      fitToContainer,
      zoomIn,
      zoomOut,
      zoomToActual,
      setSpaceHeld,
      setSnapConfig,
      subscribeToSnapMatch,
      subscribeToSmartSnapMatch,
      setSmartSnapEnabled,
      setSmartSnapCtrlDisabled,
      getLayerBBoxes,
      applyLayerOffsets,
      rotateCanvas,
      flipCanvas,
      setCanvasSize,
      setCanvasFillColor,
      trimLayer,
      mergeLayers,
      exportToDataURL,
      exportToBlob,
      exportToDataURLExcludingOverlays,
      toJSON,
      loadFromJSON,
      setTool,
      setSliceCallbacks,
      syncSliceOverlays,
      applyCrop,
      applyActiveCrop,
      selectAll,
      nudgeActiveObject,
      clearMarquee,
      growMarqueeBy,
      canGrowMarquee,
      setSelectionFromMask,
      getSelectionAsMaskCanvas,
      enterQuickMask,
      exitQuickMask,
      getQuickMaskCanvas,
      discardQuickMask,
      applyLayerMaskFromCanvas,
      setQuickMaskMode,
      enterTransformMode,
      exitTransformMode,
      applyTransformControls,
      getTransformMatrix,
      setTransformMatrix,
      applyWarpPresetToOverlay,
      invertMarquee,
      setMarqueeShape,
      setForceSquare,
      setMarqueeColor,
      setWandSettings,
      setActiveLayerId,
      setBrushColor,
      updateBrush,
      setViewportTransform,
      setBackgroundLayerId,
      setLayerVisibility,
      applyEffectiveVisibility,
      setLayerOpacity,
      setLayerBlendMode,
      setLayerEffects,
      addLayerMask,
      removeLayerMask,
      setMaskConfig,
      materializeMaskClipPath,
      setMaskVectorPath,
      getRawMaskCanvas,
      setEditTargets,
      setRetouchSettings,
      subscribeToCursorMove,
      setCloneArmed,
      setCloneSource,
      setCursorOverride,
      applyLayerOrder,
      selectLayerObjects,
      selectLayersObjects,
      removeLayerObjects,
      addImageLayer,
      addImageLayerFromCanvas,
      addAdjustmentLayer,
      getViewportTransform,
      removeBackground,
      clearCanvas,
      copySelection,
      cutSelection,
      getClipboard,
      getLastClickPoint,
      getIsCanvasInteracting,
      clearActiveSelection,
      getMarqueeBounds,
      runSmartDeleteOnActiveLayer,
      runOnActiveLayerFullImage,
      runUpscaleOnActiveLayer,
      getActiveLayerSnapshot,
      getSourceCanvasForSampling,
      getActiveAdjustmentTargets,
      paintLeafToCanvas,
      getAdjustmentBackingCanvas,
      runOutpaintOnBackground,
      setAutoSelectLayersInternal,
    ],
  );
}
